/* Clawhammer Recipe AI — recommendation-v2 frontend (guided flow). Vanilla JS, no build. */

// When embedded in the Shopify storefront, the theme sets window.BEER_BUILDER_API to the
// backend origin (Render). Standalone (localhost / direct), same-origin '' is used and
// add-to-cart falls back to a cart permalink instead of the AJAX cart.
const API = window.BEER_BUILDER_API || '';
const ON_STOREFRONT = Boolean(window.BEER_BUILDER_API);

const $ = (id) => document.getElementById(id);
const messagesEl = $('messages');
const inputEl = $('input');
const sendBtn = $('send');
const composer = $('composer');
const styleSelect = $('styleSelect');
const equipSelect = $('equipSelect');
const builtinProfiles = {}; // id -> built-in EquipmentProfile params (from /api/options), for pre-filling the editor
let inStockNames = {};      // category -> [name] from /api/options; the In-stock swap list
let inStockLabels = {};     // name -> supplier label ("Riverbend"), same source, for the picker

const history = [];
// Catalog mode: 'all' (default — full-catalog calculator, no cart) or 'instock' (commerce).
// The toggle sets this; it rides on every /api/chat and /api/calculate payload so the
// backend resolves against the matching catalog. Persisted with the session.
// NOTE: the server still falls back to 'instock' when no mode is sent, so a client
// running cached older JS keeps its original behavior instead of silently losing the cart.
let currentMode = 'all';
let currentRecipe = null; // resolved recipe (for cart)
let currentReport = null; // full report (for adjust)
let pendingImage = null; // { mimeType, data, dataUrl } — photo attached to the next send
let greeted = false; // first design turn done? (picks the longer first-time quip vs the short follow-up ones)
let transcript = []; // visible chat bubbles to persist: [{ role:'user'|'assistant', text, img? }]
let pendingRestore = null; // stashed last-session data, for late selector re-apply (custom equip)
const LS_KEY = 'bb:lastSession:v1'; // storage key for the persisted working session

// ── shared-recipe tabs ───────────────────────────────────────────────
// A brew day sheet's "Open in Beer Builder" button opens this page in a NEW TAB with
// ?shared=<token>. Such a tab keeps its working session in sessionStorage instead of
// localStorage, so opening someone's shared recipe can never overwrite the recipe the
// brewer has in progress in another tab (Kyle, 8/13). The flag is the URL itself, so a
// reload of the shared tab stays scoped — and reads its own stash, not the other tab's.
const SHARED_TOKEN = (() => {
  try { return new URLSearchParams(location.search).get('shared') || ''; } catch (e) { return ''; }
})();
// Reorder QR landing (?bbReorder=<token>&mill=..&packing=..): rebuild the recipe's cart
// with the customer's last configuration and go straight to /cart. Set by the backend's
// /reorder/:token redirect — the controller's "Brew this again?" flow.
const REORDER = (() => {
  try {
    const p = new URLSearchParams(location.search);
    const token = p.get('bbReorder') || '';
    if (!token) return null;
    return {
      token,
      mill: p.get('mill') === 'unmilled' ? 'unmilled' : 'milled',
      packing: p.get('packing') === 'separate' ? 'separate' : 'together',
    };
  } catch (e) { return null; }
})();
// ?open=recipes — the account portal's "Recipes" menu item; handled once acct state is known.
const OPEN_ON_LOAD = (() => {
  try { return new URLSearchParams(location.search).get('open') || ''; } catch (e) { return ''; }
})();
/** Where the working session is stashed; null in private mode (persistence is best-effort). */
function sessionStore() {
  try { return SHARED_TOKEN ? window.sessionStorage : window.localStorage; } catch (e) { return null; }
}

// Brewhouse-efficiency override: brewer data applied over the selected rig's
// mashEfficiency (survey asks from jasonallencascio, samasiegel8, socalripper03 —
// "let me set my efficiency without building a whole custom profile"). Durable across
// visits like the unit prefs; null means "use the rig's own number".
let effOverride = (() => {
  try {
    const v = Number(localStorage.getItem('bb:eff'));
    return v >= 30 && v <= 100 ? v : null;
  } catch { return null; }
})();

// Stable anonymous per-browser id, sent with chat turns so usage stats can count
// distinct brewers (one busy brewer ≠ ten brewers). Not tied to any account.
let anonId = '';
try {
  anonId = localStorage.getItem('bb:uid') || '';
  if (!anonId) {
    anonId = (crypto.randomUUID && crypto.randomUUID()) || (Date.now().toString(16) + Math.random().toString(16).slice(2));
    localStorage.setItem('bb:uid', anonId);
  }
} catch (e) { /* private mode — stats just won't attribute this browser */ }

// ── display units ────────────────────────────────────────────────────
// Per-category DISPLAY units: water gal/L, hops oz/g, grain lb/kg, temp °F/°C. Purely
// cosmetic — the engine, every server payload, and the cart stay in gallons/lb/oz/°F
// (ingredients are SOLD in lb and oz), so recipes stay deterministic and purchases
// unambiguous. Preference is per-browser (localStorage), applied wherever amounts
// render: the recipe panel, the process line, the detail editor, and the printed sheets.
const GAL_L = 3.785411784, LB_KG = 0.45359237, OZ_G = 28.349523;
// `salt` flips water additions g/ml ↔ teaspoons (display only, storage stays g/ml). It
// deliberately sits outside the gal/L master switch — teaspoons aren't metric-vs-imperial,
// they're a measuring-spoon convenience either kind of brewer may want.
const displayUnits = { water: 'gal', hops: 'oz', grain: 'lb', temp: 'f', salt: 'g' };
try {
  const saved = JSON.parse(localStorage.getItem('bb:units') || '{}');
  if (saved.water === 'l') displayUnits.water = 'l';
  if (saved.hops === 'g') displayUnits.hops = 'g';
  if (saved.grain === 'kg') displayUnits.grain = 'kg';
  if (saved.temp === 'c') displayUnits.temp = 'c';
  if (saved.salt === 'tsp') displayUnits.salt = 'tsp';
} catch (e) { /* imperial defaults */ }
function fmtGrain(lb) {
  if (displayUnits.grain === 'lb') {
    // Quarter-pound amounts keep the plain decimal brewers already read here ("1.25 lb").
    // Off-quarter amounts only exist in All-Ingredients mode, where grain steps by the
    // ounce — and "0.1875 lb" is noise where "3 oz" is the number in the brewer's head.
    if (Math.abs(lb * 4 - Math.round(lb * 4)) > 1e-6) {
      const totalOz = Math.round(lb * 16);
      const whole = Math.floor(totalOz / 16);
      const oz = totalOz % 16;
      if (!whole) return `${oz} oz`;
      return oz ? `${whole} lb ${oz} oz` : `${whole} lb`;
    }
    return `${lb} lb`;
  }
  const g = lb * LB_KG * 1000;
  return g < 1000 ? `${Math.round(g)} g` : `${(lb * LB_KG).toFixed(2)} kg`;
}
function fmtHop(oz) {
  return displayUnits.hops === 'oz' ? `${oz} oz` : `${Math.round(oz * OZ_G)} g`;
}
// Pooled stock is counted in whole ounces server-side; show it the way grain is sold
// ("5 lb 12 oz", never "5.75 lb"), or in grams/kg when the brewer reads metric.
function fmtStockOz(oz) {
  const total = Math.round(oz);
  if (displayUnits.grain !== 'lb') return fmtGrain(total / 16);
  const whole = Math.floor(total / 16);
  const rem = total % 16;
  if (!whole) return `${rem} oz`;
  return rem ? `${whole} lb ${rem} oz` : `${whole} lb`;
}
function fmtVol(gal) {
  return displayUnits.water === 'gal' ? `${gal} gal` : `${(gal * GAL_L).toFixed(1)} L`;
}
// Temperatures render to 0.1°C so the strike figure keeps its precision; whole-degree
// °F values come out as the round °C brewers expect (152 → 66.7, 185 → 85).
const fToC = (f) => (f - 32) / 1.8;
const cToF = (c) => c * 1.8 + 32;
function fmtTemp(f) {
  return displayUnits.temp === 'c' ? `${Math.round(fToC(f) * 10) / 10}°C` : `${f}°F`;
}
/**
 * The grain grid, in pounds. It's a SALES constraint — grain ships in 4 oz tiers — so it
 * only binds In-Stock mode. All-Ingredients mode is a pure calculator with no cart, so it
 * works in 1 oz steps, which matters most for small specialty additions where 4 oz is a
 * big fraction of the charge. Mirrors GRAIN_STEP_* in src/calc/ingredients.ts; the engine
 * re-snaps on the same grid, so an edit always lands where the panel says it will.
 */
const GRAIN_STEP_INSTOCK_LB = 0.25;
const GRAIN_STEP_ALL_LB = 0.0625;
const grainStepLb = () => (currentMode === 'all' ? GRAIN_STEP_ALL_LB : GRAIN_STEP_INSTOCK_LB);
const snapToStep = (v, step) => Math.round(Math.round(v / step) * step * 10000) / 10000;

/**
 * Detail-editor amount spec in the current display unit, with converters back to
 * lb/oz. Metric grain entries round onto the same grids the engine uses, so an edited
 * amount always lands on a weight the engine will keep (and, in In-Stock mode, one we
 * sell). Hops have no grid in the engine, so grams convert at 0.01 oz, which round-trips
 * every whole gram. They used to snap to the quarter ounce: anything from 4 to 10 g came
 * back as 7 g, and the edit looked saved but never changed (bug reports #49, #46).
 */
function detailAmountSpec(kind) {
  const id = (v) => v;
  if (kind === 'yeast') return { step: 1, min: 1, unit: 'pkg', toDisplay: id, fromDisplay: id };
  if (kind === 'hop') {
    return displayUnits.hops === 'g'
      ? { step: 1, min: 0, unit: 'g', toDisplay: (oz) => Math.round(oz * OZ_G), fromDisplay: (g) => Math.round((g / OZ_G) * 100) / 100 }
      : { step: 0.25, min: 0, unit: 'oz', toDisplay: id, fromDisplay: id };
  }
  const step = grainStepLb();
  // 1 oz ≈ 0.028 kg, so the metric step tightens with the grid rather than staying at 0.05.
  const kgStep = step < GRAIN_STEP_INSTOCK_LB ? 0.02 : 0.05;
  return displayUnits.grain === 'kg'
    ? { step: kgStep, min: kgStep, unit: 'kg', toDisplay: (lb) => Math.round(lb * LB_KG * 100) / 100, fromDisplay: (kg) => snapToStep(kg / LB_KG, step) }
    : { step, min: step, unit: 'lb', toDisplay: id, fromDisplay: id };
}

// Heartbeat so the status dashboard can show who's on the builder right now. Only
// while the tab is actually visible, so a forgotten background tab isn't counted as
// a live brewer. Fire-and-forget: a failed ping is never surfaced.
if (anonId) {
  const ping = () => {
    if (document.visibilityState !== 'visible') return;
    fetch(API + '/api/presence', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: anonId }),
      keepalive: true,
    }).catch(() => {});
  };
  ping();
  setInterval(ping, 60000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') ping(); });
}
// The shipped hint copy says "drop in a photo"; PDFs work too now (JS-side so the
// Customize-managed section liquid stays untouched). Must run before HINT_HTML is captured.
(() => {
  const intro = messagesEl.querySelector('.hint p');
  if (intro && /drop in a photo</.test(intro.innerHTML)) intro.innerHTML = intro.innerHTML.replace('drop in a photo<', 'drop in a photo or PDF<');
})();
const HINT_HTML = messagesEl.innerHTML; // captured before the hint is ever removed (for Start over)

// ── banners: pint-glass promo (dismissible) + report a problem ────────
// Both are injected rather than added to the theme's Liquid: the section markup is
// Customize-managed, and the builder's own JS is the only place that knows whether the
// promo is even running.
const PROMO_DISMISS_KEY = 'bb:glassPromoDismissed';
const PROMO_DISMISS_MS = 24 * 60 * 60 * 1000;

/** Was the promo closed within the last 24 hours? */
function glassPromoDismissed() {
  try {
    const t = Number(localStorage.getItem(PROMO_DISMISS_KEY) || 0);
    // A clock that has moved backwards (or a junk value) must not hide the banner forever.
    return t > 0 && Date.now() - t < PROMO_DISMISS_MS;
  } catch { return false; }
}

/** Add the × to the promo banner. Closing hides it for 24 hours, then it returns. */
function injectPromoClose() {
  const promo = $('glassPromo');
  if (!promo || promo.querySelector('.promo__close')) return;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'promo__close';
  btn.id = 'glassPromoClose';
  btn.setAttribute('aria-label', 'Dismiss this notice for today');
  btn.title = 'Dismiss for today';
  btn.innerHTML = '&times;';
  btn.addEventListener('click', () => {
    try { localStorage.setItem(PROMO_DISMISS_KEY, String(Date.now())); } catch { /* private mode: closes for this view only */ }
    promo.hidden = true;
    if (typeof sizeApp === 'function') sizeApp(); // the app moves back up
    if (typeof fitApp === 'function') fitApp();
  });
  promo.appendChild(btn);
}

/**
 * The "report a problem" strip, directly under the promo banner. Always present — a broken
 * builder is exactly when someone needs it, and that has nothing to do with the giveaway.
 * The page is served by the backend through the App Proxy, so on the storefront it is a
 * same-origin link; standalone it points at the local route.
 */
function injectReportBanner() {
  const panes = document.querySelector('#beer-builder-root .panes') || document.querySelector('.panes');
  if (!panes) return;
  let bar = $('reportBanner');
  if (!bar) {
    bar = document.createElement('div');
    bar.className = 'reportbar';
    bar.id = 'reportBanner';
    const href = ON_STOREFRONT ? '/apps/beer-builder/report' : '/report';
    bar.innerHTML = '<span class="reportbar__icon" aria-hidden="true">&#128027;</span>'
      + '<span>Something not working right? <a id="reportLink" href="' + href + '">Report a problem</a></span>';
  }
  // Sits directly under the promo when that's showing, and just above the panes otherwise.
  // Re-run safely: the promo is revealed asynchronously (after /api/options answers), so
  // this is called again then to drop the strip back into place beneath it.
  const promo = $('glassPromo');
  if (promo && !promo.hidden) { if (bar.previousElementSibling !== promo) promo.after(bar); }
  else if (bar.nextElementSibling !== panes) panes.parentNode.insertBefore(bar, panes);
  if (typeof sizeApp === 'function') sizeApp();
}

// ── boot: options ────────────────────────────────────────────────────
fetch(API + '/api/options')
  .then((r) => r.json())
  .then((o) => {
    // Pint-glass giveaway banner (server-gated; absent unless the promo is live)
    if (o.glassPromo && o.glassPromo.active && !glassPromoDismissed()) {
      $('glassPromoText').innerHTML =
        'Free mason jar pint glass: build a $40+ ingredient order here, take a 2-minute survey after checkout, and the glass ships in your box. First ' +
        o.glassPromo.total + ' surveys only, ' +
        o.glassPromo.remaining + ' left. <a href="/pages/pint-glass-survey-terms" target="_blank" rel="noopener">Details</a>';
      $('glassPromo').hidden = false;
      injectPromoClose();
      // The banner sits above .app, so revealing it shifts the app down — re-measure.
      if (typeof sizeApp === 'function') sizeApp();
    }
    // Re-place the report strip: it was added at boot, and if the promo just appeared it
    // belongs underneath it.
    injectReportBanner();
    for (const s of o.styles) {
      const opt = document.createElement('option');
      opt.value = s.name;
      opt.textContent = s.name;
      styleSelect.appendChild(opt);
    }
    for (const e of o.equipment) {
      const opt = document.createElement('option');
      opt.value = e.id;
      opt.textContent = e.name;
      equipSelect.appendChild(opt);
      if (e.profile) builtinProfiles[e.id] = e.profile;
    }
    // Names only (no specs) — enough to offer alternatives in In-stock mode — plus the
    // supplier label per name, which the picker shows and searches like the panel does.
    inStockNames = o.inStock || {};
    inStockLabels = o.inStockLabels || {};
  })
  .catch(() => {})
  // Restore the last session only after the style/equipment dropdowns exist, so the
  // saved selections can be re-applied.
  .finally(() => {
    let restored = false;
    try { restored = restoreSession(); } catch {}
    // Reorder landing: straight to the cart. On any failure the flow falls back to
    // opening the shared recipe in the builder so the customer is never stranded.
    if (REORDER) { runReorderFlow(); return; }
    // Opened from a share link: fetch that recipe into the panel. A RELOAD of this tab
    // restores whatever the brewer has done to it since (restoreSession above, reading
    // this tab's own sessionStorage stash) rather than re-fetching over their edits.
    if (SHARED_TOKEN && !restored) openSharedRecipe(SHARED_TOKEN);
    // A returning brewer keeps their rig: when neither the session nor anything else
    // picked one, fall back to the last explicit pick. Custom-profile ids resolve later,
    // in loadProfilesIntoSelect, once their options exist.
    const last = lastEquip();
    if (!equipSelect.value && last && [...equipSelect.options].some((o) => o.value === last)) {
      equipSelect.value = last;
    }
    if (typeof syncEffField === 'function') syncEffField();
  });

// ── chat ─────────────────────────────────────────────────────────────
// The AI answers in markdown — render a safe subset (headings, bold/italic, lists,
// rules, inline code) instead of showing raw ###/** markers. Everything is
// HTML-escaped first; only tags we emit ourselves reach innerHTML.
function mdInline(s) {
  return s
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s).,;:!?]|$)/g, '$1<em>$2</em>');
}
function mdToHtml(text) {
  const esc = String(text ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const out = [];
  let list = null; // 'ul' | 'ol'
  const closeList = () => { if (list) { out.push(`</${list}>`); list = null; } };
  for (const raw of esc.split('\n')) {
    const line = raw.trimEnd();
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    const ul = line.match(/^\s*[*-]\s+(.*)$/);
    const ol = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (h) {
      closeList();
      const lvl = Math.min(h[1].length + 2, 6); // # → h3 … capped at h6
      out.push(`<h${lvl}>${mdInline(h[2])}</h${lvl}>`);
    } else if (/^\s*(-{3,}|\*{3,})\s*$/.test(line)) {
      closeList(); out.push('<hr>');
    } else if (ul) {
      if (list !== 'ul') { closeList(); out.push('<ul>'); list = 'ul'; }
      out.push(`<li>${mdInline(ul[1])}</li>`);
    } else if (ol) {
      if (list !== 'ol') { closeList(); out.push('<ol>'); list = 'ol'; }
      out.push(`<li>${mdInline(ol[1])}</li>`);
    } else if (!line.trim()) {
      closeList();
    } else {
      closeList(); out.push(`<p>${mdInline(line)}</p>`);
    }
  }
  closeList();
  return out.join('');
}

function addMessage(role, text = '') {
  const el = document.createElement('div');
  el.className = `msg ${role}`;
  if (role === 'assistant') el.innerHTML = mdToHtml(text);
  else el.textContent = text;
  messagesEl.appendChild(el);
  messagesEl.scrollTop = messagesEl.scrollHeight;
  return el;
}

function setBusy(busy) {
  sendBtn.disabled = busy;
  inputEl.disabled = busy;
  if (!busy) inputEl.focus();
}

function addNote(text, beforeEl) {
  const el = document.createElement('div');
  el.className = 'msg note';
  el.textContent = text;
  if (beforeEl && beforeEl.parentNode === messagesEl) messagesEl.insertBefore(el, beforeEl);
  else messagesEl.appendChild(el);
  messagesEl.scrollTop = messagesEl.scrollHeight;
  return el;
}

// After a logged-out brewer's first completed exchange, drop one chat note saying what
// signing in unlocks. sessionStorage caps it at once per browser session; signed-in
// brewers (and pages where accounts are off) never see it.
function maybeAddSigninNote() {
  if (!acct.enabled || acct.loggedIn) return;
  try { if (sessionStorage.getItem('bb:loginNote')) return; } catch (e) {}
  try { sessionStorage.setItem('bb:loginNote', '1'); } catch (e) {}
  const el = document.createElement('div');
  el.className = 'msg note';
  el.append('💡 ', signinAnchor(), ' to save recipes, customize your equipment, and add your own ingredients.');
  messagesEl.appendChild(el);
  messagesEl.scrollTop = messagesEl.scrollHeight;
}

/**
 * Logged out, the account buttons stay VISIBLE but locked. Hiding them meant the
 * account had nothing to advertise: a brewer never saw what signing in would give
 * them, so the only pitch was one easily-missed chat note. Locked buttons keep their
 * click on purpose, because the click is what does the explaining.
 */
function setAccountLocked(locked) {
  const rec = $('myRecipesBtn');
  if (rec) {
    rec.hidden = false;
    rec.classList.toggle('locked', locked);
    rec.setAttribute('aria-disabled', locked ? 'true' : 'false');
    rec.title = locked ? 'Sign in to save recipes and open them on any device' : '';
  }
  const ing = $('myIngredientsBtn');
  if (ing) {
    ing.hidden = false;
    ing.classList.toggle('locked', locked);
    ing.setAttribute('aria-disabled', locked ? 'true' : 'false');
    ing.title = locked
      ? 'Sign in to add your own ingredients'
      : 'Add or edit your own ingredients (All ingredients mode)';
  }
  // Equipment and Import wear the same padlock in the ☰ menu (Kyle, 8/8) so the
  // signed-out menu reads as one consistent list. Their clicks stay live: the
  // equipment editor carries its own session-only note, and the migrate page opens
  // with its sign-in pitch.
  const gear = $('equipGearBtn');
  if (gear) {
    gear.classList.toggle('locked', locked);
    gear.title = locked ? 'Sign in to save equipment profiles to your account' : 'Equipment settings';
  }
  const mig = $('migrateBtn');
  if (mig) {
    mig.classList.toggle('locked', locked);
    mig.title = locked
      ? 'Sign in to import your recipes from Brewfather'
      : 'Import your recipes from Brewfather (BeerSmith coming soon)';
  }
}

/**
 * A locked button's explanation, as a centered pop-up (Kyle 2026-07-29: a notice in the
 * chat was too easy to miss). Same overlay pattern as the equipment/ingredients modals;
 * the Sign in inside is the gradient CTA and opens in a new tab so the recipe stays put.
 */
function showSigninPopup(text) {
  let el = $('signinModal');
  if (!el) {
    el = document.createElement('div');
    el.className = 'modal';
    el.id = 'signinModal';
    el.innerHTML = `
      <div class="modal__scrim" id="signinScrim"></div>
      <div class="modal__panel modal__panel--signin">
        <div class="modal__head">
          <h3>Sign in to unlock this</h3>
          <button id="signinClose" type="button" class="icon-btn" title="Close">×</button>
        </div>
        <div class="modal__body">
          <p id="signinModalText"></p>
          <div class="signin-modal__actions">
            <button id="signinLater" type="button" class="account__btn">Not now</button>
          </div>
        </div>
      </div>`;
    // Inside the root, like the other injected modals, so the storefront's
    // #beer-builder-root-scoped styles apply (.modal is position:fixed, so DOM
    // placement doesn't change where it renders).
    (document.getElementById('beer-builder-root') || document.body).appendChild(el);
    const close = () => { el.hidden = true; };
    $('signinScrim').addEventListener('click', close);
    $('signinClose').addEventListener('click', close);
    $('signinLater').addEventListener('click', close);
    const cta = signinAnchor('Sign in — it’s free');
    cta.className = 'signin-cta';
    $('signinLater').before(cta);
  }
  $('signinModalText').textContent = text + ' Signing in is free and only takes a moment.';
  el.hidden = false;
}

/**
 * A "Sign in" link, opened in a new tab so the builder (and the in-progress recipe)
 * stays put — the hosted-accounts flow drops return_url, so a same-tab trip would
 * strand the brewer on their account page. The click stamp lets refreshAccount()
 * offer the store-session reset if the round trip doesn't take.
 */
function signinAnchor(label = 'Sign in') {
  const a = document.createElement('a');
  a.href = '/customer_authentication/redirect?locale=en';
  a.target = '_blank';
  a.rel = 'noopener';
  a.textContent = label;
  a.addEventListener('click', () => {
    try { sessionStorage.setItem('bb:signinTry', String(Date.now())); } catch (e) {}
  });
  return a;
}

// Animated "thinking" dots inside an assistant bubble, with optional lead-in text.
// Purely a placeholder: the first streamed text overwrites innerHTML (dots included),
// and clearTyping() sweeps up if the stream ends without any text.
function setTyping(el, text) {
  const dots = '<span class="typing" aria-label="working"><span></span><span></span><span></span></span>';
  el.innerHTML = (text ? text + ' ' : '') + dots;
  messagesEl.scrollTop = messagesEl.scrollHeight;
}

function clearTyping(el) {
  const dots = el.querySelector('.typing');
  if (dots) dots.remove();
  if (!el.textContent.trim() && !el.children.length) el.remove();
}

// ── image attach (drop / paste / 📷 button) ──────────────────────────
// Downscale + re-encode client-side so a phone photo doesn't ship 8MB of base64.
// A PDF rides to the model as-is (Gemini reads PDF pages natively), capped so the
// base64 stays inside the server's body limit. Images are resized to a JPEG below.
const MAX_PDF_BYTES = 6 * 1024 * 1024;
const isPdfFile = (file) => !!file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name || ''));
// Placeholder thumb for a PDF attachment (no page render on the client).
const PDF_THUMB = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" rx="7" fill="#e8ebee"/>' +
  '<path d="M12 8h11l7 7v17H12z" fill="#fff" stroke="#8a929b"/><path d="M23 8v7h7" fill="none" stroke="#8a929b"/>' +
  '<text x="20" y="29" font-size="8" font-family="sans-serif" font-weight="700" text-anchor="middle" fill="#c8402e">PDF</text></svg>');

async function fileToAttachment(file) {
  if (!file) return null;
  if (isPdfFile(file)) {
    if (file.size > MAX_PDF_BYTES) throw new Error('That PDF is over 6 MB. Try a smaller export or a photo of the page.');
    const pdfUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(new Error('Could not read that file.'));
      r.readAsDataURL(file);
    });
    return { mimeType: 'application/pdf', data: String(pdfUrl).split(',')[1], dataUrl: PDF_THUMB, name: file.name || 'recipe.pdf' };
  }
  if (!file.type || !file.type.startsWith('image/')) return null;
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Could not read that file.'));
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('That file isn’t a readable image.'));
    i.src = dataUrl;
  });
  const MAX = 1280;
  const scale = Math.min(1, MAX / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.width * scale));
  canvas.height = Math.max(1, Math.round(img.height * scale));
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  const jpeg = canvas.toDataURL('image/jpeg', 0.85);
  return { mimeType: 'image/jpeg', data: jpeg.split(',')[1], dataUrl: jpeg };
}

const ATTACH_LABEL_PHOTO = 'Photo attached. A beer gets identified and researched; a written recipe gets transcribed and rebuilt.';
function setPendingImage(att) {
  pendingImage = att;
  const box = $('attachPreview');
  if (att) {
    $('attachThumb').src = att.dataUrl;
    $('attachThumb').alt = att.name ? 'attached PDF' : 'attached photo';
    const label = box.querySelector('.attach-preview__label');
    if (label) label.textContent = att.name ? `${att.name} attached. The recipe in it gets transcribed and rebuilt.` : ATTACH_LABEL_PHOTO;
    box.hidden = false;
    inputEl.focus();
  } else {
    box.hidden = true;
  }
}

async function attachFile(file) {
  try {
    const att = await fileToAttachment(file);
    if (att) setPendingImage(att);
    else if (file) addNote('⚠ That file type isn’t supported. Drop a photo (JPEG, PNG, WebP) or a PDF.');
  } catch (err) {
    addNote(`⚠ ${err.message}`);
  }
}

// The section markup ships accept="image/*" and photo-only copy; widen both here so
// the live theme's Customize-managed liquid stays untouched.
(() => {
  const fi = $('fileInput');
  if (fi) fi.setAttribute('accept', 'image/*,application/pdf,.pdf');
  const btn = $('attachBtn');
  if (btn) btn.title = 'Add a photo or PDF: a beer to clone or a recipe to rebuild';
  const hint = $('dropHint');
  if (hint) hint.textContent = '📷 Drop a photo or PDF: a beer to clone, or a recipe to rebuild';
})();

$('attachBtn').addEventListener('click', () => $('fileInput').click());
$('fileInput').addEventListener('change', (e) => {
  attachFile(e.target.files && e.target.files[0]);
  e.target.value = '';
});
$('attachRemove').addEventListener('click', () => setPendingImage(null));

// drag & drop anywhere on the chat pane
const chatPane = document.querySelector('.chat');
let dragDepth = 0;
chatPane.addEventListener('dragenter', (e) => {
  e.preventDefault();
  dragDepth++;
  $('dropHint').hidden = false;
  chatPane.classList.add('dragover');
});
chatPane.addEventListener('dragover', (e) => e.preventDefault());
chatPane.addEventListener('dragleave', () => {
  if (--dragDepth <= 0) {
    dragDepth = 0;
    $('dropHint').hidden = true;
    chatPane.classList.remove('dragover');
  }
});
chatPane.addEventListener('drop', (e) => {
  e.preventDefault();
  dragDepth = 0;
  $('dropHint').hidden = true;
  chatPane.classList.remove('dragover');
  const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
  attachFile(file);
});

// paste an image (e.g. screenshot) into the chat
document.addEventListener('paste', (e) => {
  const items = e.clipboardData && e.clipboardData.items;
  if (!items) return;
  for (const item of items) {
    if (item.kind === 'file' && (item.type.startsWith('image/') || item.type === 'application/pdf')) {
      e.preventDefault();
      attachFile(item.getAsFile());
      return;
    }
  }
});

/** Ask the server to intuit the style from the brief; pre-select the dropdown. Returns the name or null. */
async function autoDetectStyle(brief) {
  try {
    const res = await fetch(API + '/api/detect-style', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: brief, uid: anonId || undefined }),
    });
    const data = await res.json();
    if (data.style && [...styleSelect.options].some((o) => o.value === data.style)) {
      styleSelect.value = data.style;
      return data.style;
    }
  } catch {}
  return null;
}

/**
 * The model's copy of the recipe lives inside `history` (never in `transcript`), and it is
 * only refreshed on a chat turn that produced a recipe. Manual panel edits — a version
 * swap, an amount change, add/remove, a mode toggle, a convert — change `currentRecipe`
 * without touching history. Without this the next chat turn rebuilds from a stale recipe
 * and silently throws the brewer's edits away: swap a malt, ask for anything, and the malt
 * reverts to the AI's original pick.
 *
 * Syncing at SEND time rather than in each edit handler means a future edit path can't
 * forget to do it.
 */
let modelRecipeJson = ''; // the recipe JSON the model has actually been shown

/**
 * The model sometimes parrots that bookkeeping block into a streamed reply (a user
 * caught "[Current recipe state …]" + JSON in their chat, 2026-07-29). The block is
 * model-food, never chat — so every rendered or stored assistant text passes through
 * this cut, whatever the model does. Also trims a half-streamed marker prefix at the
 * buffer's end (so it can't flash mid-stream) and the dangling code fence an echo
 * usually rides in on.
 */
const STATE_MARKER = '[Current recipe state';
function stripStateEcho(text) {
  const i = text.indexOf(STATE_MARKER);
  let out = i === -1 ? text : text.slice(0, i);
  for (let n = Math.min(STATE_MARKER.length - 1, out.length); n >= 4; n--) {
    if (out.endsWith(STATE_MARKER.slice(0, n))) { out = out.slice(0, out.length - n); break; }
  }
  return out.replace(/\n?```(json)?\s*$/, '').trimEnd();
}

/**
 * Refresh the state the next request will carry. History is NEVER touched here: the
 * panel recipe goes to the server as its own `recipeState` field, and the SERVER puts
 * it on the newest user turn. It used to be pushed into history as assistant turns,
 * which taught the model the block was part of its own reply format — and it
 * eventually parroted it, JSON and all, into a customer's visible chat (2026-07-29).
 */
function syncRecipeToModel() {
  modelRecipeJson = currentRecipe ? JSON.stringify(currentRecipe) : '';
}

async function send(text) {
  const brief = (text || '').trim();
  const image = pendingImage; // attached photo (if any): a beer to clone or a recipe to rebuild; the server reads it
  // Nothing to act on: no text, no photo, and no style to design from.
  if (!brief && !image && !styleSelect.value) return;

  // Echo the message and put up a live typing indicator BEFORE any network wait
  // (style auto-detect included) so the chat visibly reacts the instant Send is hit.
  $('hint')?.remove();
  const userMsg = brief || (image ? (image.name ? `Build from this PDF (${image.name}).` : 'Build from this photo.') : `Design a ${styleSelect.value}.`);
  const userEl = addMessage('user', userMsg);
  if (image) {
    const thumb = document.createElement('img');
    thumb.src = image.dataUrl;
    thumb.className = 'msg-img';
    thumb.alt = image.name ? 'attached PDF' : 'attached photo';
    userEl.prepend(thumb);
    setPendingImage(null);
  }
  // Fold any panel edits into the model's view BEFORE the user's turn, so the request is
  // answered against the recipe actually on screen.
  syncRecipeToModel();
  const histEntry = { role: 'user', content: userMsg };
  if (image) histEntry.images = [{ mimeType: image.mimeType, data: image.data }];
  history.push(histEntry);
  transcript.push({ role: 'user', text: userMsg, img: !!image });
  setBusy(true);

  const assistantEl = addMessage('assistant', '');
  setTyping(assistantEl, '');

  // 1) Intuit the style from the request if none is selected. A match signals the brewer
  //    wants a recipe of that style; no match usually means a general question.
  let autoStyle = null;
  if (!styleSelect.value && brief && !image) autoStyle = await autoDetectStyle(brief);
  if (autoStyle) addNote(`📌 Note: I auto-selected "${autoStyle}" from your request. Change it above if that's not what you're after.`, assistantEl);

  // 2) Design intent = a style (picked or auto-detected) or an attached photo. A bare
  //    question with no style is NOT gated — the brewmaster can answer questions about
  //    in-stock ingredients, the selected equipment profile, or brewing process before
  //    any style is chosen. We only require an equipment profile once the brewer is
  //    actually designing, since the numbers depend on it.
  const designIntent = !!styleSelect.value || !!image;
  if (designIntent && !equipSelect.value) {
    assistantEl.textContent = 'Pick an equipment profile too: it sets the efficiency and volumes the numbers depend on. Then send again.';
    setBusy(false);
    equipSelect.focus();
    persistSession();
    return;
  }

  let assistantText = '';
  let toolEl = null;
  let recipeThisTurn = false;

  // Design turns get a casual "grab a beer" line up front EVERY time, with the dots
  // still bouncing after it, so a long build never looks frozen. It's a placeholder
  // only: the first streamed text replaces it and it's never pushed into history.
  // Plain questions keep just the dots.
  if (designIntent) {
    const quips = greeted
      ? [
          'On it. Give me about 20 seconds to build this one. 🍺',
          'Got it. Crunching the numbers now, hang tight. 🍺',
          'Working on it. This should only take a few sips. 🍺',
        ]
      : [
          'Right on. Let me crack a beer and take a few sips before I build this. Give me 20 seconds or so. 🍺',
          'Cool. Maybe grab a beer while I sort this out. Should take me less than a minute. 🍺',
          "On it. I'll pour one and think this through, about 20 seconds. 🍺",
        ];
    greeted = true;
    setTyping(assistantEl, quips[Math.floor(Math.random() * quips.length)]);
  }

  try {
    const res = await fetch(API + '/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        style: styleSelect.value,
        equipmentProfile: equipmentPayload(),
        batchSize: Number($('batchSize').value) || 5,
        messages: history,
        // The panel recipe, fresh every turn; the server injects it onto the newest
        // user turn. Kept out of `messages` so it can never masquerade as model output.
        recipeState: modelRecipeJson || undefined,
        recipeStateName: (currentSavedName || (currentReport && currentReport.recipeName)) || undefined,
        mode: currentMode,
        customIngredients: customIngredientsPayload(),
        uid: anonId || undefined,
        li: !!(acct.enabled && acct.loggedIn),
        // Display preference: when the view is °C the model speaks °C in chat.
        tempUnit: displayUnits.temp,
      }),
    });
    if (!res.ok) {
      // Surface the server's message (e.g. an invalid-equipment-profile field list) rather
      // than a bare status code.
      const body = await res.json().catch(() => null);
      // Out of guest recipes. The server only sets `signin` when signing in genuinely
      // raises the ceiling, so this is a door rather than an error: style it as a note,
      // give it the link, and don't decorate it with a warning triangle.
      if (res.status === 429 && body && body.signin) {
        assistantEl.className = 'msg note';
        assistantEl.textContent = '';
        assistantEl.append(body.error + ' ', signinAnchor());
        messagesEl.scrollTop = messagesEl.scrollHeight;
        return;
      }
      throw new Error((body && body.error) || `Server returned ${res.status}`);
    }
    if (!res.body) throw new Error('Server returned an empty response.');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const handleEvent = (evt) => {
      switch (evt.type) {
        case 'text':
          assistantText += evt.data;
          assistantEl.innerHTML = mdToHtml(stripStateEcho(assistantText));
          messagesEl.scrollTop = messagesEl.scrollHeight;
          break;
        case 'tool':
          if (!toolEl) toolEl = addMessage('tool', '');
          toolEl.textContent = `⚙ ${evt.data}`;
          break;
        case 'recipe': {
          // A chat turn replaces the recipe wholesale, so the Hazy IPA sliders — whose
          // stops describe the PREVIOUS recipe — are dropped before the new one renders.
          // renderRecipe brings the panel back, freshly read, only if what arrived is
          // itself a 21C. (Panel edits don't come through here: they keep their stops.)
          // typeof-guarded like the other cross-block calls: a ReferenceError here would
          // break recipe rendering for the whole turn if a stale cached script loads.
          if (typeof resetHopSliderPane === 'function') resetHopSliderPane();
          // A style settled IN CHAT (the brewer named one, or told the model to pick)
          // must land in the header picker too — the picker is what every later request
          // reads, so leaving it empty meant the next quiet recalc computed styleless
          // and the style check vanished (Kyle's report, 8/11). Synced BEFORE
          // renderRecipe, whose own follow-up recalcs read the picker. Same pattern as
          // the research event above; the report's style name is the bjcp full name,
          // which is exactly the dropdown's option value.
          const arrivedStyle = evt.data && evt.data.style && evt.data.style.style;
          if (arrivedStyle && styleSelect.value !== arrivedStyle) {
            const opt = [...styleSelect.options].find((o) => o.value === arrivedStyle);
            if (opt) {
              const wasEmpty = !styleSelect.value;
              styleSelect.value = opt.value;
              if (wasEmpty) addNote(`🎯 Targeting ${opt.value}.`);
            }
          }
          renderRecipe(evt.data);
          // A chat turn may deliberately change the batch size ("scale it to 3 gallons").
          // The header selector is what every later request reads, so it must follow the
          // recipe that arrived — otherwise the next panel edit, equipment change, or chat
          // turn would quietly revert the recipe to the old volume.
          if (typeof syncBatchHeaderToRecipe === 'function') syncBatchHeaderToRecipe();
          recipeThisTurn = true;
          if (toolEl) { toolEl.remove(); toolEl = null; }
          break;
        }
        case 'research': {
          // The server identified the beer + researched the clone. Persist the notes
          // into this turn's history (and drop the heavy base64) so follow-ups keep
          // the context without re-uploading or re-researching.
          if (evt.data.appended) {
            histEntry.content += evt.data.appended;
            delete histEntry.images;
          }
          const styleKnown = evt.data.style && [...styleSelect.options].some((o) => o.value === evt.data.style);
          if (styleKnown) styleSelect.value = evt.data.style;
          if (evt.data.kind === 'recipe') {
            // A written recipe: the photo supplied ingredients, not a style. The header
            // selection (or the style the brewer typed) is what the rebuild targets.
            const name = evt.data.beerName ? ` (${evt.data.beerName})` : '';
            addNote(styleKnown
              ? `📷 Read the recipe from your photo${name}, keeping ${evt.data.style}.`
              : `📷 Read the recipe from your photo${name}.`);
          } else {
            const beer = evt.data.beerName || 'your beer';
            addNote(styleKnown
              ? `📷 Identified ${beer}, targeting ${evt.data.style}.`
              : `📷 Identified ${beer}, designing a clone from research.`);
          }
          break;
        }
        case 'error':
          assistantText += `\n\n⚠ ${evt.data}`;
          assistantEl.innerHTML = mdToHtml(stripStateEcho(assistantText));
          break;
      }
    };

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const chunk = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const line = chunk.split('\n').find((l) => l.startsWith('data: '));
        if (!line) continue;
        try { handleEvent(JSON.parse(line.slice(6))); } catch {}
      }
    }

    if (assistantText) {
      // An echoed bookkeeping block must not survive into history either: the frontend
      // appends its own canonical copy below, and a second one would compound each turn.
      assistantText = stripStateEcho(assistantText);
      // The assistant turn is stored EXACTLY as spoken — no state block appended. The
      // panel recipe travels as the request's `recipeState` field instead (see
      // syncRecipeToModel), so the model never sees the block as its own output.
      if (recipeThisTurn && currentRecipe) modelRecipeJson = JSON.stringify(currentRecipe);
      history.push({ role: 'assistant', content: assistantText });
      transcript.push({ role: 'assistant', text: assistantText });
      maybeAddSigninNote();
    }
  } catch (err) {
    // A dropped connection is not a server error, and it must not read like one. The
    // recipe is often already BUILT (and paid for) by the time the stream dies on a phone
    // with two bars; the brewer just gets the browser's raw wording — Safari says "Load
    // failed", Chrome "Failed to fetch" — with nothing to click and their prompt gone.
    // Reported 2026-08-05: a Hazy IPA the server finished in 13 s never reached the phone.
    // Say what happened in plain words and offer the way back in.
    const networkDrop = err instanceof TypeError
      || /load failed|failed to fetch|network|connection|networkerror/i.test(err.message || '');
    if (networkDrop) {
      assistantEl.className = 'msg note';
      assistantEl.textContent = '';
      assistantEl.append('The connection dropped before your recipe came back. That is usually a weak signal rather than anything you did. ');
      // A photo turn can't be replayed from here: pendingImage was consumed when the
      // message was sent, so offering a button that silently drops the photo would be
      // worse than asking for it back.
      if (image) {
        assistantEl.append('Attach your photo again and resend.');
        return;
      }
      const again = document.createElement('button');
      again.type = 'button';
      again.className = 'link-btn';
      again.textContent = 'Try again';
      again.addEventListener('click', () => {
        // Roll the failed turn out of BOTH records, then resend, so the retry leaves one
        // clean exchange instead of the same question twice.
        if (history.length && history[history.length - 1].role === 'user') history.pop();
        if (transcript.length && transcript[transcript.length - 1].role === 'user') transcript.pop();
        assistantEl.remove();
        userEl.remove();
        send(userMsg);
      });
      assistantEl.append(again);
      messagesEl.scrollTop = messagesEl.scrollHeight;
    } else {
      assistantEl.innerHTML = mdToHtml((assistantText ? stripStateEcho(assistantText) + '\n\n' : '') + `⚠ ${err.message}`);
    }
  } finally {
    clearTyping(assistantEl);
    setBusy(false);
    persistSession();
    updateStartOver();
  }
}

// ── recipe panel ─────────────────────────────────────────────────────
function metric(k, v, sub, title) {
  return `<div class="metric"${title ? ` title="${escapeHtml(title)}"` : ''}><div class="k">${k}</div><div class="v">${v}${sub ? ` <small>${sub}</small>` : ''}</div></div>`;
}

// ── benchmark range bars + beer-color swatch ─────────────────────────
// SRM → approximate sRGB beer color (standard reference table, 1–40).
const SRM_HEX = ['#FFE699','#FFE699','#FFD878','#FFCA5A','#FFBF42','#FBB123','#F8A600','#F39C00','#EA8F00','#E58500','#DE7C00','#D77200','#CF6900','#CB6200','#C35900','#BB5100','#B54C00','#B04500','#A63E00','#A13700','#9B3200','#952D00','#8E2900','#882300','#821E00','#7B1A00','#771900','#701400','#6A0E00','#660D00','#5E0B00','#5A0A02','#600903','#520907','#4C0505','#470606','#440607','#3F0708','#3B0607','#3A070B','#36080A'];
function beerColor(srm) {
  const i = Math.max(1, Math.min(40, Math.round(srm || 1)));
  return SRM_HEX[i];
}

// Per-metric: fallback range (used when the style doesn't define one), the rounding step
// for the scale bookends, decimals, unit suffix, and how to format the value.
const BAR_METRICS = [
  { key: 'abv', label: 'ABV', get: (b) => b.abv, fallback: [3, 10], step: 1, dec: 0, suffix: '%', fmtVal: (v) => v.toFixed(1) + '%' },
  { key: 'og', label: 'OG', get: (b) => b.gravity.og, fallback: [1.03, 1.09], step: 0.001, dec: 3, suffix: '', fmtVal: (v) => v.toFixed(3) },
  { key: 'fg', label: 'FG', get: (b) => b.gravity.fg, fallback: [1.005, 1.02], step: 0.001, dec: 3, suffix: '', fmtVal: (v) => v.toFixed(3) },
  { key: 'srm', label: 'SRM', get: (b) => b.srm, fallback: [2, 30], step: 1, dec: 0, suffix: '', fmtVal: (v) => v.toFixed(1), swatch: true },
  { key: 'ibu', label: 'IBU', get: (b) => b.ibu, fallback: [5, 80], step: 1, dec: 0, suffix: '', fmtVal: (v) => String(Math.round(v)) },
  { key: 'bugu', label: 'BU/GU', get: (b) => b.bugu, fallback: [0.3, 1.0], step: 0.01, dec: 2, suffix: '', fmtVal: (v) => v.toFixed(2) },
];
const barPct = (v, min, max) => Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100));
const roundStep = (x, step, dec, dir) => Number(((dir === 'down' ? Math.floor(x / step) : Math.ceil(x / step)) * step).toFixed(dec));

// The bar scale hugs the style's target range (± ~10% padding, snapped to whole-ish
// numbers). If the recipe's value lands outside that window, the value itself becomes the
// bookend so it still shows, pinned at the edge.
function barScale(m, target, value) {
  const lo = target ? target[0] : m.fallback[0];
  const hi = target ? target[1] : m.fallback[1];
  const pad = (hi - lo) * 0.1;
  let min = lo - pad;
  let max = hi + pad;
  if (value != null && !isNaN(value)) {
    if (value < min) min = value;
    if (value > max) max = value;
  }
  min = roundStep(min, m.step, m.dec, 'down');
  max = roundStep(max, m.step, m.dec, 'up');
  if (max <= min) max = roundStep(min + m.step, m.step, m.dec, 'up');
  return [min, max];
}

// Range visualization: one bar per metric scaled to the style's range, the target as a
// shaded band, the recipe's value as a tick. Metrics outside the style range turn red.
function benchmarkBars(report) {
  const b = report.benchmarks;
  const fields = (report.style && report.style.fields) || {};
  return BAR_METRICS.map((m) => {
    const v = m.get(b);
    if (v == null || isNaN(v)) return '';
    const f = fields[m.key];
    const target = f ? f.target : null;
    const [smin, smax] = barScale(m, target, v);
    const p = barPct(v, smin, smax);
    const tickP = Math.max(1.5, Math.min(98.5, p));
    const out = !!f && f.verdict !== 'in range';
    const fmtBound = (x) => (m.suffix ? x + m.suffix : m.dec ? x.toFixed(m.dec) : String(x));
    const band = target
      ? `<span class="bbar__band" style="left:${barPct(target[0], smin, smax)}%;right:${100 - barPct(target[1], smin, smax)}%"></span>`
      : '';
    const tickCls = f ? (out ? 'is-out' : 'is-in') : 'is-neutral';
    const valCls = (p > 65 ? ' bbar__val--right' : '') + (out ? ' is-out' : '');
    const sw = m.swatch ? ` <span class="swatch" style="--c:${beerColor(v)}" title="beer color"></span>` : '';
    // The displayed numbers are the EXACT style-guideline bounds, anchored at the band's
    // edges. The bar keeps its padded/rounded scale for geometry only — labeling that
    // scale made 21C's 6-9% ABV read as "5-10%".
    const minLabel = target
      ? `<span class="bbar__min" style="left:calc(${barPct(target[0], smin, smax)}% + 6px)">${fmtBound(target[0])}</span>`
      : `<span class="bbar__min">${fmtBound(smin)}</span>`;
    const maxLabel = target
      ? `<span class="bbar__max" style="right:calc(${100 - barPct(target[1], smin, smax)}% + 6px)">${fmtBound(target[1])}</span>`
      : `<span class="bbar__max">${fmtBound(smax)}</span>`;
    return (
      `<div class="bbar${out ? ' bbar--out' : ''}">` +
      `<div class="bbar__lab">${m.label}${sw}</div>` +
      `<div class="bbar__track">${band}` +
      minLabel + maxLabel +
      `<span class="bbar__tick ${tickCls}" style="left:${tickP}%"></span>` +
      `<span class="bbar__val${valCls}" style="left:${tickP}%">${m.fmtVal(v)}</span>` +
      `</div></div>`
    );
  }).join('');
}

/**
 * The range end-labels and the value label are all absolutely positioned inside the same
 * track, so once the track is narrow they collide: on a phone the OG row read "1.0" with
 * the value sitting on top of the rest of "1.054", and IBU printed its max twice.
 * Whichever end label the value overlaps gets hidden — the value is the number that
 * matters, and the shaded band still shows where the style range ends.
 * Measured rather than computed from percentages, because label WIDTH depends on the text.
 */
function deconflictBars(scope) {
  for (const bar of (scope || document).querySelectorAll('.bbar')) {
    const val = bar.querySelector('.bbar__val');
    const ends = [...bar.querySelectorAll('.bbar__min, .bbar__max')];
    for (const end of ends) end.style.visibility = ''; // reset first, widths change on resize
    if (!val) continue;
    const v = val.getBoundingClientRect();
    if (!v.width) continue;
    for (const end of ends) {
      const e = end.getBoundingClientRect();
      if (v.left < e.right + 4 && e.left < v.right + 4) end.style.visibility = 'hidden';
    }
    // The end labels sit at the band's edges now, so a narrow style range (FG bands
    // especially) can make them collide with each other — drop the min, keep the max.
    const [mn, mx] = ends;
    if (mn && mx && mn.style.visibility !== 'hidden' && mx.style.visibility !== 'hidden') {
      const a = mn.getBoundingClientRect();
      const b = mx.getBoundingClientRect();
      if (a.width && b.width && a.left < b.right + 4 && b.left < a.right + 4) mn.style.visibility = 'hidden';
    }
  }
}

// Track width changes with the viewport, so the overlap has to be re-tested on resize.
let barDeconflictTimer = null;
window.addEventListener('resize', () => {
  clearTimeout(barDeconflictTimer);
  barDeconflictTimer = setTimeout(() => deconflictBars(), 120);
});

function renderRecipe(report) {
  currentRecipe = report.resolvedRecipe;
  currentReport = report;
  $('recipeEmpty').hidden = true;
  $('recipeContent').hidden = false;
  // Converted recipes carry their own before/after block; everything else hides it.
  renderConvertNote(report);

  const b = report.benchmarks;
  $('recipeTitle').textContent = report.recipeName || (report.style ? report.style.style : 'Your recipe');

  $('metrics').innerHTML = [
    metric('OG', b.gravity.og),
    metric('FG', b.gravity.fg),
    metric('ABV', b.abv, '%'),
    metric('IBU', b.ibu),
    metric('SRM', `${b.srm}<span class="swatch swatch--inline" style="--c:${beerColor(b.srm)}"></span>`),
    metric('BU:GU', b.bugu),
  ].join('');

  const est = new Set(report.resolution.filter((r) => r.status === 'defaulted').map((r) => r.input));
  const oos = new Set(report.outOfStock || []);
  // Pooled-stock shortfall (server-computed on every calculation): the shop stocks the
  // ingredient, but not as much as the recipe wants. Keyed by the CANONICAL name, so it is
  // matched on what the row resolved to, not the typed name.
  const low = new Map((report.lowStock || []).map((l) => [l.ingredient, l.availableOz]));
  const flag = (name, canonical) =>
    (oos.has(name) ? '<span class="oos-flag">out of stock</span>'
      : low.has(canonical) ? `<span class="oos-flag">only ${escapeHtml(fmtStockOz(low.get(canonical)))} in stock</span>`
      : est.has(name) ? '<span class="est-flag">est.</span>' : '');

  const r = report.resolvedRecipe;
  // The recipe carries the name that was TYPED ("Pilsner Malt"); the resolver knows which
  // catalog entry it actually used ("Pilsner (2 Row) Ger"). Show the real entry, because
  // that's what the numbers came from. resolution is in the same order as the recipe
  // arrays, so index-match rather than matching on name (names can repeat).
  const notesFor = (kind) => report.resolution.filter((n) => n.kind === kind);
  const fermNotes = notesFor('fermentable');
  const hopNotes = notesFor('hop');
  const yeastNote = notesFor('yeast')[0];
  const shown = (note, fallback) => escapeHtml(note?.resolvedAs || fallback);

  // The supplier qualifier (e.g. "Riverbend") rides alongside the generic name so the brewer
  // can see which maltster they're getting. Dash form rather than parentheses because several
  // canonicals already carry a parenthetical ("Dark Munich (Aromatic Malt)") and nesting them
  // reads badly. Yeast keeps parentheses — its label is a product code, not a supplier.
  const qualifier = (note) => (note?.label ? ` <span class="muted">- ${escapeHtml(note.label)}</span>` : '');
  // Each fermentable's share of the grain bill by weight — how brewers read a recipe.
  const fermTotal = (r.fermentables || []).reduce((s, f) => s + (f.weight || 0), 0);
  const fermPct = (w) => {
    if (!fermTotal || !w) return '';
    const p = (w / fermTotal) * 100;
    return ` <span class="sub">(${p < 1 ? '&lt;1' : Math.round(p)}%)</span>`;
  };
  $('fermentables').innerHTML = (r.fermentables || [])
    .map((f, i) => `<tr><td>${shown(fermNotes[i], f.name)}${qualifier(fermNotes[i])}${flag(f.name, fermNotes[i]?.resolvedAs || f.name)}</td>`
      + `<td class="amt">${fmtGrain(f.weight)}${fermPct(f.weight)}</td></tr>`)
    .join('');

  // Each hop row opens the hop editor at that addition, where its timing is adjustable.
  // Each addition also shows its share of the total IBU (survey ask, cglance): the
  // engine already breaks bitterness down per addition, keyed by hopSchedule index —
  // the panel just never showed it. Older restored reports may lack the breakdown.
  // The boil length heads the section as a plain row, not a tile (Kyle, 8/20 — it
  // moved here from Process when the section became "Boil & Hops"): label left,
  // editable minutes right, same shape as the hop rows below it.
  const ibuBy = new Map((b.ibuBreakdown || []).map((c) => [c.index, c.ibu]));
  const boilRow = `<tr><td>Boil<div class="sub">hop timings below count down from the end of the boil</div></td>`
    + `<td class="amt"><input id="boilTimeInput" class="inline-num" type="number" step="5" min="0" max="240" inputmode="numeric" value="${r.boilTime ?? 60}"> <span class="sub">min</span></td></tr>`;
  $('hops').innerHTML = boilRow + (r.hopSchedule || [])
    .map((h, i) => `<tr class="ing-row--tap" data-hop="${i}" title="Adjust this addition's timing and amount">`
      + `<td>${shown(hopNotes[i], h.name)}${qualifier(hopNotes[i])}${flag(h.name, hopNotes[i]?.resolvedAs || h.name)}<div class="sub">${escapeHtml(hopWhen(h))}${h.customAlpha != null ? ` · ${h.customAlpha}% AA` : ''}${ibuBy.has(i) ? ` · ${ibuBy.get(i)} IBU` : ''}</div></td>`
      + `<td class="amt">${fmtHop(h.weight)}</td></tr>`)
    .join('');
  $('hops').querySelectorAll('[data-hop]').forEach((tr) =>
    tr.addEventListener('click', () => openDetail('hop', undefined, Number(tr.dataset.hop))));

  const y = r.yeast;
  // Yeast & Fermentation (Kyle, 8/20 — the Process section is gone; its two knobs
  // moved into the sections they belong to, boil into Boil & Hops above and the
  // ferment temp here). The planned temp heads the section as a plain row shaped like
  // the ingredient rows; the yeast entry reads below it. The derived brew-day figures
  // (strike water, strike temp, pre-boil gravity) live on the Brew day steps sheet
  // (Kyle, 8/10). Batch size and the rig are header decisions made before the recipe
  // exists, and attenuation lives on the yeast entry, so none repeat here.
  const degC = displayUnits.temp === 'c';
  const degUnit = degC ? '°C' : '°F';
  // Ferment pre-fills from the yeast's published range (mid-point) when the brewer
  // hasn't set one (Kyle, 8/10). report.yeastTemp is display data from the resolver;
  // the recipe only stores fermentTemp once the brewer commits a number, so swapping
  // yeast keeps the figure following the new strain until they do. Stored and printed,
  // never modeled (survey asks, markschmitt92 + landon.s.allen). Blank is a valid
  // state: no figure invented.
  const yt = report.yeastTemp || null;
  const fermEff = r.fermentTemp ?? (yt ? yt.suggestedF : null);
  const fermVal = fermEff != null
    ? (degC ? Math.round(fToC(fermEff) * 10) / 10 : fermEff) : '';
  const fermTitle = yt
    ? `Planned fermentation temperature: pre-filled to the middle of this yeast's ${fmtTemp(yt.minF)} to ${fmtTemp(yt.maxF)} range. Edit to override; shown on the brew sheet, not modeled by the calculator`
    : 'Planned fermentation temperature: shown on the brew sheet, not modeled by the calculator';
  const yeastBox = $('yeastBlock');
  if (yeastBox) {
    // Version editing happens behind the section heading, not by clicking the line itself.
    // One table shaped like the other ingredient sections (Kyle, 8/28): the temp row and
    // the yeast row share it, so the standard row divider separates them, and the yeast
    // line carries its pack count on the right like every other amount. The pack figure
    // is the same one the cart buys (yeast.packages, whole packs, at least one).
    const pkgN = y ? Math.max(1, Math.round(y.packages ?? 1)) : 0;
    yeastBox.innerHTML = `<table class="ing"><tr title="${fermTitle}"><td>Est. Fermentation Temperature<div class="sub">planned, on the brew sheet, not modeled</div></td>`
      + `<td class="amt">${degC
        ? `<input id="fermentTempInput" class="inline-num" type="number" step="0.5" min="0" max="38" inputmode="decimal" value="${fermVal}" placeholder="—">`
        : `<input id="fermentTempInput" class="inline-num" type="number" step="1" min="32" max="100" inputmode="numeric" value="${fermVal}" placeholder="—">`}`
      + ` <span class="sub">${degUnit}</span></td></tr>`
      + (y
        ? `<tr><td>${shown(yeastNote, y.name)}${yeastNote?.label ? ` <span class="muted">(${escapeHtml(yeastNote.label)})</span>` : ''}${y.ownSupply ? ' <span class="muted">· your own, not added to cart</span>' : ''}${flag(y.name)}<div class="sub">${y.attenuation != null ? `${y.attenuation}% attenuation` : ''}</div></td>`
          + `<td class="amt">${pkgN} <span class="sub">${pkgN === 1 ? 'Pkg' : 'Pkgs'}</span></td></tr>`
        : '<tr><td class="muted">No yeast selected.</td><td class="amt"></td></tr>')
      + '</table>';
  }
  // The Process section is empty now — hide it in JS so the theme's liquid markup
  // stays untouched. It still anchors the water section's insertion point.
  const procBox = $('process');
  if (procBox) {
    procBox.innerHTML =
      // An imported recipe carries its source app's own water plan; the cross-check
      // line survives the section (fidelity contract) and rides just above Water Chem.
      (r.sourceWater && (r.sourceWater.totalGal || r.sourceWater.mashGal)
        ? `<div class="proc-imported" title="The water plan from the app this recipe was imported from">Imported water plan: <b>${fmtVol(r.sourceWater.totalGal || r.sourceWater.mashGal)}</b>${r.sourceWater.spargeGal ? ` (mash ${fmtVol(r.sourceWater.mashGal || 0)} + sparge ${fmtVol(r.sourceWater.spargeGal)})` : ''}</div>`
        : '');
    const procSec = procBox.closest('.recipe__section');
    if (procSec) {
      procSec.hidden = !procBox.innerHTML;
      const h = procSec.querySelector('h3');
      if (h) h.hidden = true;
    }
  }
  $('boilTimeInput')?.addEventListener('change', (e) => applyBoilTime(e.target.value));
  // Ferment edits in the DISPLAY unit; the recipe always stores °F. Emptying clears it.
  $('fermentTempInput')?.addEventListener('change', (e) => {
    const raw = String(e.target.value).trim();
    applyFermentTemp(raw === '' ? null
      : degC ? Math.round(cToF(Number(raw)) * 10) / 10 : Number(raw));
  });
  // Loads/restores set the header batch input programmatically; keep the metric twin honest.
  if (typeof syncBatchUnitUI === 'function') syncBatchUnitUI();

  // water chemistry — phase 2 (2026-08-08): the water math is live. The section
  // div is created by renderWaterSection rather than in the page markup so the
  // storefront theme's copy of the panel needs no liquid change to grow the feature.
  renderWaterSection(r, report);

  // mash program — rendered into the Grains & Mash section (below the grain rows),
  // created dynamically so the storefront theme's liquid needs no change.
  if (typeof renderMashSection === 'function') renderMashSection(r, report);

  // volume — batch size (editable, scales the recipe) + the calculator's starting
  // water figure. Sits between the benchmark squares and the Grain bill (Kyle, 8/12).
  if (typeof renderVolumeSection === 'function') renderVolumeSection(r, report);

  // equipment — the brew-system picker, back in the panel (Kyle, 8/12; it left with
  // the 8/10 Process restructure). Changing it recomputes the benchmarks on the new
  // profile's efficiency/boil-off/losses; nothing is rescaled.
  if (typeof renderEquipmentSection === 'function') renderEquipmentSection();

  // A recipe that arrives without source water picks up the brewer's saved default
  // (the rig contract applied to water, Kyle 8/10). One quiet recalc attaches it; the
  // re-render carries the source, so this doesn't fire again. typeof-guarded like the
  // other cross-block calls in case a stale cached script loads.
  if (typeof applyDefaultWater === 'function') applyDefaultWater();

  // flavorings
  const flav = r.flavorings || [];
  if (flav.length) {
    $('flavoringSection').hidden = false;
    $('flavorings').innerHTML = flav
      .map((fl) => {
        const amt = fl.amount != null ? `${fl.amount} ${fl.unit || ''}`.trim() : '';
        return `<tr><td>${escapeHtml(fl.name)}${fl.note ? `<div class="sub">${escapeHtml(fl.note)}</div>` : ''}</td><td class="amt">${escapeHtml(amt)}</td></tr>`;
      })
      .join('');
  } else {
    $('flavoringSection').hidden = true;
  }

  // style check + adjust button
  const adjustBtn = $('adjustBtn');
  if (report.style) {
    $('styleSection').hidden = false;
    // The heading names the style being checked against (Kyle, 8/10): "STYLE CHECK -
    // 21C. Hazy IPA". The name rides in a transform-free span so the BJCP code + name
    // keep their own casing under the heading's uppercase styling; set from JS so the
    // theme section needs no liquid edit.
    const styleH3 = document.querySelector('#styleSection h3');
    if (styleH3) styleH3.innerHTML = `Style check <span class="style-check__name">- ${escapeHtml(report.style.style)}</span>`;
    const outOfRange = [];
    Object.entries(report.style.fields).forEach(([k, f]) => {
      if (f.verdict !== 'in range') outOfRange.push(`${k.toUpperCase()} (${f.verdict})`);
    });
    $('styleCompare').innerHTML = benchmarkBars(report);
    deconflictBars($('styleCompare'));
    adjustBtn.hidden = outOfRange.length === 0;
    adjustBtn._outOfRange = outOfRange;
  } else {
    $('styleSection').hidden = true;
    adjustBtn.hidden = true;
  }

  // warnings
  if (report.warnings && report.warnings.length) {
    $('warnSection').hidden = false;
    // Warnings interpolate ingredient names, which the brewer can type — escape.
    $('warnings').innerHTML = report.warnings.map((w) => `<li>${escapeHtml(w)}</li>`).join('');
  } else {
    $('warnSection').hidden = true;
  }

  $('cartResult').hidden = true;
  afterRecipeRender();
  updateStartOver();
  reconcileBatchNote(); // keep the "batch size changed" note in sync with what's shown
}

// ── adjust to style ──────────────────────────────────────────────────
$('adjustBtn').addEventListener('click', () => {
  if (!currentReport?.style) return;
  const out = $('adjustBtn')._outOfRange || [];
  const msg = `This recipe is out of style on ${out.join(', ')}. Adjust it to bring those into the ${currentReport.style.style} range, using only in-stock ingredients, and keep the character. Then recalculate.`;
  send(msg);
});

// ── cart ─────────────────────────────────────────────────────────────

/**
 * The theme sections that render the cart, in the shape Shopify's Cart AJAX API wants.
 *
 * Our theme refreshes its drawer by re-rendering whole sections: it asks /cart/*.js for
 * `sections`, then swaps the returned markup into #ajaxCartContent / #ajaxCartBtn. This
 * mirrors the id list in the theme's own section-ajax-cart.js so we can drive the exact
 * same refresh after OUR add, instead of leaving the drawer showing stale markup.
 */
const THEME_CART_SECTION_TARGETS = ['ajaxCartContent', 'ajaxCartBtn', 'cartContent'];
function themeCartSections() {
  const ids = [];
  for (const id of THEME_CART_SECTION_TARGETS) {
    const sec = document.getElementById(id)?.closest('.shopify-section')?.id?.replace('shopify-section-', '');
    if (sec && !ids.includes(sec)) ids.push(sec);
  }
  return ids;
}

/**
 * Hand the add response to the theme so its drawer and header count re-render.
 *
 * Without this the items really ARE in the cart, but the drawer keeps the markup it was
 * built with at page load, so opening the header cart shows "your cart is empty" and the
 * brewer thinks the button did nothing (Kyle, 2026-07-31). The theme listens on its own
 * pub/sub bus, NOT on DOM events, which is why the CustomEvents below never reached it.
 */
/**
 * Scroll the recipe panel to its very bottom.
 *
 * scrollIntoView({block:'nearest'}) stops as soon as the top of the result box is in view,
 * which left the "View cart" link below the fold: the brewer saw the ingredient list
 * appear, never saw the link, and clicked the header cart instead (Kyle, 2026-07-31). The
 * last line is the whole point, so scroll the pane to its end rather than to the box. On a
 * phone the panes stack and nothing inside scrolls, hence the scrollIntoView fallback.
 */
function scrollPaneToBottom(el) {
  let pane = el?.parentElement;
  while (pane && pane !== document.body) {
    const cs = getComputedStyle(pane);
    if (/auto|scroll/.test(cs.overflowY) && pane.scrollHeight > pane.clientHeight + 2) {
      const bottom = () => pane.scrollHeight - pane.clientHeight;
      pane.scrollTo({ top: bottom(), behavior: 'smooth' });
      // The smooth request is not reliable here: on the live storefront it silently does
      // nothing on this pane, and a reduce-motion browser can drop it too. Landing at the
      // bottom is the whole requirement, so verify and snap. Re-reads scrollHeight because
      // the cart lines may still have been growing when the animation was requested.
      setTimeout(() => { if (pane.scrollTop < bottom() - 4) pane.scrollTop = bottom(); }, 450);
      return;
    }
    pane = pane.parentElement;
  }
  // Phone layout: the panes stack and nothing inside scrolls, so the PAGE is the scroller.
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'end' });
  setTimeout(() => {
    if (el.getBoundingClientRect().bottom > window.innerHeight + 4) el.scrollIntoView({ block: 'end' });
  }, 450);
}

function notifyThemeCartUpdated(addResponse) {
  if (!addResponse?.sections) return false;
  const bus = window.EventBus;
  if (!bus || typeof bus.emit !== 'function') return false;
  bus.emit('cart:updated', addResponse);
  return true;
}

// After an AJAX add, the theme's own cart UI (header count badge, drawer) is stale
// because /cart/add.js doesn't notify it. Re-read the cart and refresh what's visible:
// update any header count badge, and fire the events drawer-based themes listen for.
// Fallback path: on this theme notifyThemeCartUpdated() does the real work.
async function refreshStorefrontCart() {
  try {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const count = String(cart.item_count ?? '');
    document
      .querySelectorAll(
        '.header__cart-count, .cart-count-bubble, #cart-icon-bubble, [data-cart-count], .cart-count, .cart__count',
      )
      .forEach((el) => {
        el.textContent = count;
        el.hidden = cart.item_count === 0;
      });
    // Let drawer / section-rendering themes refresh themselves if they listen for it.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
    return cart.item_count;
  } catch {
    /* badge refresh is best-effort; the add itself already succeeded */
    return null;
  }
}

// ── replace, don't stack (survey cluster: five brewers ended up with two copies) ──
// Every Add to cart mints a fresh snapshot token on the lines, so Shopify never merges
// a re-add with the previous press: change the pilsner from 5 lb to 6 lb and the cart
// holds 11 lb across two lines. Before adding, take out the lines that belong to THIS
// recipe (matched by the Recipe property, and by the snapshot tokens this page has sent,
// in case the recipe was renamed between presses). Other recipes and anything the
// shopper added by hand stay put. Removal goes by line KEY through /cart/update.js, not
// through the theme's Remove button (which silently drops some failures).
// Tokens are remembered PER RECIPE NAME: a token this page sent for "Recipe B" must never
// make a re-add of "Recipe A" take B's lines out (that happened in the first cut).
const sentCartTokens = new Map(); // lowercased recipe name -> Set(snapshot tokens)
const cartNameKey = (name) => String(name || '').trim().toLowerCase();
async function removeRecipeLinesFromCart(recipeName) {
  const root = window.Shopify?.routes?.root || '/';
  const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const name = cartNameKey(recipeName);
  const tokens = sentCartTokens.get(name) || new Set();
  const mine = (cart.items || []).filter((it) => {
    const p = it.properties || {};
    const rcp = cartNameKey(p.Recipe);
    return (name && rcp === name) || (p._bb_rcp && tokens.has(p._bb_rcp));
  });
  if (!mine.length) return { removed: 0 };
  const updates = {};
  for (const it of mine) updates[it.key] = 0;
  const res = await fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.description || err.message || `cart/update.js ${res.status}`);
  }
  return { removed: mine.length };
}

// ── cart option validation ───────────────────────────────────────────
// A toast alone sits at the bottom of the viewport and is easy to miss on desktop,
// so the unanswered picker turns red and an explicit message appears right under
// the Add-to-cart button.
const optGroup = (name) => document.querySelector(`input[name="${name}"]`)?.closest('.cart__opt');

// The Bag Together/Separate picker was retired on 2026-07-30 — every builder order now
// tells fulfillment to bag the grain bill together. The markup still ships in the theme's
// Customize-managed section liquid, so strip it here rather than editing that section.
function removePackingOpt() {
  document.querySelector('input[name="packing"]')?.closest('.cart__opt')?.remove();
}
removePackingOpt();

function injectCartError() {
  if ($('cartError')) return;
  const btn = $('cartBtn');
  if (!btn) return;
  const el = document.createElement('div');
  el.id = 'cartError';
  el.className = 'cart__error';
  el.setAttribute('role', 'alert');
  el.hidden = true;
  btn.insertAdjacentElement('afterend', el);
}
injectCartError();

function setCartError(msg) {
  const el = $('cartError');
  if (el) {
    el.textContent = msg || '';
    el.hidden = !msg;
  }
  if (!msg) document.querySelectorAll('.cart__opt.is-error').forEach((g) => g.classList.remove('is-error'));
}

/** Clear the red state as soon as the brewer answers. */
document.querySelectorAll('input[name="grainMill"]').forEach((r) =>
  r.addEventListener('change', () => {
    r.closest('.cart__opt')?.classList.remove('is-error');
    setCartError(null);
  }),
);

$('cartBtn').addEventListener('click', async () => {
  if (!currentRecipe) return;
  // Milled/unmilled is REQUIRED before anything reaches the cart — it decides which
  // grain variants ship. No default; the brewer has to pick. (Bagging is no longer a
  // choice: the server stamps every grain line "Bag together".)
  const grainMill = document.querySelector('input[name="grainMill"]:checked')?.value;
  if (!grainMill) {
    optGroup('grainMill')?.classList.add('is-error');
    setCartError('Choose milled or unmilled grain above before adding to cart.');
    toast('Choose milled or unmilled grain first', 'err');
    $('cartOpts')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }
  setCartError(null);
  const btn = $('cartBtn');
  btn.disabled = true;
  btn.textContent = 'Building cart…';
  try {
    const cartRecipeName = currentReport?.recipeName || currentSavedName || recipeTitleText() || 'Beer Builder recipe';
    const res = await fetch(API + '/api/cart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        recipe: currentRecipe,
        grainMill,
        recipeName: cartRecipeName,
        equipment: equipmentDisplayName() || undefined,
        uid: anonId || undefined,
        style: styleSelect.value || undefined,
        // Freezes the recipe server-side under a token that rides the cart lines, so a
        // purchased order gets a permanent brew day sheet URL (packing-slip QR) and the
        // buyer's profile gets the recipe auto-saved. Same shape the Save button sends.
        snapshot: recipePayload(cartRecipeName),
      }),
    });
    const cart = await res.json();
    const box = $('cartResult');
    box.hidden = false;
    // li.ingredient carries the recipe's typed name (personal ingredients included) —
    // escape every string field.
    const lines = cart.lineItems
      .map((li) => {
        if (!li.matched)
          return `<div class="cart__line miss"><span>${escapeHtml(li.ingredient)} — not in catalog</span><span>${escapeHtml(li.recipeAmount)}</span></div>`;
        // Pooled-stock shortfall: the shop stocks it, but not this much. Nothing was added.
        if (li.short)
          return `<div class="cart__line miss"><span>${escapeHtml(li.title)} — ${escapeHtml(li.recipeAmount)} (not added)</span><span></span></div>`;
        // Own-supply yeast: informational only — nothing was added to the cart for it.
        if (!li.quantity && li.recipeAmount && /not added to cart/.test(li.recipeAmount))
          return `<div class="cart__line"><span>${escapeHtml(li.title)} — ${escapeHtml(li.recipeAmount)}</span><span></span></div>`;
        // Grain: one line, exact weight (e.g. "1 lb 8 oz") + prorated price.
        if (li.price != null)
          return `<div class="cart__line"><span>${escapeHtml(li.title)} — ${escapeHtml(li.recipeAmount)}</span><span>$${li.price.toFixed(2)}</span></div>`;
        // Hops / yeast / extracts: unchanged (whole-unit quantities).
        return `<div class="cart__line"><span>${escapeHtml(li.title)} ×${li.quantity}</span><span>${escapeHtml(li.recipeAmount)}</span></div>`;
      })
      .join('');

    let footer;
    if (cart.short && cart.short.length) {
      // Don't touch the customer's cart while any line is short: a partial cart with a
      // note is easy to miss, and the missing malt is the whole grain bill's backbone.
      // The panel already flags the row; the fix is to shrink or swap it (ask the builder).
      const list = cart.short
        .map((l) => `<li><strong>${escapeHtml(l.title)}</strong>: recipe needs ${escapeHtml(l.needed)}, only ${escapeHtml(l.available)} in stock</li>`)
        .join('');
      footer =
        `<div class="cart__short">⚠ <strong>Nothing was added to your cart yet.</strong> We're short on:` +
        `<ul>${list}</ul>` +
        `Cut that ingredient down to what's in stock, or ask the builder to swap it (for example: "we only have ${escapeHtml(cart.short[0].available)} of ${escapeHtml(cart.short[0].title)}, rework the recipe"), then add to cart again.</div>`;
    } else if (ON_STOREFRONT && cart.items && cart.items.length) {
      // Embedded in the store: add to the customer's real cart via the AJAX cart API.
      // First take out this recipe's earlier lines, so a second press (or a tweak and
      // re-add) replaces the set instead of stacking a second copy on top.
      let replaced = 0;
      let replaceFailed = false;
      try {
        replaced = (await removeRecipeLinesFromCart(cartRecipeName)).removed;
      } catch (e) {
        replaceFailed = true;
        console.warn('[cart] could not remove the earlier version', e);
      }
      for (const it of cart.items) {
        if (!it.properties || !it.properties._bb_rcp) continue;
        const k = cartNameKey(cartRecipeName);
        if (!sentCartTokens.has(k)) sentCartTokens.set(k, new Set());
        sentCartTokens.get(k).add(it.properties._bb_rcp);
      }
      const sections = themeCartSections();
      const addRes = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Asking for `sections` makes Shopify return the re-rendered cart markup alongside
        // the added items, which is what lets the theme's drawer update in place.
        body: JSON.stringify({ items: cart.items, ...(sections.length ? { sections } : {}) }),
      });
      if (addRes.ok) {
        // /cart/add.js updates the cart server-side but does NOT refresh the theme's
        // header cart count / drawer — without this the customer sees no change and
        // assumes the button did nothing. Re-render the theme's own cart sections first,
        // then fall back to the badge patch for themes that don't expose the bus.
        const added = await addRes.json().catch(() => null);
        notifyThemeCartUpdated(added);
        const count = await refreshStorefrontCart();
        const countMsg = count != null ? ` — ${count} item${count === 1 ? '' : 's'} now in your cart` : '';
        const head = replaced
          ? `<strong>Updated your cart</strong> (replaced the earlier version of this recipe)`
          : `<strong>Added to your cart</strong>`;
        const warn = replaceFailed
          ? ` <span class="muted">Couldn't remove an earlier copy of this recipe, so check the cart for duplicates.</span>`
          : '';
        footer =
          `<div class="cart__added">✅ ${head}${countMsg}. ` +
          `<a class="cart__view" href="/cart">View cart →</a>${warn}</div>`;
      } else {
        const err = await addRes.json().catch(() => ({}));
        footer = `<div style="margin-top:10px" class="muted">⚠ Couldn't add to cart${err.description ? `: ${escapeHtml(err.description)}` : ''}. ${cart.url ? `<a href="${escapeHtml(cart.url)}">Use this cart link instead →</a>` : ''}</div>`;
      }
    } else if (cart.url) {
      footer = `<div style="margin-top:10px"><a href="${cart.url}" target="_blank" rel="noopener">Open cart on store →</a></div>`;
    } else {
      footer = `<div style="margin-top:10px" class="muted">${cart.note}</div>`;
    }
    box.innerHTML = lines + footer;
    // The cart button sits at the bottom of a scrollable recipe panel, and the list plus
    // the "View cart" link render BELOW it. Scroll to the very end so the link is on
    // screen. Called straight out rather than inside requestAnimationFrame: rAF never
    // fires while a tab is hidden, and the helper re-measures on its own anyway.
    scrollPaneToBottom(box);
  } catch (err) {
    $('cartResult').hidden = false;
    $('cartResult').textContent = `Cart error: ${err.message}`;
  } finally {
    btn.disabled = false;
    btn.textContent = '🛒 Add ingredients to cart';
  }
});

// ── shopping list sheet ──────────────────────────────────────────────
// Opens a clean, self-contained sheet in a new window and prints it. Read-only: formats
// whatever recipe is on screen, no backend call. Available to everyone (no login) — the
// main use is printing the ingredient list to take to a homebrew shop. "Save as PDF" in
// the print dialog covers anyone who wants a file instead of paper. Its companion is the
// brew day sheet further down, which prints the same recipe as an ordered procedure.
function escPrint(s) {
  return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
/** Gravity reads as 1.060, never 1.06 — same three decimals the panel's metric tiles use. */
function fmtGravity(v) {
  return v == null || v === '' ? '' : Number(v).toFixed(3);
}
/**
 * Which of the four additions the engine will treat this hop as. Mirrors the dispatch in
 * calculator-v2's calculateIBUWithBreakdown — a 0-minute hop with no whirlpool spec is a
 * FLAMEOUT, not a "0 minute boil", and the hop sliders write those routinely.
 */
function hopMethodOf(h) {
  if (h.method === 'dryhop' || h.time === -1) return 'dryhop';
  if (h.method === 'whirlpool' || (h.time === 0 && h.whirlpoolTime)) return 'whirlpool';
  return (h.time ?? 60) === 0 ? 'flameout' : 'boil';
}

/** Short timing label for the recipe panel. */
/** Hop FORM: absent = pellet; 'whole' = whole dried cones; 'wet' = fresh undried hops
 *  stored at the real weight (about 5x a pellet charge). Mirrors src/calc/hopForm.ts. */
const WET_HOP_PELLET_MULTIPLIER = 5;
const HOP_FORMS = [['pellet', 'Pellet'], ['whole', 'Whole dried'], ['wet', 'Fresh (wet)']];
function hopFormOf(h) {
  const f = String((h && h.hopForm) || 'pellet').toLowerCase();
  return f === 'whole' || f === 'wet' || f === 'cryo' ? f : 'pellet';
}
function hopFormSuffix(h) {
  const f = hopFormOf(h);
  return f === 'wet' ? 'fresh hops' : f === 'whole' ? 'whole cones' : '';
}
function withFormSuffix(text, h) {
  const suffix = hopFormSuffix(h);
  return suffix ? `${text} · ${suffix}` : text;
}
function hopWhen(h) {
  switch (hopMethodOf(h)) {
    case 'dryhop': return withFormSuffix(`dry hop ${h.dryHopDays || 4}d${h.dryHopStage ? `, ${h.dryHopStage}` : ''}`, h);
    case 'whirlpool': return withFormSuffix(`whirlpool ${h.whirlpoolTime || 0}m @ ${fmtTemp(h.whirlpoolTemp || 0)}`, h);
    case 'flameout': return withFormSuffix('flameout', h);
    default: return withFormSuffix(`${h.time}m boil`, h);
  }
}

/** Long timing label for the printed brew sheet. */
function hopTiming(h) {
  switch (hopMethodOf(h)) {
    case 'dryhop': return withFormSuffix(`dry hop ${h.dryHopDays || 4} days${h.dryHopStage ? ` (${h.dryHopStage})` : ''}`, h);
    case 'whirlpool': return withFormSuffix(`whirlpool ${h.whirlpoolTime || 0} min @ ${fmtTemp(h.whirlpoolTemp || 0)}`, h);
    case 'flameout': return withFormSuffix('flameout (chill immediately)', h);
    default: return withFormSuffix(`${h.time} min boil`, h);
  }
}
// Shared look for both printouts (shopping list + brew day steps) so a brewer who prints
// both gets one document set, not two designs. Sheet-specific rules are appended per sheet.
const SHEET_CSS = `
*{box-sizing:border-box}
/* The sheet is light on paper and light on screen. Without an explicit background and
   color-scheme, a dark-mode viewer gets the OS dark canvas under this hardcoded #111
   text and the print preview is unreadable — the same trap the combo panel hit. */
body{font:13px/1.45 -apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#111;background:#fff;color-scheme:light;margin:0;padding:32px 36px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.hd{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #111;padding-bottom:10px;margin-bottom:16px}
.brand{font-weight:800;letter-spacing:.12em;font-size:12px;text-transform:uppercase}
.brand small{display:block;font-weight:600;letter-spacing:.04em;color:#666;margin-top:3px;text-transform:none}
.url{font-size:11px;color:#666;text-align:right}
h1{font-size:22px;margin:0 0 2px}
.sub{color:#555;font-size:12px;margin-bottom:16px}
.vitals{display:flex;gap:10px;margin-bottom:4px}
.vital{flex:1;border:1px solid #ccc;border-radius:6px;text-align:center;padding:8px 4px}
.vital b{display:block;font-size:18px}
.vital span{font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:#777}
h2{font-size:12px;text-transform:uppercase;letter-spacing:.08em;border-bottom:1px solid #ddd;padding-bottom:4px;margin:18px 0 8px}
ul{list-style:none;margin:0 0 6px;padding:0}
li{display:flex;align-items:baseline;padding:4px 0;border-bottom:1px dotted #e2e2e2}
li.empty{color:#999;border:0}
/* inline-block matters: inside the flex rows this box is a flex item and sizes fine, but
   the brew-day countdown puts the same box in a <td>, where a plain inline <i> collapses
   its width/height into a thin bar. */
.cb{display:inline-block;flex:none;width:13px;height:13px;border:1.5px solid #444;border-radius:2px;margin-right:10px;position:relative;top:2px}
.q{flex:none;width:118px;font-weight:700;font-variant-numeric:tabular-nums}
.q small{font-weight:400;color:#888;font-size:10px}
.n{flex:1}
.n em{color:#777;font-style:normal;font-size:11px}
.grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px 18px;margin-top:6px}
.stat{display:flex;justify-content:space-between;border-bottom:1px dotted #e2e2e2;padding:4px 0}
.stat span{color:#666}
.ft{margin-top:26px;border-top:1px solid #ddd;padding-top:8px;font-size:10px;color:#888;display:flex;justify-content:space-between}
@page{margin:14mm}
@media print{body{padding:0}}`;

// Steps, the boil countdown and the readings box. Steps avoid breaking across a page:
// a brewer reading step 4 at the kettle should not have to turn a page mid-instruction.
const BREWDAY_CSS = `
.step{display:flex;gap:12px;padding:11px 0;border-bottom:1px solid #eee;break-inside:avoid;page-break-inside:avoid}
.step:last-of-type{border-bottom:0}
.num{flex:none;width:24px;height:24px;border-radius:50%;background:#111;color:#fff;font-weight:700;font-size:12px;display:flex;align-items:center;justify-content:center}
.body{flex:1}
.body h3{margin:2px 0 3px;font-size:14px}
.body p{margin:0 0 4px;color:#444}
.body .hint{color:#777;font-size:11.5px}
.items{margin:5px 0 0}
.items li{padding:3px 0}
.items .q{width:96px}
table{width:100%;border-collapse:collapse;margin-top:6px}
th{text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:#777;border-bottom:1px solid #ddd;padding:0 6px 4px 0}
td{padding:5px 6px 5px 0;border-bottom:1px dotted #e2e2e2;vertical-align:top}
td.t{white-space:nowrap;font-weight:700;font-variant-numeric:tabular-nums;width:96px}
td.c{width:22px}
.readings{margin-top:18px;border:1px solid #ccc;border-radius:6px;padding:10px 12px;break-inside:avoid}
.readings h2{margin-top:0;border:0;padding:0}
.rgrid{display:grid;grid-template-columns:1fr 1fr;gap:8px 18px}
.r{display:flex;justify-content:space-between;align-items:baseline;border-bottom:1px solid #bbb;padding-bottom:3px}
.r span{color:#666;font-size:11px}
.r b{font-weight:400;color:#aaa;font-size:11px}`;

function brewSheetHtml(report) {
  const r = report.resolvedRecipe || {};
  const b = report.benchmarks || {};
  const g = b.gravity || {};
  const vol = b.volumes || {};
  const styleName = (report.style && report.style.style) || '';
  // The recipe's own name first — the style alone made two same-style recipes print
  // under one identical heading (reported as "my first recipe's name was retained").
  const title = report.recipeName || currentSavedName || styleName || 'Custom recipe';
  const batch = r.batchSize ?? (Number($('batchSize').value) || 5);
  const date = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const cb = '<i class="cb"></i>';
  const row = (qty, name, sub, qtyNote) =>
    `<li>${cb}<span class="q">${escPrint(qty)}${qtyNote ? `<small>${escPrint(qtyNote)}</small>` : ''}</span><span class="n">${escPrint(name)}${sub ? ` <em>${escPrint(sub)}</em>` : ''}</span></li>`;

  // Same grain-bill percentages the recipe panel shows.
  const fermTotal = (r.fermentables || []).reduce((s, f) => s + (f.weight || 0), 0);
  // Raw "<1" here — the qty note is escPrint-escaped inside row(), unlike the panel's innerHTML.
  const pctOf = (w) => (fermTotal && w ? ` (${(w / fermTotal) * 100 < 1 ? '<1' : Math.round((w / fermTotal) * 100)}%)` : '');
  // Same names the recipe panel prints: the engine's resolved name plus the supplier
  // qualifier ("Carolina Rye Malt - Riverbend"; yeast keeps its product code in
  // parentheses). Notes are index-matched to the recipe arrays, like the panel.
  const notesOf = (kind) => (report.resolution || []).filter((n) => n.kind === kind);
  const fNotes = notesOf('fermentable');
  const hNotes = notesOf('hop');
  const yNote = notesOf('yeast')[0];
  const listName = (note, fallback, paren) => {
    const base = (note && note.resolvedAs) || fallback;
    if (!note || !note.label) return base;
    if (normText(base).includes(normText(note.label))) return base;
    return paren ? `${base} (${note.label})` : `${base} - ${note.label}`;
  };
  const ferms = (r.fermentables || []).map((f, i) => row(fmtGrain(f.weight), listName(fNotes[i], f.name), undefined, pctOf(f.weight))).join('') || '<li class="empty">—</li>';
  const hops = (r.hopSchedule || []).map((h, i) => row(fmtHop(h.weight), listName(hNotes[i], h.name),
    hopFormOf(h) === 'wet' ? `${hopTiming(h)} · your own, not in cart` : hopTiming(h))).join('') || '<li class="empty">—</li>';
  const yeast = r.yeast
    ? row(r.yeast.packages && r.yeast.packages > 1 ? `${r.yeast.packages} pkg` : '1 pkg', listName(yNote, r.yeast.name, true),
        r.yeast.ownSupply ? 'your own — not in cart' : undefined)
    : '<li class="empty">—</li>';
  const others = (r.flavorings || [])
    .map((fl) => row(fl.amount != null ? `${fl.amount} ${fl.unit || ''}`.trim() : '—', fl.name, fl.note))
    .join('');
  const saltRows = (r.salts || [])
    .map((s) => row(fmtSaltAmt(s), s.name, [s.stage, s.concentration ? `${s.concentration}%` : ''].filter(Boolean).join(' · ') || undefined))
    .join('');

  const vital = (l, v) => `<div class="vital"><b>${v == null || v === '' ? '–' : v}</b><span>${l}</span></div>`;
  const stat = (l, v) => `<div class="stat"><span>${l}</span><b>${v == null || v === '' ? '–' : v}</b></div>`;

  return `<!doctype html><html><head><meta charset="utf-8"><title>${escPrint(title)} — shopping list</title><style>${SHEET_CSS}
</style></head><body>
<div class="hd"><div class="brand">Clawhammer Supply<small>Beer Builder shopping list</small></div><div class="url">clawhammersupply.com<br>${date}</div></div>
<h1>${escPrint(title)}</h1>
<div class="sub">${styleName && styleName !== title ? `${escPrint(styleName)} · ` : ''}${fmtVol(batch)} batch</div>
<div class="vitals">
${vital('OG', fmtGravity(g.og))}${vital('FG', fmtGravity(g.fg))}${vital('ABV', b.abv != null ? b.abv + '%' : '')}${vital('IBU', b.ibu)}${vital('SRM', b.srm)}${vital('BU:GU', b.bugu)}
</div>
<h2>Shopping list &mdash; fermentables</h2><ul>${ferms}</ul>
<h2>Hops</h2><ul>${hops}</ul>
<h2>Yeast</h2><ul>${yeast}</ul>
${others ? `<h2>Other</h2><ul>${others}</ul>` : ''}
${saltRows ? `<h2>Water salts &amp; acids</h2><ul>${saltRows}</ul>` : ''}
<h2>Brew day</h2>
<div class="grid">
${stat('Mash', Array.isArray(r.mashSteps) && r.mashSteps.length > 1 ? `${r.mashSteps.length}-step mash` : fmtTemp(r.mashTemp ?? 152))}${stat('Boil', (r.boilTime ?? 60) + ' min')}${stat('Batch', fmtVol(batch))}${stat('Strike water', vol.strikeWater != null ? fmtVol(vol.strikeWater) : '–')}${stat('Attenuation', b.actualAttenuation != null ? b.actualAttenuation + '%' : '–')}${stat('Equipment', b.equipment || '–')}
</div>
<div class="ft"><span>Built with the Clawhammer Beer Builder</span><span>clawhammersupply.com</span></div>
<script>window.onload=function(){setTimeout(function(){window.focus();window.print();},150);};<\/script>
</body></html>`;
}
function openPrintWindow(html) {
  const w = window.open('', '_blank', 'width=840,height=1000');
  if (!w) { toast('Allow pop-ups to print your recipe', 'err'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
function printRecipe() {
  if (!currentReport) return;
  openPrintWindow(brewSheetHtml(currentReport));
}

// ── brew day steps ───────────────────────────────────────────────────
// The shopping list answers "what do I buy"; this answers "what do I do, in order".
// Same rule as the shopping list: it formats the recipe already on screen and makes no
// backend call, so it can't disagree with the panel. Every number it prints comes from
// the engine's own report — nothing here re-derives a volume, a gravity or a temperature.
// Where the engine models nothing (strike temperature, fermentation temperature), the
// sheet says so in words instead of inventing a figure that would look authoritative.

/** Fermentables that go in the mash, vs the ones that go in the kettle. */
function isMashable(f) {
  return !f.type || f.type === 'Grain' || f.type === 'Adjunct';
}

/**
 * The boil as a countdown a brewer can follow at the kettle: one row per distinct
 * "minutes remaining" mark, start of boil first. Boil and flameout hops carry their own
 * mark from the schedule. Sugars and extracts get the conventional last-10-minutes slot —
 * the engine counts them as fully fermentable whenever they go in, so that placement is
 * process guidance and changes no benchmark.
 */
function boilCountdown(r, resolvedSteps) {
  const boil = r.boilTime ?? 60;
  const marks = new Map();
  const at = (min, text) => {
    const m = Math.max(0, Math.min(boil, min));
    if (!marks.has(m)) marks.set(m, []);
    marks.get(m).push(text);
  };
  at(boil, 'Boil begins — start the clock');
  (r.hopSchedule || []).forEach((h) => {
    const kind = hopMethodOf(h);
    if (kind === 'boil') at(h.time ?? 60, `Add ${fmtHop(h.weight)} ${h.name}`);
    else if (kind === 'flameout') at(0, `Add ${fmtHop(h.weight)} ${h.name}`);
  });
  // Non-hop kettle adds (late sugars, kettle salts, finings, boil-timed aids) come from
  // the report's resolvedSteps — the engine publishes them (kettleSteps.ts) so this
  // sheet, the server's packing-slip sheet and the kettle controller all read one set of
  // numbers. The local derivation below only serves stored reports from before the field.
  if (Array.isArray(resolvedSteps)) {
    resolvedSteps.forEach((s) => at(s.time, s.label));
  } else {
    (r.fermentables || []).filter((f) => !isMashable(f)).forEach((f) => {
      at(Math.min(10, boil), `Stir in ${fmtGrain(f.weight)} ${f.name} (kill the heat first so it can't scorch)`);
    });
    // Boil-stage water salts go in as the boil starts; mash/sparge salts print on their own steps.
    (r.salts || []).filter((s) => s.stage === 'boil').forEach((s) => {
      at(boil, `Add ${s.amount} ${s.unit} ${s.name}`);
    });
  }
  return [...marks.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([min, notes]) => ({ min, label: min === 0 ? 'Flameout' : `${min} min left`, notes }));
}

function brewDayHtml(report) {
  const r = report.resolvedRecipe || {};
  const b = report.benchmarks || {};
  const g = b.gravity || {};
  const vol = b.volumes || {};
  const styleName = (report.style && report.style.style) || '';
  const title = report.recipeName || currentSavedName || styleName || 'Custom recipe';
  const batch = r.batchSize ?? (Number($('batchSize').value) || 5);
  const date = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const mashTemp = r.mashTemp ?? 152;
  const mashTime = r.mashTime ?? 60;
  // A step program changes the mash instructions: dough-in happens at the FIRST rest's
  // temperature and the controller walks the rests from there.
  const mSteps = Array.isArray(r.mashSteps) && r.mashSteps.length > 1 ? r.mashSteps : null;
  const doughIn = mSteps ? mSteps[0].temp : mashTemp;
  const boil = r.boilTime ?? 60;
  const cb = '<i class="cb"></i>';
  const item = (qty, name, sub) =>
    `<li>${cb}<span class="q">${escPrint(qty)}</span><span class="n">${escPrint(name)}${sub ? ` <em>${escPrint(sub)}</em>` : ''}</span></li>`;

  // Names the report's resolvedSteps already timed into the boil countdown (kettle
  // sugars, boil-timed aids): keep them off the mash bill and the Flavorings step so no
  // ingredient gets two contradictory instructions. Engine-owned classification — the
  // local isMashable only serves stored reports from before resolvedSteps existed.
  const stepped = new Set((report.resolvedSteps || []).map((s) => s.name));
  const mashGrains = (r.fermentables || []).filter((f) =>
    Array.isArray(report.resolvedSteps) ? !stepped.has(f.name) : isMashable(f));
  const whirl = (r.hopSchedule || []).filter((h) => hopMethodOf(h) === 'whirlpool');
  const dry = (r.hopSchedule || []).filter((h) => hopMethodOf(h) === 'dryhop');
  const flav = (r.flavorings || []).filter((fl) => !stepped.has(fl.name));
  // Water salts land on the step their stage names; unstaged ones default to the mash
  // water (boil-stage salts ride the boil countdown instead). Display-only data — the
  // amounts come straight from the recipe, nothing here computes chemistry.
  const allSalts = r.salts || [];
  const saltQty = (s) => fmtSaltAmt(s);
  const saltName = (s) => s.concentration ? `${s.name} ${s.concentration}%` : s.name;
  const mashSalts = allSalts.filter((s) => s.stage === 'mash' || !s.stage);
  const spargeSalts = allSalts.filter((s) => s.stage === 'sparge');

  const steps = [];
  const step = (h3, html) => steps.push({ h3, html });

  // An imported recipe carries its source app's own water plan — that is the plan the
  // brewer already brews with, so it leads and the rig's full-volume figure becomes the
  // cross-check (fidelity contract: imported water matches the export).
  const sw = r.sourceWater && (r.sourceWater.totalGal || r.sourceWater.mashGal) ? r.sourceWater : null;
  if (mashGrains.length) {
    // Lead with the MASH temperature, not the strike temperature (Kyle, 2026-07-29). On an
    // all-in-one the controller is already holding a setpoint and will pull the mash back up
    // after dough-in, so "set it to your mash temp" is the instruction that matches the rig
    // most of our brewers own. Strike temp still matters on anything slow to recover, so it
    // keeps its own paragraph below rather than being dropped.
    // strikeTemp is null only when the engine sees nothing mashable. That can disagree
    // with the panel's own type check for an untyped ingredient the engine infers as
    // sugar, so fall back to the instruction rather than printing "null°F".
    const mashLabel = mSteps ? 'your mash-in temperature (the first rest)' : 'your mash temperature';
    step('Heat your water', sw ? `
      <p>Heat <b>${fmtVol(sw.mashGal || sw.totalGal)}</b> to <b>${fmtTemp(doughIn)}</b>, ${mashLabel}.</p>
      ${sw.spargeGal ? `<p>Sparge water: <b>${fmtVol(sw.spargeGal)}</b>${sw.totalGal ? ` (total water <b>${fmtVol(sw.totalGal)}</b>)` : ''}.</p>` : ''}
      <p class="hint">These are the water volumes from the recipe you imported, kept exactly as your old app
      calculated them. Full-volume on this rig's own numbers would be ${fmtVol(vol.strikeWater)}; if your first
      batch runs short or long in the kettle, that figure is the one tuned to this equipment profile.</p>
      ${b.strikeTemp != null ? `<p class="hint">If your system is slow to recover after dough-in, heat to a strike
      temperature of <b>${fmtTemp(b.strikeTemp)}</b> instead, which lands the mash on <b>${fmtTemp(doughIn)}</b> once the grain goes in.</p>` : ''}` : `
      <p>Heat <b>${fmtVol(vol.strikeWater)}</b> to <b>${fmtTemp(doughIn)}</b>, ${mashLabel}.</p>
      <p class="hint">That is the full water volume for the batch, boil-off and losses included. If you
      sparge rather than mashing full-volume, split it between mash and sparge; the total is what matters.</p>
      ${b.strikeTemp != null ? `<p class="hint">Stirring in room-temperature grain pulls the water down a few degrees.
      A controller-driven all-in-one heats it straight back, so setting it to ${mSteps ? 'the first rest' : 'your mash temperature'} is enough. If your
      system is slow to recover, heat to a strike temperature of <b>${fmtTemp(b.strikeTemp)}</b> instead, which lands the mash
      on <b>${fmtTemp(doughIn)}</b> once the grain goes in. That figure assumes grain at room temperature (about ${fmtTemp(68)}) going
      into a vessel already at heat, which an all-in-one is, since it heats the water in the kettle; preheat a
      cooler-style tun first or it will steal a few degrees. Every ${displayUnits.temp === 'c' ? '5°C' : '10°F'} your grain sits away from room temperature
      moves the strike temp about ${displayUnits.temp === 'c' ? '0.5°C' : '1°F'}${b.mashThickness != null ? `, and this mash runs ${b.mashThickness} qt/lb` : ''}.</p>` : ''}`);

    step(`Mash in at ${fmtTemp(doughIn)}`, `
      ${mashSalts.length ? `<p>Stir these water additions into the mash water first:</p>
      <ul class="items">${mashSalts.map((s) => item(saltQty(s), saltName(s))).join('')}</ul>` : ''}
      <p>Stir the grain in and break up any dough balls.</p>
      <ul class="items">${mashGrains.map((f) => item(fmtGrain(f.weight), f.name)).join('')}</ul>
      ${mSteps ? `
      <p>This is a <b>step mash</b> — walk the rests in order (${mashTime} minutes total). Program your
      controller with these setpoints and time each rest from when the mash reaches its temperature:</p>
      <ul class="items">${mSteps.map((s, i) => item(fmtTemp(s.temp),
        `${i === 0 ? 'Hold' : 'Raise to temperature, then hold'} ${s.time} min`, s.name || `Step ${i + 1}`)).join('')}</ul>` : `
      <p>Hold <b>${mashTime} minutes</b> at <b>${fmtTemp(mashTemp)}</b>.</p>`}
      ${report.water && report.water.mashPh != null ? `<p class="hint">Estimated mash pH: <b>${report.water.mashPh.toFixed(2)}</b>
      on a room-temperature sample${report.water.assumedSource ? ' (assuming an RO/distilled source — set your real tap water in the builder for a better estimate)' : ''}.
      A meter reading within about 0.1 of this is on plan.</p>` : ''}
      ${spargeSalts.length ? `<p>Add to the sparge water:</p>
      <ul class="items">${spargeSalts.map((s) => item(saltQty(s), saltName(s))).join('')}</ul>` : ''}`);

    step('Pull the grain and top up', `
      <p>Lift the basket or bag and let it drain. You are aiming for about <b>${fmtVol(vol.preBoil)}</b>
      in the kettle before the boil starts${g.preBoil != null ? `, at a gravity of about <b>${fmtGravity(g.preBoil)}</b>` : ''}.</p>
      <p class="hint">Short of that? Top up with hot water. Over it? Boil a little longer. Squeeze the bag
      if you like; the tannin worry is mostly folklore at homebrew scale.</p>`);
  } else {
    step('Heat your water', `
      <p>Heat <b>${fmtVol(vol.strikeWater)}</b> and bring it toward a boil. You are aiming for about
      <b>${fmtVol(vol.preBoil)}</b> in the kettle when the boil starts${g.preBoil != null ? `, at a gravity of about <b>${fmtGravity(g.preBoil)}</b>` : ''}.</p>
      ${mashSalts.length || spargeSalts.length ? `<p>Stir these water additions in as it heats:</p>
      <ul class="items">${[...mashSalts, ...spargeSalts].map((s) => item(saltQty(s), saltName(s))).join('')}</ul>` : ''}`);
  }

  const rows = boilCountdown(r, report.resolvedSteps).map((m) => `
    <tr><td class="c">${cb}</td><td class="t">${escPrint(m.label)}</td>
    <td>${m.notes.map((n) => escPrint(n)).join('<br>')}</td></tr>`).join('');
  step(`Boil ${boil} minutes`, `
    <p>Watch it closely for the first few minutes — this is where a boil-over happens.</p>
    <table><tr><th></th><th>Time</th><th>Do this</th></tr>${rows}</table>`);

  if (whirl.length) {
    const byStand = new Map();
    whirl.forEach((h) => {
      const k = `${h.whirlpoolTemp || 0}|${h.whirlpoolTime || 0}`;
      if (!byStand.has(k)) byStand.set(k, { temp: h.whirlpoolTemp || 0, mins: h.whirlpoolTime || 0, hops: [] });
      byStand.get(k).hops.push(h);
    });
    step('Whirlpool', [...byStand.values()].map((s) => `
      <p>Chill to <b>${fmtTemp(s.temp)}</b>, add these, stir up a whirlpool and stand <b>${s.mins} minutes</b>:</p>
      <ul class="items">${s.hops.map((h) => item(fmtHop(h.weight), h.name)).join('')}</ul>`).join('')
      + `<p class="hint">The stand temperature is part of the recipe: running hotter than this pulls more
      bitterness than the ${b.ibu != null ? `${b.ibu} IBU` : 'IBU'} target assumes.</p>`);
  }

  step('Chill and transfer', `
    <p>Chill to pitching temperature and transfer to the fermenter. Expect roughly
    <b>${fmtVol(vol.intoFermenter)}</b> going in${vol.packaged != null ? `, about <b>${fmtVol(vol.packaged)}</b> packaged after losses` : ''}.</p>
    <p>Pull a sample and take your original gravity. Target: <b>${g.og != null ? fmtGravity(g.og) : '–'}</b>.</p>`);

  if (r.yeast) {
    // The printed temp is the same figure the panel shows: the brewer's own number when
    // they set one, otherwise the yeast's mid-range fill. Labeled for which it is, and
    // the strain's pack range still gets the last word.
    const fermPlan = r.fermentTemp != null ? r.fermentTemp
      : (report.yeastTemp ? report.yeastTemp.suggestedF : null);
    step('Pitch the yeast', `
      <p>Aerate well, then pitch <b>${escPrint(r.yeast.packages && r.yeast.packages > 1 ? `${r.yeast.packages} packs` : '1 pack')}
      of ${escPrint(r.yeast.name)}</b>.</p>
      ${fermPlan != null ? `<p>Ferment at <b>${fmtTemp(fermPlan)}</b> (${r.fermentTemp != null ? 'your planned temperature' : 'mid-range for this strain'}).</p>` : ''}
      <p class="hint">${fermPlan != null ? 'Check the plan against the range printed on the pack: that range belongs to the strain, so the builder does not second-guess it.' : 'Ferment in the range printed on the pack: that range belongs to the strain, so the builder does not second-guess it.'}${b.actualAttenuation != null ? ` This recipe is built around ${b.actualAttenuation}% attenuation.` : ''}</p>`);
  }

  if (flav.length) {
    step('Flavorings and adjuncts', `
      <ul class="items">${flav.map((fl) => item(fl.amount != null ? `${fl.amount} ${fl.unit || ''}`.trim() : '—', fl.name, fl.note)).join('')}</ul>
      <p class="hint">Follow the note on each. Anything going into the fermenter should be sanitized first.</p>`);
  }

  if (dry.length) {
    step('Dry hop', `
      <ul class="items">${dry.map((h) => item(fmtHop(h.weight), h.name, `${h.dryHopDays || 4} days${h.dryHopStage ? ` · ${h.dryHopStage}` : ''}`)).join('')}</ul>
      <p class="hint">Pull them on schedule. Leaving dry hops in for weeks strips aroma rather than adding it.</p>`);
  }

  step('Package', `
    <p>Confirm gravity is stable across two days before you package. Target final gravity:
    <b>${g.fg != null ? fmtGravity(g.fg) : '–'}</b>${b.abv != null ? `, which puts this at about <b>${b.abv}% ABV</b>` : ''}.</p>`);

  const stepsHtml = steps.map((s, i) => `
    <div class="step"><div class="num">${i + 1}</div><div class="body"><h3>${s.h3}</h3>${s.html}</div></div>`).join('');

  const reading = (l) => `<div class="r"><span>${l}</span><b>_____________</b></div>`;

  return `<!doctype html><html><head><meta charset="utf-8"><title>${escPrint(title)} — brew day</title><style>${SHEET_CSS}${BREWDAY_CSS}
</style></head><body>
<div class="hd"><div class="brand">Clawhammer Supply<small>Beer Builder brew day</small></div><div class="url">clawhammersupply.com<br>${date}</div></div>
<h1>${escPrint(title)}</h1>
<div class="sub">${styleName && styleName !== title ? `${escPrint(styleName)} · ` : ''}${fmtVol(batch)} batch · ${escPrint(b.equipment || 'your rig')}</div>
<div class="vitals">
<div class="vital"><b>${g.og == null ? '–' : fmtGravity(g.og)}</b><span>Target OG</span></div>
<div class="vital"><b>${g.fg == null ? '–' : fmtGravity(g.fg)}</b><span>Target FG</span></div>
<div class="vital"><b>${b.abv == null ? '–' : b.abv + '%'}</b><span>ABV</span></div>
<div class="vital"><b>${b.ibu == null ? '–' : b.ibu}</b><span>IBU</span></div>
${b.strikeTemp != null ? `<div class="vital"><b>${fmtTemp(b.strikeTemp)}</b><span>Strike</span></div>` : ''}
<div class="vital"><b>${mSteps ? `${mSteps.length}-step` : fmtTemp(mashTemp)}</b><span>Mash</span></div>
${g.preBoil != null ? `<div class="vital"><b>${fmtGravity(g.preBoil)}</b><span>Pre-boil SG</span></div>` : ''}
${report.water && report.water.mashPh != null ? `<div class="vital"><b>${report.water.mashPh.toFixed(2)}</b><span>Est. pH</span></div>` : ''}
<div class="vital"><b>${boil}m</b><span>Boil</span></div>
</div>
<h2>Step by step</h2>
${stepsHtml}
<div class="readings"><h2>What actually happened</h2>
<div class="rgrid">
${reading('Strike temp')}${reading('Mash temp held')}
${report.water && report.water.mashPh != null ? `${reading('Mash pH')}${reading('Mash pH after adjust')}` : ''}
${reading('Pre-boil volume')}${reading('Pre-boil gravity')}
${reading('Original gravity')}${reading('Into fermenter')}
${reading('Final gravity')}${reading('Brew date')}
</div></div>
<div class="ft"><span>Built with the Clawhammer Beer Builder</span><span>clawhammersupply.com</span></div>
<script>window.onload=function(){setTimeout(function(){window.focus();window.print();},150);};<\/script>
</body></html>`;
}
function printBrewDay() {
  if (!currentReport) return;
  openPrintWindow(brewDayHtml(currentReport));
}

// The action row's markup lives in the theme's Customize-managed section liquid, which has
// diverged from public/index.html and is not ours to edit — same situation the rename
// control and the packing radios are in. So the label change and the second button are
// applied here at boot: find-or-create, so this is correct whether the surrounding markup
// already carries them (local index.html) or not (the live theme).
function setupSheetButtons() {
  const printEl = $('printBtn');
  if (!printEl) return;
  printEl.textContent = '🛒 Shopping list';
  printEl.title = 'Print the ingredient list to take to the shop';
  let brewEl = $('brewDayBtn');
  if (!brewEl) {
    brewEl = document.createElement('button');
    brewEl.id = 'brewDayBtn';
    brewEl.type = 'button';
    brewEl.className = printEl.className;
    brewEl.hidden = true;
    printEl.insertAdjacentElement('afterend', brewEl);
  }
  brewEl.textContent = '📋 Brew day steps';
  brewEl.title = 'Print step-by-step instructions for brew day';
  // Bind once per element: the labels are safe to re-apply, a second listener is not
  // (it would open two print windows per click).
  bindOnce(printEl, printRecipe);
  bindOnce(brewEl, printBrewDay);
}
function bindOnce(el, fn) {
  if (el.dataset.sheetBound) return;
  el.dataset.sheetBound = '1';
  el.addEventListener('click', fn);
}
setupSheetButtons();

// ── wire up ──────────────────────────────────────────────────────────
composer.addEventListener('submit', (e) => {
  e.preventDefault();
  const text = inputEl.value;
  inputEl.value = '';
  send(text);
});

inputEl.focus();

/* ════════════════════════════════════════════════════════════════════
   ACCOUNTS — Shopify-customer scoped equipment profiles + saved recipes.
   Account endpoints go through the same-origin App Proxy so Shopify's
   signed `logged_in_customer_id` reaches the backend; anonymous endpoints
   (chat/cart/options) keep using the direct backend origin (API).
   ════════════════════════════════════════════════════════════════════ */

// On the storefront the theme may override the proxy base; default to the App Proxy path.
// Standalone/dev: same-origin '' (the dev server, which honors DEV_FAKE_CUSTOMER_ID).
const ACCOUNT_API = ON_STOREFRONT ? (window.BEER_BUILDER_PROXY || '/apps/beer-builder') : '';

const acct = { enabled: false, loggedIn: false };
const customProfiles = new Map(); // select value `custom:<id>` -> { id, name, profile, isDefault }
let sessionProfile = null; // equipment tweaked for this session only (not signed in / not saved)
let currentSavedId = null; // the loaded saved-recipe id, if any
let currentSavedName = '';

const aFetch = (path, opts = {}) =>
  fetch(ACCOUNT_API + path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });

// ── in-page dialogs ──────────────────────────────────────────────────
// Replaces native prompt()/confirm(), which block the whole tab and clash with the UI.
function uiDialog({ title, body = '', input = null, okText = 'OK', danger = false }) {
  return new Promise((resolve) => {
    const scrim = document.createElement('div');
    scrim.className = 'bb-dialog-scrim';
    scrim.innerHTML = `
      <div class="bb-dialog" role="dialog" aria-modal="true">
        <h3></h3>
        <p class="bb-dialog__body" hidden></p>
        <input class="bb-dialog__input" type="text" maxlength="120" hidden />
        <div class="bb-dialog__actions">
          <button type="button" class="link-btn bb-dialog__cancel">Cancel</button>
          <button type="button" class="bb-dialog__ok${danger ? ' bb-dialog__ok--danger' : ''}"></button>
        </div>
      </div>`;
    scrim.querySelector('h3').textContent = title;
    const bodyEl = scrim.querySelector('.bb-dialog__body');
    if (body) { bodyEl.hidden = false; bodyEl.textContent = body; }
    const inp = scrim.querySelector('.bb-dialog__input');
    if (input !== null) { inp.hidden = false; inp.value = input; }
    const okBtn = scrim.querySelector('.bb-dialog__ok');
    okBtn.textContent = okText;
    const done = (val) => {
      document.removeEventListener('keydown', onKey, true);
      scrim.remove();
      resolve(val);
    };
    const ok = () => done(input !== null ? inp.value.trim() : true);
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); done(null); }
      else if (e.key === 'Enter' && (input === null || document.activeElement === inp)) { e.preventDefault(); ok(); }
    };
    okBtn.addEventListener('click', ok);
    scrim.querySelector('.bb-dialog__cancel').addEventListener('click', () => done(null));
    scrim.addEventListener('click', (e) => { if (e.target === scrim) done(null); });
    document.addEventListener('keydown', onKey, true);
    // Mount inside the builder root so the scoped styles + CSS vars apply.
    (document.getElementById('beer-builder-root') || document.body).appendChild(scrim);
    (input !== null ? inp : okBtn).focus();
    if (input !== null) inp.select();
  });
}
const uiPrompt = (title, value = '') => uiDialog({ title, input: value, okText: 'Save' });
const uiConfirm = (title, body = '', okText = 'Delete') =>
  uiDialog({ title, body, okText, danger: true }).then((v) => v === true);

function toast(msg, kind = '') {
  const t = $('toast');
  if (!t) return;
  t.textContent = msg;
  t.className = `toast ${kind}`;
  t.hidden = false;
  clearTimeout(toast._t);
  toast._t = setTimeout(() => { t.hidden = true; }, 3200);
}

/** What to send as equipmentProfile: a built-in id (string) or a custom profile object. */
function equipmentPayload() {
  const v = equipSelect.value;
  let out = v;
  if (v === 'session' && sessionProfile) out = sessionProfile;
  else if (v.startsWith('custom:')) {
    const entry = customProfiles.get(v);
    if (entry) out = entry.profile;
  }
  if (effOverride == null) return out;
  // A brewhouse-efficiency override rides as an inline profile — the object form every
  // route already validates — so the engine, the AI's context and saved recipes all see
  // the brewer's number, not the rig default.
  const base = typeof out === 'string' ? builtinProfiles[out] : out;
  if (!base || effOverride === base.mashEfficiency) return out;
  return { ...base, mashEfficiency: effOverride };
}

/** The selected brew system's display name — stamped on cart lines for the order. */
function equipmentDisplayName() {
  const v = equipSelect.value;
  if (!v) return '';
  if (v === 'session' && sessionProfile) return sessionProfile.name || 'Custom rig';
  if (v.startsWith('custom:')) {
    const entry = customProfiles.get(v);
    if (entry) return entry.name || 'Custom rig';
  }
  const opt = equipSelect.selectedOptions && equipSelect.selectedOptions[0];
  return (opt && opt.textContent.trim()) || v;
}

// ── boot account state ───────────────────────────────────────────────
$('account').hidden = false; // equipment settings (the gear) are available to everyone
function refreshAccount() {
  // uid rides along so a signed-in visit links this browser's stats to the customer.
  return aFetch('/api/me' + (anonId ? '?uid=' + encodeURIComponent(anonId) : ''))
    .then((r) => r.json())
    .then((me) => {
      const wasLoggedIn = acct.loggedIn;
      acct.enabled = !!me.accountsEnabled;
      acct.loggedIn = !!me.loggedIn;
      if (acct.enabled && acct.loggedIn) {
        try { sessionStorage.removeItem('bb:signinTry'); } catch (e) {}
        const rst = $('signInReset');
        if (rst) rst.remove();
        setAccountLocked(false);
        $('signInLink').hidden = true;
        const lk0 = $('bbMenuLock');
        if (lk0) lk0.hidden = true;
        if (!wasLoggedIn) { loadProfilesIntoSelect(); loadMyIngredients(); loadWaterProfiles(); checkControllerPaired(); }
        afterRecipeRender();
      } else if (acct.enabled) {
        acctWaters = []; acctWatersLoaded = false; // signed out: My water falls back to this browser's list
        setAccountLocked(true);
        $('signInLink').hidden = false;
        const lk1 = $('bbMenuLock');
        if (lk1) lk1.hidden = false;
        // The locked buttons beside this now say what signing in unlocks, so the link
        // itself can go back to being short — the top bar has to survive 390px.
        $('signInLink').textContent = 'Sign in';
        $('signInLink').title = 'Log in to customize ingredients and equipment, and to save recipes';
        // Plain login link, matching the site header — the hosted-accounts flow
        // drops return_url, and a non-standard login URL is the one thing this
        // page did differently from every other login entry point on the site.
        // Open in a new tab so the builder (and any in-progress recipe) stays
        // put; the visibilitychange re-check picks the session up on return.
        $('signInLink').href = '/customer_authentication/redirect?locale=en';
        $('signInLink').target = '_blank';
        $('signInLink').rel = 'noopener';
        if (!$('signInLink').dataset.trackTry) {
          $('signInLink').dataset.trackTry = '1';
          $('signInLink').addEventListener('click', () => {
            try { sessionStorage.setItem('bb:signinTry', String(Date.now())); } catch (e) {}
          });
        }
        // Failed sign-in round trip (portal session without a store session):
        // offer a one-tap session reset that lands back on this page.
        let bbTry = 0;
        try { bbTry = Number(sessionStorage.getItem('bb:signinTry') || 0); } catch (e) {}
        let resetEl = $('signInReset');
        if (bbTry && Date.now() - bbTry < 15 * 60 * 1000) {
          if (!resetEl) {
            resetEl = document.createElement('a');
            resetEl.id = 'signInReset';
            resetEl.className = 'account__signin';
            resetEl.href = '/customer_authentication/redirect?locale=en';
            resetEl.textContent = 'Not recognized? Try the store sign-in';
            $('signInLink').after(resetEl);
          }
        } else if (resetEl) {
          resetEl.remove();
        }
        afterRecipeRender();
      }
      // Sign in / lock badge visibility changed the cluster's width — re-measure.
      if (typeof layoutTopbar === 'function') layoutTopbar();
      // Deep link from the account portal's "Recipes" menu item (?open=recipes): land
      // straight in the saved-recipes drawer. Signed out, say why it's locked instead.
      // Fires once, on the first account check only.
      if (OPEN_ON_LOAD === 'recipes' && !refreshAccount.openedOnLoad) {
        refreshAccount.openedOnLoad = true;
        if (acct.enabled && !acct.loggedIn) {
          showSigninPopup('Saved recipes live in your account, so you can open them on any device.');
        } else {
          openRecipes();
        }
      }
    })
    .catch(() => {});
}
refreshAccount();
// Signing in happens on the hosted customer-accounts subdomain; the brewer comes
// back via history/back or a tab switch, which can serve this page from bfcache
// with stale signed-out state. Re-check whenever the page is shown again.
window.addEventListener('pageshow', (e) => { if (e.persisted) refreshAccount(); });
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && !acct.loggedIn) refreshAccount();
});

// ── equipment profiles ───────────────────────────────────────────────
async function loadProfilesIntoSelect() {
  try {
    const { profiles } = await aFetch('/api/equipment-profiles').then((r) => r.json());
    // Drop any previously-injected custom options, then add a group at the top.
    [...equipSelect.querySelectorAll('option[data-custom], optgroup[data-custom]')].forEach((n) => n.remove());
    customProfiles.clear();
    if (profiles && profiles.length) {
      const group = document.createElement('optgroup');
      group.label = 'My equipment';
      group.dataset.custom = '1';
      let defaultValue = null;
      for (const p of profiles) {
        const value = `custom:${p.id}`;
        customProfiles.set(value, p);
        const opt = document.createElement('option');
        opt.value = value;
        opt.dataset.custom = '1';
        opt.textContent = p.isDefault ? `${p.name} ★` : p.name;
        group.appendChild(opt);
        if (p.isDefault) defaultValue = value;
      }
      equipSelect.insertBefore(group, equipSelect.firstChild.nextSibling);
      // The last explicit pick may be a custom profile that only exists now. Recency
      // beats the starred default — "keep my last rig" is the ask.
      const last = lastEquip();
      if (!equipSelect.value && last && customProfiles.has(last)) equipSelect.value = last;
      if (defaultValue && !equipSelect.value) equipSelect.value = defaultValue;
      // a restored session may reference a custom profile that only exists now
      if (pendingRestore?.equip && [...equipSelect.options].some((o) => o.value === pendingRestore.equip)) {
        equipSelect.value = pendingRestore.equip;
      }
    }
    if (typeof syncEffField === 'function') syncEffField();
    // A restored/default pick that is a pre-voltage custom rig gets the one-time ask
    // now, not only on the next manual change.
    setTimeout(() => { nudgeCustomVoltage(); }, 0);
  } catch {}
}

const EQUIP_FIELDS = [
  'mashEfficiency', 'boiloffRate', 'grainAbsorption', 'trubChillerLoss', 'fermenterLoss',
  'kettleDiameter', 'kettleOpening', 'hopUtilization', 'aromaHopUtilization',
  'defaultCoolTime', 'kettleVolume',
];
// Every field the engine needs (matches the server's required NUMERIC_FIELDS). kettleVolume
// is the only optional one — it just powers the batch-fits-kettle advisory.
const REQUIRED_EQUIP_FIELDS = EQUIP_FIELDS.filter((f) => f !== 'kettleVolume');

// ── equipment editor units (bug report #30: the form was imperial only) ─────────────
// Profiles are STORED imperial (the engine, the server's validateEquipmentProfile ranges,
// and every built-in rig are gal / lb / in). The editor is the only place that converts:
// it shows and reads the seven dimensional fields in the brewer's units and converts at
// the edges. `k` is the imperial → metric factor; `dp` the display rounding in metric.
const EQUIP_UNIT_FIELDS = {
  boiloffRate:     { us: 'Boil-off gal/hr',        si: 'Boil-off L/hr',         k: GAL_L,         dp: 2, dpUs: 3 },
  grainAbsorption: { us: 'Grain absorption gal/lb', si: 'Grain absorption L/kg', k: GAL_L / LB_KG, dp: 3, dpUs: 4 },
  trubChillerLoss: { us: 'Trub/chiller loss gal',   si: 'Trub/chiller loss L',   k: GAL_L,         dp: 2, dpUs: 3 },
  fermenterLoss:   { us: 'Fermenter loss gal',      si: 'Fermenter loss L',      k: GAL_L,         dp: 2, dpUs: 3 },
  kettleDiameter:  { us: 'Kettle diameter in',      si: 'Kettle diameter cm',    k: 2.54,          dp: 1, dpUs: 2 },
  kettleOpening:   { us: 'Kettle opening in',       si: 'Kettle opening cm',     k: 2.54,          dp: 1, dpUs: 2 },
  kettleVolume:    { us: 'Kettle volume gal',       si: 'Kettle volume L',       k: GAL_L,         dp: 1, dpUs: 2 },
};
let equipFormMetric = false; // the editor's current units; seeded from the header gal/L switch on open

/** Imperial (stored) → the editor's units, rounded for display. */
function equipToForm(f, v) {
  const u = EQUIP_UNIT_FIELDS[f];
  if (!equipFormMetric || !u || v == null || v === '') return v;
  return Number((Number(v) * u.k).toFixed(u.dp));
}
/** The editor's units → imperial (stored), rounded so a converted entry doesn't carry
 *  fifteen digits. Untouched fields never go through here (see equipFieldValue). */
function equipFromForm(f, n) {
  const u = EQUIP_UNIT_FIELDS[f];
  return equipFormMetric && u ? Number((n / u.k).toFixed(u.dpUs)) : n;
}
/** Fill a field from a stored (imperial) value and remember the exact original on the
 *  input, so viewing a 10.5 gal kettle as "39.7 L" and saving without touching it stores
 *  10.5 again, not 10.4876. Typing into the field forgets the original. */
function equipFillField(f, v) {
  const inp = $(`ef-${f}`);
  inp.value = v == null || v === '' ? '' : equipToForm(f, v);
  if (v == null || v === '') delete inp.dataset.us; else inp.dataset.us = String(v);
}
/** The stored (imperial) value a field currently represents: the remembered original
 *  when the brewer hasn't touched it, else what they typed converted back. */
function equipFieldValue(f) {
  const inp = $(`ef-${f}`);
  const raw = inp.value.trim();
  if (raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return NaN;
  if (inp.dataset.us != null && Number.isFinite(Number(inp.dataset.us))) return Number(inp.dataset.us);
  return equipFromForm(f, n);
}

/** Relabel the dimensional fields for the current units. The label text is the first
 *  text node inside each <label class="ef">; the input stays put. */
function relabelEquipForm() {
  for (const [f, u] of Object.entries(EQUIP_UNIT_FIELDS)) {
    const label = $(`ef-${f}`)?.closest('label');
    if (!label) continue;
    const text = [...label.childNodes].find((n) => n.nodeType === Node.TEXT_NODE);
    if (text) text.nodeValue = equipFormMetric ? u.si : u.us;
    else label.prepend(equipFormMetric ? u.si : u.us);
  }
  const seg = $('equipUnitSeg');
  seg?.querySelector('[data-eu="gal"]')?.classList.toggle('is-on', !equipFormMetric);
  seg?.querySelector('[data-eu="l"]')?.classList.toggle('is-on', equipFormMetric);
}

/** Switch the editor between gal/in and L/cm. Values already typed convert in place so
 *  nothing the brewer entered is lost or silently reinterpreted. */
function setEquipFormUnits(metric) {
  metric = !!metric;
  if (metric === equipFormMetric) { relabelEquipForm(); return; }
  const held = {};
  for (const f of Object.keys(EQUIP_UNIT_FIELDS)) {
    const v = equipFieldValue(f); // imperial under the OLD units (exact for untouched fields)
    held[f] = v != null && Number.isFinite(v) ? v : null;
  }
  equipFormMetric = metric;
  for (const [f, v] of Object.entries(held)) if (v != null) equipFillField(f, v);
  relabelEquipForm();
}

/** The editor's own gal/L pill, same control as the header Units switch, injected above
 *  the field grid on first use (the section markup is Customize-managed, and the builder
 *  already injects its voltage field the same way). Idempotent. */
function ensureEquipUnitsSeg() {
  let seg = $('equipUnitSeg');
  if (seg) return seg;
  const grid = $('equipForm')?.querySelector('.equip-grid');
  if (!grid) return null;
  ensureUnitsCss();
  const row = document.createElement('div');
  row.className = 'equip-units';
  const lab = document.createElement('span');
  lab.className = 'equip-units__label';
  lab.textContent = 'Units';
  seg = document.createElement('span');
  seg.id = 'equipUnitSeg';
  seg.className = 'bb-units__btns';
  seg.innerHTML = '<button type="button" data-eu="gal">gal / in</button><button type="button" data-eu="l">L / cm</button>';
  seg.querySelectorAll('button').forEach((b) =>
    b.addEventListener('click', () => setEquipFormUnits(b.dataset.eu === 'l')));
  row.append(lab, seg);
  grid.insertAdjacentElement('beforebegin', row);
  for (const f of Object.keys(EQUIP_UNIT_FIELDS)) {
    $(`ef-${f}`)?.addEventListener('input', (e) => { delete e.target.dataset.us; });
  }
  return seg;
}

// The gear opens a CHOOSER first: start a brand-new profile from scratch, or copy/modify
// an existing one (the brewer's saved profiles + the built-in starter profiles). Picking
// any of those slides over to the editor form.
function openEquipModal() {
  $('equipModal').hidden = false;
  showEquipChooser();
}

function showEquipChooser() {
  $('equipChooser').hidden = false;
  $('equipForm').hidden = true;
  $('equipFormError').hidden = true;
  updateEquipModalAuthUI(); // keep the "not signed in" note correct on the chooser too
  renderEquipChooser();
}

function showEquipForm() {
  $('equipChooser').hidden = true;
  $('equipForm').hidden = false;
  updateEquipModalAuthUI();
}

function equipMeta(p) {
  const kv = p?.kettleVolume;
  const kettle = kv == null ? '—' : displayUnits.water === 'l' ? `${(kv * GAL_L).toFixed(0)} L` : `${kv} gal`;
  return `${p?.mashEfficiency ?? '—'}% eff · ${kettle} kettle`;
}

// The chooser presents three explicit options — create new, edit an existing profile, or
// copy a profile. Edit/Copy are dropdown pickers populated here.
function renderEquipChooser() {
  populateEditSelect();
  populateCopySelect();
}

// Profiles the brewer can EDIT in place: their own saved profiles, plus an unsaved session
// profile. Built-in rigs can't be edited (only copied).
function editableProfiles() {
  const items = [];
  if (acct.enabled && acct.loggedIn) {
    for (const [val, p] of customProfiles) items.push({ value: val, label: p.isDefault ? `${p.name} ★` : p.name });
  }
  if (sessionProfile) items.push({ value: 'session', label: `${sessionProfile.name || 'Custom rig'} (current session)` });
  return items;
}

function populateEditSelect() {
  const sel = $('equipEditSelect');
  const btn = $('equipEditBtn');
  if (!sel) return;
  const items = editableProfiles();
  if (!items.length) {
    sel.innerHTML = '<option value="">No saved profiles yet</option>';
    sel.disabled = true;
    if (btn) btn.disabled = true;
    return;
  }
  sel.disabled = false;
  if (btn) btn.disabled = false;
  // Profile names are brewer-typed — escape them (a name with <input>/<textarea> can
  // break out of a select during innerHTML parsing).
  sel.innerHTML = items.map((i) => `<option value="${escapeHtml(i.value)}">${escapeHtml(i.label)}</option>`).join('');
}

// Anything the brewer can COPY into a new profile: their saved profiles, a session profile,
// and the built-in starter rigs.
function populateCopySelect() {
  const sel = $('equipCopySelect');
  if (!sel) return;
  let html = '';
  if (acct.enabled && acct.loggedIn && customProfiles.size) {
    html += '<optgroup label="My profiles">' +
      [...customProfiles].map(([val, p]) => `<option value="${escapeHtml(val)}">${escapeHtml(p.name)}</option>`).join('') + '</optgroup>';
  }
  if (sessionProfile) {
    html += `<optgroup label="This session"><option value="session">${escapeHtml(sessionProfile.name || 'Custom rig')}</option></optgroup>`;
  }
  const builtins = Object.entries(builtinProfiles);
  if (builtins.length) {
    html += '<optgroup label="Starter rigs">' +
      builtins.map(([id, p]) => `<option value="${escapeHtml(id)}">${escapeHtml(p.name || id)}</option>`).join('') + '</optgroup>';
  }
  sel.innerHTML = html || '<option value="">Loading…</option>';
  // Pre-select the rig the brewer is currently using, when it's a built-in.
  if (builtinProfiles[equipSelect.value]) sel.value = equipSelect.value;
}

// Resolve a picker value to a profile. `id` is set only for the brewer's own saved profiles
// (so editing them PUTs in place rather than creating a copy).
function resolveProfileValue(v) {
  if (!v) return null;
  if (v === 'session' && sessionProfile) return { profile: sessionProfile, id: null, name: sessionProfile.name || 'Custom rig', isDefault: false };
  if (v.startsWith('custom:')) {
    const e = customProfiles.get(v);
    if (e) return { profile: e.profile, id: e.id, name: e.name, isDefault: e.isDefault };
  }
  const b = builtinProfiles[v];
  if (b) return { profile: b, id: null, name: b.name || v, isDefault: false };
  return null;
}

function editSelectedProfile() {
  const r = resolveProfileValue($('equipEditSelect').value);
  if (!r) return;
  resetEquipForm(r.profile, r.id, r.isDefault);
  showEquipForm();
}

/**
 * The equipment editor's "Element voltage" select (120 / 240 / Not sure), injected next
 * to Kettle volume on first use rather than added to the section markup: the theme
 * section is Customize-managed, and the builder already injects its own controls
 * (Share, convert). Idempotent; returns the select.
 */
function ensureVoltageField() {
  let sel = $('ef-voltage');
  if (sel) return sel;
  const anchor = $('ef-kettleVolume');
  const grid = anchor?.closest('.equip-grid');
  if (!anchor || !grid) return null;
  const label = document.createElement('label');
  label.className = 'ef';
  label.append('Element voltage');
  sel = document.createElement('select');
  sel.id = 'ef-voltage';
  for (const [value, text] of [['', 'Not sure'], ['120', '120V'], ['240', '240V']]) {
    const o = document.createElement('option');
    o.value = value;
    o.textContent = text;
    sel.appendChild(o);
  }
  label.appendChild(sel);
  const anchorLabel = anchor.closest('label') || anchor;
  anchorLabel.insertAdjacentElement('afterend', label);
  return sel;
}

/** The editor's element-voltage pick: 120, 240, or null for "not sure". */
function readVoltageField() {
  const sel = ensureVoltageField();
  const raw = sel ? sel.value : '';
  return raw === '120' || raw === '240' ? Number(raw) : null;
}

// ── one-time voltage nudge for saved rigs that predate the field ─────
// The kettle controller warns when a recipe built for a 120V element lands on a 240V
// rig (or the reverse) by reading `equipmentVoltage` off the recipe. Custom profiles
// saved before 8/17/2026 have no voltage at all, so the check is blind for them until
// the brewer says. Rather than wait for someone to reopen the editor, ask ONCE the next
// time such a rig is picked or used: 120 / 240 / not sure. "Not sure" stores an explicit
// null so the question never comes back; Escape/cancel only quiets it for this tab.
const voltNudgeQuiet = new Set();
let voltNudgeOpen = false;
async function nudgeCustomVoltage() {
  const v = equipSelect?.value || '';
  if (!v.startsWith('custom:') || voltNudgeOpen || voltNudgeQuiet.has(v)) return;
  const entry = customProfiles.get(v);
  if (!entry || !entry.profile || entry.profile.voltage !== undefined) return;
  voltNudgeOpen = true;
  try {
    const pick = await uiChoice({
      title: `Is "${entry.name || 'this rig'}" 120V or 240V?`,
      body: 'One-time question. A paired kettle controller uses this to warn you when a recipe was built for the other element size (240V boils off harder, so the same batch wants more starting water).',
      options: [
        { value: 120, label: '120V', detail: 'Standard household outlet, 1500-1800W element' },
        { value: 240, label: '240V', detail: 'Dryer/range outlet, 3500-5500W element' },
        { value: 'unknown', label: 'Not sure', detail: "Skip it. You can set it later in the equipment editor." },
      ],
    });
    if (pick === null) { voltNudgeQuiet.add(v); return; }
    const voltage = pick === 'unknown' ? null : pick;
    const profile = { ...entry.profile, voltage };
    const res = await aFetch(`/api/equipment-profiles/${entry.id}`, {
      method: 'PUT',
      body: JSON.stringify({ profile, isDefault: !!entry.isDefault }),
    });
    if (!res.ok) { voltNudgeQuiet.add(v); return; }
    entry.profile = profile;
    if (voltage) toast(`Saved: ${entry.name || 'rig'} is ${voltage}V`);
  } catch {
    voltNudgeQuiet.add(v);
  } finally {
    voltNudgeOpen = false;
  }
}

function copySelectedProfile() {
  const r = resolveProfileValue($('equipCopySelect').value);
  if (!r) return;
  resetEquipForm({ ...r.profile, name: r.id ? `Copy of ${r.name}` : `My ${r.name}` }, null);
  showEquipForm();
}

// Toggle the modal's save-vs-session affordances by auth state: signed-in brewers can save
// to their account; everyone can apply settings for the current session.
function updateEquipModalAuthUI() {
  const authed = acct.enabled && acct.loggedIn;
  if ($('equipAuthNote')) $('equipAuthNote').hidden = authed;
  if ($('equipSave')) {
    $('equipSave').hidden = !authed;
    $('equipSave').classList.toggle('ra-btn--primary', authed);
  }
  if ($('equipUseSession')) $('equipUseSession').classList.toggle('ra-btn--primary', !authed);
}
function closeEquipModal() { $('equipModal').hidden = true; }

function resetEquipForm(profile = null, id = null, isDefault = false) {
  $('equipId').value = id || '';
  $('equipFormTitle').textContent = id ? 'Edit profile' : 'New profile';
  $('ef-name').value = profile?.name || '';
  // The editor opens in the brewer's units (the header gal/L switch is the master) and
  // can be flipped on its own pill; stored values are imperial, converted on the way in.
  ensureEquipUnitsSeg();
  equipFormMetric = displayUnits.water === 'l';
  relabelEquipForm();
  for (const f of EQUIP_FIELDS) equipFillField(f, profile && profile[f] != null ? profile[f] : null);
  // Element voltage rides separately from the numeric fields: it's a 120/240/not-sure
  // pick, and "not sure" (blank) is a legitimate answer the engine never needs.
  const voltSel = ensureVoltageField();
  if (voltSel) voltSel.value = profile && (profile.voltage === 120 || profile.voltage === 240) ? String(profile.voltage) : '';
  $('ef-isDefault').checked = !!isDefault;
  $('equipFormError').hidden = true;
  // Delete only applies to an existing saved profile.
  if ($('equipDelete')) $('equipDelete').hidden = !id;
}

async function deleteProfile(id) {
  if (!(await uiConfirm('Delete this equipment profile?'))) return;
  await aFetch(`/api/equipment-profiles/${id}`, { method: 'DELETE' });
  await loadProfilesIntoSelect();
  showEquipChooser();
  toast('Profile deleted');
}

async function submitEquipForm(e) {
  e.preventDefault();
  const profile = { name: $('ef-name').value.trim() };
  for (const f of EQUIP_FIELDS) {
    const v = equipFieldValue(f);
    if (v != null) profile[f] = v;
  }
  // Explicit null = the brewer answered "not sure", so the one-time voltage nudge
  // (see nudgeCustomVoltage) doesn't ask again. Undefined = never answered.
  profile.voltage = readVoltageField();
  const id = $('equipId').value;
  const body = JSON.stringify({ profile, isDefault: $('ef-isDefault').checked });
  const res = id
    ? await aFetch(`/api/equipment-profiles/${id}`, { method: 'PUT', body })
    : await aFetch('/api/equipment-profiles', { method: 'POST', body });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    $('equipFormError').textContent = err.error || 'Could not save profile.';
    $('equipFormError').hidden = false;
    return;
  }
  const saved = (await res.json().catch(() => ({}))).profile;
  resetEquipForm();
  await loadProfilesIntoSelect();
  // The rig just saved becomes the one in use, and the recipe follows it (bug report
  // #41). Before this, a copied Clawhammer rig saved as a new profile while the header
  // stayed on the original, nothing recalculated, and a header efficiency override kept
  // winning over the number the brewer had just saved, so the edit looked lost.
  const value = saved?.id != null ? `custom:${saved.id}` : '';
  if (value && customProfiles.has(value)) {
    equipSelect.value = value;
    rememberEquip();
    if (effOverride != null && profile.mashEfficiency != null) {
      effOverride = null;
      try { localStorage.removeItem('bb:eff'); } catch (e) { /* private mode */ }
    }
    syncEffField();
    recalcForEquipmentChange();
  }
  showEquipChooser();
  toast('Profile saved');
}

/**
 * Make `profile` the rig for this browser session (no account needed) and select it in
 * the header. Used by the equipment editor's "use for this session" and by a shared
 * recipe that arrives on a rig the viewer doesn't have.
 */
function useSessionProfile(profile) {
  sessionProfile = profile;
  let opt = equipSelect.querySelector('option[data-session]');
  if (!opt) {
    opt = document.createElement('option');
    opt.dataset.session = '1';
    opt.value = 'session';
    equipSelect.insertBefore(opt, equipSelect.firstChild.nextSibling);
  }
  opt.textContent = `${profile.name || 'Custom rig'} (this session)`;
  equipSelect.value = 'session';
}

// Apply the form's values for this browser session only — no account needed. Signed-out
// brewers use this; they're told to log in if they want it kept.
function applySessionProfile() {
  const profile = { name: $('ef-name').value.trim() || 'Custom rig' };
  // "Use for this session" is a type=button, so the form's HTML `required` never fires —
  // validate every required field here, or the engine falls back to a generic rig.
  const problems = [];
  for (const f of EQUIP_FIELDS) {
    const raw = $(`ef-${f}`).value.trim();
    if (raw === '') {
      if (REQUIRED_EQUIP_FIELDS.includes(f)) problems.push(f);
      continue;
    }
    const n = equipFieldValue(f);
    if (!Number.isFinite(n)) { problems.push(f); continue; }
    profile[f] = n;
  }
  if (problems.length) {
    $('equipFormError').textContent = `Fill in all required fields with valid numbers: ${problems.join(', ')}.`;
    $('equipFormError').hidden = false;
    return;
  }
  profile.voltage = readVoltageField();
  useSessionProfile(profile);
  closeEquipModal();
  toast(acct.loggedIn ? 'Using these settings for this session.' : 'Using these settings this session — log in to save them.');
  // Setting equipSelect.value in code doesn't fire 'change', so recompute the recipe against
  // the new rig here too (so the panel doesn't keep showing the old equipment's numbers).
  if (currentRecipe) recalcForEquipmentChange();
}

// ── saved recipes ────────────────────────────────────────────────────
/** Show/hide the save/export/etc. action row depending on auth + whether a recipe exists. */
function afterRecipeRender() {
  // The Hazy IPA slider panel is reconciled FIRST, before the no-recipe early return
  // below: clearing the panel (Start over, a mode switch with nothing stashed) has to
  // take the slider window with it, and that path has no current recipe by definition.
  if (typeof syncHopSliderPane === 'function') syncHopSliderPane();
  // Measured-vs-estimated section rides every render AND every auth refresh (this
  // function runs on both), so signing in fills the Actual column without a reload.
  if (typeof renderMeasuredSection === 'function') renderMeasuredSection();
  const row = $('recipeActions');
  if (!row) return;
  // The action row shows whenever a recipe exists, so Print is always reachable.
  if (!currentRecipe) {
    row.hidden = true;
    return;
  }
  row.hidden = false;
  // Both sheets + BeerXML need no account: available to everyone with a recipe on screen.
  if ($('printBtn')) $('printBtn').hidden = false;
  if ($('brewDayBtn')) $('brewDayBtn').hidden = false;
  if ($('beerXmlBtn')) $('beerXmlBtn').hidden = false;
  // Save & friends require a signed-in customer.
  const authed = acct.enabled && acct.loggedIn;
  const loaded = authed && !!currentSavedId;
  $('saveBtn').hidden = !authed;
  $('saveBtn').textContent = loaded ? '💾 Update' : '💾 Save';
  $('saveAsBtn').hidden = !loaded;
  $('dupBtn').hidden = !loaded;
  $('deleteBtn').hidden = !loaded;
  const shareEl = ensureShareBtn();
  if (shareEl) shareEl.hidden = !loaded;
  const sendEl = ensureSendBtn();
  if (sendEl) sendEl.hidden = !(loaded && ctrl.paired);
  // The bottom-of-panel saved name is gone (Kyle, 8/28): the title at the top carries
  // the name, and the edit button beside it is the rename control now. The element
  // stays in the markup (theme liquid untouched), just permanently empty.
  const sn = $('savedName');
  if (sn) { sn.textContent = ''; sn.hidden = true; }
  ensureTitleEditBtn();
  // Reconcile cart/convert visibility for the current mode (All mode hides the cart).
  if (typeof applyModeUI === 'function') applyModeUI();
  if (typeof wireSectionHeadings === 'function') wireSectionHeadings();
}

/**
 * Share button: find-or-create like the sheet buttons (the theme's action-row liquid is
 * Customize-managed and diverged, so new controls are injected at runtime, never added
 * to markup). Only shown for a signed-in customer with the recipe SAVED — the share
 * token lives on the saved row.
 */
function ensureShareBtn() {
  const anchor = $('dupBtn');
  if (!anchor) return null;
  let el = $('shareBtn');
  if (!el) {
    el = document.createElement('button');
    el.id = 'shareBtn';
    el.type = 'button';
    el.className = anchor.className;
    el.hidden = true;
    el.textContent = '🔗 Share';
    el.title = 'Get a link anyone can open — recipe, numbers, and brew day sheet';
    anchor.insertAdjacentElement('afterend', el);
    el.addEventListener('click', shareCurrentRecipe);
  }
  return el;
}

async function shareCurrentRecipe() {
  if (!currentSavedId) return;
  const res = await aFetch(`/api/recipes/${currentSavedId}/share`, { method: 'POST' });
  if (!res.ok) { toast('Could not create a share link', 'err'); return; }
  const { shareToken } = await res.json();
  if (!shareToken) { toast('Could not create a share link', 'err'); return; }
  // The human-facing share URL is the brew day sheet: recipe, benchmarks, and the
  // step-by-step, served publicly by token. On the storefront that's the App Proxy
  // path; locally the backend serves /brewday directly.
  const url = `${location.origin}${ACCOUNT_API}/brewday/${shareToken}`;
  const copied = await uiDialog({
    title: 'Share this recipe',
    body: 'Anyone with this link can see the recipe and its brew day sheet. It never shows your name or account.',
    input: url,
    okText: 'Copy link',
  });
  if (copied === null) return;
  try {
    await navigator.clipboard.writeText(url);
    toast('Link copied');
  } catch {
    toast('Copy blocked — select the link and copy it by hand', 'err');
  }
}

/**
 * The recipe name reads from the panel title, ignoring the injected edit button.
 * Fallback readers used to take $('recipeTitle').textContent directly; with a button
 * living inside the h2 that would append its label to the name, so they use this.
 */
function recipeTitleText() {
  const h = $('recipeTitle');
  if (!h) return '';
  let t = '';
  h.childNodes.forEach((n) => { if (n.nodeType === 3) t += n.textContent; });
  return t.trim();
}

/**
 * The "edit" control beside the title (Kyle, 8/28: the rename affordance moved here
 * from the bottom-of-panel saved name, which is gone). Injected because the theme's
 * panel liquid is Customize-managed. Every title write wipes the h2's children, so
 * callers re-append via afterRecipeRender; the node is kept in a module var because
 * a detached element can't be found by id.
 */
let titleEditBtnEl = null;
function ensureTitleEditBtn() {
  const h = $('recipeTitle');
  if (!h) return null;
  if (!titleEditBtnEl) {
    titleEditBtnEl = document.createElement('button');
    titleEditBtnEl.id = 'titleEditBtn';
    titleEditBtnEl.type = 'button';
    titleEditBtnEl.className = 'title-edit';
    titleEditBtnEl.title = 'Rename this recipe';
    titleEditBtnEl.innerHTML = '<span class="section-link__hint">edit</span>';
    titleEditBtnEl.addEventListener('click', editRecipeName);
  }
  if (titleEditBtnEl.parentElement !== h) h.appendChild(titleEditBtnEl);
  return titleEditBtnEl;
}

/** Title edit: the server rename for a loaded saved recipe, a working-copy name otherwise. */
async function editRecipeName() {
  if (acct.enabled && acct.loggedIn && currentSavedId) return renameRecipe();
  if (!currentReport) return;
  const cur = currentReport.recipeName || (currentReport.style ? currentReport.style.style : '') || '';
  const name = await uiPrompt('Name this recipe', cur);
  if (!name || name === cur) return;
  currentReport.recipeName = name;
  $('recipeTitle').textContent = name;
  afterRecipeRender();
  persistSession();
  toast('Name updated');
}

/** Rename the saved recipe in place — name only, the stored recipe content is untouched. */
async function renameRecipe() {
  if (!currentSavedId) return;
  const name = await uiPrompt('Rename this recipe', currentSavedName);
  if (!name || name === currentSavedName) return;
  const res0 = await aFetch(`/api/recipes/${currentSavedId}`);
  if (!res0.ok) { toast('Could not rename', 'err'); return; }
  const { recipe: row } = await res0.json();
  const res = await aFetch(`/api/recipes/${currentSavedId}`, {
    method: 'PUT',
    body: JSON.stringify({ name, recipe: row.recipe, styleName: row.styleName, batchSize: row.batchSize, equipmentRef: row.equipmentRef }),
  });
  if (!res.ok) { toast('Could not rename', 'err'); return; }
  currentSavedName = name;
  // Keep the on-screen title and the report (brew sheet / cart label) on the new name.
  if (currentReport) { currentReport.recipeName = name; $('recipeTitle').textContent = name; }
  afterRecipeRender();
  persistSession();
  toast('Recipe renamed');
}

/** Assemble the save payload from the current recipe + selected style/equipment. */
function recipePayload(name) {
  const v = equipSelect.value;
  let equipmentRef = null;
  const prof = activeEquipProfile();
  if (effOverride != null && prof && effOverride !== prof.mashEfficiency) {
    // The rig plus the brewer's efficiency, saved as an inline profile so reopening
    // recomputes with the numbers the brewer actually saw (the bug-9/13 fidelity rule:
    // a saved recipe must never come back with different benchmarks).
    equipmentRef = { kind: 'custom', profile: { ...prof, mashEfficiency: effOverride } };
  } else if (v === 'session' && sessionProfile) {
    equipmentRef = { kind: 'custom', profile: sessionProfile };
  } else if (v.startsWith('custom:')) {
    const entry = customProfiles.get(v);
    if (entry) equipmentRef = { kind: 'custom', profile: entry.profile };
  } else if (v) {
    equipmentRef = { kind: 'builtin', id: v };
  }
  return {
    name,
    recipe: currentRecipe,
    styleName: styleSelect.value || null,
    batchSize: Number($('batchSize').value) || null,
    equipmentRef,
    // The catalog mode this recipe was built in. Reopening recomputes in the SAME mode,
    // so an All-mode recipe can never come back rewritten onto stocked products.
    mode: currentMode,
  };
}

async function saveRecipe(forceNew = false) {
  if (!currentRecipe) return;
  const wasUpdate = currentSavedId && !forceNew;
  let name = currentSavedName;
  if (!wasUpdate) {
    name = await uiPrompt('Name this recipe', name || currentReport?.recipeName || (recipeTitleText() || 'My recipe'));
    if (!name) return;
  }
  const body = JSON.stringify(recipePayload(name));
  const res = wasUpdate
    ? await aFetch(`/api/recipes/${currentSavedId}`, { method: 'PUT', body })
    : await aFetch('/api/recipes', { method: 'POST', body });
  if (!res.ok) {
    // A 401 here means the Shopify sign-in lapsed while the page sat open — the builder
    // still shows the Update button, but the App Proxy no longer vouches for the customer.
    // Say what actually happened (the generic toast sent a real bug report our way,
    // 2026-08-27) and re-check auth so the UI locks to match reality.
    if (res.status === 401) {
      toast('Your sign-in expired, so the recipe could not be saved. Log in again and your changes will go through.', 'err');
      refreshAccount();
      return;
    }
    const err = await res.json().catch(() => ({}));
    toast(err.error || 'Could not save recipe', 'err');
    return;
  }
  const { recipe } = await res.json();
  currentSavedId = recipe.id;
  currentSavedName = recipe.name;
  // The chosen save name becomes the recipe's name everywhere (title, brew sheet, cart).
  if (currentReport) { currentReport.recipeName = recipe.name; $('recipeTitle').textContent = recipe.name; }
  afterRecipeRender();
  persistSession();
  toast(wasUpdate ? 'Recipe updated' : 'Recipe saved');
}

/** Re-open the brew list under a recipe row after the drawer re-renders. */
function expandBrews(recipeId) {
  const panel = $('recipesList')?.querySelector(`[data-brews-for="${CSS.escape(recipeId)}"]`);
  if (panel) panel.hidden = false;
}

// ── manual brew log / edit ───────────────────────────────────────────
// The controller isn't the only way a brew happens. This form writes the SAME summary
// shape the controller sends (readings × {gravity, volume, ph}, units, og/fg/abv, notes)
// so a hand-logged brew renders exactly like a kettle-logged one, in the drawer and on
// /brew/<token>. Numbers stay raw in the unit the brewer picked; blank stays absent.
const BREW_POINTS = [
  ['mash', 'Mash', ['ph']],
  ['preBoil', 'Pre-boil', ['gravity', 'volume', 'ph']],
  ['postBoil', 'Post-boil', ['gravity', 'volume', 'ph']],
  ['intoFermenter', 'Into fermenter', ['gravity', 'volume', 'ph']],
  ['packaging', 'Packaging', ['gravity', 'ph']],
];
const dateInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * Edit an existing brew (pass `existing`) or log a new one against a recipe (pass
 * `ctx = {recipeId, recipeName}`). Resolves true when saved, false/null otherwise.
 */
function editBrewDialog(ctx, existing) {
  return new Promise((resolve) => {
    const s = (existing && existing.summary) || {};
    const readings = (s.readings && typeof s.readings === 'object') ? s.readings : {};
    const units = (s.units && typeof s.units === 'object') ? s.units : {};
    const gUnit = units.gravity === 'plato' ? 'plato' : 'sg';
    const vUnit = units.volume === 'l' ? 'l' : 'gal';
    const scrim = document.createElement('div');
    scrim.className = 'bb-dialog-scrim';
    const num = (v) => (v == null || v === '' ? '' : String(v));
    const rowsHtml = BREW_POINTS.map(([key, label, fields]) => {
      const p = (readings[key] && typeof readings[key] === 'object') ? readings[key] : {};
      const cell = (f) => fields.includes(f)
        ? `<input type="number" step="any" inputmode="decimal" data-pt="${key}" data-f="${f}" value="${escapeHtml(num(p[f]))}" placeholder="${f === 'gravity' ? (gUnit === 'plato' ? '°P' : 'SG') : f === 'volume' ? (vUnit === 'l' ? 'L' : 'gal') : 'pH'}" />`
        : `<span class="brew-ed__na"></span>`;
      return `<div class="brew-ed__row"><span class="brew-ed__lbl">${escapeHtml(label)}</span>${cell('gravity')}${cell('volume')}${cell('ph')}</div>`;
    }).join('');
    scrim.innerHTML = `
      <div class="bb-dialog bb-dialog--wide brew-ed" role="dialog" aria-modal="true">
        <h3></h3>
        <p class="bb-dialog__body"></p>
        <div class="brew-ed__top">
          <label>Brew date<input type="date" class="brew-ed__date" /></label>
          <label>Gravity<select class="brew-ed__gunit"><option value="sg">SG</option><option value="plato">°Plato</option></select></label>
          <label>Volume<select class="brew-ed__vunit"><option value="gal">gal</option><option value="l">L</option></select></label>
        </div>
        <div class="brew-ed__grid">
          <div class="brew-ed__row brew-ed__head"><span class="brew-ed__lbl"></span><span>Gravity</span><span>Volume</span><span>pH</span></div>
          ${rowsHtml}
        </div>
        <label class="brew-ed__notes">Notes<textarea rows="3" maxlength="2000"></textarea></label>
        <div class="bb-dialog__err" hidden></div>
        <div class="bb-dialog__actions">
          <button type="button" class="link-btn bb-dialog__cancel">Cancel</button>
          <button type="button" class="bb-dialog__ok">Save</button>
        </div>
      </div>`;
    const name = existing ? (existing.recipeName || 'this brew') : (ctx?.recipeName || 'this recipe');
    scrim.querySelector('h3').textContent = existing ? `Edit brew: ${name}` : `Brew stats: ${name}`;
    scrim.querySelector('.bb-dialog__body').textContent = existing
      ? 'Fix a reading or add the ones you took later. Blank means not measured.'
      : 'Enter what you measured. Blank means not measured. OG and FG come from the Into fermenter and Packaging gravities.';
    const dateEl = scrim.querySelector('.brew-ed__date');
    dateEl.value = dateInputValue(existing ? (existing.finishedAt || existing.startedAt || existing.createdAt) : new Date().toISOString());
    scrim.querySelector('.brew-ed__gunit').value = gUnit;
    scrim.querySelector('.brew-ed__vunit').value = vUnit;
    scrim.querySelector('textarea').value = typeof s.notes === 'string' ? s.notes : '';
    const errEl = scrim.querySelector('.bb-dialog__err');
    const done = (val) => { document.removeEventListener('keydown', onKey, true); scrim.remove(); resolve(val); };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); done(null); } };
    const save = async () => {
      const g = scrim.querySelector('.brew-ed__gunit').value;
      const v = scrim.querySelector('.brew-ed__vunit').value;
      const out = {};
      let bad = false;
      for (const inp of scrim.querySelectorAll('input[data-pt]')) {
        const raw = inp.value.trim();
        if (raw === '') continue;
        const n = Number(raw);
        if (!Number.isFinite(n)) { bad = true; inp.focus(); break; }
        (out[inp.dataset.pt] ||= {})[inp.dataset.f] = n;
      }
      if (bad) { errEl.textContent = 'Every reading has to be a number.'; errEl.hidden = false; return; }
      // Preserve anything else the controller stored (run stats, energy, etc.); replace
      // only what this form owns.
      const summary = { ...s };
      delete summary.readings; delete summary.units; delete summary.og; delete summary.fg; delete summary.abv; delete summary.notes;
      if (Object.keys(out).length) { summary.readings = out; summary.units = { gravity: g, volume: v }; }
      const og = out.intoFermenter?.gravity;
      const fg = out.packaging?.gravity;
      if (og != null) summary.og = og;
      if (fg != null) summary.fg = fg;
      // ABV only when both gravities are SG (the report page converts Plato itself).
      if (og != null && fg != null && g === 'sg' && og > fg) summary.abv = Number(((og - fg) * 131.25).toFixed(1));
      const notes = scrim.querySelector('textarea').value.trim();
      if (notes) summary.notes = notes;
      const dateVal = dateEl.value;
      const finishedAt = dateVal ? new Date(`${dateVal}T12:00:00`).toISOString() : null;
      const body = { summary, finishedAt };
      if (!existing) body.recipeId = ctx?.recipeId || null;
      const okBtn = scrim.querySelector('.bb-dialog__ok');
      okBtn.disabled = true;
      const res = existing
        ? await aFetch(`/api/brews/${existing.id}`, { method: 'PUT', body: JSON.stringify(body) })
        : await aFetch('/api/brews', { method: 'POST', body: JSON.stringify(body) });
      okBtn.disabled = false;
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        errEl.textContent = err.error || 'Could not save.'; errEl.hidden = false; return;
      }
      toast(existing ? 'Brew updated' : 'Brew logged');
      done(true);
    };
    scrim.querySelector('.bb-dialog__ok').addEventListener('click', save);
    scrim.querySelector('.bb-dialog__cancel').addEventListener('click', () => done(null));
    scrim.addEventListener('click', (e) => { if (e.target === scrim) done(null); });
    document.addEventListener('keydown', onKey, true);
    (document.getElementById('beer-builder-root') || document.body).appendChild(scrim);
    scrim.querySelector('input[data-pt]')?.focus();
  });
}

/** One brew-history line: date + whatever compact results the controller reported. */
function brewLine(b) {
  const when = b.finishedAt || b.startedAt || b.createdAt;
  const date = when ? new Date(when).toLocaleDateString() : '';
  const s = b.summary || {};
  const bits = [];
  // A brew with no saved recipe (manual brew on the controller, imported recipe,
  // or a recipe deleted later) leads with the name the record arrived with —
  // otherwise a nearly-empty controller record renders as a bare date.
  if (!b.recipeId && b.recipeName) bits.push(b.recipeName);
  if (s.og != null) bits.push(`OG ${s.og}`);
  if (s.fg != null) bits.push(`FG ${s.fg}`);
  if (s.abv != null) bits.push(`${s.abv}% ABV`);
  if (typeof s.notes === 'string' && s.notes.trim()) bits.push(s.notes.trim().slice(0, 80));
  // The iteration that was on the kettle, when the controller reported one.
  const ver = b.recipeVersion ? `v${b.recipeVersion}` : '';
  // Everything here arrived from a device over the vendor Worker — escape it all,
  // same rule as brewer-typed names (bug report #23).
  return escapeHtml([date, ver, bits.join(' · ')].filter(Boolean).join(' · '));
}

// ── brew-day readings (controller contract, agreed with the vendor 8/17/2026) ──
// summary.readings = { mash{ph}, preBoil{gravity,volume,ph}, postBoil{...},
//                      intoFermenter{...}, packaging{gravity,ph} }
// summary.units    = { gravity: 'sg'|'plato', volume: 'gal'|'l', temp: 'f'|'c' }
// Numbers arrive RAW in the units the brewer captured; the controller converts nothing,
// so this renders them as-is with their unit. Absent keys are absent (never zero) on the
// device side, and an absent reading prints nothing here, not a blank cell.
const READING_POINTS = [
  ['mash', 'Mash'],
  ['preBoil', 'Pre-boil'],
  ['postBoil', 'Post-boil'],
  ['intoFermenter', 'Into fermenter'],
  ['packaging', 'Packaging'],
];
function fmtReadingGravity(v, unit) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (unit === 'plato') return `${n}°P`;
  // SG: keep three decimals so 1.05 reads as 1.050 like a hydrometer.
  return n < 2 ? n.toFixed(3) : String(n);
}
function fmtReadingVolume(v, unit) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `${n} ${unit === 'l' ? 'L' : 'gal'}`;
}
function fmtReadingPh(v) {
  const n = Number(v);
  return Number.isFinite(n) ? `pH ${n}` : null;
}
/** The expanded readings block for one brew, or '' when the controller sent none. */
function brewReadingsHtml(b) {
  const s = b.summary || {};
  const r = s.readings;
  if (!r || typeof r !== 'object') return '';
  const units = (s.units && typeof s.units === 'object') ? s.units : {};
  const rows = [];
  for (const [key, label] of READING_POINTS) {
    const p = r[key];
    if (!p || typeof p !== 'object') continue;
    const cells = [];
    const g = p.gravity != null ? fmtReadingGravity(p.gravity, units.gravity) : null;
    const vol = p.volume != null ? fmtReadingVolume(p.volume, units.volume) : null;
    const ph = p.ph != null ? fmtReadingPh(p.ph) : null;
    if (g) cells.push(g);
    if (vol) cells.push(vol);
    if (ph) cells.push(ph);
    if (!cells.length) continue;
    rows.push(`<span class="brew-rd__pt"><b>${escapeHtml(label)}</b> ${escapeHtml(cells.join(' / '))}</span>`);
  }
  if (!rows.length) return '';
  return `<span class="brew-rd">${rows.join('')}</span>`;
}

/** The brew's own public report page (/brew/<token>): what it hit vs what the recipe aimed at. */
function brewReportUrl(b) {
  if (!b || !b.shareToken) return '';
  return `${location.origin}${ACCOUNT_API}/brew/${encodeURIComponent(b.shareToken)}`;
}

/** Render the expandable brew list under a recipe row. */
function brewListHtml(entries) {
  return entries
    .map(
      (b) => {
        const url = brewReportUrl(b);
        return `<div class="rec-row rec-row--brew" style="padding:4px 6px 4px 22px" data-brew="${escapeHtml(String(b.id))}">
        <span class="rec-row__sub" style="flex:1">🍺 ${brewLine(b)}${brewReadingsHtml(b)}</span>
        ${url ? `<a class="link-btn" href="${escapeHtml(url)}" target="_blank" rel="noopener" title="Open this brew's report">Report</a>` : ''}
        <button type="button" class="link-btn" data-act="editbrew" title="Edit readings and notes">Edit</button>
        <button type="button" class="link-btn link-btn--danger" data-act="delbrew" title="Remove this brew log entry">Remove</button>
      </div>`;
      },
    )
    .join('');
}

// ── measured benchmarks (panel section, Kyle 8/28) ───────────────────
// The panel's benchmark tiles are the CALCULATOR's estimates. What actually happened
// on brew day lives in the brew log (controller write-backs land there, and so do
// hand-logged brews). This section puts the two side by side inside the recipe panel:
// Estimated from the engine, Actual from the latest logged brew of this saved recipe,
// editable in place so stats can be logged without opening the My Recipes drawer.
// Injected under the Notes section because the theme's section liquid is
// Customize-managed; same find-or-create pattern as the water section.
let measOpen = false;
let measBrews = null;      // brew entries for the recipe the section last loaded
let measLoadedFor = null;  // saved-recipe id the cache belongs to
let measLoading = false;

const MEAS_CSS = `
.title-edit{background:none;border:0;padding:0;margin-left:8px;cursor:pointer;vertical-align:3px}
.meas__head{cursor:pointer;user-select:none}
.meas__head .water-chev{flex:none;color:var(--muted)}
.meas__head.is-collapsed .water-chev{transform:rotate(-90deg)}
.meas__tbl th{font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:var(--muted);font-weight:600;text-align:right;padding:0 0 4px;border-bottom:1px solid var(--line)}
.meas__tbl th:first-child{text-align:left}
.meas__tbl td.amt .inline-num{width:78px}
.meas__tbl input::placeholder{color:var(--muted);-webkit-text-fill-color:var(--muted);opacity:1}
.meas__tbl td:first-child .sub{display:block}
.meas__abv{font-weight:600}
.meas__src{font-size:11.5px;color:var(--muted);margin:8px 0 0}
.meas__hint{font-size:12px;color:var(--muted);margin:8px 0 0}
.meas__save{margin-top:10px}
.meas__err{color:var(--bad);font-size:12px;margin-top:6px}
`;
function ensureMeasCss() {
  if (document.getElementById('measCss')) return;
  const st = document.createElement('style');
  st.id = 'measCss';
  st.textContent = MEAS_CSS;
  document.head.appendChild(st);
}

/** Rows the section compares: [label, sub, estimated display value, reading point, field]. */
function measRowDefs() {
  const b = currentReport?.benchmarks;
  if (!b) return [];
  const w = currentReport.water || null;
  return [
    ['Mash pH', '', w && w.mashPh != null ? w.mashPh.toFixed(2) : '', 'mash', 'ph'],
    ['Pre-boil gravity', '', fmtGravity(b.gravity.preBoil), 'preBoil', 'gravity'],
    ['Pre-boil volume', '', fmtVol(b.volumes.preBoil), 'preBoil', 'volume'],
    ['Post-boil volume', '', fmtVol(b.volumes.postBoil), 'postBoil', 'volume'],
    ['OG', 'into the fermenter', fmtGravity(b.gravity.og), 'intoFermenter', 'gravity'],
    ['Volume into fermenter', '', fmtVol(b.volumes.intoFermenter), 'intoFermenter', 'volume'],
    ['FG', 'at packaging', fmtGravity(b.gravity.fg), 'packaging', 'gravity'],
  ];
}

/** Fetch this recipe's brew log for the section (latest first, same API the drawer uses). */
async function measLoad() {
  if (measLoading || !(acct.enabled && acct.loggedIn && currentSavedId)) return;
  const id = currentSavedId;
  measLoading = true;
  try {
    const { brews } = await aFetch('/api/brews').then((r) => r.json());
    measBrews = (brews || []).filter((x) => String(x.recipeId) === String(id));
  } catch (e) {
    measBrews = [];
  }
  measLoadedFor = id;
  measLoading = false;
  renderMeasuredSection();
}

function renderMeasBody(body) {
  const authed = acct.enabled && acct.loggedIn;
  const saved = authed && !!currentSavedId;
  const loaded = saved && measLoadedFor === currentSavedId && Array.isArray(measBrews);
  if (saved && !loaded) {
    body.innerHTML = '<p class="meas__hint">Loading your brew log…</p>';
    measLoad();
    return;
  }
  const entries = loaded ? measBrews : [];
  const latest = entries.length ? entries[0] : null;
  const s = (latest && latest.summary) || {};
  const readings = (s.readings && typeof s.readings === 'object') ? s.readings : {};
  const units = (s.units && typeof s.units === 'object') ? s.units : {};
  const gUnit = units.gravity === 'plato' ? 'plato' : 'sg';
  const vUnit = units.volume === 'l' || units.volume === 'gal'
    ? units.volume
    : (displayUnits.water === 'l' ? 'l' : 'gal');
  // A recalc re-renders the panel; carry unsaved typing across it for the same recipe.
  const held = {};
  if (body.dataset.for && body.dataset.for === String(currentSavedId || '')) {
    body.querySelectorAll('input[data-pt]').forEach((inp) => { held[`${inp.dataset.pt}.${inp.dataset.f}`] = inp.value; });
  }
  body.dataset.for = String(currentSavedId || '');
  const num = (v) => (v == null || v === '' ? '' : String(v));
  const prefill = (pt, f) => {
    const k = `${pt}.${f}`;
    if (k in held) return held[k];
    const p = (readings[pt] && typeof readings[pt] === 'object') ? readings[pt] : {};
    if (p[f] != null) return num(p[f]);
    // Older brews (and some controller payloads) carry og/fg at the top level only.
    if (pt === 'intoFermenter' && f === 'gravity') return num(s.og);
    if (pt === 'packaging' && f === 'gravity') return num(s.fg);
    return '';
  };
  // No value-shaped placeholders: the panel forces -webkit-text-fill-color on inputs
  // (counter to the host theme painting controls white), and that ALSO paints
  // placeholder text full-strength — an empty box with "1.050" in it read as an entered
  // value (Kyle's 8/28 bug). Units sit OUTSIDE the box like the boil row's "min", and
  // the inputs are text+decimal so a scroll over the column can't spin a value and no
  // spinner arrows crowd 62px boxes.
  const unitOf = (f) => (f === 'gravity' ? (gUnit === 'plato' ? '°P' : '') : f === 'volume' ? (vUnit === 'l' ? 'L' : 'gal') : '');
  const dis = saved ? '' : ' disabled';
  const rows = measRowDefs().map(([label, sub, est, pt, f]) => {
    const u = unitOf(f);
    return `<tr><td>${escapeHtml(label)}${sub ? `<span class="sub">${escapeHtml(sub)}</span>` : ''}</td>`
    + `<td class="amt">${est !== '' ? escapeHtml(String(est)) : '<span class="sub">–</span>'}</td>`
    + `<td class="amt"><input class="inline-num" type="text" inputmode="decimal" autocomplete="off" data-pt="${pt}" data-f="${f}" value="${escapeHtml(prefill(pt, f))}"${dis}>${u ? ` <span class="sub">${escapeHtml(u)}</span>` : ''}</td></tr>`;
  }).join('');
  const b = currentReport.benchmarks;
  const abvRow = `<tr><td>ABV</td><td class="amt">${escapeHtml(String(b.abv))}%</td>`
    + `<td class="amt"><span class="meas__abv" id="measAbv"></span></td></tr>`;
  let src = '';
  if (latest) {
    const when = latest.finishedAt || latest.startedAt || latest.createdAt;
    const date = when ? new Date(when).toLocaleDateString() : '';
    const url = brewReportUrl(latest);
    src = `<p class="meas__src">From your ${latest.source === 'manual' ? 'logged' : 'controller'} brew${date ? ' on ' + escapeHtml(date) : ''}`
      + (entries.length > 1 ? ` · latest of ${entries.length} (full history in My recipes)` : '')
      + (url ? ` · <a class="link-btn" href="${escapeHtml(url)}" target="_blank" rel="noopener">Report</a>` : '')
      + '</p>';
  } else if (saved) {
    src = '<p class="meas__src">Nothing logged for this recipe yet. Enter what you measured and hit Save. Brews finished on the controller land here on their own.</p>';
  }
  const hint = !authed
    ? '<p class="meas__hint">Sign in to log the stats you measure on brew day.</p>'
    : (!saved ? '<p class="meas__hint">Save this recipe to log measured stats against it.</p>' : '');
  body.innerHTML = `<table class="ing meas__tbl">`
    + `<tr><th></th><th>Estimated</th><th>Actual</th></tr>${rows}${abvRow}</table>`
    + src + hint
    + (saved ? '<button type="button" class="ra-btn meas__save" id="measSaveBtn">💾 Save measured stats</button><div class="meas__err" id="measErr" hidden></div>' : '');
  // Actual ABV derives from the measured OG/FG (SG only; the report page converts Plato).
  const abvOut = body.querySelector('#measAbv');
  const derive = () => {
    if (gUnit !== 'sg') { abvOut.textContent = s.abv != null ? `${s.abv}%` : ''; return; }
    const og = Number(body.querySelector('input[data-pt="intoFermenter"][data-f="gravity"]')?.value);
    const fg = Number(body.querySelector('input[data-pt="packaging"][data-f="gravity"]')?.value);
    abvOut.textContent = Number.isFinite(og) && Number.isFinite(fg) && og > fg && og > 0.9
      ? `${((og - fg) * 131.25).toFixed(1)}%` : '';
  };
  derive();
  body.querySelectorAll('input[data-f="gravity"]').forEach((inp) => inp.addEventListener('input', derive));
  body.querySelector('#measSaveBtn')?.addEventListener('click', () => measSave(body, latest, gUnit, vUnit));
}

/** Write the Actual column back to the brew log: update the latest brew, or start one. */
async function measSave(body, latest, gUnit, vUnit) {
  const errEl = body.querySelector('#measErr');
  const out = {};
  for (const inp of body.querySelectorAll('input[data-pt]')) {
    const raw = inp.value.trim();
    if (raw === '') continue;
    const n = Number(raw);
    if (!Number.isFinite(n)) { errEl.textContent = 'Every reading has to be a number.'; errEl.hidden = false; inp.focus(); return; }
    (out[inp.dataset.pt] ||= {})[inp.dataset.f] = n;
  }
  errEl.hidden = true;
  // Same merge the brew-edit dialog does: replace what this form owns, keep everything
  // else the controller stored (run stats, energy, notes).
  const summary = { ...((latest && latest.summary) || {}) };
  delete summary.readings; delete summary.units; delete summary.og; delete summary.fg; delete summary.abv;
  if (Object.keys(out).length) { summary.readings = out; summary.units = { gravity: gUnit, volume: vUnit }; }
  const og = out.intoFermenter?.gravity;
  const fg = out.packaging?.gravity;
  if (og != null) summary.og = og;
  if (fg != null) summary.fg = fg;
  if (og != null && fg != null && gUnit === 'sg' && og > fg) summary.abv = Number(((og - fg) * 131.25).toFixed(1));
  const btn = body.querySelector('#measSaveBtn');
  if (btn) btn.disabled = true;
  const res = latest
    ? await aFetch(`/api/brews/${latest.id}`, { method: 'PUT', body: JSON.stringify({ summary }) })
    : await aFetch('/api/brews', { method: 'POST', body: JSON.stringify({ summary, recipeId: currentSavedId, finishedAt: new Date().toISOString() }) });
  if (btn) btn.disabled = false;
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    errEl.textContent = err.error || 'Could not save.'; errEl.hidden = false; return;
  }
  toast('Measured stats saved');
  measLoadedFor = null; // refetch so the section (and the drawer next time) show what stuck
  renderMeasuredSection();
}

function renderMeasuredSection() {
  ensureMeasCss();
  let sec = $('measuredSection');
  const warnSec = $('warnSection');
  if (!sec) {
    if (!warnSec) return;
    sec = document.createElement('div');
    sec.className = 'recipe__section';
    sec.id = 'measuredSection';
    warnSec.insertAdjacentElement('afterend', sec);
  }
  if (!currentReport || !currentReport.benchmarks) { sec.hidden = true; return; }
  sec.hidden = false;
  if (!sec.dataset.wired) {
    sec.dataset.wired = '1';
    // Same visual language as the Water Chem heading (the section-link box + the drawn
    // stroke chevron — never a "▾" character, see the water section's note), but the
    // box runs the full column width like Yeast & Fermentation because the chevron
    // lives INSIDE the box: this heading only ever expands/collapses, so the whole box
    // is the toggle and no outside control is needed (Kyle, 8/28).
    const chevSvg = '<svg class="water-chev" viewBox="0 0 12 7" aria-hidden="true" focusable="false">'
      + '<polyline points="1,1.3 6,5.7 11,1.3" fill="none" stroke="currentColor" stroke-width="1.5"'
      + ' stroke-linecap="round" stroke-linejoin="round"/></svg>';
    sec.innerHTML = '<h3 class="section-link meas__head" role="button" title="What you measured on brew day, next to what the calculator estimated">'
      + `<span>Measured Benchmarks</span>${chevSvg}</h3><div id="measBody" hidden></div>`;
    sec.querySelector('.meas__head').addEventListener('click', () => {
      measOpen = !measOpen;
      renderMeasuredSection();
    });
  }
  const head = sec.querySelector('.meas__head');
  head.classList.toggle('is-collapsed', !measOpen);
  head.setAttribute('aria-expanded', String(measOpen));
  const body = sec.querySelector('#measBody');
  body.hidden = !measOpen;
  if (measOpen) renderMeasBody(body);
}

async function openRecipes() {
  $('recipesDrawer').hidden = false;
  const list = $('recipesList');
  list.innerHTML = '<p class="muted">Loading…</p>';
  // Brew history rides along: controller write-backs land in /api/brews and show under
  // the recipe they were brewed from. A failed brews fetch degrades to no history.
  const [{ recipes }, { brews }] = await Promise.all([
    aFetch('/api/recipes').then((r) => r.json()).catch(() => ({ recipes: [] })),
    aFetch('/api/brews').then((r) => r.json()).catch(() => ({ brews: [] })),
  ]);
  const byRecipe = new Map();
  const orphans = [];
  for (const b of brews || []) {
    if (b.recipeId) {
      if (!byRecipe.has(b.recipeId)) byRecipe.set(b.recipeId, []);
      byRecipe.get(b.recipeId).push(b);
    } else {
      orphans.push(b);
    }
  }
  if ((!recipes || !recipes.length) && !orphans.length) {
    list.innerHTML = '<p class="muted">No saved recipes yet. Build one, then hit Save.</p>';
    return;
  }
  // One row template shared by active and archived sections. Stars pin a recipe to the
  // top; the vN badge opens version history; brew info comes from the write-back log.
  const rowHtml = (r) => {
    const b = r.computed?.benchmarks;
    // name and styleName are brewer-typed — escape them like every other panel row
    // (bug report #23: a recipe named with an <img onerror> payload ran on render).
    const sub = b ? `${b.gravity.og} OG · ${b.abv}% ABV · ${b.ibu} IBU` : escapeHtml(r.styleName || '');
    const entries = byRecipe.get(r.id) || [];
    const last = entries[0];
    const lastWhen = last ? (last.finishedAt || last.startedAt || last.createdAt) : null;
    const brewed = entries.length
      ? ` · brewed ${entries.length}×${lastWhen ? ', last ' + new Date(lastWhen).toLocaleDateString() : ''}`
      : '';
    const brewsBtn = entries.length
      ? `<button type="button" class="link-btn" data-act="brews" title="Brew history">${entries.length} brew${entries.length === 1 ? '' : 's'}</button>`
      : '';
    const verBadge = r.version > 1
      ? `<button type="button" class="link-btn" data-act="history" title="Version history — view and restore earlier iterations">v${r.version}</button>`
      : '';
    const archBtn = r.archived
      ? `<button type="button" class="link-btn" data-act="unarch" title="Bring this recipe back to the main list">Unarchive</button>`
      : `<button type="button" class="link-btn" data-act="arch" title="Hide from the list (kept forever, restorable any time)">Archive</button>`;
    return `<div class="rec-row" data-id="${escapeHtml(String(r.id))}">
      <button type="button" class="link-btn" data-act="fav" title="${r.favorite ? 'Unstar' : 'Star this recipe'}" style="font-size:16px;line-height:1;padding:2px 4px">${r.favorite ? '★' : '☆'}</button>
      <button type="button" class="rec-row__load" data-act="load">
        <span class="rec-row__name">${escapeHtml(r.name)}</span>
        <span class="rec-row__sub">${r.styleName ? escapeHtml(r.styleName) + ' · ' : ''}${sub}${escapeHtml(brewed)}</span>
      </button>
      ${verBadge}
      ${brewsBtn}
      <button type="button" class="link-btn" data-act="logbrew" title="Log the stats you measured for a brew of this recipe (no controller needed)">Stats</button>
      ${archBtn}
      <button type="button" class="link-btn link-btn--danger" data-act="del" title="Delete">Delete</button>
    </div>
    <div class="rec-brews" data-brews-for="${escapeHtml(String(r.id))}" hidden>${entries.length ? brewListHtml(entries) : ''}</div>`;
  };
  const active = (recipes || []).filter((r) => !r.archived);
  const archived = (recipes || []).filter((r) => r.archived);
  // Favorites first; within each group keep the API's most-recently-updated order.
  const ordered = [...active.filter((r) => r.favorite), ...active.filter((r) => !r.favorite)];
  list.innerHTML =
    ordered.map(rowHtml).join('')
    + (archived.length
      ? `<button type="button" class="link-btn" id="archToggle" style="margin:12px 6px 4px">Archived (${archived.length}) ▸</button>
         <div id="archList" hidden>${archived.map(rowHtml).join('')}</div>`
      : '')
    + (orphans.length
      ? `<p class="muted" style="margin:12px 6px 4px">Brews without a saved recipe</p>${brewListHtml(orphans)}`
      : '');
  $('archToggle')?.addEventListener('click', () => {
    const el = $('archList');
    el.hidden = !el.hidden;
    $('archToggle').textContent = `Archived (${archived.length}) ${el.hidden ? '▸' : '▾'}`;
  });
  list.querySelectorAll('.rec-row[data-id]').forEach((row) => {
    const id = row.dataset.id;
    row.querySelector('[data-act="load"]').addEventListener('click', () => loadRecipe(id));
    row.querySelector('[data-act="del"]').addEventListener('click', () => deleteRecipe(id));
    row.querySelector('[data-act="brews"]')?.addEventListener('click', () => {
      const panel = list.querySelector(`[data-brews-for="${CSS.escape(id)}"]`);
      if (panel) panel.hidden = !panel.hidden;
    });
    row.querySelector('[data-act="fav"]')?.addEventListener('click', async () => {
      const rec = (recipes || []).find((x) => x.id === id);
      const res = await aFetch(`/api/recipes/${id}/flags`, { method: 'POST', body: JSON.stringify({ favorite: !rec?.favorite }) });
      if (!res.ok) { toast('Could not update star', 'err'); return; }
      await openRecipes();
    });
    row.querySelector('[data-act="arch"]')?.addEventListener('click', async () => {
      const res = await aFetch(`/api/recipes/${id}/flags`, { method: 'POST', body: JSON.stringify({ archived: true }) });
      if (!res.ok) { toast('Could not archive', 'err'); return; }
      toast('Recipe archived');
      await openRecipes();
    });
    row.querySelector('[data-act="unarch"]')?.addEventListener('click', async () => {
      const res = await aFetch(`/api/recipes/${id}/flags`, { method: 'POST', body: JSON.stringify({ archived: false }) });
      if (!res.ok) { toast('Could not unarchive', 'err'); return; }
      await openRecipes();
    });
    row.querySelector('[data-act="history"]')?.addEventListener('click', () => showVersionHistory(id));
    row.querySelector('[data-act="logbrew"]')?.addEventListener('click', async () => {
      const rec = (recipes || []).find((x) => String(x.id) === id);
      const saved = await editBrewDialog({ recipeId: id, recipeName: rec?.name || '' }, null);
      if (saved) { await openRecipes(); expandBrews(id); }
    });
  });
  const brewById = new Map((brews || []).map((b) => [String(b.id), b]));
  list.querySelectorAll('[data-act="editbrew"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const row = btn.closest('[data-brew]');
      const b = row && brewById.get(row.dataset.brew);
      if (!b) return;
      const saved = await editBrewDialog(null, b);
      if (saved) { await openRecipes(); if (b.recipeId) expandBrews(String(b.recipeId)); }
    });
  });
  list.querySelectorAll('[data-act="delbrew"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const row = btn.closest('[data-brew]');
      if (!row || !(await uiConfirm('Remove this brew log entry?'))) return;
      const res = await aFetch(`/api/brews/${row.dataset.brew}`, { method: 'DELETE' });
      if (!res.ok) { toast('Could not remove brew entry', 'err'); return; }
      row.remove();
      toast('Brew entry removed');
    });
  });
}
function closeRecipes() { $('recipesDrawer').hidden = true; }

/**
 * Version history: past iterations of a saved recipe, each restorable and annotatable.
 * Restoring never rewinds — the current state is snapshotted first, so history only
 * grows (the dial-in loop: brew v3, tweak to v4, compare, restore if v3 was better).
 */
async function showVersionHistory(id) {
  const res = await aFetch(`/api/recipes/${id}/versions`);
  if (!res.ok) { toast('Could not load version history', 'err'); return; }
  const { versions } = await res.json();
  const scrim = document.createElement('div');
  scrim.className = 'bb-dialog-scrim';
  const rows = (versions || [])
    .map(
      (v) => `<div class="rec-row" data-v="${v.version}">
        <div class="rec-row__load" style="cursor:default">
          <span class="rec-row__name">v${v.version} · ${escapeHtml(v.name)}</span>
          <span class="rec-row__sub">${new Date(v.createdAt).toLocaleDateString()}${v.styleName ? ' · ' + escapeHtml(v.styleName) : ''}${v.note ? ' · ' + escapeHtml(v.note) : ''}</span>
        </div>
        <button type="button" class="link-btn" data-act="note" title="Annotate this version">${v.note ? 'Edit note' : 'Add note'}</button>
        <button type="button" class="link-btn" data-act="restore" title="Make this the current version again">Restore</button>
      </div>`,
    )
    .join('');
  scrim.innerHTML = `
    <div class="bb-dialog" role="dialog" aria-modal="true" style="max-width:520px">
      <h3>Version history</h3>
      <p class="bb-dialog__body">Every edit keeps the old iteration. Restore brings one back as a new version — nothing is ever lost.</p>
      <div style="max-height:50vh;overflow-y:auto">${rows || '<p class="muted">No earlier versions yet — edits create them.</p>'}</div>
      <div class="bb-dialog__actions"><button type="button" class="bb-dialog__ok">Close</button></div>
    </div>`;
  const close = () => scrim.remove();
  scrim.querySelector('.bb-dialog__ok').addEventListener('click', close);
  scrim.addEventListener('click', (e) => { if (e.target === scrim) close(); });
  scrim.querySelectorAll('[data-act="restore"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const v = btn.closest('[data-v]').dataset.v;
      const r = await aFetch(`/api/recipes/${id}/versions/${v}/restore`, { method: 'POST' });
      if (!r.ok) { toast('Could not restore', 'err'); return; }
      close();
      toast(`Restored v${v} as the current version`);
      await openRecipes();
      if (currentSavedId === id) await loadRecipe(id);
    });
  });
  scrim.querySelectorAll('[data-act="note"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const v = btn.closest('[data-v]').dataset.v;
      const existing = (versions || []).find((x) => String(x.version) === String(v));
      const note = await uiPrompt(`Note for v${v}`, existing?.note || '');
      if (note === null) return;
      const r = await aFetch(`/api/recipes/${id}/versions/${v}`, { method: 'PUT', body: JSON.stringify({ note }) });
      if (!r.ok) { toast('Could not save note', 'err'); return; }
      close();
      showVersionHistory(id);
    });
  });
  (document.getElementById('beer-builder-root') || document.body).appendChild(scrim);
}

/**
 * Reorder landing (?bbReorder=): fetch the shared recipe, rebuild its cart with the
 * configuration the redirect resolved (the customer's last order), add everything to
 * the real store cart, and go to /cart. Any failure falls back to opening the recipe
 * in the builder so the customer can shop by hand.
 */
async function runReorderFlow() {
  const ov = document.createElement('div');
  ov.setAttribute('style', 'position:fixed;inset:0;z-index:2147483100;background:rgba(255,255,255,.94);display:flex;align-items:center;justify-content:center;font-size:18px;text-align:center;padding:24px');
  ov.textContent = 'Rebuilding your ingredient cart…';
  document.body.appendChild(ov);
  try {
    const shared = await fetch(API + '/api/shared/' + encodeURIComponent(REORDER.token)).then((r) => (r.ok ? r.json() : null));
    const meta = shared?.recipe;
    if (!meta?.recipe) throw new Error('shared fetch failed');
    const cart = await fetch(API + '/api/cart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        recipe: meta.recipe,
        grainMill: REORDER.mill,
        packing: REORDER.packing,
        recipeName: meta.name || 'Beer Builder recipe',
        style: meta.styleName || undefined,
        uid: anonId || undefined,
        // Fresh snapshot: the reorder buys THIS iteration's grain bill and the order
        // gets its own brew day sheet, exactly like a cart built in the panel.
        snapshot: { styleName: meta.styleName ?? null, batchSize: meta.batchSize ?? null, mode: shared.mode ?? null },
      }),
    }).then((r) => (r.ok ? r.json() : null));
    if (!ON_STOREFRONT || !cart?.items?.length) throw new Error('no cart items');
    const addRes = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cart.items }),
    });
    if (!addRes.ok) throw new Error('cart add failed');
    location.replace('/cart');
  } catch (e) {
    // Strand nobody: show the recipe in the builder instead, cart button one tap away.
    ov.remove();
    toast('Loading the recipe instead — add it to your cart from the panel', 'err');
    openSharedRecipe(REORDER.token);
  }
}

/** Send-to-controller: only for accounts with a paired controller (vendor /rcp lane). */
const ctrl = { checked: false, paired: false };
async function checkControllerPaired() {
  if (ctrl.checked) return;
  ctrl.checked = true;
  try {
    const r = await aFetch('/api/controller/paired').then((x) => x.json());
    ctrl.paired = !!r.paired;
  } catch (e) { ctrl.paired = false; }
  if (ctrl.paired) afterRecipeRender();
}

function ensureSendBtn() {
  const anchor = $('shareBtn') || $('dupBtn');
  if (!anchor) return null;
  let el = $('sendCtrlBtn');
  if (!el) {
    el = document.createElement('button');
    el.id = 'sendCtrlBtn';
    el.type = 'button';
    el.className = anchor.className;
    el.hidden = true;
    el.textContent = '📡 Send to controller';
    el.title = 'Load this recipe onto your paired brewing controller';
    anchor.insertAdjacentElement('afterend', el);
    el.addEventListener('click', async () => {
      if (!currentSavedId) return;
      el.disabled = true;
      const res = await aFetch(`/api/recipes/${currentSavedId}/send-to-controller`, { method: 'POST' });
      el.disabled = false;
      if (res.ok) { toast('Sent — check the controller screen'); return; }
      const err = await res.json().catch(() => ({}));
      toast(err.error || 'Could not reach the controller', 'err');
    });
  }
  return el;
}

/**
 * Land the builder in the catalog mode a recipe was built in. The report was computed in
 * that mode; staying in the other one would make the first panel edit recalculate through
 * the wrong lens and rewrite the recipe (bug reports 9 + 13). Inline switch rather than
 * setMode(): that would restore the target mode's stashed panel over the recipe.
 */
function adoptRecipeMode(mode) {
  const targetMode = mode === 'instock' ? 'instock' : 'all';
  if (targetMode === currentMode) return;
  modeStash[currentMode] = panelState();
  currentMode = targetMode;
  applyModeUI();
}

/**
 * Put the setup selectors (style, batch, rig) on what a recipe was built with, so further
 * edits and saves keep its context. `shared` marks a recipe opened from someone else's
 * share link.
 */
function applyRecipeSetup(recipe, report, { shared = false } = {}) {
  if (recipe.styleName && [...styleSelect.options].some((o) => o.value === recipe.styleName)) {
    styleSelect.value = recipe.styleName;
  }
  if (recipe.batchSize) $('batchSize').value = recipe.batchSize;
  const ref = recipe.equipmentRef;
  if (ref?.kind === 'builtin' && [...equipSelect.options].some((o) => o.value === ref.id)) {
    equipSelect.value = ref.id;
  } else if (ref?.kind === 'custom' && shared) {
    // A shared recipe brings the SHARER's rig with it — run it as this session's profile
    // so the panel reproduces the numbers on their sheet. Matching by name against the
    // viewer's own profiles (what an owned recipe does below) would quietly brew it on
    // someone else's specs, and the viewer's saved profiles must not be touched either.
    useSessionProfile(ref.profile);
  } else if (ref?.kind === 'custom') {
    // match by name to a loaded custom profile if present
    let matched = false;
    for (const [val, entry] of customProfiles) if (entry.name === ref.profile.name) { equipSelect.value = val; matched = true; break; }
    if (!matched) {
      // A builtin rig saved with a brewhouse-efficiency override rides as a custom
      // profile (recipePayload). Find the builtin by name and restore the override into
      // the header field, so the next recalc brews with the same efficiency the recipe
      // was saved at instead of quietly reverting to the rig default.
      const opt = [...equipSelect.options].find((o) => o.textContent.trim() === String(ref.profile.name || '').trim());
      if (opt) {
        equipSelect.value = opt.value;
        const base = builtinProfiles[opt.value];
        if (base && Number.isFinite(ref.profile.mashEfficiency) && ref.profile.mashEfficiency !== base.mashEfficiency) {
          effOverride = ref.profile.mashEfficiency;
          try { localStorage.setItem('bb:eff', String(effOverride)); } catch (e) {}
        }
      }
    }
  } else if (report.equipmentProfile) {
    // No stored rig. Imported recipes never carry one, since the export doesn't say what
    // the brewer brews on. The report was still computed against SOME profile, and the
    // panel already shows it, so put the header selector on that same rig instead of
    // leaving it blank: a blank selector makes the next chat turn stop and ask for a rig
    // while the panel is visibly using one.
    // The report field is an id when the caller passed one ("clawhammer-aio") but the
    // resolved profile's NAME when it fell back to a default ("Generic Homebrew Setup"),
    // so match on either.
    const opts = [...equipSelect.options];
    const hit = opts.find((o) => o.value === report.equipmentProfile)
      || opts.find((o) => o.textContent.trim() === String(report.equipmentProfile).trim());
    if (hit) equipSelect.value = hit.value;
  }
}

async function loadRecipe(id) {
  const res = await aFetch(`/api/recipes/${id}`);
  if (!res.ok) { toast('Could not load recipe', 'err'); return; }
  const { recipe, report, mode } = await res.json();
  adoptRecipeMode(mode);
  // Restore the setup selectors so further edits/saves keep context.
  applyRecipeSetup(recipe, report);
  currentSavedId = recipe.id;
  currentSavedName = recipe.name;
  // The saved name IS this recipe's name — stored reports predate recipeName, so stamp
  // it; the title, brew sheet, and cart label all read the name off the report.
  report.recipeName = recipe.name;
  // Fresh conversation for the loaded recipe. No JSON is seeded into history — the
  // panel state (set below) rides the next request's `recipeState` field, on the USER
  // turn, so "tweak" requests still land on THIS recipe without the model ever seeing
  // recipe JSON presented as something it once said.
  history.length = 0;
  modelRecipeJson = JSON.stringify(recipe.recipe);
  transcript = [{ role: 'assistant', text: `Loaded your saved recipe "${recipe.name}".` }];
  $('hint')?.remove();
  renderTranscript();
  renderRecipe(report); // sets currentRecipe/currentReport + re-shows actions
  closeRecipes();
  persistSession();
  toast(`Loaded "${recipe.name}"`);
}

/**
 * Open a shared recipe (?shared=<token>, from a brew day sheet's "Open in Beer Builder"
 * button) as a working copy: every ingredient, the mash, the water, the rig and the
 * numbers land in the panel with nothing to retype. It is NOT the viewer's saved recipe —
 * no saved id, so Save writes a fresh copy to their own account and the sharer's row is
 * untouched. This tab persists to sessionStorage (see sessionStore), so the recipe the
 * brewer may have in progress in another tab survives untouched too.
 */
async function openSharedRecipe(token) {
  let data = null;
  try {
    const res = await aFetch(`/api/shared/${encodeURIComponent(token)}`);
    if (res.ok) data = await res.json();
  } catch (e) { /* offline / blocked — reported below */ }
  if (!data || !data.report || !data.recipe) {
    toast('That shared recipe link could not be opened', 'err');
    return;
  }
  if (data.app === 'mash') {
    toast('That link is a mash recipe. Open it in the Mash Maker.', 'err');
    return;
  }
  const { recipe, report } = data;
  adoptRecipeMode(data.mode);
  applyRecipeSetup(recipe, report, { shared: true });
  currentSavedId = null;
  currentSavedName = '';
  report.recipeName = recipe.name || report.recipeName;
  // Fresh conversation, same as loading a saved recipe: the panel state rides the next
  // request's `recipeState` field, so tweaks land on THIS recipe.
  history.length = 0;
  modelRecipeJson = JSON.stringify(recipe.recipe);
  transcript = [{
    role: 'assistant',
    text: `Opened the shared recipe "${recipe.name}". This is your own copy, so change anything you like. Saving it puts it on your account, and any recipe you had open in another tab is untouched.`,
  }];
  $('hint')?.remove();
  renderTranscript();
  renderRecipe(report); // sets currentRecipe/currentReport + shows the panel
  persistSession();
  toast(`Opened "${recipe.name}"`);
}

async function deleteRecipe(id) {
  if (!(await uiConfirm('Delete this saved recipe?'))) return;
  await aFetch(`/api/recipes/${id}`, { method: 'DELETE' });
  if (currentSavedId === id) { currentSavedId = null; currentSavedName = ''; afterRecipeRender(); }
  await openRecipes();
  toast('Recipe deleted');
}

async function duplicateRecipe() {
  if (!currentSavedId) return;
  const res = await aFetch(`/api/recipes/${currentSavedId}/duplicate`, { method: 'POST' });
  if (!res.ok) { toast('Could not duplicate', 'err'); return; }
  const { recipe } = await res.json();
  currentSavedId = recipe.id;
  currentSavedName = recipe.name;
  afterRecipeRender();
  toast(`Duplicated as "${recipe.name}"`);
}

// ── BeerXML export ───────────────────────────────────────────────────
// Builds a BeerXML 1.0 file from the current resolved recipe so brewers can
// import it into BeerSmith, Brewfather, Brewer's Friend, or a brewing
// controller. Every constant (ppg, Lovibond, alpha acid, attenuation) comes
// from the resolved recipe, i.e. the engine's authoritative ingredient DB.
const BX_LB_TO_KG = 0.45359237;
const BX_OZ_TO_KG = 0.028349523;
const BX_GAL_TO_L = 3.785411784;
const bxC = (f) => (f - 32) / 1.8; // °F → °C

function bxEsc(s) {
  return String(s ?? '').replace(/[<>&'"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' }[c]));
}
/** Number formatted for XML: fixed decimals with trailing zeros trimmed. */
function bxN(v, dp = 4) {
  const n = Number(v);
  if (v == null || !isFinite(n)) return '0';
  return String(Number(n.toFixed(dp)));
}
function bxTag(name, val) { return `<${name}>${bxEsc(val)}</${name}>`; }

/** Normalize ppg that may arrive as 37, 1.037, or 1037. */
function bxPpg(f) {
  let p = f.ppg ?? 35;
  if (p > 1 && p < 2) p = (p - 1) * 1000;
  else if (p > 200) p = p - 1000;
  return p;
}

/** The equipment profile object currently in effect (built-in, custom, or session). */
function activeEquipProfile() {
  const v = equipSelect.value;
  if (v === 'session' && sessionProfile) return sessionProfile;
  if (v.startsWith('custom:')) return customProfiles.get(v)?.profile || null;
  return builtinProfiles[v] || null;
}

function beerXml(report) {
  const r = report.resolvedRecipe || {};
  const b = report.benchmarks || {};
  const g = b.gravity || {};
  const vol = b.volumes || {};
  const eq = activeEquipProfile();
  const name = report.recipeName || currentSavedName || (report.style && report.style.style) || 'Beer Builder recipe';
  const batchGal = r.batchSize ?? 5;
  const boilTime = r.boilTime ?? 60;

  const ferms = r.fermentables || [];
  const hasMash = ferms.some((f) => !f.type || f.type === 'Grain' || f.type === 'Adjunct');
  const hasExtract = ferms.some((f) => f.type === 'Extract' || f.type === 'Dry Extract');
  const recipeType = hasMash && hasExtract ? 'Partial Mash' : hasMash ? 'All Grain' : 'Extract';

  const yeastName = (r.yeast?.name || '').toLowerCase();
  const isLager = /lager|pilsner|34\/70|w-?34|s-23|diamond/.test(yeastName + ' ' + name.toLowerCase());
  const isLiquidYeast = /wlp|wyeast|omega|imperial|oyl/.test(yeastName);

  const fermXml = ferms.map((f) => {
    const ppg = bxPpg(f);
    return '    <FERMENTABLE>\n      ' + [
      bxTag('NAME', f.name), '<VERSION>1</VERSION>',
      bxTag('TYPE', f.type || 'Grain'),
      bxTag('AMOUNT', bxN(f.weight * BX_LB_TO_KG)),          // kg
      bxTag('YIELD', bxN((ppg / 46) * 100, 2)),              // % of sucrose
      bxTag('COLOR', bxN(f.lovibond ?? 2, 1)),               // °Lovibond
    ].join('\n      ') + '\n    </FERMENTABLE>';
  }).join('\n');

  const hopXml = (r.hopSchedule || []).map((h) => {
    let use = 'Boil';
    let time = h.time ?? 60;
    if (h.method === 'dryhop' || h.time === -1) {
      use = 'Dry Hop';
      time = (h.dryHopDays || 4) * 1440; // BeerXML dry-hop TIME is minutes
    } else if (h.method === 'whirlpool' || (h.time === 0 && h.whirlpoolTime)) {
      use = 'Aroma';
      time = h.whirlpoolTime || 0;
    } else if (h.time === 0) {
      use = 'Aroma';
      time = 0;
    }
    const notes = [];
    if (use === 'Aroma' && h.whirlpoolTemp) notes.push(`Whirlpool at ${h.whirlpoolTemp}F`);
    if (h.hopForm === 'cryo') notes.push('Cryo / concentrated lupulin');
    if (h.hopForm === 'wet') notes.push(`Fresh (wet) hops at real weight, about ${WET_HOP_PELLET_MULTIPLIER}x a pellet charge`);
    return '    <HOP>\n      ' + [
      bxTag('NAME', h.name), '<VERSION>1</VERSION>',
      bxTag('ALPHA', bxN(h.aa ?? h.alphaAcid ?? 8, 2)),
      bxTag('AMOUNT', bxN(h.weight * BX_OZ_TO_KG, 5)),       // kg
      bxTag('USE', use),
      bxTag('TIME', bxN(time, 0)),
      bxTag('FORM', h.hopForm === 'whole' || h.hopForm === 'wet' ? 'Leaf' : 'Pellet'),
      ...(notes.length ? [bxTag('NOTES', notes.join('. '))] : []),
    ].join('\n      ') + '\n    </HOP>';
  }).join('\n');

  const yeastXml = r.yeast ? '    <YEAST>\n      ' + [
    bxTag('NAME', r.yeast.name), '<VERSION>1</VERSION>',
    bxTag('TYPE', isLager ? 'Lager' : 'Ale'),
    bxTag('FORM', isLiquidYeast ? 'Liquid' : 'Dry'),
    bxTag('AMOUNT', '0.0115'), '<AMOUNT_IS_WEIGHT>TRUE</AMOUNT_IS_WEIGHT>', // one 11.5 g pack
    ...(r.yeast.attenuation != null ? [bxTag('ATTENUATION', bxN(r.yeast.attenuation, 1))] : []),
  ].join('\n      ') + '\n    </YEAST>' : '';

  const miscXml = (r.flavorings || []).map((fl) => {
    const unit = (fl.unit || '').toLowerCase();
    let kg = 0;
    if (fl.amount != null) {
      if (unit.startsWith('oz')) kg = fl.amount * BX_OZ_TO_KG;
      else if (unit.startsWith('lb')) kg = fl.amount * BX_LB_TO_KG;
      else if (unit === 'g' || unit.startsWith('gram')) kg = fl.amount / 1000;
    }
    const human = fl.amount != null ? `${fl.amount} ${fl.unit || ''}`.trim() : '';
    return '    <MISC>\n      ' + [
      bxTag('NAME', fl.name), '<VERSION>1</VERSION>',
      bxTag('TYPE', 'Flavor'),
      bxTag('USE', 'Boil'),
      bxTag('TIME', '0'),
      bxTag('AMOUNT', bxN(kg, 4)),
      '<AMOUNT_IS_WEIGHT>TRUE</AMOUNT_IS_WEIGHT>',
      bxTag('NOTES', [human, fl.note].filter(Boolean).join('. ')),
    ].join('\n      ') + '\n    </MISC>';
  }).join('\n');

  // Water salts/acids export as Water Agent miscs. Stored g/ml map to BeerXML's kg/L.
  // The spec's USE has no "Sparge" value, so sparge additions say Mash and keep the
  // stage in NOTES; importers (ours included) read the amount either way.
  const saltXml = (r.salts || []).map((s) => {
    const isWeight = s.unit !== 'ml';
    const human = [`${s.amount} ${s.unit}`, s.stage, s.concentration ? `${s.concentration}%` : ''].filter(Boolean).join(', ');
    return '    <MISC>\n      ' + [
      bxTag('NAME', s.concentration ? `${s.name} ${s.concentration}%` : s.name), '<VERSION>1</VERSION>',
      bxTag('TYPE', 'Water Agent'),
      bxTag('USE', s.stage === 'boil' ? 'Boil' : 'Mash'),
      bxTag('TIME', '0'),
      bxTag('AMOUNT', bxN(s.amount / 1000, 5)),
      `<AMOUNT_IS_WEIGHT>${isWeight ? 'TRUE' : 'FALSE'}</AMOUNT_IS_WEIGHT>`,
      bxTag('NOTES', human),
    ].join('\n      ') + '\n    </MISC>';
  }).join('\n');
  const miscsAll = [miscXml, saltXml].filter(Boolean).join('\n');

  // Style block: name + the ranges the builder compared against.
  let styleXml = '';
  if (report.style) {
    const f = report.style.fields || {};
    const range = (tag, key, dp) => f[key]?.target
      ? [bxTag(`${tag}_MIN`, bxN(f[key].target[0], dp)), bxTag(`${tag}_MAX`, bxN(f[key].target[1], dp))] : [];
    styleXml = '    <STYLE>\n      ' + [
      bxTag('NAME', report.style.style), '<VERSION>1</VERSION>',
      bxTag('CATEGORY', report.style.style),
      bxTag('CATEGORY_NUMBER', '0'), bxTag('STYLE_LETTER', ''),
      bxTag('STYLE_GUIDE', 'BJCP'),
      bxTag('TYPE', isLager ? 'Lager' : 'Ale'),
      ...range('OG', 'og', 3), ...range('FG', 'fg', 3),
      ...range('IBU', 'ibu', 0), ...range('COLOR', 'srm', 1), ...range('ABV', 'abv', 1),
    ].join('\n      ') + '\n    </STYLE>';
  }

  // Mash profile (only when there is grain to mash). A step program exports one
  // MASH_STEP per rest so brewing controllers (and BeerSmith/Brewfather) can program
  // the whole schedule: the first step is the Infusion (full strike water goes in
  // there on a full-volume rig), later rests are Temperature steps — the controller
  // heats between setpoints. Single infusion keeps its classic one-step block.
  const bxSteps = Array.isArray(r.mashSteps) && r.mashSteps.length > 1
    ? r.mashSteps
    : [{ name: 'Saccharification', temp: r.mashTemp ?? 152, time: r.mashTime ?? 60 }];
  const bxStepXml = bxSteps.map((s, i) => '        <MASH_STEP>\n          ' + [
    bxTag('NAME', s.name || `Step ${i + 1}`), '<VERSION>1</VERSION>',
    bxTag('TYPE', s.type === 'decoction' ? 'Decoction' : i === 0 ? 'Infusion' : 'Temperature'),
    bxTag('STEP_TEMP', bxN(bxC(s.temp), 1)),
    bxTag('STEP_TIME', bxN(s.time, 0)),
    ...(i === 0 && vol.strikeWater != null ? [bxTag('INFUSE_AMOUNT', bxN(vol.strikeWater * BX_GAL_TO_L, 2))] : []),
  ].join('\n          ') + '\n        </MASH_STEP>').join('\n');
  const mashXml = hasMash ? '    <MASH>\n      ' + [
    bxTag('NAME', bxSteps.length > 1 ? 'Step Mash' : 'Single Infusion'), '<VERSION>1</VERSION>',
    bxTag('GRAIN_TEMP', bxN(bxC(68), 1)),
    '<MASH_STEPS>\n' + bxStepXml + '\n      </MASH_STEPS>',
  ].join('\n      ') + '\n    </MASH>' : '';

  const equipXml = eq ? '    <EQUIPMENT>\n      ' + [
    bxTag('NAME', eq.name || b.equipment || 'Homebrew rig'), '<VERSION>1</VERSION>',
    bxTag('BATCH_SIZE', bxN(batchGal * BX_GAL_TO_L, 2)),
    ...(vol.preBoil != null ? [bxTag('BOIL_SIZE', bxN(vol.preBoil * BX_GAL_TO_L, 2))] : []),
    bxTag('BOIL_TIME', bxN(boilTime, 0)),
    ...(eq.kettleVolume != null ? [bxTag('TUN_VOLUME', bxN(eq.kettleVolume * BX_GAL_TO_L, 2))] : []),
  ].join('\n      ') + '\n    </EQUIPMENT>' : '';

  // PRIMARY_TEMP exports the plan the panel shows: the brewer's own figure, or the
  // yeast's mid-range fill when they never set one.
  const fermPlanF = r.fermentTemp ?? (report.yeastTemp ? report.yeastTemp.suggestedF : null);
  const recipeFields = [
    bxTag('NAME', name), '<VERSION>1</VERSION>',
    bxTag('TYPE', recipeType),
    bxTag('BREWER', 'Clawhammer Beer Builder'),
    bxTag('BATCH_SIZE', bxN(batchGal * BX_GAL_TO_L, 3)),
    ...(vol.preBoil != null ? [bxTag('BOIL_SIZE', bxN(vol.preBoil * BX_GAL_TO_L, 3))] : []),
    bxTag('BOIL_TIME', bxN(boilTime, 0)),
    ...(effOverride != null || eq?.mashEfficiency != null ? [bxTag('EFFICIENCY', bxN(effOverride ?? eq.mashEfficiency, 1))] : []),
    ...(fermPlanF != null ? [bxTag('PRIMARY_TEMP', bxN((fermPlanF - 32) / 1.8, 1))] : []),
    ...(g.og != null ? [bxTag('OG', bxN(g.og, 3)), bxTag('EST_OG', bxN(g.og, 3))] : []),
    ...(g.fg != null ? [bxTag('FG', bxN(g.fg, 3)), bxTag('EST_FG', bxN(g.fg, 3))] : []),
    ...(b.ibu != null ? [bxTag('IBU', bxN(b.ibu, 1))] : []),
    ...(b.srm != null ? [bxTag('EST_COLOR', bxN(b.srm, 1))] : []),
    ...(b.abv != null ? [bxTag('ABV', bxN(b.abv, 1)), bxTag('EST_ABV', bxN(b.abv, 1))] : []),
    bxTag('NOTES', 'Built with the Clawhammer Beer Builder (clawhammersupply.com/beer-builder)'),
  ].join('\n    ');

  return '<?xml version="1.0" encoding="UTF-8"?>\n<RECIPES>\n  <RECIPE>\n    ' + recipeFields + '\n' +
    (styleXml ? styleXml + '\n' : '') +
    (equipXml ? equipXml + '\n' : '') +
    '    <FERMENTABLES>\n' + fermXml + '\n    </FERMENTABLES>\n' +
    '    <HOPS>\n' + hopXml + '\n    </HOPS>\n' +
    '    <YEASTS>\n' + yeastXml + '\n    </YEASTS>\n' +
    '    <MISCS>\n' + miscsAll + '\n    </MISCS>\n' +
    '    <WATERS></WATERS>\n' +
    (mashXml ? mashXml + '\n' : '') +
    '  </RECIPE>\n</RECIPES>\n';
}

function downloadBeerXml() {
  if (!currentReport) return;
  const xml = beerXml(currentReport);
  const base = (currentReport.recipeName || currentSavedName || recipeTitleText() || 'recipe')
    .trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'recipe';
  const blob = new Blob([xml], { type: 'application/xml' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${base}.xml`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  toast('BeerXML downloaded');
}

// ── wire account UI ──────────────────────────────────────────────────
$('manageEquipBtn')?.addEventListener('click', openEquipModal);
$('equipGearBtn')?.addEventListener('click', openEquipModal);
$('equipNewBtn')?.addEventListener('click', () => { resetEquipForm(null, null); showEquipForm(); });
$('equipEditBtn')?.addEventListener('click', editSelectedProfile);
$('equipCopyBtn')?.addEventListener('click', copySelectedProfile);
$('equipDelete')?.addEventListener('click', () => { const id = $('equipId').value; if (id) deleteProfile(id); });
$('equipBack')?.addEventListener('click', showEquipChooser);
$('equipUseSession')?.addEventListener('click', applySessionProfile);
$('equipClose')?.addEventListener('click', closeEquipModal);
// A scrim tap throws away everything typed into the editor. That's fair on the chooser,
// which holds no input, but on a phone the editor is taller than the screen and the taps
// aimed at its buttons land on the scrim by accident (bug report #15) — so while the form
// is up, only × and ← Back close it.
$('equipScrim')?.addEventListener('click', () => {
  if (!$('equipForm').hidden) return;
  closeEquipModal();
});
$('equipForm')?.addEventListener('submit', submitEquipForm);
$('equipReset')?.addEventListener('click', () => resetEquipForm());
$('myRecipesBtn')?.addEventListener('click', () => {
  // Locked: an empty drawer teaches nothing, so say what it's for and how to get it.
  if (acct.enabled && !acct.loggedIn) {
    showSigninPopup('Saved recipes live in your account, so you can open them on any device.');
    return;
  }
  openRecipes();
});
$('recipesClose')?.addEventListener('click', closeRecipes);
$('recipesScrim')?.addEventListener('click', closeRecipes);
$('saveBtn')?.addEventListener('click', () => saveRecipe(false));
$('saveAsBtn')?.addEventListener('click', () => saveRecipe(true));
$('beerXmlBtn')?.addEventListener('click', downloadBeerXml);
$('dupBtn')?.addEventListener('click', duplicateRecipe);
$('deleteBtn')?.addEventListener('click', () => currentSavedId && deleteRecipe(currentSavedId));

/* ════════════════════════════════════════════════════════════════════
   PERSISTENCE — keep the last recipe across navigations (cart ↔ back).
   Anonymous brewers have no account, so the working session (computed
   recipe + chat + setup selectors) is stashed in localStorage and restored
   on load. So a brewer who adds to cart, balks at the price, and comes back
   still has their recipe to tweak. "Start over" wipes it.
   ════════════════════════════════════════════════════════════════════ */
function persistSession() {
  try {
    const data = {
      v: 1,
      style: styleSelect.value || '',
      equip: equipSelect.value || '',
      batch: $('batchSize').value || '',
      // drop base64 photos — they're folded into the turn's text after research
      history: history.map((h) => { const c = { ...h }; delete c.images; return c; }),
      transcript,
      report: currentReport,
      savedId: currentSavedId,
      savedName: currentSavedName,
      greeted,
      mode: currentMode,
      // The other mode's recipe, so flipping the toggle after a reload still finds it.
      stash: { all: currentMode === 'all' ? null : modeStash.all, instock: currentMode === 'instock' ? null : modeStash.instock },
      // Mid-slider-tuning state, so a reload doesn't lose the recipe Reset restores to.
      hsOriginal: hsDirty ? hsOriginalRecipeJson : undefined,
      // Which share link this tab was opened from (blank in a normal tab) — see restoreSession.
      sharedToken: SHARED_TOKEN || undefined,
    };
    sessionStore()?.setItem(LS_KEY, JSON.stringify(data));
  } catch {
    /* private mode / quota — persistence is best-effort */
  }
}

function clearSession() {
  try { sessionStore()?.removeItem(LS_KEY); } catch {}
}

// ── remember the rig across visits (survey asks: ian, silentbob) ────
// The working-session stash above only survives while a recipe exists and Start over
// wipes it, so the rig pick gets its own durable key. 'session' rigs are skipped —
// they live only in memory and can't be restored after a reload.
function rememberEquip() {
  const v = equipSelect.value;
  if (!v || v === 'session') return;
  try { localStorage.setItem('bb:lastEquip', v); } catch (e) {}
}
function lastEquip() {
  try { return localStorage.getItem('bb:lastEquip') || ''; } catch (e) { return ''; }
}

/** Rebuild the visible chat bubbles from the persisted transcript. */
function renderTranscript() {
  messagesEl.innerHTML = '';
  for (const m of transcript) addMessage(m.role, (m.img ? '📷 ' : '') + (m.text || ''));
}

function restoreSession() {
  let data = null;
  try { data = JSON.parse(sessionStore()?.getItem(LS_KEY) || 'null'); } catch {}
  if (!data) return false;
  // A stash belongs to the share link (if any) it was made under. sessionStorage outlives
  // navigations within a tab, so opening a DIFFERENT ?shared= link in a tab that already
  // had one must fetch that recipe rather than resurrect the previous one.
  if ((data.sharedToken || '') !== SHARED_TOKEN) return false;
  const hasContent = data.report || (data.history && data.history.length) || (data.transcript && data.transcript.length);
  if (!hasContent) return false;
  pendingRestore = data;
  if (data.style && [...styleSelect.options].some((o) => o.value === data.style)) styleSelect.value = data.style;
  if (data.equip && [...equipSelect.options].some((o) => o.value === data.equip)) equipSelect.value = data.equip;
  if (data.batch) $('batchSize').value = data.batch;
  // Honor an explicitly saved mode; anything older (saved before modes existed) picks
  // up the current default rather than being pinned to the old one.
  currentMode = data.mode === 'instock' ? 'instock' : 'all';
  modeStash.all = data.stash?.all || null;
  modeStash.instock = data.stash?.instock || null;
  applyModeUI();
  history.length = 0;
  (data.history || []).forEach((h) => history.push(h));
  transcript = data.transcript || [];
  greeted = !!data.greeted;
  currentSavedId = data.savedId || null;
  currentSavedName = data.savedName || '';
  modelRecipeJson = ''; // unknown after a reload — re-sync on the next send rather than assume
  $('hint')?.remove();
  renderTranscript();
  if (data.report) renderRecipe(data.report); // sets currentRecipe/currentReport + shows the panel
  // Restore the pre-slider snapshot AFTER the render: the render itself captures the
  // restored (slider-modified) recipe as the baseline, which is exactly what a reload
  // mid-tuning must not do.
  if (data.hsOriginal && currentRecipe) {
    hsOriginalRecipeJson = data.hsOriginal;
    hsLastSliderRecipeJson = JSON.stringify(currentRecipe);
    hsDirty = hsOriginalRecipeJson !== hsLastSliderRecipeJson;
    hsSyncResetBtn();
  }
  updateStartOver();
  return true;
}

/** Show the Start-over button only when there's something to clear. */
function updateStartOver() {
  const btn = $('startOverBtn');
  if (btn) btn.hidden = !(currentReport || history.length);
  // Its appearance widens the cluster — the bar re-measures whether one line still fits.
  if (typeof layoutTopbar === 'function') layoutTopbar();
}

async function startOver() {
  if (currentReport || history.length) {
    const ok = await uiConfirm('Start over?', 'This clears the current recipe and conversation.', 'Start over');
    if (!ok) return;
  }
  clearSession();
  history.length = 0;
  transcript = [];
  currentRecipe = null;
  currentReport = null;
  currentSavedId = null;
  currentSavedName = '';
  modeStash.all = null;
  modeStash.instock = null;
  modelRecipeJson = '';
  const cn = $('convertNote');
  if (cn) { cn.hidden = true; delete cn.dataset.for; }
  greeted = false;
  setPendingImage(null);
  styleSelect.value = '';
  waterUi.collapsed = true; // the water section starts minimized on every fresh recipe
  // The rig deliberately survives Start over: the style changes per recipe, the brew
  // system doesn't (survey asks: ian, silentbob). Picking a different one is one click.
  $('batchSize').value = '5';
  if (typeof syncBatchUnitUI === 'function') syncBatchUnitUI();
  messagesEl.innerHTML = HINT_HTML; // restore the opening prompt
  $('recipeContent').hidden = true;
  $('recipeEmpty').hidden = false;
  $('cartResult').hidden = true;
  afterRecipeRender();
  updateStartOver();
  inputEl.focus();
}

/** Add the Start-over control to the top bar (JS-injected so no theme/Liquid edit needed). */
function injectStartOverButton() {
  if ($('startOverBtn')) return;
  const topbar = document.querySelector('#beer-builder-root .topbar') || document.querySelector('.topbar');
  if (!topbar) return;
  const btn = document.createElement('button');
  btn.id = 'startOverBtn';
  btn.type = 'button';
  btn.className = 'account__btn startover';
  btn.textContent = '↺ Start over';
  btn.title = 'Clear this recipe and start fresh';
  btn.hidden = true;
  btn.addEventListener('click', startOver);
  // Belongs INSIDE .account, not loose in the topbar: as a fourth flex child of the bar
  // it wrapped on its own, stranding one button on a line while the other three sat
  // elsewhere. In .account it travels with the rest of the button cluster at every width.
  const acct = $('account');
  if (acct) acct.appendChild(btn);
  else topbar.appendChild(btn);
}
injectStartOverButton();

/** Condense the account cluster into a hamburger menu (Kyle, 2026-08-08): the bar keeps
 * only ☰ + the mode toggle + Start over, so desktop fits one line and mobile gets a
 * single compact row above the fields. Equipment (as a word, not the gear icon), Import,
 * My ingredients, My recipes and Sign in all live in the dropdown. JS-injected like the
 * rest of the cluster, so no theme/Liquid edit; the later-injected buttons target
 * #bbMenu themselves. */
function injectMenuBar() {
  if ($('bbMenuBtn')) return;
  const acctEl = $('account');
  if (!acctEl) return;
  const wrap = document.createElement('span');
  wrap.className = 'bb-menu-wrap';
  const btn = document.createElement('button');
  btn.id = 'bbMenuBtn';
  btn.type = 'button';
  btn.className = 'account__gear';
  btn.title = 'Menu';
  btn.setAttribute('aria-label', 'Menu');
  btn.setAttribute('aria-haspopup', 'true');
  btn.setAttribute('aria-expanded', 'false');
  btn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16"/></svg>';
  // Signed-out hint (Kyle, 2026-08-08): a small padlock badge on the ☰ corner says
  // "some of what's in here needs an account". refreshAccount() toggles it with the
  // same condition as the Sign in button.
  const lock = document.createElement('span');
  lock.id = 'bbMenuLock';
  lock.textContent = '🔒';
  lock.setAttribute('aria-hidden', 'true');
  lock.hidden = true;
  btn.appendChild(lock);
  const panel = document.createElement('div');
  panel.id = 'bbMenu';
  panel.className = 'bb-menu';
  panel.hidden = true;
  wrap.appendChild(btn);
  wrap.appendChild(panel);
  acctEl.insertBefore(wrap, acctEl.firstChild);
  // The equipment-settings button drops its gear icon for the word so the menu reads
  // as a list, not a mystery icon row. Same id, so every existing handler still fits.
  const gear = $('equipGearBtn');
  if (gear) { gear.textContent = 'Equipment'; panel.appendChild(gear); }
  const rec = $('myRecipesBtn');
  if (rec) panel.appendChild(rec);
  // Sign in stays OUT of the menu (Kyle, 2026-08-08): it's the bar's one CTA, so it
  // rides the visible cluster after Start over instead of hiding in the dropdown.
  const signin = $('signInLink');
  if (signin) acctEl.appendChild(signin);
  const setOpen = (open) => {
    panel.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
  };
  btn.addEventListener('click', () => setOpen(panel.hidden));
  // Bubbling order runs the item's own handler first, then this closes the menu.
  panel.addEventListener('click', (e) => { if (e.target.closest('button, a')) setOpen(false); });
  document.addEventListener('mousedown', (e) => {
    if (!panel.hidden && !wrap.contains(e.target)) setOpen(false);
  }, true);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !panel.hidden) { setOpen(false); btn.focus(); }
  });
}
injectMenuBar();

/**
 * MEASURED topbar layout: one line when it genuinely fits, stacked when it doesn't.
 *
 * The breakpoint approach failed twice in one day (2026-08-08): thresholds computed
 * from Chrome font metrics overflowed in Kyle's Safari, which renders every control
 * wider — Sign in wrapped to a second line on his phone-width window and the desktop
 * bar broke raggedly across two lines. So instead of guessing pixels, render the
 * one-line layout, ask the flexbox whether anything wrapped, and stack only then.
 * Immune to browser font metrics, zoom, and whichever cluster buttons happen to be
 * visible. Runs at boot, on resize, when webfonts land, and whenever Start over or
 * Sign in toggle visibility (both change the cluster's width).
 */
function layoutTopbar() {
  const tb = document.querySelector('#beer-builder-root .topbar') || document.querySelector('.topbar');
  const setup = tb && tb.querySelector('.setup');
  const acct = tb && tb.querySelector('.account');
  if (!tb || !setup || !acct) return;
  const was = tb.hasAttribute('data-stacked');
  tb.removeAttribute('data-stacked'); // measure the one-line layout
  // Wrapped if the cluster fell below the fields, or the fields wrapped inside .setup
  // (a single field row is ~60px tall; labels sit above controls). Compare BOTTOMS:
  // the bar bottom-aligns its children (align-items: flex-end), so on a shared line
  // the shorter cluster's top sits ~22px below the fields' top while the bottoms
  // coincide — a top comparison reads every width as wrapped.
  const wrapped = (acct.getBoundingClientRect().bottom - setup.getBoundingClientRect().bottom) > 10
    || setup.getBoundingClientRect().height > 78;
  if (wrapped) tb.setAttribute('data-stacked', '');
  // The bar's height changes when the mode flips; the app pane is sized to what's
  // above it, so re-measure (covers font-load and visibility-driven flips — resize
  // already calls sizeApp on its own).
  if (was !== wrapped && typeof sizeApp === 'function') sizeApp();
}
layoutTopbar();
window.addEventListener('resize', () => requestAnimationFrame(layoutTopbar));
if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => layoutTopbar());

/**
 * Move the BEER BLDR mark up into the site header, immediately right of the hammer.
 *
 * It's authored in the section (so the logo's asset_url stays in Liquid) and relocated
 * here rather than being hardcoded into the shared header — this file only loads on the
 * builder page, so the site header is untouched everywhere else. If this never runs the
 * mark simply stays in the builder's own top bar, which still reads fine.
 *
 * The page renders the theme's `header--simple` variant, which already shows the hammer
 * alone; the CLAWHAMMER SUPPLY wordmark lockup belongs to the fancy homepage header.
 */
function relocateBrandIntoHeader() {
  const brand = document.querySelector('#beer-builder-root .brand');
  const logoLink = document.querySelector('.shopify-section-group-header-group .header__logo');
  if (!brand || !logoLink || document.querySelector('.bb-headermark')) return;
  const mark = document.createElement('div');
  mark.className = 'bb-headermark';
  while (brand.firstChild) mark.appendChild(brand.firstChild);
  logoLink.insertAdjacentElement('afterend', mark);
  brand.remove();
  syncMarkOrder(logoLink, mark);
  window.addEventListener('resize', () => syncMarkOrder(logoLink, mark));
  sizeApp(); // the top bar just lost a row's worth of height
}

/**
 * Sit the mark immediately after the hammer at EVERY breakpoint.
 *
 * The theme reorders its header children with explicit `order` values that change by media
 * query (at tablet the logo is order:1, the hamburger order:4). An unstyled element defaults
 * to order:0, which shoved the mark to the far LEFT of the hammer below 1024px. Copying the
 * logo's own order means equal-order items fall back to DOM order — and the mark is the
 * logo's next sibling — so it lands just right of it whatever the theme decides to do.
 */
function syncMarkOrder(logoLink, mark) {
  mark.style.order = getComputedStyle(logoLink).order;
}

/**
 * Size the app to exactly the space left under whatever sits above it.
 *
 * The CSS fallback subtracts a fixed 180px of storefront chrome, but the real figure moves:
 * the site nav is hidden on this page, the promo bars vary, and the pint-glass banner wraps
 * to three or four lines on a phone. Measuring beats guessing — otherwise the panes either
 * overflow the viewport or leave a dead strip under the fold.
 *
 * Skipped below 920px, where the CSS switches the panes to a stacked, naturally-scrolling
 * layout and a pinned height would fight it.
 */
function sizeApp() {
  const app = document.querySelector('#beer-builder-root .app') || document.querySelector('.app');
  if (!app) return;
  if (window.innerWidth <= 920) { app.style.height = ''; return; }
  // Offset from the top of the DOCUMENT, so the result doesn't change as the page scrolls.
  const docTop = app.getBoundingClientRect().top + window.scrollY;
  app.style.height = Math.max(560, window.innerHeight - docTop) + 'px';
}
relocateBrandIntoHeader();
sizeApp();
window.addEventListener('resize', sizeApp);
// The banner arrives asynchronously from /api/options and changes the offset when it does.
window.addEventListener('load', sizeApp);

// ── catalog-mode toggle (In Stock ⇄ All Ingredients) ─────────────────
// Injected (not in Liquid) so no theme-editor-managed section is touched. In Stock is the
// default commerce path; All Ingredients is the pure calculator (full catalog, no cart).
function injectModeToggle() {
  if ($('modeToggle')) return;
  const acctEl = $('account');
  if (!acctEl) return;
  // Lives in the quick cluster (☰ · toggle · Start over) since the 2026-08-08 header
  // condense — unlabeled, right after the hamburger, matching Kyle's mobile mock.
  const el = document.createElement('div');
  el.className = 'mode-toggle';
  el.id = 'modeToggle';
  el.setAttribute('role', 'tablist');
  el.setAttribute('aria-label', 'Ingredient catalog mode');
  el.innerHTML =
    // Order and initial selection are NOT hardcoded here — applyModeUI() runs right
    // after this and marks whichever mode is current.
    '<button type="button" class="mode-toggle__opt" data-mode="all" role="tab" title="Design with the full ingredient catalog — a pure calculator, no cart">All ingredients</button>' +
    '<button type="button" class="mode-toggle__opt" data-mode="instock" role="tab" title="Only ingredients we stock, so you can add the recipe to your cart">In stock</button>';
  const wrap = acctEl.querySelector('.bb-menu-wrap');
  acctEl.insertBefore(el, wrap ? wrap.nextSibling : acctEl.firstChild);
  el.querySelectorAll('.mode-toggle__opt').forEach((b) =>
    b.addEventListener('click', () => setMode(b.dataset.mode)));
}

/** Reflect the current mode across the toggle UI + cart visibility. */
function applyModeUI() {
  const root = document.querySelector('#beer-builder-root') || document.body;
  root.setAttribute('data-mode', currentMode);
  document.querySelectorAll('#modeToggle .mode-toggle__opt').forEach((b) => {
    const on = b.dataset.mode === currentMode;
    b.classList.toggle('is-active', on);
    b.setAttribute('aria-selected', on ? 'true' : 'false');
  });
  // All mode is calculation-only — the cart controls have no meaning there.
  const allMode = currentMode === 'all';
  const cartOpts = $('cartOpts');
  const cartBtn = $('cartBtn');
  if (cartOpts) cartOpts.hidden = allMode;
  if (cartBtn) cartBtn.hidden = allMode;
  if (allMode) { const cr = $('cartResult'); if (cr) cr.hidden = true; }
  // The buy path only exists in All mode, and only with a recipe to convert. Hide the
  // whole block, not just the button, or the "Want to order online?" prompt is left
  // dangling over nothing.
  const convert = $('convertBtn');
  const convertBlock = $('convertBlock');
  if (convertBlock) convertBlock.hidden = !allMode || !currentRecipe;
  if (convert) convert.textContent = modeStash.instock ? CONVERT_LABEL_REBUILD : CONVERT_LABEL;
}

// Each mode keeps its own recipe panel. Toggling swaps which one is on screen and never
// destroys the other, so a brewer can convert to in-stock and still flip back to the
// original all-catalog version.
const modeStash = { all: null, instock: null };

function panelState() {
  return currentRecipe
    ? { recipe: currentRecipe, report: currentReport, savedId: currentSavedId, savedName: currentSavedName }
    : null;
}

function clearPanel() {
  currentRecipe = null;
  currentReport = null;
  currentSavedId = null;
  currentSavedName = '';
  $('recipeContent').hidden = true;
  $('recipeEmpty').hidden = false;
  $('cartResult').hidden = true;
  afterRecipeRender();
}

function setMode(mode) {
  const next = mode === 'all' ? 'all' : 'instock';
  if (next === currentMode) return;
  modeStash[currentMode] = panelState(); // stash what's on screen now
  currentMode = next;
  const restore = modeStash[next];
  if (restore) {
    currentSavedId = restore.savedId;
    currentSavedName = restore.savedName;
    renderRecipe(restore.report); // sets currentRecipe/currentReport + repaints the panel
  } else {
    clearPanel();
  }
  applyModeUI();
  updateStartOver();
  persistSession();
}

/** Two-option chooser (reuses the in-page dialog shell). Resolves to a value or null. */
function uiChoice({ title, body = '', options }) {
  return new Promise((resolve) => {
    const scrim = document.createElement('div');
    scrim.className = 'bb-dialog-scrim';
    scrim.innerHTML = `
      <div class="bb-dialog bb-dialog--wide" role="dialog" aria-modal="true">
        <h3></h3>
        <p class="bb-dialog__body" hidden></p>
        <div class="bb-choices"></div>
        <div class="bb-dialog__actions">
          <button type="button" class="link-btn bb-dialog__cancel">Cancel</button>
        </div>
      </div>`;
    scrim.querySelector('h3').textContent = title;
    const bodyEl = scrim.querySelector('.bb-dialog__body');
    if (body) { bodyEl.hidden = false; bodyEl.textContent = body; }
    const done = (val) => {
      document.removeEventListener('keydown', onKey, true);
      scrim.remove();
      resolve(val);
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); done(null); } };
    const box = scrim.querySelector('.bb-choices');
    for (const o of options) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bb-choice';
      b.innerHTML = `<span class="bb-choice__t"></span><span class="bb-choice__d"></span>`;
      b.querySelector('.bb-choice__t').textContent = o.label;
      b.querySelector('.bb-choice__d').textContent = o.detail || '';
      b.addEventListener('click', () => done(o.value));
      box.appendChild(b);
    }
    scrim.querySelector('.bb-dialog__cancel').addEventListener('click', () => done(null));
    scrim.addEventListener('click', (e) => { if (e.target === scrim) done(null); });
    document.addEventListener('keydown', onKey, true);
    (document.getElementById('beer-builder-root') || document.body).appendChild(scrim);
    box.querySelector('.bb-choice')?.focus();
  });
}

const CONVERT_LABEL = '🛒 Build with in-Stock Ingredients';
const CONVERT_LABEL_REBUILD = '🛒 Rebuild with in-Stock Ingredients';

/** The buy path gets its own block under the Print/BeerXML/Save row, introduced by a
 *  short prompt — it's a different kind of action from the neutral panel controls, so
 *  it shouldn't sit in the same wrapping row competing with them. */
function injectConvertButton() {
  if ($('convertBtn')) return;
  const row = document.querySelector('#beer-builder-root .recipe-actions__btns') || document.querySelector('.recipe-actions__btns');
  if (!row) return;
  const block = document.createElement('div');
  block.id = 'convertBlock';
  block.className = 'ra-convert';
  block.hidden = true;
  const ask = document.createElement('p');
  ask.className = 'ra-convert__ask';
  ask.textContent = 'Want to order online?';
  const btn = document.createElement('button');
  btn.id = 'convertBtn';
  btn.type = 'button';
  btn.className = 'ra-btn ra-btn--convert';
  btn.textContent = CONVERT_LABEL;
  btn.title = 'Swap this recipe onto ingredients we stock, so you can buy it';
  btn.addEventListener('click', convertToInStock);
  block.appendChild(ask);
  block.appendChild(btn);
  row.insertAdjacentElement('afterend', block);
}
injectConvertButton();

/** Note above the recipe showing what a conversion changed. */
function injectConvertNote() {
  if ($('convertNote')) return;
  const content = $('recipeContent');
  if (!content) return;
  const note = document.createElement('div');
  note.id = 'convertNote';
  note.className = 'convert-note';
  note.hidden = true;
  content.insertBefore(note, content.firstChild);
}
injectConvertNote();

function renderConvertNote(report) {
  const note = $('convertNote');
  if (!note) return;
  const result = report && report.conversion;
  // Only a converted recipe carries this block — everything else hides the note.
  if (!result || !result.deltas) { note.hidden = true; return; }
  const d = result.deltas;
  // The "after" side is read off the report being rendered, NEVER off the stored deltas.
  // `conversion` is carried forward onto every later recalculation (the hop sliders, the
  // detail editor, a swap) because its substitution map has to survive for the rebuild —
  // and that used to freeze these numbers at the moment of conversion while the metrics
  // and style bars below moved. This strip sits at the very top of the panel, so a frozen
  // IBU there is the first number a brewer reads (Kyle, 2026-07-31).
  const b = (report.benchmarks) || {};
  const now = {
    og: b.gravity ? b.gravity.og : d.og.to,
    ibu: b.ibu != null ? b.ibu : d.ibu.to,
    abv: b.abv != null ? b.abv : d.abv.to,
    srm: b.srm != null ? b.srm : d.srm.to,
  };
  const moved = (label, from, to, dp = 0) => {
    const same = Math.abs(from - to) < (dp ? 5e-4 : 0.05);
    const f = dp ? from.toFixed(dp) : Math.round(from);
    const t = dp ? to.toFixed(dp) : Math.round(to);
    return same ? `${label} ${t}` : `${label} <s>${f}</s> ${t}`;
  };
  // Once the recipe has moved off what the conversion produced, the strategy line stops
  // being true of what's on screen — say so rather than claiming the amounts are original.
  const edited = Math.abs(d.og.to - now.og) >= 5e-4 || Math.abs(d.ibu.to - now.ibu) >= 0.05
    || Math.abs(d.abv.to - now.abv) >= 0.05 || Math.abs(d.srm.to - now.srm) >= 0.05;
  const head = edited
    ? 'In-stock version (edited since converting)'
    : `In-stock version${result.strategy === 'scale' ? ' (scaled to the original targets)' : ' (original amounts kept)'}`;
  const swapped = (result.substitutions || []).filter((s) => s.to && s.to !== s.from);
  const swapList = swapped.length
    ? `<div class="convert-note__subs">${swapped.map((s) => `${escapeHtml(s.from)} → <strong>${escapeHtml(s.to)}</strong>`).join(' · ')}</div>`
    : '';
  const unmatched = (result.unmatched || []).length
    ? `<div class="convert-note__warn">No stocked match: ${escapeHtml(result.unmatched.join(', '))} — left in the recipe, but we can't sell it to you.</div>`
    : '';
  note.innerHTML =
    `<div class="convert-note__head">${head}</div>` +
    `<div class="convert-note__nums">${[
      moved('OG', d.og.from, now.og, 3),
      moved('IBU', d.ibu.from, now.ibu),
      moved('ABV', d.abv.from, now.abv, 1) + '%',
      moved('SRM', d.srm.from, now.srm),
    ].join(' &nbsp;·&nbsp; ')}</div>` +
    swapList + unmatched;
  note.hidden = false;
}

/** All → In-Stock. One-way by design; the all-catalog version is never touched. */
async function convertToInStock() {
  if (!currentRecipe || currentMode !== 'all') return;
  // Same gate the composer uses: the conversion re-runs the whole calculation, so it
  // depends on the rig exactly as designing does. A LOADED recipe is how this got hit —
  // the recipe comes back with the select still empty, so nothing had asked yet, and the
  // blank profile reached the engine as an unknown id.
  if (!equipSelect.value) {
    toast('Pick an equipment profile first: it sets the efficiency and volumes the numbers depend on.', 'err');
    equipSelect.focus();
    equipSelect.scrollIntoView({ block: 'center', behavior: 'smooth' });
    return;
  }
  if (modeStash.instock) {
    const ok = await uiConfirm(
      'Rebuild the in-stock version?',
      'This replaces the in-stock version you already have (including any edits to it). Your all-ingredients recipe is untouched.',
      'Rebuild',
    );
    if (!ok) return;
  }
  const strategy = await uiChoice({
    title: 'How should we handle the amounts?',
    body: 'Our ingredients have different specs, so the numbers move unless we compensate.',
    options: [
      { value: 'scale', label: 'Match the original numbers', detail: 'Adjusts amounts so OG and IBU land as close as our stock allows. Recommended.' },
      { value: 'keep', label: 'Keep my amounts exactly', detail: 'Same weights, and the benchmarks land where they land.' },
    ],
  });
  if (!strategy) return;

  const btn = $('convertBtn');
  const label = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Converting…';
  try {
    const res = await fetch(API + '/api/convert', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        recipe: currentRecipe,
        styleName: styleSelect.value || undefined,
        equipmentProfile: equipmentPayload(),
        strategy,
        uid: anonId || undefined,
      }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.error || `Server returned ${res.status}`);
    }
    const result = await res.json();
    // Carry the recipe's name over, and remember the substitutions so this exact
    // in-stock version can be rebuilt identically later.
    result.report.recipeName = currentReport?.recipeName || recipeTitleText() || '';
    // Everything needed to redraw the before/after note and to rebuild this exact
    // version later rides on the report, so it survives a save and a reload.
    result.report.conversion = {
      substitutions: result.substitutions,
      strategy: result.strategy,
      deltas: result.deltas,
      unmatched: result.unmatched,
    };

    modeStash.all = panelState();               // keep the all-catalog original intact
    modeStash.instock = { recipe: result.report.resolvedRecipe, report: result.report, savedId: null, savedName: '' };
    currentMode = 'instock';
    currentSavedId = null;
    currentSavedName = '';
    renderRecipe(result.report);
    applyModeUI();
    persistSession();
    toast('In-stock version ready — you can add it to your cart.', 'ok');
  } catch (err) {
    toast(err.message || 'Could not convert this recipe.', 'err');
  } finally {
    btn.disabled = false;
    btn.textContent = label;
  }
}

injectModeToggle();
applyModeUI();

// Persist setup tweaks (style/equipment/batch) as they change, so they survive a
// round-trip to the cart even before the next "Brew it".
['styleSelect', 'equipSelect', 'batchSize'].forEach((id) => $(id)?.addEventListener('change', persistSession));

// A "numbers reflect the previous batch size" banner, injected so no theme/Liquid edit is
// needed (kept self-contained in this file, which is synced to the theme asset).
function injectBatchStaleNote() {
  if ($('batchStaleNote')) return;
  const content = $('recipeContent');
  if (!content) return;
  const note = document.createElement('div');
  note.id = 'batchStaleNote';
  note.hidden = true;
  // colors live in styles.css (#batchStaleNote) so dark mode can flip them
  note.style.cssText = 'margin:0 0 10px;padding:8px 10px;border-radius:8px;font-size:13px;line-height:1.35;';
  content.insertBefore(note, content.firstChild);
}
injectBatchStaleNote();

// The shown recipe is "stale" for batch size whenever the batch selector no longer matches
// the batch the current recipe was built at. (Changing the batch alone can't just be
// recomputed — the grain/hop bill was sized by the AI for the old volume.)
function reconcileBatchNote() {
  const note = $('batchStaleNote');
  if (!note) return;
  const uiBatch = Number($('batchSize').value);
  const recipeBatch = currentRecipe && currentRecipe.batchSize;
  const stale = !!currentRecipe && uiBatch > 0 && recipeBatch > 0 && Math.abs(uiBatch - recipeBatch) > 1e-9;
  if (stale) {
    note.textContent = `These numbers still reflect the previous batch size (${fmtVol(recipeBatch)}). Hit "Brew it" to rescale the recipe to ${fmtVol(uiBatch)}.`;
  }
  note.hidden = !stale;
}

// A chat turn can change the batch size now (the model passes a new batchSize when the
// brewer asks to brew a different volume). Every later request — panel edits, equipment
// recalcs, the next chat message — reads the header selector, so the header must follow
// the recipe that arrived or those paths would quietly revert the change.
function syncBatchHeaderToRecipe() {
  const b = currentRecipe && currentRecipe.batchSize;
  const header = $('batchSize');
  if (!header || !(b > 0)) return;
  if (Math.abs((Number(header.value) || 0) - b) > 1e-9) {
    header.value = String(b);
    if (typeof syncBatchUnitUI === 'function') syncBatchUnitUI();
    persistSession();
  }
  reconcileBatchNote(); // also clears a stale "hit Brew it" note once the volumes agree
}

// Changing the equipment profile changes efficiency, boil-off, losses and kettle geometry —
// i.e. OG/FG/IBU/volumes. Recompute the current recipe deterministically so the panel never
// shows numbers from the previously selected rig.
let recalcSeq = 0;
async function recalcForEquipmentChange() {
  if (!currentRecipe) return;
  const prev = currentReport;
  const seq = ++recalcSeq; // guard against out-of-order responses on rapid equipment toggles
  try {
    const res = await fetch(API + '/api/calculate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        recipe: currentRecipe,
        equipmentProfile: equipmentPayload(),
        styleName: styleSelect.value || undefined,
        mode: currentMode,
        customIngredients: customIngredientsPayload(),
      }),
    });
    if (seq !== recalcSeq) return; // a newer change superseded this one — drop this response
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      toast(err.error || 'Could not recalculate for that equipment.', 'err');
      return;
    }
    const report = await res.json();
    // /api/calculate doesn't run the in-stock check, so carry the prior flags forward.
    report.outOfStock = (prev && prev.outOfStock) || [];
    renderRecipe(report); // sets currentRecipe/currentReport + re-renders the panel
    persistSession();
  } catch {
    if (seq === recalcSeq) toast('Could not recalculate for that equipment.', 'err');
  }
}

equipSelect?.addEventListener('change', recalcForEquipmentChange);
equipSelect?.addEventListener('change', () => { rememberEquip(); syncEffField(); nudgeCustomVoltage(); });
$('batchSize')?.addEventListener('change', reconcileBatchNote);

// ── fit the app to the available viewport ────────────────────────────
// Embedded in the storefront, the app sits below the theme's header, so a flat
// 100vh runs off the bottom of the screen (composer + Add-to-cart below the fold).
// Measure the app's real top offset and size it to exactly fill the remaining
// viewport. Desktop (two-column) only — the mobile breakpoint scrolls naturally.
function fitApp() {
  const app = document.querySelector('#beer-builder-root .app') || document.querySelector('.app');
  if (!app) return;
  if (window.innerWidth <= 920) {
    app.style.removeProperty('--bb-app-h');
    return;
  }
  const top = app.getBoundingClientRect().top; // viewport-relative top of the app
  const h = Math.max(420, Math.round(window.innerHeight - top));
  app.style.setProperty('--bb-app-h', h + 'px');
  // The chat/slider split is measured off the sized app, so it has to follow every resize.
  if (typeof fitHopSliderPane === 'function') fitHopSliderPane();
}
window.addEventListener('resize', fitApp);
window.addEventListener('orientationchange', fitApp);
if (document.readyState !== 'loading') fitApp();
else document.addEventListener('DOMContentLoaded', fitApp);
// re-measure after late layout shifts (web fonts, header images, announcement bars)
setTimeout(fitApp, 200);
setTimeout(fitApp, 800);

// ── personal ingredient database ─────────────────────────────────────
// A brewer's own ingredients (invented, or edited copies of catalog entries). They
// resolve in All-Ingredients mode only: In-Stock always uses Clawhammer's canonical
// specs, because that's what actually ships. Entirely JS-injected so no theme-editor
// managed Liquid is touched.

// Same escaping the print sheet uses; aliased so there's one escaper, not two.
const escapeHtml = escPrint;

const ING_KINDS = [
  { kind: 'fermentable', label: 'Fermentables' },
  { kind: 'hop', label: 'Hops' },
  { kind: 'yeast', label: 'Yeast' },
  { kind: 'flavoring', label: 'Flavorings' },
];

// Which facets each kind exposes, and how they're validated server-side.
const ING_FIELDS = {
  fermentable: [
    { key: 'type', label: 'Type', type: 'select', options: ['Grain', 'Adjunct', 'Sugar', 'Extract', 'Dry Extract'], required: true },
    { key: 'ppg', label: 'Yield', unit: 'ppg', type: 'number', step: '0.1', required: true, hint: 'Points per pound per gallon, e.g. 37 for 2-row.' },
    { key: 'lovibond', label: 'Color', unit: '°L', type: 'number', step: '0.1', required: true },
    { key: 'lintner', label: 'Diastatic power', unit: '°Lintner', type: 'number', step: '1', hint: 'Leave blank for non-mashable ingredients.' },
  ],
  hop: [
    { key: 'alphaAcid', label: 'Alpha acid', unit: '%', type: 'number', step: '0.1', required: true },
    { key: 'beta', label: 'Beta acid', unit: '%', type: 'number', step: '0.1' },
  ],
  yeast: [
    { key: 'attenuation', label: 'Attenuation', unit: '%', type: 'number', step: '0.1', required: true },
    { key: 'maxAttenuation', label: 'Max attenuation', unit: '%', type: 'number', step: '0.1', hint: 'Defaults to attenuation.' },
    { key: 'flocculation', label: 'Flocculation', type: 'select', options: ['low', 'medium-low', 'medium', 'medium-high', 'high'] },
  ],
  flavoring: [
    { key: 'unit', label: 'Unit', type: 'select', options: ['oz', 'lb', 'g', 'each'] },
    { key: 'typicalUse', label: 'Typical use', type: 'text', hint: 'e.g. 4–8 oz in secondary for 3–5 days.' },
  ],
};

let myIngredients = [];   // the brewer's rows, as returned by the API

/**
 * The brewer's ingredients, shaped for a calculate/chat request.
 *
 * /api/calculate and /api/chat go to the backend origin directly, NOT through the Shopify
 * App Proxy, so those requests carry no verified customer and the server cannot look these
 * up — it re-validates and applies whatever we send. Without this, editing an ingredient
 * changed nothing on screen. Only meaningful in All mode; In-Stock always uses Clawhammer's
 * canonical specs.
 */
function customIngredientsPayload() {
  if (currentMode !== 'all' || !myIngredients.length) return undefined;
  return myIngredients.map((i) => ({ kind: i.kind, name: i.name, basedOn: i.basedOn, spec: i.spec }));
}
let ingEditing = null;    // the row being edited, or null when adding
let catalogCache = null;  // /api/catalog, fetched once for the copy-from-catalog picker

/**
 * Fetch the All-Ingredients catalog once per page load, bypassing the browser cache.
 *
 * `no-store` is load-bearing, not belt-and-braces: the catalog changes whenever we add or
 * rename an ingredient, and Safari's "Reload Page From Origin" does NOT bypass the cache
 * for a fetch() issued by JS at runtime. A brewer could hard-reload all day and keep
 * getting the stored list, which is exactly what happened after the Bohemian Pilsner malt
 * shipped (Kyle's Safari, 2026-07-29, while the same page in Chrome showed it). Costs one
 * ~300 KB request per page load, and only when a picker is actually opened.
 */
async function fetchCatalog() {
  if (!catalogCache) catalogCache = await (await fetch(API + '/api/catalog', { cache: 'no-store' })).json();
  return catalogCache;
}

/** Build the modal shell once. Reuses the equipment modal's classes for styling. */
function injectIngredientsModal() {
  if ($('ingModal')) return;
  const el = document.createElement('div');
  el.className = 'modal';
  el.id = 'ingModal';
  el.hidden = true;
  el.innerHTML = `
    <div class="modal__scrim" id="ingScrim"></div>
    <div class="modal__panel">
      <div class="modal__head">
        <h3>My ingredients</h3>
        <button id="ingClose" type="button" class="icon-btn" title="Close">×</button>
      </div>
      <div class="modal__body">
        <p class="equip-note" id="ingAuthNote" hidden>Sign in to keep your own ingredients. <a href="/account/login">Sign in</a></p>
        <p class="ing-intro">Ingredients you add or edit here are yours alone, and they show up in <strong>All ingredients</strong> mode. Editing never changes the shared catalog for anyone else.</p>

        <div id="ingList"></div>

        <div class="ing-actions">
          <button type="button" class="ra-btn ra-btn--primary" id="ingAddBtn">+ Add an ingredient</button>
          <button type="button" class="ra-btn" id="ingCopyBtn">Copy from the catalog</button>
        </div>

        <form id="ingForm" hidden>
          <p class="equip-section-title" id="ingFormTitle">New ingredient</p>
          <div class="ing-grid">
            <label class="ing-f"><span>Kind</span>
              <select id="ingKind">${ING_KINDS.map((k) => `<option value="${k.kind}">${k.label.replace(/s$/, '')}</option>`).join('')}</select>
            </label>
            <label class="ing-f ing-f--wide"><span>Name</span><input id="ingName" type="text" maxlength="120" placeholder="e.g. My House Pale Malt" /></label>
          </div>
          <div class="ing-grid" id="ingFacets"></div>
          <label class="ing-f ing-f--wide"><span>Character <em>(optional, comma separated)</em></span>
            <input id="ingCharacter" type="text" maxlength="200" placeholder="bready, biscuit, malty" />
          </label>
          <p class="ing-error" id="ingFormError" hidden></p>
          <div class="ing-actions">
            <button type="submit" class="ra-btn ra-btn--primary" id="ingSaveBtn">Save</button>
            <button type="button" class="ra-btn" id="ingCancelBtn">Cancel</button>
          </div>
        </form>

        <div id="ingPicker" hidden>
          <p class="equip-section-title">Copy an ingredient from the catalog</p>
          <input id="ingSearch" type="text" placeholder="Search 1,900+ ingredients…" autocomplete="off" />
          <div id="ingResults" class="ing-results"></div>
          <div class="ing-actions"><button type="button" class="ra-btn" id="ingPickerCancel">Cancel</button></div>
        </div>
      </div>
    </div>`;
  (document.getElementById('beer-builder-root') || document.body).appendChild(el);

  $('ingScrim').addEventListener('click', closeIngModal);
  $('ingClose').addEventListener('click', closeIngModal);
  $('ingAddBtn').addEventListener('click', () => startIngForm(null));
  $('ingCancelBtn').addEventListener('click', hideIngForm);
  $('ingCopyBtn').addEventListener('click', openIngPicker);
  $('ingPickerCancel').addEventListener('click', () => { $('ingPicker').hidden = true; });
  $('ingKind').addEventListener('change', () => renderIngFacets($('ingKind').value));
  $('ingForm').addEventListener('submit', saveIngredient);
  $('ingSearch').addEventListener('input', renderIngResults);
}

/** The "My ingredients" item lives beside My recipes in the ☰ menu; injected to avoid a Liquid edit. */
function injectIngredientsButton() {
  if ($('myIngredientsBtn')) return;
  const host = $('bbMenu') || $('account');
  if (!host) return;
  const btn = document.createElement('button');
  btn.id = 'myIngredientsBtn';
  btn.type = 'button';
  btn.className = 'account__btn';
  btn.textContent = 'My ingredients';
  btn.title = 'Add or edit your own ingredients (All ingredients mode)';
  btn.hidden = true;
  btn.addEventListener('click', openIngModal);
  host.insertBefore(btn, $('myRecipesBtn') || null);
}

/** "Migrate" — bring recipes over from Brewfather/BeerSmith. Always visible (the page
 * itself explains the sign-in requirement), injected to avoid a Liquid edit. */
function injectMigrateButton() {
  if ($('migrateBtn')) return;
  const host = $('bbMenu') || $('account');
  if (!host) return;
  const btn = document.createElement('button');
  btn.id = 'migrateBtn';
  btn.type = 'button';
  btn.className = 'account__btn';
  btn.textContent = 'Import';
  btn.title = 'Import your recipes from Brewfather (BeerSmith coming soon)';
  btn.addEventListener('click', () => {
    window.location.href = ON_STOREFRONT ? '/apps/beer-builder/migrate' : '/migrate';
  });
  host.insertBefore(btn, $('myIngredientsBtn') || $('myRecipesBtn') || null);
}
injectMigrateButton();

function openIngModal() {
  // Locked: same sign-in pop-up as My recipes, not an empty editor with a footnote.
  if (acct.enabled && !acct.loggedIn) {
    showSigninPopup('Your own ingredients live in your account, so you can use them in any recipe.');
    return;
  }
  injectIngredientsModal();
  $('ingModal').hidden = false;
  hideIngForm();
  $('ingPicker').hidden = true;
  $('ingAuthNote').hidden = !!(acct.enabled && acct.loggedIn);
  loadMyIngredients();
}
function closeIngModal() { const m = $('ingModal'); if (m) m.hidden = true; }
function hideIngForm() { const f = $('ingForm'); if (f) { f.hidden = true; ingEditing = null; } }

async function loadMyIngredients() {
  if (!(acct.enabled && acct.loggedIn)) { myIngredients = []; renderIngList(); return; }
  try {
    const res = await aFetch('/api/ingredients');
    const data = await res.json();
    myIngredients = data.ingredients || [];
  } catch { myIngredients = []; }
  renderIngList();
}

function ingMeta(row) {
  const s = row.spec || {};
  if (row.kind === 'fermentable') return `${s.ppg} ppg · ${s.lovibond}°L${s.lintner != null ? ` · ${s.lintner}°L intner` : ''} · ${s.type}`;
  if (row.kind === 'hop') return `${s.alphaAcid}% AA${s.beta != null ? ` · ${s.beta}% beta` : ''}`;
  if (row.kind === 'yeast') return `${s.attenuation}% atten · ${s.flocculation} floc`;
  return `${s.unit}${s.typicalUse ? ` · ${s.typicalUse}` : ''}`;
}

function renderIngList() {
  const box = $('ingList');
  if (!box) return;
  if (!(acct.enabled && acct.loggedIn)) { box.innerHTML = ''; return; }
  if (!myIngredients.length) {
    box.innerHTML = '<p class="muted" style="font-size:13px;margin:0 0 14px">You haven\'t added any ingredients yet.</p>';
    return;
  }
  box.innerHTML = ING_KINDS.map(({ kind, label }) => {
    const rows = myIngredients.filter((i) => i.kind === kind);
    if (!rows.length) return '';
    return `<p class="equip-section-title">${label}</p>` + rows.map((r) => `
      <div class="equip-row">
        <div class="equip-row__name">${escapeHtml(r.name)}${r.basedOn ? ' <span class="ing-tag">edited</span>' : ''}</div>
        <div class="equip-row__meta">${escapeHtml(ingMeta(r))}</div>
        <div class="equip-row__btns">
          <button type="button" class="link-btn" data-ing-edit="${r.id}">Edit</button>
          <button type="button" class="link-btn link-btn--danger" data-ing-del="${r.id}">Delete</button>
        </div>
      </div>`).join('');
  }).join('');
  box.querySelectorAll('[data-ing-edit]').forEach((b) =>
    b.addEventListener('click', () => startIngForm(myIngredients.find((i) => i.id === b.dataset.ingEdit))));
  box.querySelectorAll('[data-ing-del]').forEach((b) =>
    b.addEventListener('click', () => removeIngredient(b.dataset.ingDel)));
}

function renderIngFacets(kind, spec = {}) {
  const box = $('ingFacets');
  box.innerHTML = (ING_FIELDS[kind] || []).map((f) => {
    const v = spec[f.key] ?? '';
    const inner = f.type === 'select'
      ? `<select id="ingf_${f.key}">${f.options.map((o) => `<option value="${o}"${String(v) === o ? ' selected' : ''}>${o}</option>`).join('')}</select>`
      : `<input id="ingf_${f.key}" type="${f.type}" ${f.step ? `step="${f.step}"` : ''} value="${v === null ? '' : escapeHtml(String(v))}" />`;
    return `<label class="ing-f${f.type === 'text' ? ' ing-f--wide' : ''}">
      <span>${f.label}${f.unit ? ` <em>(${f.unit})</em>` : ''}${f.required ? ' *' : ''}</span>
      ${inner}${f.hint ? `<small>${f.hint}</small>` : ''}
    </label>`;
  }).join('');
}

/** Open the form to add (row=null), edit an existing row, or seed from a catalog entry. */
function startIngForm(row, seed) {
  injectIngredientsModal();
  ingEditing = row || null;
  $('ingPicker').hidden = true;
  $('ingForm').hidden = false;
  $('ingFormError').hidden = true;
  const kind = row?.kind || seed?.kind || $('ingKind').value || 'fermentable';
  $('ingKind').value = kind;
  $('ingKind').disabled = !!row; // changing kind mid-edit would orphan the spec
  $('ingFormTitle').textContent = row ? `Edit ${row.name}` : seed ? `Copy of ${seed.name}` : 'New ingredient';
  $('ingName').value = row?.name || seed?.name || '';
  const spec = row?.spec || seed?.spec || {};
  renderIngFacets(kind, spec);
  $('ingCharacter').value = Array.isArray(spec.character) ? spec.character.join(', ') : '';
  $('ingForm').dataset.basedOn = row?.basedOn || seed?.basedOn || '';
  $('ingName').focus();
}

async function saveIngredient(e) {
  e.preventDefault();
  const kind = $('ingKind').value;
  const name = $('ingName').value.trim();
  if (!name) { showIngError('Give it a name.'); return; }
  const spec = { character: $('ingCharacter').value.split(',').map((s) => s.trim()).filter(Boolean) };
  for (const f of ING_FIELDS[kind] || []) {
    const el = $(`ingf_${f.key}`);
    if (!el) continue;
    const raw = el.value;
    // Blank optional numbers stay absent so the server keeps its default.
    if (raw === '' && !f.required) continue;
    spec[f.key] = raw;
  }
  const basedOn = $('ingForm').dataset.basedOn || null;
  const btn = $('ingSaveBtn');
  btn.disabled = true;
  try {
    const path = ingEditing ? `/api/ingredients/${ingEditing.id}` : '/api/ingredients';
    const res = await aFetch(path, {
      method: ingEditing ? 'PUT' : 'POST',
      body: JSON.stringify({ kind, name, basedOn, spec }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Server returned ${res.status}`);
    }
    hideIngForm();
    await loadMyIngredients();
    // The recipe on screen may use this ingredient — recompute so its numbers reflect the edit.
    if (currentRecipe && currentMode === 'all') recalcForEquipmentChange();
    toast('Ingredient saved.', 'ok');
  } catch (err) {
    showIngError(err.message || 'Could not save that ingredient.');
  } finally {
    btn.disabled = false;
  }
}

function showIngError(msg) {
  const el = $('ingFormError');
  el.textContent = msg;
  el.hidden = false;
}

async function removeIngredient(id) {
  const row = myIngredients.find((i) => i.id === id);
  if (!row) return;
  const ok = await uiConfirm('Delete this ingredient?', `"${row.name}" will be removed from your list.`, 'Delete');
  if (!ok) return;
  try {
    const res = await aFetch(`/api/ingredients/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('delete failed');
    await loadMyIngredients();
    if (currentRecipe && currentMode === 'all') recalcForEquipmentChange();
    toast('Ingredient deleted.', 'ok');
  } catch {
    toast('Could not delete that ingredient.', 'err');
  }
}

// ── copy from catalog ────────────────────────────────────────────────
async function openIngPicker() {
  injectIngredientsModal();
  hideIngForm();
  $('ingPicker').hidden = false;
  $('ingSearch').value = '';
  $('ingResults').innerHTML = '<p class="muted" style="font-size:13px">Loading the catalog…</p>';
  $('ingSearch').focus();
  if (!catalogCache) {
    try {
      await fetchCatalog();
    } catch {
      $('ingResults').innerHTML = '<p class="muted" style="font-size:13px">Could not load the catalog.</p>';
      return;
    }
  }
  renderIngResults();
}

function catalogRows() {
  if (!catalogCache) return [];
  const map = (arr, kind) => (arr || []).map((r) => ({ ...r, kind }));
  return [
    ...map(catalogCache.fermentables, 'fermentable'),
    ...map(catalogCache.hops, 'hop'),
    ...map(catalogCache.yeasts, 'yeast'),
  ];
}

function renderIngResults() {
  const q = ($('ingSearch').value || '').toLowerCase().trim();
  const box = $('ingResults');
  if (!q) { box.innerHTML = '<p class="muted" style="font-size:13px">Start typing to find an ingredient.</p>'; return; }
  const hits = rankSearch(catalogRows(), q).slice(0, 40);
  if (!hits.length) { box.innerHTML = '<p class="muted" style="font-size:13px">Nothing matched.</p>'; return; }
  box.innerHTML = hits.map((r, i) => `
    <button type="button" class="ing-result" data-i="${i}">
      <span class="ing-result__n">${escapeHtml(r.name)}</span>
      <span class="ing-result__m">${escapeHtml(catalogMeta(r))}</span>
    </button>`).join('');
  box.querySelectorAll('.ing-result').forEach((b) =>
    b.addEventListener('click', () => {
      const r = hits[Number(b.dataset.i)];
      startIngForm(null, {
        kind: r.kind,
        name: r.name,
        basedOn: r.id,
        spec: specFromCatalog(r),
      });
    }));
}

function catalogMeta(r) {
  if (r.kind === 'fermentable') return `${r.ppg} ppg · ${r.lovibond}°L · ${r.type}`;
  if (r.kind === 'hop') return `${r.alphaAcid}% AA${r.origin ? ` · ${r.origin}` : ''}`;
  return `${r.attenuation}% atten${r.lab ? ` · ${r.lab}` : ''}${r.productId ? ` ${r.productId}` : ''}`;
}

function specFromCatalog(r) {
  if (r.kind === 'fermentable') return { ppg: r.ppg, lovibond: r.lovibond, lintner: r.lintner ?? '', type: r.type, character: [] };
  if (r.kind === 'hop') return { alphaAcid: r.alphaAcid, beta: r.beta ?? '', character: [] };
  return { attenuation: r.attenuation, maxAttenuation: r.maxAttenuation ?? '', flocculation: r.flocculation || 'medium', character: [] };
}

injectIngredientsButton();

// ── ingredient detail + version swap (grain, hops, yeast) ────────────
// The panel has room for a name and an amount, but the specs behind the numbers matter,
// and a generic name like "Pilsner Malt" resolves to one specific catalog entry out of
// many. Each section heading opens the full picture and lets the brewer pick a different
// version of any ingredient — including their own, which sort to the top.
//
// Two views, never both: the LIST (every ingredient of that kind + its specs) and the
// PICKER (one ingredient's alternatives). Swapping rewrites the name and recalculates
// through the normal deterministic path; no numbers are computed here.

const DETAIL_KINDS = {
  fermentable: { title: 'Grains & Mash', unit: 'lb', catalogKey: 'fermentables', stockKey: 'fermentable' },
  hop: { title: 'Boil & Hops', unit: 'oz', catalogKey: 'hops', stockKey: 'hop' },
  yeast: { title: 'Yeast & Fermentation', unit: '', catalogKey: 'yeasts', stockKey: 'yeast' },
};

let detailKind = 'fermentable';
let swapIndex = null;
// When true the picker appends a new entry instead of replacing swapIndex. Kept separate
// from swapIndex so 'adding' is never confused with 'editing row 0'.
let pickerAdds = false;

/** The recipe's entries for a kind, as a list (yeast is a single object). */
function detailItems(kind) {
  if (!currentRecipe) return [];
  if (kind === 'fermentable') return currentRecipe.fermentables || [];
  if (kind === 'hop') return currentRecipe.hopSchedule || [];
  return currentRecipe.yeast?.name ? [currentRecipe.yeast] : [];
}

/** Resolution notes for a kind, in the same order as detailItems(). */
function detailNotes(kind) {
  return (currentReport?.resolution || []).filter((n) => n.kind === kind);
}

/** The spec line shown under each entry, per kind. */
function detailSpecs(kind, x) {
  if (kind === 'fermentable') {
    return [
      x.ppg != null ? `${x.ppg} ppg` : null,
      x.lovibond != null ? `${x.lovibond}°L` : null,
      x.lintner != null ? `${x.lintner}°Lintner` : null,
      x.type || null,
    ].filter(Boolean).join(' · ');
  }
  if (kind === 'hop') {
    // Timing AND alpha are deliberately absent: both have their own editable controls
    // on the row, and printing them here too would show the same numbers twice.
    return '';
  }
  return [
    x.attenuation != null ? `${x.attenuation}% attenuation` : null,
    x.maxAttenuation != null && x.maxAttenuation !== x.attenuation ? `${x.maxAttenuation}% max` : null,
  ].filter(Boolean).join(' · ');
}

/** The amount shown on the right of each row, in the brewer's display unit. */
function detailAmount(kind, x) {
  if (kind === 'yeast') return '';
  const w = x.weight;
  if (w == null) return '';
  return kind === 'fermentable' ? fmtGrain(w) : fmtHop(w);
}

function injectDetailModal() {
  if ($('detailModal')) return;
  const el = document.createElement('div');
  el.className = 'modal';
  el.id = 'detailModal';
  el.hidden = true;
  el.innerHTML = `
    <div class="modal__scrim" id="detailScrim"></div>
    <div class="modal__panel">
      <div class="modal__head">
        <h3 id="detailTitle">Grains &amp; Mash</h3>
        <button id="detailClose" type="button" class="icon-btn" title="Close">×</button>
      </div>
      <div class="modal__body">
        <div id="detailListView">
          <p class="ing-intro">The exact catalog entries the calculator used. Add, remove or swap anything here and the recipe recalculates.</p>
          <button type="button" class="ra-btn detail-add" id="detailAdd">+ Add an ingredient</button>
          <div id="detailRows"></div>
          <div id="detailMashBlock" hidden>
            <h4 class="detail-mash__head">Mash</h4>
            <div id="mashEditorBody"></div>
          </div>
        </div>
        <div id="detailPickerView" hidden>
          <button type="button" class="link-btn grain-back" id="detailBack">← Back</button>
          <p class="grain-picker__for" id="detailPickerFor"></p>
          <input type="text" id="detailPickerQ" class="grain-picker__q" placeholder="Search ingredients…" autocomplete="off" />
          <div id="detailPickerResults" class="grain-picker__results"></div>
        </div>
      </div>
    </div>`;
  (document.getElementById('beer-builder-root') || document.body).appendChild(el);
  $('detailScrim').addEventListener('click', closeDetailModal);
  $('detailClose').addEventListener('click', closeDetailModal);
  $('detailBack').addEventListener('click', showDetailList);
  $('detailAdd').addEventListener('click', openAddPicker);
  $('detailPickerQ').addEventListener('input', renderPickerResults);
}

function closeDetailModal() { const m = $('detailModal'); if (m) m.hidden = true; swapIndex = null; pickerAdds = false; }

function showDetailList() {
  swapIndex = null;
  pickerAdds = false;
  $('detailListView').hidden = false;
  $('detailPickerView').hidden = true;
  $('detailTitle').textContent = DETAIL_KINDS[detailKind].title;
  // A recipe has exactly one yeast, so there is nothing to add to and nothing to remove.
  const addBtn = $('detailAdd');
  if (addBtn) addBtn.hidden = detailKind === 'yeast';
  renderDetailRows();
}

/**
 * Open the detail view for a kind. `index` goes straight to that entry's version picker;
 * `focusIndex` opens the list instead and brings that entry into view — which is what a
 * click on a recipe-panel row wants, since the row's amount and timing live there.
 */
function openDetail(kind, index, focusIndex) {
  if (!currentRecipe || !currentReport || !DETAIL_KINDS[kind]) return;
  if (!detailItems(kind).length) return;
  detailKind = kind;
  injectDetailModal();
  $('detailModal').hidden = false;
  if (typeof index === 'number') { openPicker(index); return; }
  showDetailList();
  if (typeof focusIndex === 'number') focusDetailRow(focusIndex);
}

/** Scroll a detail row into view and flash it, so a click from the panel lands somewhere. */
function focusDetailRow(i) {
  const row = $('detailRows')?.children?.[i];
  if (!row) return;
  row.scrollIntoView({ block: 'nearest' });
  row.classList.add('grain-row--flash');
  setTimeout(() => row.classList.remove('grain-row--flash'), 1200);
}

/** A guess at the search term for "other versions of this": the first distinctive word. */
function baseTerm(name) {
  const stop = new Set(['malt', 'malts', 'organic', 'extract', 'liquid', 'dry', 'row', 'pale', 'dark', 'light', 'extra', 'premium', 'blend', 'flaked', 'torrefied', 'yeast', 'ale']);
  const words = String(name).toLowerCase().replace(/\([^)]*\)/g, ' ').replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean);
  return words.find((w) => w.length >= 4 && !stop.has(w)) || words.sort((a, b) => b.length - a.length)[0] || '';
}

/**
 * Per-hop timing editor. Each addition carries its own controls, because "when" is as much
 * of a hop's identity as "how much": the same ounce of Citra is bitterness at 60 minutes,
 * flavor in a 185°F whirlpool, and pure aroma in the fermenter.
 *
 * The four stops are the four the ENGINE models (see hopMethodOf), so what the row offers
 * and what the calculator does can't drift apart. Fields are the ones that method actually
 * uses — a dry hop has no kettle temperature — and switching method rewrites the entry
 * with sane defaults rather than leaving a half-populated mix behind.
 */
const HOP_STAGES = ['active fermentation', 'near terminal gravity', 'post-fermentation'];
function hopTimingControls(h, i) {
  const method = hopMethodOf(h);
  const opt = (v, label) => `<option value="${v}"${v === method ? ' selected' : ''}>${label}</option>`;
  const num = (attr, value, min, max, step, unit, title) =>
    `<span class="proc-f" title="${escapeHtml(title)}">`
    + `<input class="inline-num" type="number" data-${attr}="${i}" value="${value}" min="${min}" max="${max}" step="${step}" inputmode="numeric" />`
    + `<span class="grain-row__unit">${unit}</span></span>`;

  let fields = '';
  if (method === 'boil') {
    fields = num('hoptime', h.time ?? 60, 1, 240, 5, 'min', 'Minutes in the boil — longer means more bitterness');
  } else if (method === 'whirlpool') {
    fields = num('hopwptime', h.whirlpoolTime ?? 30, 0, 120, 5, 'min', 'How long the hops steep after the boil')
      + (displayUnits.temp === 'c'
        ? num('hopwptemp', Math.round(fToC(h.whirlpoolTemp ?? 185)), 38, 100, 1, '°C', 'Steep temperature — cooler extracts less bitterness')
        : num('hopwptemp', h.whirlpoolTemp ?? 185, 100, 212, 5, '°F', 'Steep temperature — cooler extracts less bitterness'));
  } else if (method === 'dryhop') {
    fields = num('hopdays', h.dryHopDays ?? 3, 1, 14, 1, 'days', 'Days in contact with the beer')
      + `<span class="proc-f"><select class="inline-select" data-hopstage="${i}" title="When in fermentation this charge goes in">`
      + `<option value=""${h.dryHopStage ? '' : ' selected'}>any time</option>`
      + HOP_STAGES.map((s) => `<option value="${s}"${h.dryHopStage === s ? ' selected' : ''}>${s}</option>`).join('')
      + '</select></span>';
  }
  // Alpha acid rides EVERY method — dry-hop IBU (the humulinone model) reads it too.
  // Hand-rolled rather than num(): alpha wants a decimal keypad and one-decimal values
  // (a brewer's aged Saaz is 2.2%, not 2).
  const effAlpha = h.customAlpha ?? h.aa ?? h.alphaAcid;
  fields += `<span class="proc-f" title="Alpha acid — set your bag's measured % and the IBU follows it">`
    + `<input class="inline-num" type="number" data-hopalpha="${i}" value="${effAlpha ?? ''}" min="0.5" max="30" step="0.1" inputmode="decimal" />`
    + `<span class="grain-row__unit">% AA</span></span>`
    + (h.customAlpha != null ? `<button type="button" class="link-btn" data-hopalphaclear="${i}" title="Drop your measured alpha and go back to the catalog spec">catalog α</button>` : '');
  // Form: pellets (what we sell), whole dried cones, or fresh (wet) hops off the bine.
  // Switching to or from fresh rescales the weight (about 5x) so the beer stays the same.
  const form = hopFormOf(h);
  const formSel = form === 'cryo' ? '' : `<span class="proc-f" title="Pellets, whole dried cones, or fresh (wet) hops. Fresh hops are mostly water: about 5x the pellet weight, and you supply them"><select class="inline-select" data-hopform="${i}">`
    + HOP_FORMS.map(([v, label]) => `<option value="${v}"${v === form ? ' selected' : ''}>${label}</option>`).join('')
    + '</select></span>';
  return `<div class="proc-line grain-row__timing">`
    + `<span class="proc-f"><select class="inline-select" data-hopmethod="${i}" title="When this hop goes in">`
    + opt('boil', 'Boil') + opt('flameout', 'Flameout') + opt('whirlpool', 'Whirlpool') + opt('dryhop', 'Dry hop')
    + `</select></span>${fields}${formSel}</div>`;
}

function renderDetailRows() {
  const box = $('detailRows');
  if (!box) return;
  const items = detailItems(detailKind);
  const notes = detailNotes(detailKind);
  // Grain steps on the mode's grid (4 oz when shopping, 1 oz in the calculator); hops in
  // quarter ounces; yeast in whole packs. Yeast packs don't touch the benchmarks
  // (attenuation is the strain's, not the pitch's) — they only decide how many packs reach
  // the cart. The input edits in the brewer's DISPLAY unit (kg/g when toggled);
  // applyAmount converts back to lb/oz, and the engine re-snaps grain to the same grid.
  const amountSpec = detailAmountSpec(detailKind);

  box.innerHTML = items.map((x, i) => {
    const resolved = notes[i]?.resolvedAs || x.name;
    const label = notes[i]?.label;
    const estimated = notes[i]?.status === 'defaulted';
    const renamed = resolved.toLowerCase() !== String(x.name).toLowerCase();
    const value = detailKind === 'yeast' ? (x.packages ?? 1) : amountSpec.toDisplay(x.weight ?? 0);
    return `
      <div class="grain-row">
        <div class="grain-row__top">
          <button type="button" class="grain-row__pick" data-swap="${i}">
            <span class="grain-row__name">${escapeHtml(resolved)}${label ? (detailKind === 'yeast' ? ` <span class="muted">(${escapeHtml(label)})</span>` : ` <span class="muted">- ${escapeHtml(label)}</span>`) : ''}${estimated ? ' <span class="est-flag">est.</span>' : ''}</span>
          </button>
          <span class="grain-row__amtbox">
            <input class="grain-row__amtinput" type="number" data-amt="${i}"
                   value="${value}" step="${amountSpec.step}" min="${amountSpec.min}" inputmode="decimal" />
            <span class="grain-row__unit">${amountSpec.unit}</span>
          </span>
        </div>
        <div class="grain-row__specs">${escapeHtml(detailSpecs(detailKind, x))}</div>
        ${detailKind === 'hop' ? hopTimingControls(x, i) : ''}
        ${renamed ? `<div class="grain-row__from">matched from “${escapeHtml(x.name)}”</div>` : ''}
        <div class="grain-row__actions">
          <button type="button" class="link-btn grain-row__cta" data-swap="${i}">Use a different version →</button>
          ${detailKind === 'yeast' ? '' : `<button type="button" class="link-btn link-btn--danger" data-remove="${i}">Remove</button>`}
        </div>
      </div>`;
  }).join('');
  box.querySelectorAll('[data-swap]').forEach((b) =>
    b.addEventListener('click', () => openPicker(Number(b.dataset.swap))));
  box.querySelectorAll('[data-remove]').forEach((b) =>
    b.addEventListener('click', () => removeItem(Number(b.dataset.remove))));
  // Commit on change/blur rather than each keystroke, so typing "12" isn't read as "1".
  box.querySelectorAll('[data-amt]').forEach((inp) =>
    inp.addEventListener('change', () => applyAmount(Number(inp.dataset.amt), inp.value)));

  // Hop timing. Each control patches one field of one addition and recalculates.
  box.querySelectorAll('[data-hopmethod]').forEach((sel) => {
    sel.addEventListener('change', () => applyHopMethod(Number(sel.dataset.hopmethod), sel.value));
    if (typeof enhanceSearchSelect === 'function') enhanceSearchSelect(sel);
  });
  box.querySelectorAll('[data-hopstage]').forEach((sel) => {
    sel.addEventListener('change', () => applyHopTiming(Number(sel.dataset.hopstage), { dryHopStage: sel.value || undefined }, 'Dry-hop stage updated.'));
    if (typeof enhanceSearchSelect === 'function') enhanceSearchSelect(sel);
  });
  box.querySelectorAll('[data-hopform]').forEach((sel) => {
    sel.addEventListener('change', () => applyHopForm(Number(sel.dataset.hopform), sel.value));
    if (typeof enhanceSearchSelect === 'function') enhanceSearchSelect(sel);
  });
  const numField = (attr, build) => box.querySelectorAll(`[data-${attr}]`).forEach((inp) =>
    inp.addEventListener('change', () => {
      const n = Number(inp.value);
      if (!Number.isFinite(n)) { renderDetailRows(); return; }
      const { patch, msg } = build(Math.round(n));
      applyHopTiming(Number(inp.dataset[attr]), patch, msg);
    }));
  numField('hoptime', (n) => ({ patch: { time: Math.min(240, Math.max(1, n)) }, msg: `Boil time set to ${Math.min(240, Math.max(1, n))} min.` }));
  numField('hopwptime', (n) => ({ patch: { whirlpoolTime: Math.min(120, Math.max(0, n)) }, msg: `Whirlpool set to ${Math.min(120, Math.max(0, n))} min.` }));
  numField('hopwptemp', (n) => {
    // The field edits in the DISPLAY unit; the recipe stores °F.
    const f = Math.min(212, Math.max(100, displayUnits.temp === 'c' ? Math.round(cToF(n)) : n));
    return { patch: { whirlpoolTemp: f }, msg: `Whirlpool temp set to ${fmtTemp(f)}.` };
  });
  numField('hopdays', (n) => ({ patch: { dryHopDays: Math.min(14, Math.max(1, n)) }, msg: `Dry hop set to ${Math.min(14, Math.max(1, n))} days.` }));
  // Alpha keeps its decimal — numField rounds to whole numbers, and 2.2% is the point.
  // Writes customAlpha (brewer data the resolver honors), never aa (a resolver constant).
  box.querySelectorAll('[data-hopalpha]').forEach((inp) =>
    inp.addEventListener('change', () => {
      const n = Number(inp.value);
      if (!Number.isFinite(n) || n <= 0) { renderDetailRows(); return; }
      const v = Math.round(Math.min(30, Math.max(0.5, n)) * 10) / 10;
      applyHopTiming(Number(inp.dataset.hopalpha), { customAlpha: v }, `Alpha set to ${v}%.`);
    }));
  box.querySelectorAll('[data-hopalphaclear]').forEach((b) =>
    b.addEventListener('click', () => applyHopTiming(Number(b.dataset.hopalphaclear), { customAlpha: undefined }, 'Back to the catalog alpha.')));

  // The grains modal also owns the mash program (Kyle, 8/20): the step editor renders
  // below the grain rows so one tap edits the bill and the mash together. Living here
  // keeps it fresh through recalcFromDetail's re-render, same as the rows above.
  const mashBlock = $('detailMashBlock');
  if (mashBlock) {
    mashBlock.hidden = detailKind !== 'fermentable';
    if (detailKind === 'fermentable') { ensureMashCss(); renderMashEditor(); }
  }
}

/**
 * Patch one hop's timing fields and recalculate. Resolver-filled constants are stripped
 * from every entry (same as the swap path) so the engine re-derives alpha for all of them.
 */
async function applyHopTiming(index, patch, okMessage) {
  if (!currentRecipe?.hopSchedule?.[index]) return;
  const recipe = { ...currentRecipe };
  recipe.hopSchedule = currentRecipe.hopSchedule.map((x, i) => {
    const { aa, alphaAcid, ...rest } = x;
    return i === index ? { ...rest, ...patch } : rest;
  });
  await recalcFromDetail(recipe, okMessage);
}

/**
 * Change a hop's form. Pellet <-> whole keeps the weight (same dry matter). Anything
 * <-> fresh rescales it by 5 so the bitterness and aroma stay put: the stored weight is
 * always the real weight the brewer adds, and the engine reads fresh hops' IBU off the
 * dry matter behind that weight.
 */
async function applyHopForm(index, form) {
  const cur = currentRecipe?.hopSchedule?.[index];
  if (!cur) return;
  const was = hopFormOf(cur);
  const next = form === 'whole' || form === 'wet' ? form : 'pellet';
  if (next === was) return;
  const q4 = (v) => Math.round(v * 4) / 4;
  let weight = cur.weight;
  let msg;
  if (next === 'wet' && was !== 'wet') {
    weight = q4((cur.weight || 0) * WET_HOP_PELLET_MULTIPLIER);
    msg = `Fresh hops: ${fmtHop(weight)} (about ${WET_HOP_PELLET_MULTIPLIER}x the ${fmtHop(cur.weight)} pellet charge, they're mostly water). You supply these; they won't be in the cart.`;
  } else if (was === 'wet') {
    weight = Math.max(0.25, q4((cur.weight || 0) / WET_HOP_PELLET_MULTIPLIER));
    msg = `Back to ${next === 'whole' ? 'whole dried cones' : 'pellets'}: ${fmtHop(weight)}.`;
  } else {
    msg = next === 'whole' ? 'Whole dried cones: same weight, slightly less kettle bitterness.' : 'Back to pellets.';
  }
  await applyHopTiming(index, { hopForm: next === 'pellet' ? undefined : next, weight }, msg);
}

/**
 * Move a hop to a different addition. The entry is REBUILT rather than merged: a hop that
 * stops being a whirlpool must lose its whirlpool time and temp, or the engine still sees
 * a whirlpool spec and keeps computing it as one.
 */
async function applyHopMethod(index, method) {
  const cur = currentRecipe?.hopSchedule?.[index];
  if (!cur) return;
  const recipe = { ...currentRecipe };
  recipe.hopSchedule = currentRecipe.hopSchedule.map((x, i) => {
    const { aa, alphaAcid, time, method: m, whirlpoolTime, whirlpoolTemp, dryHopDays, dryHopStage, coolTime, ...rest } = x;
    if (i !== index) return { ...rest, time, method: m, whirlpoolTime, whirlpoolTemp, dryHopDays, dryHopStage, coolTime };
    // Defaults match the definitions the hop sliders use, so a hand-built addition and a
    // slider-built one mean the same thing: whirlpool = 30 min at 185°F, dry hop = 3 days.
    if (method === 'dryhop') return { ...rest, method: 'dryhop', dryHopDays: dryHopDays ?? 3, ...(dryHopStage ? { dryHopStage } : {}) };
    if (method === 'whirlpool') return { ...rest, method: 'whirlpool', time: 0, whirlpoolTime: whirlpoolTime ?? 30, whirlpoolTemp: whirlpoolTemp ?? 185 };
    if (method === 'flameout') return { ...rest, method: 'boil', time: 0 };
    return { ...rest, method: 'boil', time: time && time > 0 ? time : 60 };
  });
  const label = { boil: 'the boil', flameout: 'flameout', whirlpool: 'the whirlpool', dryhop: 'the dry hop' }[method] || method;
  await recalcFromDetail(recipe, `Moved to ${label}.`);
}

async function openPicker(i) {
  injectDetailModal();
  const item = detailItems(detailKind)[i];
  if (!item) return;
  swapIndex = i;
  const current = detailNotes(detailKind)[i]?.resolvedAs || item.name;
  const amount = detailAmount(detailKind, item);
  $('detailListView').hidden = true;
  $('detailPickerView').hidden = false;
  $('detailTitle').textContent = 'Choose a version';
  $('detailPickerFor').innerHTML = `Replacing <strong>${escapeHtml(current)}</strong>${amount ? ` · ${amount}` : ''}`;
  $('detailPickerQ').value = baseTerm(current);
  $('detailPickerResults').innerHTML = '<p class="grain-picker__empty">Loading…</p>';
  // All mode picks from the full catalog (needs a fetch); In-stock picks from what we sell.
  if (currentMode === 'all' && !catalogCache) {
    try { await fetchCatalog(); }
    catch { $('detailPickerResults').innerHTML = '<p class="grain-picker__empty">Could not load the catalog.</p>'; return; }
  }
  renderPickerResults();
  $('detailPickerQ').focus();
  $('detailPickerQ').select();
}

/**
 * Supplier qualifier for a stocked name, or nothing when the name already carries it
 * (a canonical that spells the brand out would otherwise render as "X (Brand) - Brand",
 * which reads as two different things).
 */
function stockLabel(name) {
  const l = inStockLabels[name];
  if (!l) return undefined;
  return normText(name).includes(normText(l)) ? undefined : l;
}

/**
 * The muted qualifier beside a picker name. Same markup and the same dash-vs-parentheses
 * convention the grain bill rows use, so a malt reads identically in both places
 * ("Munich Malt - Riverbend") and a yeast keeps its product code in parentheses.
 */
function brandTag(label) {
  if (!label) return '';
  return detailKind === 'yeast'
    ? ` <span class="muted">(${escapeHtml(label)})</span>`
    : ` <span class="muted">- ${escapeHtml(label)}</span>`;
}

/** Meta line for a catalog row in the picker, per kind. */
function catalogRowMeta(kind, r) {
  if (kind === 'fermentable') return `${r.ppg} ppg · ${r.lovibond}°L · ${r.type}${r.origin ? ` · ${r.origin}` : ''}`;
  if (kind === 'hop') return `${r.alphaAcid}% alpha${r.origin ? ` · ${r.origin}` : ''}`;
  return `${r.attenuation}% atten · ${r.flocculation} floc${r.lab ? ` · ${r.lab}` : ''}${r.productId ? ` ${r.productId}` : ''}`;
}

/** Meta line for one of the brewer's own ingredients. */
function mineRowMeta(kind, spec) {
  if (kind === 'fermentable') return `${spec.ppg} ppg · ${spec.lovibond}°L · ${spec.type}`;
  if (kind === 'hop') return `${spec.alphaAcid}% alpha`;
  return `${spec.attenuation}% atten · ${spec.flocculation} floc`;
}

// ── ingredient search ────────────────────────────────────────────────
// Searching used to be one contiguous `name.includes(query)`, so "black patent" could
// never reach "Black (Patent) Malt" — the parentheses sit between the two words, and the
// box just said "Nothing matched". Match on WORDS instead, against the display name and
// the catalog aliases (brewers type the alias: "black patent" is what every maltster's
// black malt answers to), then rank so the tightest name surfaces first.

/** Lowercase, drop trademark marks and punctuation, collapse whitespace. */
function normText(s) {
  return String(s ?? '').toLowerCase().replace(/[®™©]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * Words a brewer types that name the KIND, not the ingredient: "alora hops", "pomona
 * yeast", "vienna malt". Every word had to appear in the name, so "alora hops" scored 0
 * against "Alora" and the box said nothing matched (Kyle, 2026-08-17). These are only
 * dropped as a fallback, after the full query has had its chance, so a name that really
 * carries the word ("Wheat Malt") still ranks above one that does not.
 */
const SEARCH_KIND_WORDS = new Set(['hop', 'hops', 'malt', 'malts', 'grain', 'grains', 'yeast', 'yeasts', 'strain', 'dry', 'liquid', 'pellet', 'pellets']);

/** Score a candidate against a query. 0 = no match; higher is better. */
function searchScore(cand, q) {
  const s = scoreTerms(cand, normText(q));
  if (s) return s;
  const kept = normText(q).split(' ').filter((t) => t && !SEARCH_KIND_WORDS.has(t));
  const stripped = kept.join(' ');
  if (!stripped || stripped === normText(q)) return 0;
  // a hair under the same tier for the real query, so "alora hops" ranks Alora, but any
  // candidate that matched the full phrase stays ahead of it
  return Math.max(0, scoreTerms(cand, stripped) - 1);
}

function scoreTerms(cand, qn) {
  if (!qn) return 1;
  const qt = qn.split(' ').filter(Boolean);
  const name = normText(cand.name);
  // whole query as a phrase in the name is the strongest signal, then a prefix
  if (name === qn) return 1000;
  if (name.startsWith(qn)) return 900;
  if (name.includes(qn)) return 800;
  // spacing differences are not differences: "cara munich" is Caramunich, "wb 06" is WB-06
  const qp = qn.replace(/ /g, '');
  if (name.replace(/ /g, '').includes(qp)) return 790;
  // every word present somewhere in the name — this is the "black patent" case
  if (allWords(name, qt)) return 700 - Math.min(99, name.length);
  // The supplier label is on screen beside the name, so it has to be searchable too:
  // "riverbend" must find every Riverbend malt, not just the one whose canonical happens
  // to spell the brand out. Ranked under the name hits, above the aliases.
  if (cand.label) {
    const full = normText(`${cand.name} ${cand.label}`);
    if (full.includes(qn)) return 650;
    if (allWords(full, qt)) return 600 - Math.min(99, full.length);
  }
  // fall back to the aliases, ranked below any name hit so display matches lead
  const aliases = (cand.aliases || []).map(normText);
  if (aliases.some((a) => a === qn)) return 500;
  if (aliases.some((a) => a.includes(qn))) return 400;
  if (aliases.some((a) => a.replace(/ /g, '').includes(qp))) return 390;
  if (aliases.some((a) => allWords(a, qt))) return 300;
  // Last, the qualifiers printed on the row's meta line: lab, product code, origin,
  // supplier. "Lallemand Pomona" is how the bag reads, but the catalog name is "LalBrew
  // Pomona (…)", so the brand word only exists in the lab field. Name + aliases + those
  // together have to cover every query word.
  const extra = (cand.extra || [cand.lab, cand.productId, cand.origin, cand.supplier]).filter(Boolean).map(normText);
  if (extra.length) {
    const hay = [name, ...aliases, ...extra].join(' ');
    const packed = hay.replace(/ /g, ''); // "us05" for a code printed "US-05"
    if (qt.every((t) => wordStart(hay, t) || packed.includes(t))) return 200 - Math.min(99, name.length);
  }
  return 0;
}

/**
 * Every query word begins a word in the text. Word STARTS, not substrings: "us-05" used to
 * light up "Lactobacillus Blend (Omega OYL-605)" (lactobacill-US, 6-05) above the strain
 * whose alias IS us-05, because any letters anywhere counted. Prefixes still work, which is
 * what typing-as-you-go needs ("pils" → Pilsner, "cara" → Caramunich).
 */
function wordStart(text, t) {
  return text.startsWith(t) || text.includes(' ' + t);
}
function allWords(text, qt) {
  return qt.every((t) => wordStart(text, t));
}

/** Filter + rank candidates for a query, best first. */
function rankSearch(list, q) {
  return list
    .map((c) => ({ c, s: searchScore(c, q) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .map((x) => x.c);
}

/** Candidates for the current kind. In-stock has names only, and that's enough. */
function swapCandidates() {
  const kind = detailKind;
  const cfg = DETAIL_KINDS[kind];
  // In-stock mode is the commerce path: only what we actually sell, and never a personal
  // ingredient (those exist for the calculator).
  if (currentMode !== 'all') {
    return (inStockNames[cfg.stockKey] || []).map((n) => ({ name: n, label: stockLabel(n), meta: 'in stock' }));
  }
  // The brewer's own ingredients come FIRST — if they made their own version, that is the
  // one they are looking for, and burying it under hundreds of catalog entries (or
  // omitting it, as this list originally did) makes it look like the edit never saved.
  const mine = myIngredients
    .filter((i) => i.kind === kind)
    .map((i) => ({ name: i.name, meta: mineRowMeta(kind, i.spec), mine: true }));
  const catalog = (catalogCache?.[cfg.catalogKey] || []).map((r) => ({
    name: r.name, meta: catalogRowMeta(kind, r), aliases: r.aliases,
    // searchable qualifiers (they're on the meta line, so a brewer will type them)
    extra: [r.lab, r.productId, r.origin, r.supplier].filter(Boolean),
  }));
  // A personal entry with the same name replaces the catalog row rather than sitting
  // beside a near-identical twin.
  const claimed = new Set(mine.map((m) => m.name.toLowerCase()));
  return [...mine, ...catalog.filter((c) => !claimed.has(c.name.toLowerCase()))];
}

function renderPickerResults() {
  const box = $('detailPickerResults');
  if (!box || (swapIndex == null && !pickerAdds)) return;
  const q = ($('detailPickerQ').value || '').toLowerCase().trim();
  const all = swapCandidates();
  const matched = q ? rankSearch(all, q) : all;
  const hits = matched.slice(0, 60);
  if (!hits.length) { box.innerHTML = '<p class="grain-picker__empty">Nothing matched. Try a shorter word.</p>'; return; }
  // Blank add-search on the grain bill: surface the boil sugars/adjuncts a brewer would
  // never think to look for under "grain" (triage 8/14: "couldn't find a way to add lactose").
  // Matched by pattern, not exact name: In-Stock calls it "Lactose", the full catalog
  // "Milk Sugar (Lactose)"; each chip resolves to whatever THIS mode's list calls it.
  const QUICK_PATTERNS = [/^lactose$|milk sugar/i, /^corn sugar|dextrose\)$/i, /^table sugar|^cane sugar|sucrose/i, /maltodextrin/i, /rice hulls?/i];
  const QUICK = detailKind === 'fermentable' && pickerAdds && !q
    ? QUICK_PATTERNS.map((re) => all.find((c) => re.test(c.name))?.name).filter(Boolean)
    : [];
  const quickHtml = QUICK.length
    ? `<div class="grain-picker__quick"><span class="grain-picker__quicklab">Boil sugars &amp; adjuncts:</span>${QUICK.map((n) => `<button type="button" class="chip" data-quick="${escapeHtml(n)}">${escapeHtml(n)}</button>`).join('')}</div>`
    : '';
  const item = detailItems(detailKind)[swapIndex];
  const current = detailNotes(detailKind)[swapIndex]?.resolvedAs || item?.name;
  box.innerHTML = quickHtml +
    (matched.length > hits.length ? `<p class="grain-picker__count">Showing ${hits.length} of ${matched.length} — keep typing to narrow it down.</p>` : '') +
    hits.map((c, n) => `
      <button type="button" class="grain-opt${c.name === current ? ' is-current' : ''}" data-pick="${n}">
        <span class="grain-opt__n">${escapeHtml(c.name)}${brandTag(c.label)}${c.mine ? ' <span class="ing-tag">yours</span>' : ''}</span>
        <span class="grain-opt__m">${escapeHtml(c.meta)}${c.name === current ? ' · current' : ''}</span>
      </button>`).join('');
  box.querySelectorAll('[data-pick]').forEach((b) =>
    b.addEventListener('click', () => {
      const name = hits[Number(b.dataset.pick)].name;
      if (pickerAdds) addItem(name); else applySwap(swapIndex, name);
    }));
  box.querySelectorAll('[data-quick]').forEach((b) => b.addEventListener('click', () => addItem(b.dataset.quick)));
}

// applyMashTemp left with the mash tile (Kyle, 8/20): single-infusion temp edits go
// through the Grains & Mash editor, which commits via applyMashSteps.

/** Planned fermentation temp: stored and printed, never modeled; benchmarks won't move. */
async function applyFermentTemp(vF) {
  if (!currentRecipe) return;
  if (vF == null) {
    // Nothing stored: re-render so the input picks the yeast's pre-fill back up.
    if (currentRecipe.fermentTemp == null) { renderRecipe(currentReport); return; }
    const { fermentTemp, ...rest } = currentRecipe;
    await recalcFromDetail(rest, 'Fermentation temperature cleared.');
    return;
  }
  const n = Number(vF);
  if (!Number.isFinite(n) || n < 32 || n > 100) { renderRecipe(currentReport); return; }
  if (n === currentRecipe.fermentTemp) return;
  await recalcFromDetail({ ...currentRecipe, fermentTemp: n }, `Fermentation temp set to ${fmtTemp(n)} (noted on the brew sheet, not modeled).`);
}

/** Boil length changes hop utilisation and boil-off, so it moves IBU and gravity. */
async function applyBoilTime(raw) {
  const n = Number(raw);
  if (!currentRecipe || !Number.isFinite(n) || n < 0 || n > 240) { renderRecipe(currentReport); return; }
  if (n === (currentRecipe.boilTime ?? 60)) return;
  await recalcFromDetail({ ...currentRecipe, boilTime: n }, `Boil set to ${n} min.`);
}

// applyBatchSize and applyEquipment left with the Process restructure (Kyle, 8/10):
// batch size and the rig are header decisions made before the recipe exists. The header
// equipment selector recalcs the live recipe (recalcForEquipmentChange), and a header
// batch change goes through chat ("Brew it" on the stale note), where runRecipeTool
// applies the salt-scaling BATCH RESCALE RULE server-side.

// ── water chemistry (phase 2, 2026-08-08) ──────────────────────────────────
// The server's water chemistry block (report.water) renders here, and the brewer edits the
// stored water data — salts/acids, source water, dilution, target profile — in place.
// Every edit patches the recipe and rides recalcFromDetail like any other panel edit;
// the engine's benchmarks never move with it (pinned server-side).

/** Mirror of the server's salt catalog (salts.ts) — the closed, hand-authored set. */
const WATER_AGENTS = [
  { name: 'Gypsum', unit: 'g' },
  { name: 'Calcium Chloride', unit: 'g' },
  { name: 'Epsom Salt', unit: 'g' },
  { name: 'Canning Salt', unit: 'g' },
  { name: 'Baking Soda', unit: 'g' },
  { name: 'Chalk', unit: 'g' },
  { name: 'Magnesium Chloride', unit: 'g' },
  { name: 'Slaked Lime', unit: 'g' },
  { name: 'Campden Tablet', unit: 'g' },
  { name: 'Whirlfloc', unit: 'g', stage: 'boil' },
  { name: 'Lactic Acid', unit: 'ml', conc: 88 },
  { name: 'Phosphoric Acid', unit: 'ml', conc: 10 },
  { name: 'Hydrochloric Acid', unit: 'ml', conc: 37 },
  { name: 'Sulfuric Acid', unit: 'ml', conc: 10 },
];

/** Mirror of the server's TARGET_PROFILES (water.ts) — style-CATEGORY targets.
 *  Kyle 8/8: the old color × body list ("Yellow Dry", "Black Full") reads as
 *  calculator jargon — vague and unhelpful. These are broader style families in the
 *  spirit of Brewfather's list but one level up, distilled from common brewing
 *  guidance (chloride:sulfate balance per family). Cities join the target picker
 *  too, from WATER_CITIES. */
const WATER_TARGETS = [
  { name: 'Light Lager', ca: 45, mg: 5, na: 5, so4: 50, cl: 55, hco3: 10 },
  { name: 'Amber Lager', ca: 55, mg: 5, na: 10, so4: 70, cl: 60, hco3: 40 },
  { name: 'Dark Lager', ca: 55, mg: 5, na: 20, so4: 50, cl: 55, hco3: 90 },
  { name: 'Blonde & Cream Ale', ca: 50, mg: 5, na: 10, so4: 60, cl: 65, hco3: 25 },
  { name: 'Wheat Beer', ca: 45, mg: 5, na: 10, so4: 50, cl: 60, hco3: 30 },
  { name: 'Pale Ale', ca: 110, mg: 5, na: 15, so4: 230, cl: 60, hco3: 45 },
  { name: 'West Coast IPA', ca: 110, mg: 5, na: 10, so4: 275, cl: 50, hco3: 30 },
  { name: 'Hazy IPA', ca: 100, mg: 5, na: 15, so4: 75, cl: 175, hco3: 40 },
  { name: 'Amber & Red Ale', ca: 75, mg: 5, na: 15, so4: 110, cl: 80, hco3: 60 },
  { name: 'Brown Ale', ca: 70, mg: 5, na: 20, so4: 80, cl: 80, hco3: 110 },
  { name: 'Porter', ca: 75, mg: 5, na: 25, so4: 70, cl: 80, hco3: 140 },
  { name: 'Stout', ca: 80, mg: 5, na: 30, so4: 65, cl: 80, hco3: 160 },
  { name: 'Belgian Ale', ca: 60, mg: 5, na: 15, so4: 85, cl: 75, hco3: 60 },
  { name: 'Saison & Farmhouse', ca: 70, mg: 5, na: 10, so4: 130, cl: 60, hco3: 30 },
  { name: 'Sour Beer', ca: 45, mg: 5, na: 15, so4: 50, cl: 70, hco3: 20 },
  { name: 'English & Scottish Ale', ca: 70, mg: 5, na: 20, so4: 90, cl: 85, hco3: 80 },
];

const WATER_IONS = ['ca', 'mg', 'na', 'so4', 'cl', 'hco3'];
const WATER_ION_LABELS = { ca: 'Ca', mg: 'Mg', na: 'Na', so4: 'SO4', cl: 'Cl', hco3: 'HCO3' };

/** Famous brewing-city source waters (ppm), the approximate profiles brewing
 *  references publish — city water varies by season and treatment plant, so these
 *  are starting points to tweak, not gospel. Source pH is deliberately NOT a field:
 *  mash pH is driven by the grist plus the water's alkalinity (HCO3), and water has
 *  so little buffering that its own pH barely moves anything. */
const WATER_CITIES = [
  { name: 'Asheville, NC', ca: 8, mg: 2, na: 6, so4: 6, cl: 8, hco3: 30 },
  { name: 'Bamberg, Germany', ca: 75, mg: 20, na: 8, so4: 15, cl: 25, hco3: 280 },
  { name: 'Boston, MA', ca: 6, mg: 1, na: 30, so4: 8, cl: 35, hco3: 35 },
  { name: 'Brussels, Belgium', ca: 100, mg: 10, na: 30, so4: 70, cl: 35, hco3: 260 },
  { name: 'Burton-on-Trent, England', ca: 275, mg: 40, na: 25, so4: 610, cl: 35, hco3: 270 },
  { name: 'Chicago, IL', ca: 34, mg: 12, na: 8, so4: 27, cl: 13, hco3: 130 },
  { name: 'Cologne, Germany', ca: 85, mg: 8, na: 45, so4: 70, cl: 75, hco3: 230 },
  { name: 'Denver, CO', ca: 38, mg: 8, na: 17, so4: 45, cl: 25, hco3: 75 },
  { name: 'Dortmund, Germany', ca: 225, mg: 40, na: 60, so4: 120, cl: 60, hco3: 220 },
  { name: 'Dublin, Ireland', ca: 118, mg: 4, na: 12, so4: 55, cl: 19, hco3: 319 },
  { name: 'Düsseldorf, Germany', ca: 90, mg: 12, na: 40, so4: 60, cl: 55, hco3: 220 },
  { name: 'Edinburgh, Scotland', ca: 125, mg: 25, na: 55, so4: 140, cl: 65, hco3: 225 },
  { name: 'London, England', ca: 52, mg: 32, na: 86, so4: 32, cl: 34, hco3: 104 },
  { name: 'Munich, Germany', ca: 76, mg: 18, na: 2, so4: 10, cl: 2, hco3: 152 },
  { name: 'Pilsen, Czechia', ca: 7, mg: 3, na: 2, so4: 5, cl: 5, hco3: 25 },
  { name: 'Portland, OR', ca: 2, mg: 1, na: 2, so4: 1, cl: 2, hco3: 8 },
  { name: 'Prague, Czechia', ca: 40, mg: 4, na: 10, so4: 25, cl: 18, hco3: 95 },
  { name: 'San Diego, CA', ca: 95, mg: 24, na: 100, so4: 120, cl: 98, hco3: 210 },
  { name: 'Seattle, WA', ca: 18, mg: 1, na: 4, so4: 2, cl: 4, hco3: 40 },
  { name: 'Vienna, Austria', ca: 200, mg: 60, na: 8, so4: 125, cl: 12, hco3: 120 },
];

/** The brewer's own saved source waters ("My water"). Signed in, they live on the
 *  account (/api/water-profiles) and follow the brewer to every device; signed out
 *  they stay in this browser's localStorage (bb:waters), and the first signed-in
 *  visit folds the browser list into the account. A NAMED apply in the water editor
 *  is the save; the same name saves in place. Entries are flat {name, ca..hco3,
 *  dilutionPct?, id?} — `id` only for account rows. */
let acctWaters = [];
let acctWatersLoaded = false;
const acctOn = () => acct.enabled && acct.loggedIn;
function loadLocalWaters() {
  try { return JSON.parse(localStorage.getItem('bb:waters') || '[]'); } catch (e) { return []; }
}
function writeLocalWaters(list) {
  try { localStorage.setItem('bb:waters', JSON.stringify(list)); } catch (e) { /* private mode */ }
}
function loadMyWaters() {
  return acctOn() ? acctWaters : loadLocalWaters();
}
const flatWater = (p) => {
  const w = { id: p.id, name: p.name, isDefault: !!p.isDefault };
  for (const k of WATER_IONS) w[k] = Number((p.ions || {})[k]) || 0;
  if (p.dilutionPct) w.dilutionPct = p.dilutionPct;
  return w;
};
const sameName = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
/** Pull the account list; on the first signed-in load also carry the browser's saved
 *  waters up to the account (names it doesn't have yet) and seed the browser default
 *  from the account default when this browser has none. Re-renders the editor if open. */
async function loadWaterProfiles() {
  if (!acctOn()) { acctWaters = []; acctWatersLoaded = false; return; }
  try {
    const { profiles } = await aFetch('/api/water-profiles').then((r) => r.json());
    acctWaters = (profiles || []).map(flatWater);
    acctWatersLoaded = true;
  } catch (e) { return; }
  // Fold the browser-local list in, once.
  const local = loadLocalWaters();
  if (local.length) {
    let moved = 0;
    for (const w of local) {
      if (!w?.name || acctWaters.some((a) => sameName(a.name, w.name))) { moved++; continue; }
      try {
        const r = await aFetch('/api/water-profiles', { method: 'POST', body: JSON.stringify({ ...w, dilutionPct: w.dilutionPct || null }) });
        if (r.ok) { const { profile } = await r.json(); acctWaters.push(flatWater(profile)); moved++; }
      } catch (e) { /* leave it local, retry next load */ }
    }
    if (moved === local.length) writeLocalWaters([]);
    acctWaters.sort((a, b) => (b.isDefault - a.isDefault) || a.name.localeCompare(b.name));
  }
  const def = acctWaters.find((w) => w.isDefault);
  if (def && !defaultWater()) {
    const source = { name: def.name };
    for (const k of WATER_IONS) source[k] = def[k];
    saveDefaultWater({ source, dilutionPct: def.dilutionPct }, { local: true });
    if (typeof applyDefaultWater === 'function') applyDefaultWater();
  }
  if ($('waterModal') && !$('waterModal').hidden && typeof renderWaterEditor === 'function') renderWaterEditor();
}
/** Save (upsert by name). Account when signed in, browser otherwise. Resolves when the
 *  list reflects the save so the editor can re-render with it. */
async function saveMyWater(source, dilutionPct, makeDefault = false) {
  if (!source || !source.name) return;
  const clean = { name: source.name };
  for (const k of WATER_IONS) clean[k] = Number(source[k]) || 0;
  if (dilutionPct > 0) clean.dilutionPct = Math.min(Number(dilutionPct), 100);
  if (acctOn()) {
    try {
      const r = await aFetch('/api/water-profiles', { method: 'POST', body: JSON.stringify({ ...clean, dilutionPct: clean.dilutionPct || null, isDefault: makeDefault }) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); toast(e.error || 'Could not save that water profile.', 'warn'); return; }
      const { profile } = await r.json();
      const flat = flatWater(profile);
      acctWaters = acctWaters.filter((w) => w.id !== flat.id && !sameName(w.name, flat.name));
      if (flat.isDefault) acctWaters.forEach((w) => { w.isDefault = false; });
      acctWaters.push(flat);
      acctWaters.sort((a, b) => (b.isDefault - a.isDefault) || a.name.localeCompare(b.name));
    } catch (e) { toast('Could not save that water profile.', 'warn'); }
    return;
  }
  const list = loadLocalWaters().filter((w) => !sameName(w.name, clean.name));
  list.push(clean);
  list.sort((a, b) => a.name.localeCompare(b.name));
  writeLocalWaters(list);
}
/** Remove a saved water by name (account or browser). */
async function deleteMyWater(name) {
  if (acctOn()) {
    const row = acctWaters.find((w) => sameName(w.name, name));
    if (!row) return false;
    try {
      const r = await aFetch('/api/water-profiles/' + encodeURIComponent(row.id), { method: 'DELETE' });
      if (!r.ok) { toast('Could not remove that water profile.', 'warn'); return false; }
    } catch (e) { toast('Could not remove that water profile.', 'warn'); return false; }
    acctWaters = acctWaters.filter((w) => w.id !== row.id);
    return true;
  }
  const list = loadLocalWaters();
  const next = list.filter((w) => !sameName(w.name, name));
  if (next.length === list.length) return false;
  writeLocalWaters(next);
  return true;
}

/** The brewer's default source water (Kyle, 8/10): applying or picking a profile in the
 *  water editor saves it here, and every recipe that arrives WITHOUT a source picks it
 *  up automatically, the same contract as the rig selection (bb:lastEquip): your tap
 *  water doesn't change per recipe. Clearing the source in the editor clears the
 *  default too, so "no source water" sticks just as durably as a profile does. */
function saveDefaultWater(wp, opts = {}) {
  try {
    if (wp && wp.source) {
      const d = { source: wp.source };
      if (wp.dilutionPct) d.dilutionPct = wp.dilutionPct;
      localStorage.setItem('bb:defaultWater', JSON.stringify(d));
    } else {
      localStorage.removeItem('bb:defaultWater');
    }
  } catch (e) { /* private mode */ }
  // Signed in, the default is an account fact too (is_default on the saved profile), so
  // a fresh browser picks up the same tap water. Only a SAVED profile can be the account
  // default; an unnamed one-off apply stays browser-local. `opts.local` = seeding from
  // the account, don't echo it back.
  if (opts.local || !acctOn()) return;
  if (wp && wp.source) {
    const row = acctWaters.find((w) => sameName(w.name, wp.source.name));
    if (!row || row.isDefault) return;
    aFetch('/api/water-profiles/' + encodeURIComponent(row.id) + '/default', { method: 'POST' })
      .then((r) => { if (r.ok) { acctWaters.forEach((w) => { w.isDefault = w.id === row.id; }); redrawWaterEditor(); } })
      .catch(() => {});
  } else if (acctWaters.some((w) => w.isDefault)) {
    aFetch('/api/water-profiles-default', { method: 'DELETE' })
      .then((r) => { if (r.ok) { acctWaters.forEach((w) => { w.isDefault = false; }); redrawWaterEditor(); } })
      .catch(() => {});
  }
}
/** Re-draw the water editor if it's open (the ★ default marker lives in its picker). */
function redrawWaterEditor() {
  if ($('waterModal') && !$('waterModal').hidden && typeof renderWaterEditor === 'function') renderWaterEditor();
}
function defaultWater() {
  try { return JSON.parse(localStorage.getItem('bb:defaultWater') || 'null'); } catch (e) { return null; }
}

/** Attach the saved default source water to a recipe that has none, then recalculate so
 *  the mash pH and ion tables reflect it. Called from renderRecipe: the recalc's own
 *  re-render arrives carrying the source (resolvedRecipe passes waterProfile through),
 *  so it doesn't fire twice; the busy flag guards the in-flight window regardless. */
let applyingDefaultWater = false;
function applyDefaultWater() {
  if (applyingDefaultWater || !currentRecipe) return;
  const dw = defaultWater();
  if (!dw || !dw.source) return;
  const wp = currentRecipe.waterProfile || {};
  if (wp.source) return;
  const wpNext = { ...wp, source: dw.source };
  if (dw.dilutionPct && wpNext.dilutionPct == null) wpNext.dilutionPct = dw.dilutionPct;
  applyingDefaultWater = true;
  const next = { ...currentRecipe, waterProfile: wpNext };
  const label = `${dw.source.name || 'Saved'} water applied.`;
  // Auto-dosed salts were solved for the assumed-RO base; the brewer's real water just
  // arrived, so re-match them for it (same solve + cap as the panel's target picker).
  const done = wpNext.target
    ? matchSaltsToTarget(next, label).then((ok) => { if (!ok) return recalcFromDetail(next, label); })
    : Promise.resolve(recalcFromDetail(next, label));
  done.finally(() => { applyingDefaultWater = false; });
}

// grams per level teaspoon (liquids: 4.93 ml/tsp). DISPLAY ONLY — storage stays g/ml,
// and tsp figures are inherently approximate (bulk density varies by grind and brand),
// hence the ≈ everywhere they render. Campden has no entry: it's dosed in tablets.
const SALT_G_PER_TSP = {
  'Gypsum': 4.0, 'Calcium Chloride': 3.4, 'Epsom Salt': 4.5, 'Canning Salt': 5.7,
  'Baking Soda': 4.4, 'Chalk': 1.8, 'Magnesium Chloride': 3.3, 'Slaked Lime': 2.2,
};
const ML_PER_TSP = 4.93;

/** g-or-ml per teaspoon for an addition; undefined = this agent has no tsp measure. */
function saltTspFactor(name, unit) {
  return unit === 'ml' ? ML_PER_TSP : SALT_G_PER_TSP[name];
}
function toTsp(amount, per) { return Math.round((amount / per) * 100) / 100; }
/** Display string for a stored addition, honoring the g/tsp display unit. */
function fmtSaltAmt(s) {
  if (displayUnits.salt === 'tsp') {
    const per = saltTspFactor(s.name, s.unit);
    if (per) return `≈ ${toTsp(s.amount, per)} tsp`;
  }
  return `${s.amount} ${s.unit}`;
}

/** UI state that must survive the full re-render every recalc performs. The section
 *  starts MINIMIZED on every launch and after Start over (Kyle, 8/8) — water chemistry
 *  is opt-in reading, not something every brewer wants open. Expanding lasts for the
 *  session only; deliberately NOT persisted across loads. */
const waterUi = { collapsed: true };

/** Table-cell ppm: one decimal under 100, whole numbers above — the sheet's own habit. */
function fmtIon(v) {
  if (v == null) return '–';
  const n = Math.round(v * 10) / 10;
  return Math.abs(n) >= 100 || Number.isInteger(n) ? String(Math.round(n)) : n.toFixed(1);
}
function fmtDelta(v) {
  const n = Math.round(v);
  return n === 0 ? '0' : n > 0 ? `+${n}` : String(n);
}

/**
 * The panel section reads like the Grain bill / Hop schedule sections (Kyle's spec,
 * 2026-08-08): the heading is a section-link with an "edit" chip, the body lists only
 * the additions actually in use (amount > 0) as plain ingredient rows, and ALL editing
 * lives in a water modal that mirrors the ingredient detail modal — the full salt
 * roster with amounts defaulting to 0, plus source water and target profile.
 */
// ── water limit tiers (engine-computed, waterLimits.ts) ─────────────────────
// The ENGINE evaluates every threshold and ships tiers on report.water.limits; the UI
// only colors and explains. Manual entries are never blocked or clamped — tiers are
// warnings. Which agents feed which ion, for highlighting the contributing rows:
const WATER_ION_SOURCES = {
  ca: ['Gypsum', 'Calcium Chloride', 'Chalk', 'Slaked Lime'],
  mg: ['Epsom Salt', 'Magnesium Chloride'],
  na: ['Canning Salt', 'Baking Soda'],
  cl: ['Calcium Chloride', 'Canning Salt', 'Magnesium Chloride', 'Hydrochloric Acid'],
  so4: ['Gypsum', 'Epsom Salt', 'Sulfuric Acid'],
  hco3: ['Baking Soda', 'Chalk', 'Slaked Lime'],
  lactate: ['Lactic Acid'],
  h3po4: ['Phosphoric Acid'],
};
const WATER_LIMIT_LABELS = {
  ca: 'Calcium', mg: 'Magnesium', na: 'Sodium', cl: 'Chloride', so4: 'Sulfate',
  hco3: 'Bicarbonate', lactate: 'Lactate', h3po4: 'Phosphoric acid',
};
const TIER_RANK = { ok: 0, orange: 1, red: 2 };

/** Worst tier per ion across the mash and overall profiles, with the ppm to quote and
 *  the helper copy. Illness language is allowed for Mg and SO4 red ONLY (their red
 *  levels approach drinking-water advisory territory); everything else is flavor. */
function waterLimitFindings(w) {
  if (!w || !w.limits) return []; // reports stored before limits existed
  const out = [];
  for (const k of Object.keys(WATER_ION_SOURCES)) {
    const mashTier = (w.limits.mash && w.limits.mash[k]) || 'ok';
    const overallTier = (w.limits.overall && w.limits.overall[k]) || 'ok';
    const tier = TIER_RANK[overallTier] > TIER_RANK[mashTier] ? overallTier : mashTier;
    if (tier === 'ok') continue;
    const fromOverall = TIER_RANK[overallTier] > TIER_RANK[mashTier];
    const isAcid = k === 'lactate' || k === 'h3po4';
    const ppm = isAcid
      ? ((fromOverall ? w.overallAcidPpm : w.acidPpm) || w.acidPpm || {})[k] || 0
      : ((fromOverall ? w.overall : w.mash) || w.mash)[k];
    const label = WATER_LIMIT_LABELS[k];
    // Kyle's exact copy (8/10): health-risk language for Mg and SO4 red ONLY; every
    // other ion is a flavor problem at any tier.
    let text;
    if (tier === 'orange') {
      text = `${label} at ${Math.round(ppm)} ppm: outside normal brewing ranges, flavor may suffer.`;
    } else if (k === 'mg' || k === 'so4') {
      text = `${label} at ${Math.round(ppm)} ppm: very high levels. Your beer may suck. Potential digestive risks.`;
    } else {
      text = `${label} at ${Math.round(ppm)} ppm: very high levels. Your beer may suck.`;
    }
    out.push({ ion: k, tier, text });
  }
  return out;
}

/** td class for a profile cell, from an engine tier record ('' when clean/unknown). */
function ionTierClass(tiers, k) {
  const t = tiers && tiers[k];
  return t === 'red' ? ' ion--red' : t === 'orange' ? ' ion--orange' : '';
}

function waterFindingsHtml(w) {
  const finds = waterLimitFindings(w);
  if (!finds.length) return '';
  return `<ul class="water-notes">${finds.map((f) =>
    `<li class="water-limit-note water-limit-note--${f.tier}">${escapeHtml(f.text)}</li>`).join('')}</ul>`;
}

function renderWaterSection(r, report) {
  // Water sits with the other INGREDIENT sections — right after Yeast (Kyle, 8/8).
  // Salts are things you buy and weigh out, so they belong beside the grain, hops and
  // yeast rather than down among the flavorings. The hidden Process section (emptied
  // 8/20 when its knobs moved into Boil & Hops / Yeast & Fermentation) still serves
  // as the insertion anchor.
  let sec = $('waterSection');
  const processSection = $('process')?.closest('.recipe__section');
  if (!sec && processSection) {
    sec = document.createElement('div');
    sec.className = 'recipe__section';
    sec.id = 'waterSection';
    processSection.parentNode.insertBefore(sec, processSection);
  }
  if (!sec) return;
  const w = report.water || null;
  const salts = (r.salts || []).filter((s) => (s.amount ?? 0) > 0);

  // ── in-use additions, presented like any other ingredient list ──
  const saltRows = salts.map((s) => {
    const sub = [s.stage || 'mash', s.concentration ? `${s.concentration}%` : ''].filter(Boolean).join(' · ');
    return `<tr><td>${escapeHtml(s.name)}${sub ? `<div class="sub">${sub}</div>` : ''}</td><td class="amt">${fmtSaltAmt(s)}</td></tr>`;
  }).join('');

  // ── computed chemistry ──
  // Stat tiles instead of a run-on "a · b · c" line — the prose version wrapped into an
  // unreadable tangle at phone widths (Kyle's 8/8 screenshot). Same tile language as the
  // OG/FG metrics row, sized down. The pH value colors softly by the advisory band.
  let chem = '';
  if (w) {
    const st = w.mashStats;
    // The pH tile judges by the TARGET's own range when the engine ships one
    // (report.water.phTarget, per-profile and per-city ranges), else the generic band.
    const phBand = w.phTarget || { low: 5.2, high: 5.6 };
    const phClass = w.mashPh == null ? ''
      : w.mashPh >= phBand.low - 0.021 && w.mashPh <= phBand.high + 0.021 ? ' wstat__v--good' : ' wstat__v--warn';
    const tile = (v, label, cls = '') => `<div class="wstat"><b class="wstat__v${cls}">${v}</b><span>${label}</span></div>`;
    // The panel keeps the figures a brewer acts on mid-build: mash pH always, and the
    // SO4:Cl ratio only where the ratio is the point (every IPA style). The reference
    // stats (alkalinity, residual alkalinity, hardness) moved into the water chem
    // window (Kyle, 8/10).
    const isIpa = /ipa/i.test((report.style && report.style.style) || styleSelect.value || '');
    // The ratio normalizes with "1" on the SMALLER side (Kyle, 8/10): a chloride-forward
    // hazy reads "1 : 2.3" (SO4 is the 1), a sulfate-forward west coast reads "2.8 : 1"
    // (Cl is the 1). Never a bare figure that could pass for a percentage.
    const ratioText = st.so4ClRatio == null ? null
      : st.so4ClRatio >= 1 ? `${Math.round(st.so4ClRatio * 10) / 10} : 1`
        : st.so4ClRatio > 0 ? `1 : ${Math.round((1 / st.so4ClRatio) * 10) / 10}`
          : '0 : 1';
    const tiles = (w.mashPh != null ? tile(w.mashPh.toFixed(2), 'Est. mash pH', phClass) : '')
      + (isIpa && ratioText != null ? tile(ratioText, 'SO4 : Cl') : '');
    if (tiles) {
      chem += `<div class="water-stats">${tiles}</div>`;
      if (w.mashPh != null) {
        chem += `<div class="sub muted water-statsub">${w.phTarget
          ? `Target mash pH ${w.phTarget.low.toFixed(2)}–${w.phTarget.high.toFixed(2)}`
          : 'Most beers mash at pH 5.3–5.6'}</div>`;
      }
    }
    // Ions read DOWN the left; columns in Kyle's order (8/10): Target, Source,
    // Additions, Actual, Δ — the arithmetic reads left to right: aim at the target,
    // start from the source, add the salts, land on the actual, and Δ is what's left
    // vs target. Source shows even when assumed RO (zeros) so the addition math stays
    // legible (the bullet note says it's RO); the diluted base stands in for the raw
    // source when RO dilution is set. Kettle appends only when boil/sparge doses make
    // the overall water differ from the mash. Tier coloring rides the treated columns.
    const base = w.diluted || w.source;
    const cols = [];
    if (w.target) cols.push({ label: 'Target', ions: w.target });
    cols.push({ label: 'Source', ions: base });
    cols.push({ label: 'Additions', adds: true });
    cols.push({ label: 'Actual', ions: w.mash, tiers: w.limits && w.limits.mash, main: true });
    if (w.target) cols.push({ label: 'Δ', delta: true });
    if (w.overall) cols.push({ label: 'Kettle', ions: w.overall, tiers: w.limits && w.limits.overall });
    const cell = (c, k) => {
      if (c.delta) return `<td class="num water-tbl__diffc">${fmtDelta(w.mash[k] - w.target[k])}</td>`;
      if (c.adds) return `<td class="num water-tbl__diffc">${fmtDelta(w.mash[k] - base[k])}</td>`;
      return `<td class="num${c.tiers ? ionTierClass(c.tiers, k) : ''}${c.main ? ' water-tbl__mainc' : ''}">${fmtIon(c.ions[k])}</td>`;
    };
    chem += '<div class="water-scroll"><table class="ing water-tbl water-tbl--ions">'
      + `<tr><th></th>${cols.map((c) => `<th>${c.label}</th>`).join('')}</tr>`
      + WATER_IONS.map((k) => `<tr><td>${WATER_ION_LABELS[k]}</td>${cols.map((c) => cell(c, k)).join('')}</tr>`).join('')
      + '</table></div>';
    const notes = [...(w.notes || [])];
    if (w.unmodeled && w.unmodeled.length) {
      notes.push(`${w.unmodeled.join(', ')} do${w.unmodeled.length === 1 ? 'es' : ''}n't change these numbers.`);
    }
    if (notes.length) chem += `<ul class="water-notes">${notes.map((n) => `<li>${escapeHtml(n)}</li>`).join('')}</ul>`;
    chem += waterFindingsHtml(w);
  } else {
    chem = '<div class="sub muted">Water chemistry is in development — check back for a refined version. No water additions yet. Edit to add salts and acids, set your source water, and see the estimated mash pH.</div>';
  }

  // The heading is the edit button, exactly like the grain/hop sections; the chevron on
  // its left is the minimize toggle and is always visible (affordances people return
  // for stay visible). Collapsed, a one-line summary keeps the essentials on screen.
  // Heading row mirrors the grain/hops sections: [minimize] [section-link heading] [g/tsp].
  // The heading itself carries ONLY "Water" + the edit chip (Kyle, 8/8) — no summary text,
  // collapsed or not, so the row stays narrow enough for the unit toggle at any width.
  //
  // A drawn STROKE chevron (Kyle, 8/8: a solid triangle "looks bad"), not a "▾" character:
  // a text glyph fills only a fraction of its em box, so font-size could never make one
  // read right — don't go back to a character. The viewBox is cropped to the stroke, so
  // the CSS width is the chevron's real width.
  if (typeof ensureUnitsCss === 'function') ensureUnitsCss();
  const chevSvg = '<svg class="water-chev" viewBox="0 0 12 7" aria-hidden="true" focusable="false">'
    + '<polyline points="1,1.3 6,5.7 11,1.3" fill="none" stroke="currentColor" stroke-width="1.5"'
    + ' stroke-linecap="round" stroke-linejoin="round"/></svg>';
  sec.innerHTML = `<div class="bb-sec-head">`
    + `<button type="button" id="waterToggle" class="water-toggle${waterUi.collapsed ? ' is-collapsed' : ''}" aria-expanded="${!waterUi.collapsed}" title="${waterUi.collapsed ? 'Show the water section' : 'Minimize the water section'}">${chevSvg}</button>`
    + `<h3 class="section-link" id="waterHead" title="Add salts and acids, set your source water">`
    + `<span>Water Chem <span class="water-beta">Beta</span></span><span class="section-link__hint">edit</span></h3>`
    + `<span id="waterUnitSeg" class="bb-units__btns bb-units__btns--head"><button type="button" data-u="g">g</button><button type="button" data-u="tsp">tsp</button></span></div>`
    + `<div id="waterBody"${waterUi.collapsed ? ' hidden' : ''}>`
    // The expanded section leads with the target being built toward (Kyle, 8/10).
    + (() => {
      const tn = (w && w.targetName) || (r.waterProfile && r.waterProfile.target && r.waterProfile.target.name);
      return tn ? `<div class="water-target-line">Target Profile: <b>${escapeHtml(tn)}</b></div>` : '';
    })()
    + (saltRows ? `<table class="ing" id="waterSalts">${saltRows}</table>` : '')
    + chem + '</div>';

  // Heading opens the editor; the standalone chevron only collapses.
  $('waterHead').addEventListener('click', openWaterEditor);
  $('waterToggle').addEventListener('click', () => {
    waterUi.collapsed = !waterUi.collapsed;
    renderWaterSection(currentRecipe, currentReport); // pure DOM, no recalc
  });
  $('waterUnitSeg').querySelectorAll('button').forEach((b) => {
    b.classList.toggle('is-on', displayUnits.salt === b.dataset.u);
    b.addEventListener('click', () => setDisplayUnit('salt', b.dataset.u));
  });

  // A recalc re-renders the panel; if the water editor is open, refresh it in place —
  // the same contract renderDetailRows has with the ingredient modal.
  if ($('waterModal') && !$('waterModal').hidden) renderWaterEditor();
}

// ── the water editor modal (mirrors the ingredient detail modal) ────────────

function injectWaterModal() {
  if ($('waterModal')) return;
  const el = document.createElement('div');
  el.className = 'modal';
  el.id = 'waterModal';
  el.hidden = true;
  el.innerHTML = `
    <div class="modal__scrim" id="waterModalScrim"></div>
    <div class="modal__panel">
      <div class="modal__head">
        <h3>Water</h3>
        <button id="waterModalClose" type="button" class="icon-btn" title="Close">×</button>
      </div>
      <div class="modal__body"><div id="waterEditorBody"></div></div>
    </div>`;
  (document.getElementById('beer-builder-root') || document.body).appendChild(el);
  $('waterModalScrim').addEventListener('click', closeWaterModal);
  $('waterModalClose').addEventListener('click', closeWaterModal);
}

function closeWaterModal() { const m = $('waterModal'); if (m) m.hidden = true; }

function openWaterEditor() {
  if (!currentRecipe) return;
  injectWaterModal();
  $('waterModal').hidden = false;
  renderWaterEditor();
  // The modal is reused across opens and the browser can hand back the scroll position
  // from last time (the salt roster at the bottom is where most editing happens, so
  // that's where it opened). Every open starts at the top of the column.
  const body = $('waterModal').querySelector('.modal__body');
  if (body) body.scrollTop = 0;
}

/**
 * Auto-match the salts to the target (Kyle 8/8: picking a target should DO something).
 * The engine solves it — POST /api/water/suggest returns mash salt amounts — and the
 * panel then recalculates through the normal pipeline, so every ppm on screen still
 * comes from the engine rather than from the suggestion. Acids and any addition the
 * solver doesn't handle (campden, boil/sparge-stage rows) are left exactly as they are.
 */
// Must stay identical to SUGGEST_SALT_NAMES in src/calc/water.ts. Baking Soda left
// the solver 8/14: alkalinity is now dosed only by the engine's one-sided pH step
// (acid above the target band, soda below, never both), so soda rows — manual or
// engine-dosed — are "kept" here just like acids.
const SUGGEST_NAMES = ['Gypsum', 'Calcium Chloride', 'Epsom Salt', 'Canning Salt'];
async function matchSaltsToTarget(recipe, note) {
  if (!recipe?.waterProfile?.target) return false;
  try {
    // API (the builder backend), not aFetch — that one is the accounts App Proxy base.
    const res = await fetch(API + '/api/water/suggest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipe, equipmentProfile: equipmentPayload() }),
    });
    if (!res.ok) return false;
    const { salts: suggested, limitNotes } = await res.json();
    if (!Array.isArray(suggested)) return false;
    // keep everything the solver doesn't own; replace the rest with its numbers
    // autoPh rows are the engine's own pH dose: always re-solved, never inherited
    const kept = (recipe.salts || []).filter((s) => !(SUGGEST_NAMES.includes(s.name) && (s.stage || 'mash') === 'mash') && !s.autoPh);
    const next = { ...recipe, salts: [...kept, ...suggested] };
    if (!next.salts.length) delete next.salts;
    recalcFromDetail(next, note);
    // The engine capped or skipped part of the suggestion (waterLimits.ts aiMax): say
    // so after the "matched" toast rather than failing silently.
    if (Array.isArray(limitNotes) && limitNotes.length) {
      setTimeout(() => toast(limitNotes.join(' '), 'warn'), 3400);
    }
    return true;
  } catch (e) {
    return false; // offline or endpoint missing: the target still applies, salts stay put
  }
}

/**
 * The editor lists EVERY water agent, amounts defaulting to 0 unless the recipe already
 * carries one (imported, AI-carried, or set here earlier). Only amounts > 0 are stored —
 * zeroing a row removes the addition, so recipes, exports, the brew sheet and the cart
 * never carry silent 0 g lines. Stored additions keep their own rows (an import can
 * legally hold the same salt at two stages), then the rest of the roster follows.
 */
function renderWaterEditor() {
  const host = $('waterEditorBody');
  if (!host || !currentRecipe) return;
  // A recalc landing mid-typing re-renders this editor under the brewer. The source-water
  // fields only commit on Apply, so a rebuild would silently wipe half-entered numbers.
  // Carry them (and focus) across, but ONLY while a source field actually has focus:
  // Apply and Clear are button clicks, and those must render from the stored profile.
  const active = document.activeElement;
  const typing = active && host.contains(active) && /^wsrc_/.test(active.id || '');
  const keep = {};
  if (typing) for (const el of host.querySelectorAll('[id^="wsrc_"]')) keep[el.id] = el.value;
  const stored = currentRecipe.salts || [];
  const wp = currentRecipe.waterProfile || {};
  const stageOpts = (cur) => ['mash', 'sparge', 'boil']
    .map((s) => `<option value="${s}"${(cur || 'mash') === s ? ' selected' : ''}>${s}</option>`).join('');

  const rows = stored.map((s, i) => ({
    si: i, name: s.name, unit: s.unit, amount: s.amount, stage: s.stage || 'mash',
    conc: s.concentration ?? WATER_AGENTS.find((a) => a.name === s.name)?.conc,
    isAcid: /acid/i.test(s.name),
  }));
  for (const a of WATER_AGENTS) {
    if (!stored.some((s) => s.name === a.name)) {
      rows.push({ si: null, name: a.name, unit: a.unit, amount: 0, stage: a.stage || 'mash', conc: a.conc, isAcid: /acid/i.test(a.name) });
    }
  }

  // Rows that FEED an over-limit ion get the matching highlight (waterLimits.ts tiers,
  // engine-computed): high sulfate lights up gypsum, Epsom and sulfuric acid when they
  // carry an amount. Highlighting only — nothing is blocked or clamped here.
  const trippedIons = {};
  for (const f of waterLimitFindings(currentReport && currentReport.water)) trippedIons[f.ion] = f.tier;
  const rowTierClass = (name, amount) => {
    if (!(amount > 0)) return '';
    let worst = '';
    for (const ion of Object.keys(trippedIons)) {
      if (!(WATER_ION_SOURCES[ion] || []).includes(name)) continue;
      if (trippedIons[ion] === 'red') worst = 'red';
      else if (worst !== 'red') worst = 'orange';
    }
    return worst === 'red' ? ' water-row--danger' : worst === 'orange' ? ' water-row--warn' : '';
  };

  // In tsp mode the amount inputs EDIT in teaspoons and convert to grams on commit
  // (same view-only contract as the °C temperature fields). Agents with no teaspoon
  // measure (campden tablets) keep their gram input either way.
  // Four fixed columns — name | acid strength | amount | stage — so every input lands
  // in the same place on every row. The old inline "% after the name" jagged the name
  // column and wrapped onto its own line at phone widths (Kyle, 8/8).
  const rowHtml = rows.map((row, idx) => {
    const per = saltTspFactor(row.name, row.unit);
    const tspMode = displayUnits.salt === 'tsp' && per;
    return `<tr class="${row.amount > 0 ? 'water-row--on' : 'water-row--off'}${rowTierClass(row.name, row.amount)}">`
    + `<td class="wname">${escapeHtml(row.name)}</td>`
    + `<td class="wstr">${row.isAcid ? `<input class="inline-num water-conc" data-wr="${idx}" type="number" step="1" min="1" max="100" inputmode="decimal" value="${row.conc ?? ''}" title="Acid strength %">%` : ''}</td>`
    + `<td class="amt"><input class="inline-num water-amt" data-wr="${idx}" data-tsp="${tspMode ? per : ''}" type="number" step="${tspMode ? '0.05' : '0.1'}" min="0" inputmode="decimal" value="${tspMode ? toTsp(row.amount, per) : row.amount}"><span class="wunit">${tspMode ? '≈ tsp' : row.unit}</span></td>`
    + `<td class="wstage"><select class="inline-select water-stage" data-wr="${idx}" title="Which water this goes into">${stageOpts(row.stage)}</select></td></tr>`;
  }).join('');

  const targetName = wp.target && wp.target.name;
  // Presets live in two groups: style categories and the same beer cities the source
  // Profile picker offers (names don't collide, so plain-name option values work).
  const targetIsPreset = WATER_TARGETS.some((t) => t.name === targetName)
    || WATER_CITIES.some((t) => t.name === targetName);
  const targetOpts = ['<option value="">No target</option>']
    .concat(wp.target && !targetIsPreset ? [`<option value="__keep" selected>${escapeHtml(targetName || 'Imported target')}</option>`] : [])
    .concat([`<optgroup label="Styles">${WATER_TARGETS.map((t) => `<option value="${t.name}"${t.name === targetName ? ' selected' : ''}>${t.name}</option>`).join('')}</optgroup>`])
    .concat([`<optgroup label="Beer cities">${WATER_CITIES.map((t) => `<option value="${escapeHtml(t.name)}"${t.name === targetName ? ' selected' : ''}>${escapeHtml(t.name)}</option>`).join('')}</optgroup>`])
    .join('');
  // Target shown + compared (Kyle 8/8): once picked, its ppm's land in editable inputs
  // (tweak one and it becomes a custom target) with the RESULT — mash water after
  // dilution and every addition — right underneath.
  const wMash = currentReport && currentReport.water && currentReport.water.mash;
  // Target block shows the TARGET only (Kyle 8/8); what the water actually achieves
  // renders under Salts & acids, where the additions that produced it are.
  const goalTable = wp.target ? `
    <div class="water-scroll"><table class="ing water-tbl water-goal">
      <tr><th></th>${WATER_IONS.map((k) => `<th>${WATER_ION_LABELS[k]}</th>`).join('')}</tr>
      <tr><td>Target</td>${WATER_IONS.map((k) => `<td class="num"><input class="inline-num wtgt" id="wtgt_${k}" type="number" step="1" min="0" inputmode="decimal" value="${Math.round(wp.target[k] ?? 0)}"></td>`).join('')}</tr>
    </table></div>` : '';
  const wRep = (currentReport && currentReport.water) || null;
  const wLimMash = (wRep && wRep.limits && wRep.limits.mash) || null;
  const resultTable = wMash ? `
    <div class="water-modal-sub">Result${wp.target ? ' vs target' : ''}</div>
    <div class="water-scroll"><table class="ing water-tbl water-goal">
      <tr><th></th>${WATER_IONS.map((k) => `<th>${WATER_ION_LABELS[k]}</th>`).join('')}</tr>
      <tr class="water-goal__result"><td>Result</td>${WATER_IONS.map((k) => `<td class="num${ionTierClass(wLimMash, k)}">${Math.round(wMash[k] ?? 0)}</td>`).join('')}</tr>
      ${wp.target ? `<tr class="water-tbl__diff"><td>&Delta;</td>${WATER_IONS.map((k) => `<td class="num">${fmtDelta((wMash[k] ?? 0) - (wp.target[k] ?? 0))}</td>`).join('')}</tr>` : ''}
    </table></div>` : '';
  // The reference stats moved here from the panel (Kyle, 8/10): they describe the mash
  // water as computed, so they sit under the result.
  const statsHtml = wRep ? (() => {
    const stt = wRep.mashStats;
    const t = (v, label) => `<div class="wstat"><b class="wstat__v">${v}</b><span>${label}</span></div>`;
    return `<div class="water-modal-sub">Water stats</div><div class="water-stats">`
      + t(Math.round(stt.alkalinity), 'Alkalinity')
      + t(Math.round(stt.residualAlkalinity), 'Residual alk.')
      + t(Math.round(stt.hardness), 'Hardness')
      + `</div><div class="sub muted water-statsub">Alkalinity and hardness in ppm as CaCO3</div>`;
  })() : '';

  const src = wp.source || {};
  const srcField = (k) => `<label>${WATER_ION_LABELS[k]} (ppm)<input class="inline-num" id="wsrc_${k}" type="number" step="1" min="0" inputmode="decimal" value="${src[k] ?? ''}" placeholder="0"></label>`;

  host.innerHTML = `
    <div class="water-modal-sub">Your source water</div>
    <div class="water-src">
      <div class="water-city"><span class="water-city__lab">Profile</span>
        <select id="waterCitySel">
          <option value="">Choose a water profile…</option>
          ${(() => {
            const mine = loadMyWaters();
            const cur = src.name || '';
            const mineOpts = mine.map((w) => `<option value="mine:${escapeHtml(w.name)}"${sameName(w.name, cur) ? ' selected' : ''}>${escapeHtml(w.name)}${w.isDefault ? ' ★' : ''}</option>`).join('');
            const cityOpts = WATER_CITIES.map((w) => `<option value="city:${escapeHtml(w.name)}"${w.name === cur && !mine.some((m) => sameName(m.name, cur)) ? ' selected' : ''}>${escapeHtml(w.name)}</option>`).join('');
            return (mineOpts ? `<optgroup label="My water">${mineOpts}</optgroup>` : '')
              + `<optgroup label="Beer cities">${cityOpts}</optgroup>`;
          })()}
        </select></div>
      <div class="water-src__grid">
        ${WATER_IONS.map(srcField).join('')}
        <label>Dilute w/ RO %<input class="inline-num" id="wsrc_dilution" type="number" step="5" min="0" max="100" inputmode="decimal" value="${wp.dilutionPct ?? ''}" placeholder="0"></label>
        <label class="water-src__name">Name<input class="inline-num" id="wsrc_name" type="text" value="${escapeHtml(src.name || '')}" placeholder="My tap water"></label>
      </div>
      <button class="link-btn" id="waterSrcSave">Save to My water</button>
      <button class="link-btn" id="waterSrcApply">Apply source water</button>
      ${wp.source ? '<button class="link-btn link-btn--danger" id="waterSrcClear">Clear</button>' : ''}
      ${src.name && loadMyWaters().some((w) => sameName(w.name, src.name)) ? `<button class="link-btn link-btn--danger" id="waterSrcForget" title="Remove this profile from My water">Remove from My water</button>` : ''}
      <p class="sub muted water-src__hint">${(() => {
        // Where a named apply lands: the account when signed in, this browser otherwise.
        if (acctOn()) return '<b>Save to My water</b> keeps this water on your account, so it shows in the Profile list on every device. <b>Apply</b> just uses it on this recipe.';
        if (acct.enabled) return '<b>Save to My water</b> keeps this water on this browser (sign in to keep it on every device). <b>Apply</b> just uses it on this recipe.';
        return '<b>Save to My water</b> keeps this water on this browser. <b>Apply</b> just uses it on this recipe.';
      })()}</p>
    </div>
    <div class="water-modal-sub">Target profile</div>
    <div class="water-city"><span class="water-city__lab">Target</span>
      <select id="waterTargetSel">${targetOpts}</select></div>
    ${goalTable}
    <div class="water-modal-sub">Salts &amp; acids</div>
    ${currentReport && currentReport.water && currentReport.water.assumedSource && stored.length
      ? '<p class="ing-intro water-ro-note">Salt additions assume reverse osmosis water is being used.</p>' : ''}
    <p class="ing-intro">Set an amount and the addition joins the recipe: the ion levels and estimated mash pH recalculate as you go. Zero an amount to take it back out.${displayUnits.salt === 'tsp' ? ' Teaspoon figures are approximate (densities vary by grind and brand); switch to grams for exact dosing.' : ''}</p>
    <table class="ing water-salts">${rowHtml}</table>
    ${resultTable}${statsHtml}${waterFindingsHtml(wRep)}`;

  // Restore whatever the brewer was mid-way through typing (see `keep` above).
  if (typing) {
    for (const [id, v] of Object.entries(keep)) { const el = $(id); if (el) el.value = v; }
    $(active.id)?.focus();
  }

  // ── events ──
  const commitRow = (idx) => {
    const row = rows[idx];
    const tr = host.querySelectorAll('.water-salts tr')[idx];
    const amtInput = tr.querySelector('.water-amt');
    let amt = Number(amtInput.value);
    // tsp-mode inputs carry their per-tsp factor; storage is always grams/ml.
    const per = Number(amtInput.dataset.tsp);
    if (per > 0 && Number.isFinite(amt)) amt = amt * per;
    const n = Number.isFinite(amt) && amt > 0 ? Math.round(amt * 100) / 100 : 0;
    const stage = tr.querySelector('.water-stage').value;
    const concRaw = Number(tr.querySelector('.water-conc')?.value);
    const conc = Number.isFinite(concRaw) && concRaw > 0 && concRaw <= 100 ? concRaw : undefined;
    let salts;
    if (row.si != null) {
      salts = n > 0
        ? stored.map((s, j) => {
            if (j !== row.si) return s;
            // The brewer touched this row, so it's theirs now: drop the engine's
            // autoPh provenance and the never-fight-the-brewer gate protects it.
            const { autoPh, ...own } = s;
            return { ...own, amount: n, stage, ...(row.isAcid && conc ? { concentration: conc } : {}) };
          })
        : stored.filter((_, j) => j !== row.si);
    } else {
      if (!(n > 0)) { renderWaterEditor(); return; } // nothing to store, reset the row
      const addition = { name: row.name, amount: n, unit: row.unit, stage };
      if (row.isAcid && conc) addition.concentration = conc;
      salts = [...stored, addition];
    }
    const next = { ...currentRecipe, salts };
    if (!next.salts.length) delete next.salts;
    recalcFromDetail(next, n > 0 ? 'Water updated.' : `Removed ${row.name}.`);
  };
  host.querySelectorAll('.water-amt').forEach((el) => el.addEventListener('change', (e) => commitRow(Number(e.target.dataset.wr))));
  host.querySelectorAll('.water-stage').forEach((el) => el.addEventListener('change', (e) => {
    const row = rows[Number(e.target.dataset.wr)];
    if (row.si != null) commitRow(Number(e.target.dataset.wr));
    // roster rows: the stage is read when an amount lands, nothing stored yet
  }));
  host.querySelectorAll('.water-conc').forEach((el) => el.addEventListener('change', (e) => {
    const row = rows[Number(e.target.dataset.wr)];
    if (row.si != null) commitRow(Number(e.target.dataset.wr));
  }));
  // Any source-water change re-solves the doses: solver-owned salts and the engine's
  // pH dose were sized for the PREVIOUS base water (often the assumed-RO birth solve),
  // and the base just changed under them. Same contract as applyDefaultWater: leaving
  // them put is how stale RO-sized doses ended up stacked on real tap water (bug
  // report 35). Without a target there's nothing to match, so plain recalc.
  const applySourceChange = (next, label, matchedLabel) => {
    if (next.waterProfile && next.waterProfile.target) {
      matchSaltsToTarget(next, matchedLabel).then((ok) => { if (!ok) recalcFromDetail(next, label); });
    } else {
      recalcFromDetail(next, label);
    }
  };
  // Read the source-water fields into a waterProfile patch. All-blank is a legitimate
  // apply: it declares "my water is RO/distilled" explicitly.
  const readSourceFields = () => {
    const source = {};
    let any = false;
    for (const k of WATER_IONS) {
      const raw = $(`wsrc_${k}`)?.value?.trim();
      const n = Number(raw);
      source[k] = raw === '' || !Number.isFinite(n) || n < 0 ? 0 : n;
      if (raw !== '') any = true;
    }
    const name = $('wsrc_name')?.value?.trim() || '';
    if (name) source.name = name;
    const dil = Number($('wsrc_dilution')?.value);
    const wpNext = { ...(currentRecipe.waterProfile || {}), source };
    if (Number.isFinite(dil) && dil > 0) wpNext.dilutionPct = Math.min(dil, 100);
    else delete wpNext.dilutionPct;
    if (!any && !name) source.name = 'RO/distilled';
    return { wpNext, name };
  };
  // Apply: use these numbers on this recipe (and as the default for new ones). No save.
  $('waterSrcApply')?.addEventListener('click', () => {
    if (!currentRecipe) return;
    const { wpNext } = readSourceFields();
    saveDefaultWater(wpNext); // this water is now the default for every new recipe
    applySourceChange({ ...currentRecipe, waterProfile: wpNext },
      'Source water set.', 'Source water set, salts re-matched.');
  });
  // Save: keep it in My water (account when signed in, this browser otherwise), make it
  // the default, and use it on this recipe. Needs a name so the list has something to show.
  $('waterSrcSave')?.addEventListener('click', () => {
    if (!currentRecipe) return;
    const { wpNext, name } = readSourceFields();
    if (!name) {
      toast('Give this water a name first.', 'warn');
      $('wsrc_name')?.focus();
      return;
    }
    // The save resolves first so the picker lists it, then saveDefaultWater can find the
    // account row to flag; the recalc's re-render would otherwise race the async save.
    saveMyWater(wpNext.source, wpNext.dilutionPct, true).then(() => {
      saveDefaultWater(wpNext);
      redrawWaterEditor();
    });
    applySourceChange({ ...currentRecipe, waterProfile: wpNext },
      `${name} saved to My water.`, `${name} saved to My water, salts re-matched.`);
  });
  // Forget a saved profile. The recipe keeps its current source; only the list changes.
  $('waterSrcForget')?.addEventListener('click', async () => {
    const name = currentRecipe?.waterProfile?.source?.name;
    if (!name) return;
    if (!confirm(`Remove "${name}" from My water?`)) return;
    if (await deleteMyWater(name)) {
      const dw = defaultWater();
      if (dw && dw.source && sameName(dw.source.name, name)) saveDefaultWater(null);
      toast(`${name} removed from My water.`, 'ok');
      renderWaterEditor();
    }
  });
  // Picking a profile fills the source fields AND applies it in one step — same
  // spirit as picking a target: choose, done, the panel recalculates.
  const citySel = $('waterCitySel');
  if (citySel) {
    citySel.addEventListener('change', () => {
      const v = citySel.value;
      if (!v || !currentRecipe) return;
      const name = v.slice(v.indexOf(':') + 1);
      const prof = v.startsWith('mine:')
        ? loadMyWaters().find((w) => sameName(w.name, name))
        : WATER_CITIES.find((w) => w.name === name);
      if (!prof) return;
      const source = { name: prof.name };
      for (const k of WATER_IONS) source[k] = prof[k] ?? 0;
      const wpNext = { ...(currentRecipe.waterProfile || {}), source };
      // A saved water carries its own RO dilution; a city preset means "no dilution".
      if (prof.dilutionPct > 0) wpNext.dilutionPct = prof.dilutionPct; else delete wpNext.dilutionPct;
      saveDefaultWater(wpNext); // a picked profile is now the default for every new recipe
      applySourceChange({ ...currentRecipe, waterProfile: wpNext },
        `${prof.name} water applied.`, `${prof.name} water applied, salts re-matched.`);
    });
    // Same searchable combo the Style and Equipment pickers use on the main page.
    if (typeof enhanceSearchSelect === 'function') enhanceSearchSelect(citySel);
  }
  $('waterSrcClear')?.addEventListener('click', () => {
    if (!currentRecipe?.waterProfile) return;
    // Clear the default FIRST: the recalc's re-render would otherwise re-attach it.
    saveDefaultWater(null);
    const { source, dilutionPct, ...rest } = currentRecipe.waterProfile;
    const next = { ...currentRecipe };
    if (rest.target) next.waterProfile = rest; else delete next.waterProfile;
    // Clearing is a source change too: doses sized for the tap water would go stale
    // over the now-assumed RO base in the same way.
    applySourceChange(next, 'Source water cleared.', 'Source water cleared, salts re-matched.');
  });
  $('waterTargetSel')?.addEventListener('change', (e) => {
    if (!currentRecipe) return;
    const v = e.target.value;
    if (v === '__keep') return; // the imported target stayed selected — nothing changed
    const wpCur = currentRecipe.waterProfile || {};
    if (!v) {
      const { target, autoTarget, ...rest } = wpCur;
      const next = { ...currentRecipe };
      if (rest.source || rest.dilutionPct) next.waterProfile = rest; else delete next.waterProfile;
      recalcFromDetail(next, 'Target profile cleared.');
      return;
    }
    const preset = WATER_TARGETS.find((t) => t.name === v) || WATER_CITIES.find((t) => t.name === v);
    if (!preset) return;
    // A picked target is the brewer's own: drop the style-link flag so it never moves.
    const wpNext = { ...wpCur, target: { ...preset } };
    delete wpNext.autoTarget;
    const withTarget = { ...currentRecipe, waterProfile: wpNext };
    // Salts follow the target automatically; if the solver can't be reached the target
    // still lands on its own.
    matchSaltsToTarget(withTarget, `Target ${preset.name} — salts matched.`)
      .then((done) => { if (!done) recalcFromDetail(withTarget, `Target set to ${preset.name}.`); });
  });
  // The target picker searches like the Style/Equipment combos too.
  if ($('waterTargetSel') && typeof enhanceSearchSelect === 'function') enhanceSearchSelect($('waterTargetSel'));
  // Editing a target ppm turns the selection into a custom target (name keeps its
  // origin: "Hazy IPA (edited)"), stored and recalculated like any preset pick.
  host.querySelectorAll('.wtgt').forEach((el) => el.addEventListener('change', () => {
    if (!currentRecipe?.waterProfile?.target) return;
    const cur = currentRecipe.waterProfile.target;
    const next = { name: /\(edited\)$/.test(cur.name || '') ? cur.name : `${cur.name || 'Custom target'} (edited)` };
    for (const k of WATER_IONS) {
      const n = Number($(`wtgt_${k}`)?.value);
      next[k] = Number.isFinite(n) && n >= 0 ? Math.round(n) : (cur[k] ?? 0);
    }
    const editedWp = { ...currentRecipe.waterProfile, target: next };
    delete editedWp.autoTarget; // hand-edited ppm = the brewer's target now
    const edited = { ...currentRecipe, waterProfile: editedWp };
    matchSaltsToTarget(edited, 'Target updated — salts matched.')
      .then((done) => { if (!done) recalcFromDetail(edited, 'Target updated.'); });
  }));
}

/** Change how much of an ingredient the recipe uses, then recalculate. */
/** Open the picker in add mode: choosing an entry appends it rather than replacing one. */
async function openAddPicker() {
  injectDetailModal();
  pickerAdds = true;
  swapIndex = null;
  $('detailListView').hidden = true;
  $('detailPickerView').hidden = false;
  $('detailTitle').textContent = detailKind === 'hop' ? 'Add a hop' : 'Add an ingredient';
  $('detailPickerFor').innerHTML = `Adding to the <strong>${escapeHtml(DETAIL_KINDS[detailKind].title.toLowerCase())}</strong>`;
  // No seed term — an add starts from a blank search rather than "more like this one".
  $('detailPickerQ').value = '';
  $('detailPickerResults').innerHTML = '<p class="grain-picker__empty">Loading…</p>';
  if (currentMode === 'all' && !catalogCache) {
    try { await fetchCatalog(); }
    catch { $('detailPickerResults').innerHTML = '<p class="grain-picker__empty">Could not load the catalog.</p>'; return; }
  }
  renderPickerResults();
  $('detailPickerQ').focus();
}

/**
 * Append a new entry, then recalculate.
 *
 * Defaults are the smallest sensible amount rather than zero: grain at one step of the
 * active grid (the engine drops a zero-weight fermentable outright), hops at a quarter
 * ounce with a 60-minute boil addition, which is the one timing that always makes sense
 * as a starting point. The brewer adjusts amount and timing from the row like any other.
 */
async function addItem(name) {
  if (!name || !currentRecipe) return;
  const kind = detailKind;
  const recipe = { ...currentRecipe };
  if (kind === 'fermentable') {
    recipe.fermentables = [...(currentRecipe.fermentables || []).map((x) => ({ name: x.name, weight: x.weight })),
                           { name, weight: grainStepLb() }];
  } else if (kind === 'hop') {
    // strip the resolved constants so the engine re-derives them for every row
    const kept = (currentRecipe.hopSchedule || []).map(({ aa, alphaAcid, ...rest }) => rest);
    recipe.hopSchedule = [...kept, { name, weight: 0.25, method: 'boil', time: 60 }];
  } else {
    return; // one yeast per recipe
  }
  $('detailPickerResults').innerHTML = '<p class="grain-picker__empty">Recalculating…</p>';
  await recalcFromDetail(recipe, `Added ${name}.`);
  showDetailList();
}

/** Drop an entry, then recalculate. The last one can't go — an empty bill won't compute. */
async function removeItem(index) {
  if (!currentRecipe) return;
  const kind = detailKind;
  const items = detailItems(kind);
  if (!items[index] || kind === 'yeast') return;
  if (items.length <= 1) {
    toast(kind === 'hop' ? 'A recipe needs at least one hop.' : 'A recipe needs at least one fermentable.');
    return;
  }
  const gone = detailNotes(kind)[index]?.resolvedAs || items[index].name;
  const recipe = { ...currentRecipe };
  if (kind === 'fermentable') {
    recipe.fermentables = currentRecipe.fermentables
      .filter((_, i) => i !== index).map((x) => ({ name: x.name, weight: x.weight }));
  } else {
    recipe.hopSchedule = currentRecipe.hopSchedule
      .filter((_, i) => i !== index).map(({ aa, alphaAcid, ...rest }) => rest);
  }
  await recalcFromDetail(recipe, `Removed ${gone}.`);
}

async function applyAmount(index, raw) {
  const kind = detailKind;
  const items = detailItems(kind);
  if (!items[index]) return;
  let n = Number(raw);
  if (!Number.isFinite(n) || n < 0) { renderDetailRows(); return; }
  // The input edits in the display unit; the recipe carries lb/oz.
  n = detailAmountSpec(kind).fromDisplay(n);

  const recipe = { ...currentRecipe };
  if (kind === 'fermentable') {
    // The engine snaps grain to the active grid on its own; a zero would silently drop
    // the ingredient, so treat one step as the floor instead.
    recipe.fermentables = currentRecipe.fermentables.map((x, i) =>
      ({ name: x.name, weight: i === index ? Math.max(grainStepLb(), n) : x.weight }));
  } else if (kind === 'hop') {
    recipe.hopSchedule = currentRecipe.hopSchedule.map((x, i) => {
      const { aa, alphaAcid, ...rest } = x;
      return i === index ? { ...rest, weight: n } : rest;
    });
  } else {
    recipe.yeast = { name: currentRecipe.yeast.name, packages: Math.max(1, Math.round(n)) };
  }
  await recalcFromDetail(recipe, 'Amount updated.');
}

/** Shared recalculation for the detail modal's edits (amount + version swap). */
async function recalcFromDetail(recipe, okMessage) {
  try {
    const res = await fetch(API + '/api/calculate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        // Fall back to the rig this recipe was already computed on, so an edit still works
        // if the equipment selector is empty (restored session, cleared dropdown).
        recipe, equipmentProfile: equipmentPayload() || currentReport?.equipmentProfile || undefined,
        styleName: styleSelect.value || undefined, mode: currentMode,
        customIngredients: customIngredientsPayload(),
      }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Server returned ${res.status}`);
    const report = await res.json();
    // /api/calculate skips the in-stock check; carry the prior flags so nothing flickers.
    report.outOfStock = currentReport?.outOfStock || [];
    report.recipeName = currentReport?.recipeName;
    if (currentReport?.conversion) report.conversion = currentReport.conversion;
    renderRecipe(report);
    persistSession();
    if (!$('detailModal')?.hidden) renderDetailRows();
    if (okMessage) toast(okMessage, 'ok');
  } catch (err) {
    toast(err.message || 'Could not update the recipe.', 'err');
    renderDetailRows();
  }
}

/** Rewrite the ingredient name and recalculate. All numbers come from the engine. */
async function applySwap(index, newName) {
  const kind = detailKind;
  const item = detailItems(kind)[index];
  if (!item || !newName) return;
  if (item.name === newName) { showDetailList(); return; }
  // Send names + amounts only — the resolver fills the constants for the new entry.
  const recipe = { ...currentRecipe };
  if (kind === 'fermentable') {
    recipe.fermentables = currentRecipe.fermentables.map((x, i) => ({ name: i === index ? newName : x.name, weight: x.weight }));
  } else if (kind === 'hop') {
    recipe.hopSchedule = currentRecipe.hopSchedule.map((x, i) => {
      const { aa, alphaAcid, ...rest } = x;
      if (i !== index) return rest;
      // Swapping to a different product: the measured alpha belonged to the old hop.
      const { customAlpha, ...fresh } = rest;
      return { ...fresh, name: newName };
    });
  } else {
    recipe.yeast = { name: newName };
  }
  $('detailPickerResults').innerHTML = '<p class="grain-picker__empty">Recalculating…</p>';
  try {
    const res = await fetch(API + '/api/calculate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        // Fall back to the rig this recipe was already computed on, so a swap still works
        // if the equipment selector is empty (restored session, cleared dropdown).
        recipe, equipmentProfile: equipmentPayload() || currentReport?.equipmentProfile || undefined,
        styleName: styleSelect.value || undefined, mode: currentMode,
        customIngredients: customIngredientsPayload(),
      }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Server returned ${res.status}`);
    const report = await res.json();
    // /api/calculate skips the in-stock check; carry the prior flags so nothing flickers.
    report.outOfStock = currentReport?.outOfStock || [];
    report.recipeName = currentReport?.recipeName;
    if (currentReport?.conversion) report.conversion = currentReport.conversion;
    renderRecipe(report);
    persistSession();
    showDetailList();
    toast(`Swapped to ${newName}.`, 'ok');
  } catch (err) {
    toast(err.message || 'Could not swap that ingredient.', 'err');
    showDetailList();
  }
}

/**
 * Turn each section heading into a button so it reads as clickable. Done in JS so the
 * theme-editor-managed Liquid keeps its plain markup.
 */
function wireSectionHeadings() {
  // The label override renames "Grain bill" → "Grains & Mash" here in JS (Kyle, 8/20)
  // so the theme-editor-managed Liquid keeps its original markup untouched.
  const map = [
    ['fermentables', 'fermentable', 'Grains & Mash', 'See the full specs, swap versions, and edit the mash program'],
    ['hops', 'hop', 'Boil & Hops'],
    ['yeastBlock', 'yeast', 'Yeast & Fermentation'],
  ];
  for (const [id, kind, label, title] of map) {
    const host = $(id);
    const h = host?.closest('.recipe__section')?.querySelector('h3');
    if (!h || h.dataset.wired) continue;
    h.dataset.wired = '1';
    h.classList.add('section-link');
    h.title = title || 'See the full specs and swap versions';
    h.innerHTML = `<span>${escapeHtml(label || h.textContent)}</span><span class="section-link__hint">edit</span>`;
    h.addEventListener('click', () => openDetail(kind));
  }
}
wireSectionHeadings();

// ── the mash rows + step editor ──────────────────────────────────
// The mash lives inside the Grains & Mash section (Kyle, 8/20 — it was previously its
// own section AND a Process tile, the same figure three times). The rests render as
// rows under the grain bill behind a small "Mash" divider, and ALL editing lives in
// the Grains & Mash modal — the step editor (add, remove, rename, retemper, retime)
// mounts below the grain rows there. A recipe with no program shows its single
// infusion as one row; the editor turns it into a program by adding steps. The row
// container is created here rather than in the page markup so the storefront theme
// needs no liquid change.

/** The recipe's mash as editor rows: the step program, or the single infusion as one row. */
function mashStepsOf(r) {
  if (Array.isArray(r?.mashSteps) && r.mashSteps.length) return r.mashSteps.map((s) => ({ ...s }));
  return [{ name: 'Saccharification rest', temp: r?.mashTemp ?? 152, time: r?.mashTime ?? 60 }];
}

/**
 * Store an edited program on the recipe and recalculate. One step collapses back to
 * the classic single-infusion fields (the server does the same); two or more ride as
 * mashSteps and the engine derives fermentability/strike from the program.
 */
async function applyMashSteps(steps, okMessage) {
  if (!currentRecipe) return;
  const next = { ...currentRecipe };
  if (steps && steps.length > 1) {
    next.mashSteps = steps;
  } else {
    delete next.mashSteps;
    if (steps && steps.length === 1) {
      next.mashTemp = steps[0].temp;
      next.mashTime = steps[0].time;
    }
  }
  await recalcFromDetail(next, okMessage);
}

function renderMashSection(r, report) {
  const fermTable = $('fermentables');
  if (!fermTable) return;
  let box = $('grainMashRows');
  if (!box) {
    box = document.createElement('div');
    box.id = 'grainMashRows';
    fermTable.insertAdjacentElement('afterend', box);
  }
  const steps = Array.isArray(r.mashSteps) && r.mashSteps.length > 1 ? r.mashSteps : null;
  // The time rides inline after the name (Kyle, 8/20): it's an inline nowrap span, so
  // it sits to the right when the cell has room and drops to a second line only when
  // the name pushes it out — no fixed two-line layout.
  const rows = (steps || mashStepsOf(r)).map((s, i) => {
    const label = s.name || (steps ? `Step ${i + 1}` : 'Saccharification rest');
    return `<tr><td>${steps ? `${i + 1}. ` : ''}${escapeHtml(label)} <span class="sub mash-step-time">${s.time} min</span></td>`
      + `<td class="amt">${fmtTemp(s.temp)}</td></tr>`;
  }).join('');
  // Styles with a traditional program get a one-line nudge while the recipe is still
  // single-infusion — the deterministic twin of the AI's own recommendation.
  const sm = report && report.styleMash;
  const hint = !steps && sm
    ? `<div class="sub muted mash-hint">This style traditionally uses a ${escapeHtml(sm.label)} — tap the heading to apply it.</div>`
    : '';
  box.innerHTML = `<div class="grain-mash__divider" title="Edit via the Grains &amp; Mash heading">Mash</div>`
    + `<table class="ing" id="mashRows">${rows}</table>${hint}`;
  ensureMashCss();
  // A chat-driven recalc re-renders the panel; if the Grains & Mash modal is open on
  // its list view, refresh the step editor in place (same contract as before, when the
  // mash had its own modal).
  if ($('detailModal') && !$('detailModal').hidden && detailKind === 'fermentable'
    && !$('detailListView')?.hidden && $('mashEditorBody')) renderMashEditor();
}

function renderMashEditor() {
  const box = $('mashEditorBody');
  if (!box || !currentRecipe) return;
  const degC = displayUnits.temp === 'c';
  const steps = mashStepsOf(currentRecipe);
  const isProgram = Array.isArray(currentRecipe.mashSteps) && currentRecipe.mashSteps.length > 1;
  const tval = (f) => (degC ? Math.round(fToC(f) * 10) / 10 : f);
  const rowsHtml = steps.map((s, i) => `
    <div class="mash-row" data-i="${i}">
      <input type="text" class="mash-row__name" data-mname="${i}" value="${escapeHtml(s.name || '')}" placeholder="Step ${i + 1}" maxlength="60">
      <span class="mash-row__field"><input type="number" class="inline-num" data-mtemp="${i}" ${degC
        ? 'step="0.5" min="33" max="82" inputmode="decimal"'
        : 'step="1" min="90" max="180" inputmode="numeric"'} value="${tval(s.temp)}"><small>${degC ? '°C' : '°F'}</small></span>
      <span class="mash-row__field"><input type="number" class="inline-num" data-mtime="${i}" step="5" min="1" max="240" inputmode="numeric" value="${s.time}"><small>min</small></span>
      ${steps.length > 1 ? `<button type="button" class="icon-btn mash-row__del" data-mdel="${i}" title="Remove this step">×</button>` : '<span class="mash-row__delspacer"></span>'}
    </div>`).join('');
  // Presets: the style's traditional program (when the style has one) and the way back
  // to a single infusion. Both recalc through the engine like every panel edit.
  const sm = currentReport && currentReport.styleMash;
  box.innerHTML = `
    <p class="ing-intro">Steps run in order — dough-in happens at the first step's temperature, and your controller heats between rests. Fermentability comes from the rests in the 140–163°F range.</p>
    <div id="mashEditorRows">${rowsHtml}</div>
    <div class="mash-editor__actions">
      <button type="button" class="link-btn" id="mashAddStep">+ Add a step</button>
    </div>
    <div class="mash-editor__presets">
      ${sm ? `<button type="button" class="btn-secondary" id="mashUseStyle">Use the ${escapeHtml(sm.label)}</button>
      <div class="sub muted">${escapeHtml(sm.why)}</div>` : ''}
      ${isProgram ? `<button type="button" class="btn-secondary" id="mashSingle">Back to a single infusion</button>` : ''}
    </div>`;

  const commit = (msg) => {
    const next = [];
    box.querySelectorAll('.mash-row').forEach((row) => {
      const i = Number(row.dataset.i);
      const name = row.querySelector(`[data-mname="${i}"]`).value.trim();
      let temp = Number(row.querySelector(`[data-mtemp="${i}"]`).value);
      if (degC) temp = Math.round(cToF(temp) * 10) / 10;
      const time = Math.round(Number(row.querySelector(`[data-mtime="${i}"]`).value));
      if (!(temp >= 90 && temp <= 180) || !(time >= 1 && time <= 240)) return;
      next.push({ ...(name ? { name } : {}), temp, time });
    });
    if (!next.length) { renderMashEditor(); return; }
    applyMashSteps(next, msg);
  };

  box.querySelectorAll('[data-mname],[data-mtemp],[data-mtime]').forEach((inp) =>
    inp.addEventListener('change', () => commit('Mash updated.')));
  box.querySelectorAll('[data-mdel]').forEach((b) =>
    b.addEventListener('click', () => {
      const i = Number(b.dataset.mdel);
      const next = mashStepsOf(currentRecipe).filter((_, j) => j !== i);
      applyMashSteps(next, next.length > 1 ? 'Step removed.' : 'Back to a single infusion.');
    }));
  $('mashAddStep')?.addEventListener('click', () => {
    const cur = mashStepsOf(currentRecipe);
    const last = cur[cur.length - 1];
    // A sensible next rest: after a saccharification rest the natural addition is the
    // mash out; anything already up there just steps up a few degrees within bounds.
    const next = last.temp < 162
      ? { name: 'Mash out', temp: 168, time: 10 }
      : { temp: Math.min(180, last.temp + 5), time: 10 };
    applyMashSteps([...cur, next], 'Step added.');
  });
  $('mashUseStyle')?.addEventListener('click', () =>
    applyMashSteps(sm.steps.map((s) => ({ ...s })), `Applied the ${sm.label}.`));
  $('mashSingle')?.addEventListener('click', () => {
    // Collapse to one rest at the program's effective character: keep the recipe's
    // engine-derived mashTemp (brew() set it from the sacc rests) and the total time.
    const t = currentRecipe.mashTemp ?? 152;
    applyMashSteps([{ name: 'Saccharification rest', temp: Math.round(t), time: 60 }], 'Back to a single infusion.');
  });
}

/** Editor layout CSS, injected like the other JS-built UI so the theme css needs no edit. */
function ensureMashCss() {
  if ($('mashCss')) return;
  const st = document.createElement('style');
  st.id = 'mashCss';
  st.textContent = `
#beer-builder-root .mash-row,.mash-row{display:flex;align-items:center;gap:8px;margin:6px 0}
#beer-builder-root .mash-row__name,.mash-row__name{flex:1;min-width:0;padding:6px 8px;font-family:inherit;font-size:13.5px;font-weight:650;color:inherit}
#beer-builder-root #mashEditorBody .sub,#mashEditorBody .sub{font-size:12px;font-weight:400;color:var(--muted,#6b7280)}
#beer-builder-root .mash-row__field,.mash-row__field{display:inline-flex;align-items:center;gap:3px;white-space:nowrap}
#beer-builder-root .mash-row__field .inline-num,.mash-row__field .inline-num{width:58px}
#beer-builder-root .mash-row__delspacer,.mash-row__delspacer{width:28px}
#beer-builder-root .mash-editor__actions,.mash-editor__actions{margin:10px 0 14px}
#beer-builder-root .mash-editor__presets,.mash-editor__presets{display:flex;flex-direction:column;gap:8px;border-top:1px solid rgba(128,128,128,.25);padding-top:12px}
#beer-builder-root .mash-hint,.mash-hint{margin-top:4px;font-size:12.5px;font-weight:400;color:var(--muted,#6b7280)}
#beer-builder-root .mash-step-time,.mash-step-time{white-space:nowrap;margin-left:2px}
#beer-builder-root .grain-mash__divider,.grain-mash__divider{margin-top:10px;padding-top:8px;border-top:1px solid rgba(128,128,128,.25);font-size:10.5px;text-transform:uppercase;letter-spacing:.04em;font-weight:600;color:var(--faint,#8a8f98)}
#beer-builder-root .detail-mash__head,.detail-mash__head{margin:18px 0 6px;padding-top:14px;border-top:1px solid rgba(128,128,128,.25);font-size:12px;text-transform:uppercase;letter-spacing:.04em}
`;
  document.head.appendChild(st);
}

/**
 * Volume rows: the two entries are full-column slim rectangles, not the Process
 * squares — label left, value right, standard 9px stack gap (Kyle, 8/12). Injected
 * like the mash/units CSS so the theme stylesheet needs no upload; rules carry both
 * the storefront-scoped and bare selectors, same as ensureMashCss.
 */
function ensureVolumeCss() {
  if ($('volumeCss')) return;
  const st = document.createElement('style');
  st.id = 'volumeCss';
  st.textContent = `
/* Two slim tiles on one line. Inside each tile the label and value sit side by side
   when the pane is wide enough and WRAP into label-over-value when it isn't — the
   pane's width varies with the three-column layout independent of the viewport, so
   wrapping beats a viewport media query here. */
#beer-builder-root .metrics--vol,.metrics--vol{grid-template-columns:1fr 1fr}
#beer-builder-root .metrics--vol .metric,.metrics--vol .metric{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;column-gap:8px;row-gap:2px;padding:7px 12px;min-width:0}
#beer-builder-root .metrics--vol .metric .k,.metrics--vol .metric .k{white-space:nowrap}
#beer-builder-root .metrics--vol .metric .v,.metrics--vol .metric .v{margin-top:0}
`;
  document.head.appendChild(st);
}

// ── the Volume section ───────────────────────────────────────────────
// Between the benchmark squares and the Grain bill (Kyle, 8/12), styled exactly like
// the Process metrics row. Two tiles: Batch size is the editable knob — changing it
// scales every amount in the recipe deterministically and recalculates — and Starting
// water is the calculator's own figure (batch + grain/hop absorption + boil-off +
// losses), shown as a plain value like the benchmark squares because it is computed,
// never typed. The section div is created here so the theme liquid needs no change.

function renderVolumeSection(r, report) {
  let sec = $('volumeSection');
  const grainSection = $('fermentables')?.closest('.recipe__section');
  if (!sec && grainSection) {
    sec = document.createElement('div');
    sec.className = 'recipe__section';
    sec.id = 'volumeSection';
    grainSection.parentNode.insertBefore(sec, grainSection);
  }
  if (!sec) return;
  const liters = displayUnits.water === 'l';
  const unit = liters ? 'L' : 'gal';
  const batch = r.batchSize ?? 5;
  const batchVal = liters ? Math.round(batch * GAL_L * 10) / 10 : batch;
  const sw = report && report.benchmarks && report.benchmarks.volumes
    ? report.benchmarks.volumes.strikeWater : null;
  const swVal = sw == null ? '–' : liters ? (sw * GAL_L).toFixed(1) : sw;
  // The batch input edits in the DISPLAY unit and stores gallons, like every other
  // panel knob. Header input attrs mirrored (min 0.5 gal, 0.5 steps).
  // Full-width slim rows rather than the square tiles (Kyle, 8/12): each entry spans
  // the column, label left, value right — see ensureVolumeCss.
  ensureVolumeCss();
  sec.innerHTML = `<h3>Volume</h3><div class="metrics metrics--proc metrics--vol">`
    + metric('Batch size', liters
      ? `<input id="batchSizePanel" class="inline-num" type="number" step="1" min="2" max="380" inputmode="decimal" value="${batchVal}">`
      : `<input id="batchSizePanel" class="inline-num" type="number" step="0.5" min="0.5" max="100" inputmode="decimal" value="${batchVal}">`,
      unit, 'Packaged batch size — changing it scales the whole recipe to the new volume')
    + metric('Starting water', String(swVal), unit,
      'Total water to heat at the start of the brew. The calculator computes it from the batch size, grain and hop absorption, boil-off and losses — it is not directly editable')
    + `</div>`;
  $('batchSizePanel')?.addEventListener('change', (e) => {
    const raw = Number(e.target.value);
    applyBatchScale(liters ? raw / GAL_L : raw);
  });
}

/**
 * Scale the whole recipe to a new batch size, deterministically: every amount that
 * doses the batch (grain, hops, flavorings, water salts/acids) moves by the same
 * ratio — concentrations hold — and the engine recomputes. The imported source-water
 * plan is dropped like the chat rescale drops it: it described the old batch. The
 * header batch field follows the recipe so every later request agrees.
 */
async function applyBatchScale(newGal) {
  if (!currentRecipe) return;
  const cur = currentRecipe.batchSize ?? 5;
  const gal = Math.round(Math.min(100, Math.max(0.5, newGal)) * 100) / 100;
  if (!(gal > 0) || Math.abs(gal - cur) < 1e-9) {
    renderVolumeSection(currentRecipe, currentReport); // reset a no-op/junk entry
    return;
  }
  const ratio = gal / cur;
  const amt = (v, dp = 10000) => (typeof v === 'number' ? Math.round(v * ratio * dp) / dp : v);
  const next = { ...currentRecipe, batchSize: gal };
  next.fermentables = (currentRecipe.fermentables || []).map((f) => ({ ...f, weight: amt(f.weight) }));
  next.hopSchedule = (currentRecipe.hopSchedule || []).map((h) => ({ ...h, weight: amt(h.weight, 100) }));
  if (currentRecipe.flavorings && currentRecipe.flavorings.length) {
    next.flavorings = currentRecipe.flavorings.map((fl) => ({ ...fl, amount: amt(fl.amount, 100) }));
  }
  if (currentRecipe.salts && currentRecipe.salts.length) {
    next.salts = currentRecipe.salts.map((s) => ({ ...s, amount: amt(s.amount, 100) }));
  }
  delete next.sourceWater;
  await recalcFromDetail(next, `Scaled to ${fmtVol(gal)}.`);
  if (typeof syncBatchHeaderToRecipe === 'function') syncBatchHeaderToRecipe();
}

// ── the Equipment section ────────────────────────────────────────────
// The brew-system picker, back in the panel (Kyle, 8/12; it left the panel in the
// 8/10 Process restructure). It is not a second implementation: the panel select
// mirrors the header select's options and writes every change back THROUGH it, so the
// one established path runs for both — rememberEquip, syncEffField and
// recalcForEquipmentChange, which re-runs the SAME recipe on the new profile. So the
// benchmarks move with the rig's efficiency, boil-off, absorption and losses, and no
// ingredient amount changes: scaling is the brewer's own call, in Volume above.

function renderEquipmentSection() {
  let sec = $('equipSection');
  const grainSection = $('fermentables')?.closest('.recipe__section');
  if (!sec && grainSection) {
    sec = document.createElement('div');
    sec.className = 'recipe__section';
    sec.id = 'equipSection';
    // .field select is the topbar's own control styling, reused verbatim — the same
    // way the water editor's pickers do it. No new CSS.
    sec.innerHTML = '<h3>Equipment</h3>'
      + '<div class="field"><select id="equipSelectPanel" aria-label="Brew system"></select></div>';
    grainSection.parentNode.insertBefore(sec, grainSection);
    const sel = $('equipSelectPanel');
    sel.innerHTML = equipSelect.innerHTML;
    sel.value = equipSelect.value;
    sel.addEventListener('change', () => {
      if (equipSelect.value === sel.value) return;
      equipSelect.value = sel.value;
      // One path for both controls: the header's own listeners recalc the recipe,
      // remember the choice, and refresh the efficiency field.
      equipSelect.dispatchEvent(new Event('change'));
    });
    // The same searchable picker the header and the water modal use (it carries the
    // elementFromPoint guard, so an overlay can't have its taps stolen).
    if (typeof enhanceSearchSelect === 'function') enhanceSearchSelect(sel);
  }
  if (!sec) return;
  // Options load asynchronously and grow (a saved profile, a session rig), so re-sync
  // from the header on every render rather than only at birth.
  const sel = $('equipSelectPanel');
  if (sel) {
    if (sel.innerHTML !== equipSelect.innerHTML) sel.innerHTML = equipSelect.innerHTML;
    if (sel.value !== equipSelect.value) sel.value = equipSelect.value;
  }
}

// ── background scroll lock while a modal is open ─────────────────────
// Without this the page behind a modal scrolls under your finger on mobile, which reads
// as the whole screen coming unpinned. Locking `position: fixed` on the body (rather than
// `overflow: hidden`, which iOS Safari ignores) is what actually holds, so the scroll
// offset has to be saved and restored by hand.
/**
 * The cart bar is sticky so its button stays reachable while a long recipe scrolls. That
 * breaks down once the bar itself outgrows the scrollport: a sticky element taller than
 * its container pins across the whole visible area, so after "add to cart" the options +
 * button + result list (~600px in a 370px pane) covered the benchmarks and the brewer
 * could not scroll back up to them. Derived from measurement rather than toggled at each
 * call site, for the same reason the scroll lock is — a future path can't forget it.
 */
function syncCartSticky() {
  const cart = document.querySelector('#beer-builder-root .cart') || document.querySelector('.cart');
  const scroller = $('recipeContent');
  if (!cart || !scroller || !scroller.clientHeight) return;
  // Leave a little headroom so the bar doesn't flip state while it's borderline.
  const tooTall = cart.offsetHeight > scroller.clientHeight - 40;
  if (cart.classList.contains('cart--unstuck') !== tooTall) {
    cart.classList.toggle('cart--unstuck', tooTall); // guarded: an unconditional toggle
  }                                                  // re-triggers the MutationObserver
}

let scrollLockY = 0;
let scrollLocked = false;

function anyModalOpen() {
  return [...document.querySelectorAll('.modal, .bb-dialog-scrim')]
    .some((el) => !el.hidden && getComputedStyle(el).display !== 'none');
}

function syncScrollLock() {
  const shouldLock = anyModalOpen();
  if (shouldLock === scrollLocked) return;
  const body = document.body;
  if (shouldLock) {
    scrollLockY = window.scrollY || document.documentElement.scrollTop || 0;
    body.style.position = 'fixed';
    body.style.top = `-${scrollLockY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
  } else {
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.style.overflow = '';
    window.scrollTo(0, scrollLockY);
  }
  scrollLocked = shouldLock;
}

// Derived from what's on screen rather than counted per open/close: nesting a picker
// inside the detail modal, or any future path that forgets to call it, can't then leave
// the page stuck locked. Coalesced to one check per frame since renders mutate a lot.
let scrollSyncQueued = false;
function queueScrollLockSync() {
  if (scrollSyncQueued) return;
  scrollSyncQueued = true;
  // setTimeout, not requestAnimationFrame: rAF is paused in a hidden or backgrounded tab,
  // which left the lock un-applied entirely.
  setTimeout(() => { scrollSyncQueued = false; syncScrollLock(); syncCartSticky(); }, 0);
}
window.addEventListener('resize', queueScrollLockSync);
new MutationObserver(queueScrollLockSync).observe(
  document.getElementById('beer-builder-root') || document.body,
  { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'style', 'class'] },
);

// ── Beer Traits panel (sliders) ──────────────────────────────────────
// A separate window below the chat that appears whenever a recipe is on screen.
// Two slider sets share it:
//   - 21C Hazy IPA keeps its purpose-built set: Hop Intensity (total oz/gal),
//     Bitterness (target IBU, hit by solving the 60-min charge), Hop Emphasis
//     (WHERE the ounces go), Dry-Hop Schedule/Contact, plus Strength and Finish.
//   - Every other style gets the universal set — Strength, Bitterness, Color,
//     Finish — with stops spread across the loaded style's own BJCP range, so
//     "Bold" on a mild and "Bold" on a barleywine each mean bold FOR THAT style.
// Every move calls the deterministic /api/hop-sliders transform; no AI in this
// path. The DOM is injected here so the theme's Liquid never needs to change, and
// the panel gets the same bottom resize grip as the chat and recipe panes.
/**
 * Each stop carries `c: [from, to]` — the two-tone gradient its HANDLE wears, painted at
 * 310° like the storefront banner so it reads iridescent rather than as a flat dot. The
 * ramps are chosen to match what the metric actually does to the beer: strength runs pale
 * gold → deep red, hops pale lime → deep green, bitterness cool blue → violet, emphasis
 * kettle steel → juicy orange, finish pillowy pink → icy cyan.
 *
 * The bar carries the same ramp, anchored at the tick positions (see hsRampCss), so the
 * colour sitting under a tick is exactly the colour the handle turns when it lands there.
 */
const HS_STOPS = {
  strength: { title: 'Strength', group: 'strength',
    stops: [
      { v: 6, label: '6% ABV', c: ['#f6d365', '#fda085'] },
      { v: 7, label: '7% ABV', c: ['#f7b733', '#fc7c4f'] },
      { v: 8, label: '8% ABV', c: ['#ef8a2b', '#e8483f'] },
      { v: 9, label: '9% ABV', c: ['#e0533f', '#a9182a'] },
    ] },
  intensity: { title: 'Hop Intensity', group: 'hop',
    stops: [
      { v: 'low', label: 'Low', sub: '1.5 oz/gal', c: ['#d9f99d', '#86efac'] },
      { v: 'medium', label: 'Medium', sub: '2.0 oz/gal', c: ['#a3e635', '#34d399'] },
      { v: 'high', label: 'High', sub: '2.5 oz/gal', c: ['#65d63a', '#16a34a'] },
      { v: 'extra', label: 'Extra', sub: '3.5 oz/gal', c: ['#22c55e', '#046c4e'] },
    ] },
  bitterness: { title: 'Bitterness', group: 'hop',
    stops: [
      { v: 'soft', label: 'Soft', sub: '25 IBU', c: ['#7dd3fc', '#a5b4fc'] },
      { v: 'balanced', label: 'Balanced', sub: '35 IBU', c: ['#38bdf8', '#818cf8'] },
      { v: 'firm', label: 'Firm', sub: '45 IBU', c: ['#6366f1', '#a855f7'] },
      { v: 'assertive', label: 'Assertive', sub: '55 IBU', c: ['#4338ca', '#7e22ce'] },
    ] },
  emphasis: { title: 'Hop Emphasis', group: 'hop',
    help: 'Controls whether hops emphasize kettle structure, whirlpool flavor or dry-hop aroma. Same total ounces either way.',
    stops: [
      { v: 'structured', label: 'Structured', c: ['#64748b', '#94a3b8'] },
      { v: 'balanced', label: 'Balanced', c: ['#22d3ee', '#67e8f9'] },
      { v: 'juicy', label: 'Juicy', c: ['#fbbf24', '#fb923c'] },
      { v: 'aroma', label: 'Aroma-Heavy', c: ['#f97316', '#dc4a0a'] },
    ] },
  dryHopSchedule: { title: 'Dry-Hop Schedule', group: 'hop',
    stops: [
      { v: 'early', label: 'Early', sub: 'active fermentation', c: ['#a7f3d0', '#6ee7b7'] },
      { v: 'split', label: 'Split', sub: '35% active / 65% late', c: ['#34d399', '#14b8a6'] },
      { v: 'late', label: 'Late', sub: 'near terminal gravity', c: ['#0d9488', '#0f5f5c'] },
    ] },
  finish: { title: 'Finish', group: 'finish',
    stops: [
      { v: 'pillowy', label: 'Pillowy', subTempF: 157, c: ['#fecdd3', '#fbcfe8'] },
      { v: 'soft', label: 'Soft', subTempF: 154, c: ['#fda4af', '#f0abfc'] },
      { v: 'balanced', label: 'Balanced', subTempF: 152, c: ['#c4b5fd', '#a5f3fc'] },
      { v: 'crisp', label: 'Crisp', subTempF: 149, c: ['#67e8f9', '#06b6d4'] },
    ] },
  dryHopContact: { title: 'Dry-Hop Contact', group: 'hop', advanced: true,
    stops: [
      { v: 'short', label: 'Short', sub: '2 days', c: ['#a7f3d0', '#6ee7b7'] },
      { v: 'standard', label: 'Standard', sub: '3 days', c: ['#34d399', '#14b8a6'] },
      { v: 'extended', label: 'Extended', sub: '4 days', c: ['#0d9488', '#0f5f5c'] },
    ] },
};
const HS_ORDER = ['strength', 'intensity', 'bitterness', 'emphasis', 'dryHopSchedule', 'finish'];

// ── universal trait stops (every style except 21C) ──────────────────
// Derived, not authored: stops spread across the style's target range from the
// report's style comparison (report.style.fields.*.target). Styles without a
// defined range (the BJCP specialty styles) fall back to whole-beer-world spans.
const HS_UNI_FALLBACK = { abv: [4, 9], ibu: [10, 60], srm: [3, 35] };
const HS_UNI_LABELS = {
  bitterness: { 4: ['Soft', 'Balanced', 'Firm', 'Bold'], 3: ['Soft', 'Balanced', 'Bold'], 2: ['Softer', 'Bolder'] },
  color: { 4: ['Lightest', 'Lighter', 'Darker', 'Darkest'], 3: ['Lightest', 'Middle', 'Darkest'], 2: ['Lighter', 'Darker'] },
};
// Universal Finish: the same mash-temp stops the hazy set uses, minus the hazy word.
const HS_UNI_FINISH = { title: 'Finish', group: 'finish',
  stops: [
    { v: 'pillowy', label: 'Full', subTempF: 157, c: ['#fecdd3', '#fbcfe8'] },
    { v: 'soft', label: 'Soft', subTempF: 154, c: ['#fda4af', '#f0abfc'] },
    { v: 'balanced', label: 'Balanced', subTempF: 152, c: ['#c4b5fd', '#a5f3fc'] },
    { v: 'crisp', label: 'Crisp', subTempF: 149, c: ['#67e8f9', '#06b6d4'] },
  ] };

/** Four stops spread across [lo, hi], rounded to the metric's grid, deduped in order. */
function hsSpread(lo, hi, round) {
  const out = [];
  for (const raw of [lo, lo + (hi - lo) / 3, lo + (2 * (hi - lo)) / 3, hi]) {
    const v = round(raw);
    if (!out.includes(v)) out.push(v);
  }
  return out;
}
/** Handle colours for n stops, sampled off a 4-stop ramp so 3- and 2-stop rows still span it. */
function hsRampPick(ramp, n) {
  const at = n >= 4 ? [0, 1, 2, 3] : n === 3 ? [0, 1, 3] : [0, 3];
  return at.slice(0, n).map((i) => ramp[i]);
}

/**
 * Build the universal defs for the style on screen. A row appears only when its
 * range survives rounding with at least two distinct stops; Finish always appears.
 */
function hsUniversalStops() {
  const fields = currentReport?.style?.fields || {};
  const range = (k) => {
    const t = fields[k]?.target;
    return Array.isArray(t) && t[1] > t[0] ? t : HS_UNI_FALLBACK[k];
  };
  const defs = {};

  const abv = hsSpread(range('abv')[0], range('abv')[1], (v) => Math.round(v * 10) / 10);
  if (abv.length >= 2) {
    const c = hsRampPick(HS_STOPS.strength.stops.map((s) => s.c), abv.length);
    defs.strength = { title: 'Strength', group: 'strength',
      stops: abv.map((v, i) => ({ v, label: `${v}% ABV`, c: c[i] })) };
  }

  const ibu = hsSpread(range('ibu')[0], range('ibu')[1], Math.round);
  if (ibu.length >= 2) {
    const labels = HS_UNI_LABELS.bitterness[ibu.length] || HS_UNI_LABELS.bitterness[4];
    const c = hsRampPick(HS_STOPS.bitterness.stops.map((s) => s.c), ibu.length);
    defs.bitterness = { title: 'Bitterness', group: 'bitter',
      stops: ibu.map((v, i) => ({ v, label: labels[i], sub: `${v} IBU`, c: c[i] })) };
  }

  const [sLo, sHi] = range('srm');
  const srm = hsSpread(sLo, sHi, (v) => (sHi - sLo < 8 ? Math.round(v * 2) / 2 : Math.round(v)));
  if (srm.length >= 2) {
    const labels = HS_UNI_LABELS.color[srm.length] || HS_UNI_LABELS.color[4];
    // The handle wears the beer itself: each stop's actual SRM colour.
    const step = Math.max(1, (sHi - sLo) / 6);
    defs.color = { title: 'Color', group: 'color',
      stops: srm.map((v, i) => ({ v, label: labels[i], sub: `${v} SRM`, c: [beerColor(v), beerColor(v + step)] })) };
  }

  defs.finish = HS_UNI_FINISH;
  return defs;
}

// The ACTIVE defs — either the hazy constants above or a style-derived universal
// set. Everything below (row HTML, labels, seating, apply) reads these, never
// HS_STOPS directly, so both panels share one pipeline.
let hsDefs = HS_STOPS;
let hsOrderActive = HS_ORDER;
let hsPanelKey = ''; // which body is currently in the DOM — rebuilt when it changes

/**
 * Where a stop's handle centre sits along the bar. The bar spans the full row while the
 * input is inset 15% per side, so the handle never parks on the very ends; the thumb's own
 * width has to come out of the travel, which is what --hs-thumb is doing here. Ticks, the
 * grey mask and the ramp's colour anchors all use this one formula, so they can't drift
 * apart if the handle size changes.
 */
function hsStopPos(i, n) {
  const f = n <= 1 ? 0 : i / (n - 1);
  return `calc(15% + (var(--hs-thumb) / 2) + ${f} * (70% - var(--hs-thumb)))`;
}

/** The metric's ramp, with each stop's colour pinned at that stop's tick position. */
function hsRampCss(def) {
  const n = def.stops.length;
  const parts = def.stops.map((s, i) => `${s.c[0]} ${(15 + (n <= 1 ? 0 : i / (n - 1)) * 70).toFixed(1)}%`);
  parts.push(`${def.stops[n - 1].c[1]} 100%`);
  return `linear-gradient(90deg, ${parts.join(', ')})`;
}

function hsRowHtml(key) {
  const def = hsDefs[key];
  const ticks = def.stops.map((_, i) =>
    `<span class="hs-tick" style="left: ${hsStopPos(i, def.stops.length)}"></span>`).join('');
  return `<div class="hs-row">
    <div class="hs-row__top">
      <span class="hs-row__label">${def.title}</span>
      <span class="hs-row__val" id="hs-${key}-val"></span>
    </div>
    <div class="hs-slider" id="hs-${key}-bar" style="--hs-ramp: ${hsRampCss(def)}">${ticks}<input type="range" id="hs-${key}" min="0" max="${def.stops.length - 1}" step="1" value="1"
      aria-label="${def.title}" /></div>
    ${def.help ? `<p class="hs-help">${def.help}</p>` : ''}
  </div>`;
}

function buildHopSliderPane() {
  if ($('hopSliderPane') || !chatPane) return;
  const pane = document.createElement('section');
  pane.className = 'hopslider';
  pane.id = 'hopSliderPane';
  pane.hidden = true;
  pane.innerHTML = `
    <div class="hopslider__head">
      <h3>Beer Traits</h3>
      <span class="hopslider__hint" id="hsHint">Moves recompute the recipe deterministically</span>
      <button type="button" id="hsResetBtn" class="link-btn" style="margin-left:auto"
        title="Becomes available once a slider changes the recipe">Reset to original</button>
    </div>
    <div class="hopslider__body" id="hsBody"></div>`;
  chatPane.after(pane);
  $('hsResetBtn')?.addEventListener('click', () => hsResetToOriginal());
}

/**
 * Point the panel at the right slider set for the recipe on screen, rebuilding the
 * rows only when the set actually changes (a style change, or the hazy panel taking
 * over). The key carries the stop values, so a new style's ranges rebuild too.
 */
function hsEnsureRows() {
  const body = $('hsBody');
  if (!body) return;
  const hazy = hopSliderStyleActive();
  const defs = hazy ? HS_STOPS : hsUniversalStops();
  const order = hazy ? HS_ORDER : ['strength', 'bitterness', 'color', 'finish'].filter((k) => defs[k]);
  const key = hazy ? 'hazy' : 'uni:' + order.map((k) => defs[k].stops.map((s) => s.v).join(',')).join('|');
  if (key === hsPanelKey) return;
  hsDefs = defs;
  hsOrderActive = order;
  hsPanelKey = key;
  const hint = $('hsHint');
  if (hint) {
    const styleName = currentReport?.style?.style;
    hint.textContent = hazy
      ? 'Hazy IPA (21C): moves recompute the recipe deterministically'
      : styleName
        ? `${styleName}: stops follow the style's range`
        : 'Moves recompute the recipe deterministically';
  }
  body.innerHTML = order.map(hsRowHtml).join('')
    + (hazy ? `<details><summary>Advanced</summary>${hsRowHtml('dryHopContact')}</details>` : '');
  for (const key2 of Object.keys(hsDefs)) {
    const input = $('hs-' + key2);
    if (!input) continue;
    input.addEventListener('input', () => hsShowLabel(key2));
    input.addEventListener('change', () => applyHopSlider(key2));
  }
}

function hsShowLabel(key) {
  const def = hsDefs[key];
  const i = Number($('hs-' + key).value);
  const stop = def.stops[i] || def.stops[0];
  // Temperature subs are stored as °F numbers and formatted here, so the label follows
  // the display-unit preference (a static 'mash 152°F' string couldn't).
  const sub = stop.subTempF != null ? `mash ${fmtTemp(stop.subTempF)}` : stop.sub;
  $('hs-' + key + '-val').innerHTML = `${stop.label}${sub ? ` <small>· ${sub}</small>` : ''}`;
  // Paint from the same value the label just read, so colour, fill and text can never
  // disagree. Runs on every `input` event too, so the drag is live.
  const bar = $('hs-' + key + '-bar');
  if (bar) {
    bar.style.setProperty('--hs-a', stop.c[0]);
    bar.style.setProperty('--hs-b', stop.c[1]);
    bar.style.setProperty('--hs-pos', hsStopPos(i, def.stops.length));
  }
}

const hsNearestIdx = (vals, x) => {
  let bi = 0, bd = Infinity;
  vals.forEach((v, i) => { const d = Math.abs(v - x); if (d < bd) { bd = d; bi = i; } });
  return bi;
};

/** Set every control to the stop nearest the recipe on screen (no request fired). */
function hsInitFromReport() {
  const r = currentRecipe || {};
  if (hsPanelKey !== 'hazy') {
    // Universal rows seat off the benchmarks the panel already shows.
    const b = currentReport?.benchmarks || {};
    const cur = { strength: b.abv, bitterness: b.ibu, color: b.srm, finish: r.mashTemp ?? 152 };
    for (const key of hsOrderActive) {
      const def = hsDefs[key];
      const input = $('hs-' + key);
      if (!def || !input) continue;
      const vals = def.stops.map((s) => (s.subTempF != null ? s.subTempF : s.v));
      input.value = String(hsNearestIdx(vals, Number(cur[key] ?? vals[0])));
      hsShowLabel(key);
    }
    return;
  }
  const hops = r.hopSchedule || [];
  const gal = r.batchSize > 0 ? r.batchSize : 5;
  const isDry = (h) => h.method === 'dryhop' || h.time === -1;
  const isBitter = (h) => !isDry(h) && (!h.method || h.method === 'boil') && (h.time == null || h.time >= 30);
  const oz = (list) => list.reduce((s, h) => s + (h.weight || 0), 0);
  const total = oz(hops);
  const aromaOz = oz(hops.filter((h) => !isBitter(h)));
  const dryOz = oz(hops.filter(isDry));
  const dryDays = hops.filter(isDry).map((h) => h.dryHopDays || 3);
  const stages = new Set(hops.filter(isDry).map((h) => h.dryHopStage).filter(Boolean));
  const idx = {
    strength: hsNearestIdx([6, 7, 8, 9], currentReport?.benchmarks?.abv ?? 6.5),
    intensity: hsNearestIdx([1.5, 2, 2.5, 3.5], gal ? total / gal : 2),
    bitterness: hsNearestIdx([25, 35, 45, 55], currentReport?.benchmarks?.ibu ?? 35),
    emphasis: hsNearestIdx([40, 45, 55, 65], aromaOz > 0 ? (dryOz / aromaOz) * 100 : 50),
    dryHopSchedule: stages.size > 1 ? 1 : stages.has('active fermentation') ? 0 : stages.has('near terminal gravity') ? 2 : 1,
    dryHopContact: hsNearestIdx([2, 3, 4], dryDays.length ? dryDays.reduce((a, b) => a + b, 0) / dryDays.length : 3),
    finish: hsNearestIdx([157, 154, 152, 149], r.mashTemp ?? 152),
  };
  for (const [key, i] of Object.entries(idx)) {
    const input = $('hs-' + key);
    if (input) { input.value = String(i); hsShowLabel(key); }
  }
}

function hopSliderStyleActive() {
  // The panel follows the RECIPE on screen: the report's style ("21C. Hazy IPA") is
  // what the recipe was actually computed as. The header dropdown only decides when
  // there's no styled report yet — flipping it alone doesn't restyle the recipe.
  const rs = currentReport?.style?.style;
  return rs ? rs.startsWith('21C') : (styleSelect.value || '').startsWith('21C');
}

/**
 * The original recipe, kept safe from the sliders.
 *
 * The slider stops are TEMPLATES: the panel seats each control at the stop nearest the
 * recipe on screen, but the first move rebuilds the hop schedule onto the template grid,
 * and the configuration the brewer landed on — the AI's build, a loaded save, their own
 * hand edits — was gone with no way back (Kyle, 2026-07-30). So the panel snapshots the
 * recipe whenever it changes through any NON-slider path, and a Reset control restores it.
 *
 * The baseline is "the latest non-slider state", not "before all sliders ever": if the
 * brewer slides, then hand-edits a grain in the modal, the combined state becomes the new
 * original — resetting past their own deliberate edit would be a worse surprise.
 */
let hsOriginalRecipeJson = null; // the snapshot (names + amounts, resolved recipe)
let hsLastSliderRecipeJson = ''; // what the last slider apply produced, to tell renders apart
let hsDirty = false;             // sliders have changed the recipe since the snapshot

function hsSyncResetBtn() {
  // Always present, disabled until the sliders have diverged from the snapshot. It started
  // out hidden-until-dirty, and Kyle looked for it before moving a slider and reasonably
  // concluded it didn't exist (2026-07-30) — a control someone might come back for has to
  // be visible before it's needed.
  const btn = $('hsResetBtn');
  if (!btn) return;
  btn.hidden = false;
  btn.disabled = !hsDirty;
  btn.style.opacity = hsDirty ? '' : '0.45';
  btn.style.cursor = hsDirty ? '' : 'default';
  btn.style.textDecoration = hsDirty ? '' : 'none';
  btn.title = hsDirty
    ? 'Put the recipe back to how it was before the slider changes'
    : 'Becomes available once a slider changes the recipe';
}

function hsMaybeCaptureOriginal() {
  if (!currentRecipe) return;
  const cur = JSON.stringify(currentRecipe);
  // A re-render of the same slider-produced recipe (a rename, a mode-UI reflow) is not a
  // new baseline — keep the original the brewer can still reset to.
  if (hsDirty && cur === hsLastSliderRecipeJson) return;
  hsOriginalRecipeJson = cur;
  hsDirty = false;
  hsSyncResetBtn();
}

/** Put the recipe back to the snapshot, recalculated on the CURRENT rig and mode. */
async function hsResetToOriginal() {
  if (!hsOriginalRecipeJson || !currentRecipe) return;
  const pane = $('hopSliderPane');
  const seq = ++hsSeq; // participates in the same guard as slider moves — no races
  pane?.classList.add('is-busy');
  try {
    // Through /api/calculate rather than a cached report: the equipment, batch or mode may
    // have changed since the snapshot, and the restored recipe must be computed on today's
    // settings, not yesterday's.
    const res = await fetch(API + '/api/calculate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        recipe: JSON.parse(hsOriginalRecipeJson),
        equipmentProfile: equipmentPayload() || currentReport?.equipmentProfile || undefined,
        styleName: styleSelect.value || undefined,
        mode: currentMode,
        customIngredients: customIngredientsPayload(),
      }),
    });
    if (seq !== hsSeq) return;
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Server returned ${res.status}`);
    const report = await res.json();
    report.outOfStock = currentReport?.outOfStock || [];
    report.recipeName = currentReport?.recipeName;
    if (currentReport?.conversion) report.conversion = currentReport.conversion;
    renderRecipe(report); // NOT hsApplying: the sliders re-seat on the original and the snapshot refreshes
    persistSession();
    hopSliderNote('Recipe restored to how it was before the slider changes.');
  } catch (err) {
    if (seq === hsSeq) hopSliderNote(`Could not restore the original: ${err.message || 'recalculation failed.'}`);
  } finally {
    if (seq === hsSeq) pane?.classList.remove('is-busy');
  }
}

let hsApplying = false;
function setHopSliderVisible(show) {
  const pane = $('hopSliderPane');
  if (!pane) return;
  pane.hidden = !show;
  document.querySelector('#beer-builder-root .panes, .panes')?.classList.toggle('has-hopslider', show);
}

function syncHopSliderPane() {
  if (!$('hopSliderPane')) return;
  // Every styled recipe gets the panel now — 21C keeps its expanded hop set, every
  // other style gets the universal Strength/Bitterness/Color/Finish set.
  const show = Boolean(currentRecipe);
  setHopSliderVisible(show);
  if (show) hsEnsureRows();
  // Re-assert the chat/slider split every time the panel comes or goes — appearing is
  // exactly the moment it used to steal the chat's height.
  if (typeof fitHopSliderPane === 'function') fitHopSliderPane();
  // A slider-driven re-render keeps the brewer's stops; anything else re-reads the recipe
  // AND becomes the new baseline the Reset control restores to.
  if (show && !hsApplying) {
    hsInitFromReport();
    hsMaybeCaptureOriginal();
  }
}

/**
 * Take the panel down now, without waiting for a render. Used when the recipe it belongs
 * to is about to be replaced or cleared, so its stops never linger over a different beer.
 */
function resetHopSliderPane() {
  setHopSliderVisible(false);
}

/**
 * Slider feedback, written into the chat as a quiet note.
 *
 * Consecutive slider moves REPLACE the note rather than stacking: tuning six sliders in a
 * row would otherwise bury the conversation under six near-identical lines. Once the brewer
 * says something else, the next move starts a fresh note, so the chat still reads in order.
 *
 * Like every other note (sign-in nudge, auto-detected style), this stays out of `history` —
 * it's for the brewer, not the model, which sees the recipe itself on the next turn.
 */
function hopSliderNote(text) {
  $('hint')?.remove();
  const last = messagesEl.lastElementChild;
  if (last && last.classList.contains('note') && last.dataset.hopslider) {
    last.textContent = text;
    messagesEl.scrollTop = messagesEl.scrollHeight;
    return;
  }
  const el = addNote(text);
  if (el) el.dataset.hopslider = '1';
}

let hsSeq = 0;
async function applyHopSlider(changedKey) {
  if (!currentRecipe) return;
  const group = hsDefs[changedKey].group;
  const stopOf = (key) => hsDefs[key].stops[Number($('hs-' + key).value)]?.v;
  const sliders = group === 'strength' ? { strengthAbv: stopOf('strength') }
    : group === 'finish' ? { finish: stopOf('finish') }
    : group === 'bitter' ? { bitternessIbu: stopOf('bitterness') }
    : group === 'color' ? { colorSrm: stopOf('color') }
    : { hop: {
        intensity: stopOf('intensity'),
        bitterness: stopOf('bitterness'),
        emphasis: stopOf('emphasis'),
        dryHopSchedule: stopOf('dryHopSchedule'),
        dryHopContact: stopOf('dryHopContact'),
        changed: changedKey,
      } };
  const pane = $('hopSliderPane');
  const seq = ++hsSeq;
  pane?.classList.add('is-busy');
  try {
    const res = await fetch(API + '/api/hop-sliders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        recipe: currentRecipe,
        sliders,
        equipmentProfile: equipmentPayload() || currentReport?.equipmentProfile || undefined,
        styleName: styleSelect.value || undefined,
        mode: currentMode,
        customIngredients: customIngredientsPayload(),
      }),
    });
    if (seq !== hsSeq) return; // a newer move superseded this one
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Server returned ${res.status}`);
    const report = await res.json();
    // Same carry-forward as the detail editor: /api/hop-sliders doesn't run the
    // in-stock check, and the recipe keeps its saved name.
    report.outOfStock = currentReport?.outOfStock || [];
    report.recipeName = currentReport?.recipeName;
    if (currentReport?.conversion) report.conversion = currentReport.conversion;
    hsApplying = true;
    try { renderRecipe(report); } finally { hsApplying = false; }
    // The sliders now own the difference from the snapshot — offer the way back.
    hsLastSliderRecipeJson = JSON.stringify(currentRecipe);
    hsDirty = true;
    hsSyncResetBtn();
    persistSession();
    // Straight into the chat, not a toast: the toast landed on top of the very panel the
    // brewer was working in, and these notes are worth reading rather than dodging
    // (Kyle, 2026-07-29). Every note the transform returned goes in — the summary line and,
    // when it applies, the "your late additions already exceed that IBU target" warning.
    hopSliderNote(report.hopSliders?.notes?.length ? report.hopSliders.notes.join('\n') : 'Sliders applied.');
  } catch (err) {
    if (seq === hsSeq) {
      hopSliderNote(`That slider didn't apply: ${err.message || 'the recipe could not be recalculated.'}`);
      hsInitFromReport(); // snap the controls back to the recipe that's actually on screen
    }
  } finally {
    if (seq === hsSeq) pane?.classList.remove('is-busy');
  }
}

buildHopSliderPane();
// The report link must exist even if /api/options never answers — that failure is exactly
// when someone wants to report something.
injectReportBanner();

// ── resize grips on the chat and recipe panes ────────────────────────
// Both panes are sized to fit the viewport, which is a guess about what the brewer wants
// to look at. A grip on the bottom edge lets them give the chat more room to read, or the
// recipe more room to scan, and the choice sticks for the session.
const PANE_HEIGHTS_KEY = 'bb:paneHeights:v1';

function loadPaneHeights() {
  try { return JSON.parse(localStorage.getItem(PANE_HEIGHTS_KEY) || '{}'); } catch { return {}; }
}
function savePaneHeights(h) {
  try { localStorage.setItem(PANE_HEIGHTS_KEY, JSON.stringify(h)); } catch { /* private mode */ }
}

/**
 * Chat ⇄ hop-slider split.
 *
 * With the slider showing, the left column is a two-row grid: chat on top (1fr), slider
 * below (auto, sized by its own height). The grid total is pinned to the viewport and
 * clipped, so a slider height that doesn't fit comes ENTIRELY out of the chat — a 302px
 * default already squeezed the chat to 150px at a 720px viewport, and dragging the slider
 * taller crushed the chat to 2px, taking its composer and grip with it (Kyle, 2026-07-29).
 *
 * So the slider's height is not free: it is clamped to what's left after the chat keeps a
 * usable minimum, re-clamped whenever the panel appears or the viewport changes, and a
 * stale saved height can never collapse the chat again. The slider's body scrolls, so a
 * shorter slider loses nothing.
 */
const CHAT_MIN_H = 220;      // messages + composer + grip: below this the chat is unusable
const SLIDER_MIN_H = 150;    // header + a couple of rows; the body scrolls for the rest
const SLIDER_DEFAULT_H = 302;
/** Chat height once the window is short enough that the page scrolls instead. */
const SCROLL_MODE_CHAT_H = 340;

/**
 * Wide desktop lays chat, sliders and recipe out as three side-by-side columns, so the
 * chat/slider split above simply doesn't exist there. The breakpoint is read from the
 * stylesheet's own query rather than re-declared as a pixel number, so CSS and JS can
 * never disagree about which layout is on screen — keep this string in step with the
 * `@media (min-width: 1240px)` block in styles.css.
 */
const THREE_COL_MQ = '(min-width: 1240px)';
function hopSliderThreeCol() {
  return typeof window.matchMedia === 'function' && window.matchMedia(THREE_COL_MQ).matches;
}

/** The room the two rows share, and the biggest the slider may be inside it. */
function hopSliderLimits() {
  const panes = document.querySelector('#beer-builder-root .panes') || document.querySelector('.panes');
  if (!panes) return null;
  const cs = getComputedStyle(panes);
  const available = panes.clientHeight
    - parseFloat(cs.paddingTop || '0') - parseFloat(cs.paddingBottom || '0')
    - parseFloat(cs.rowGap || cs.gap || '0');
  return { panes, available, max: Math.max(SLIDER_MIN_H, available - CHAT_MIN_H) };
}

/** Size the slider to `desired`, clamped so the chat keeps its floor. Returns the height used. */
function setHopSliderHeight(desired, persist) {
  const pane = $('hopSliderPane');
  const lim = hopSliderLimits();
  if (!pane || !lim) return 0;
  const h = Math.round(Math.max(SLIDER_MIN_H, Math.min(desired, lim.max)));
  pane.style.height = `${h}px`;
  if (persist) {
    const heights = loadPaneHeights();
    heights.hops = h;
    savePaneHeights(heights);
  }
  return h;
}

/**
 * Re-assert the split. Called when the panel appears and on every resize, so a height
 * saved at one viewport can't wreck the layout at another. Mobile is a plain stacked
 * column (the panel is auto-height there), so it's left alone.
 */
function fitHopSliderPane() {
  const pane = $('hopSliderPane');
  const chat = document.querySelector('#beer-builder-root .chat') || document.querySelector('.chat');
  if (!pane || !chat) return;
  if (window.innerWidth <= 920 || pane.hidden) {
    // Stacked layout, or no panel: the slider's height is CSS's business again, and the
    // chat goes back to whatever the brewer had dragged it to.
    pane.style.removeProperty('height');
    const saved = loadPaneHeights();
    if (pane.hidden && saved.chat) chat.style.height = `${saved.chat}px`;
    return;
  }
  const app = document.querySelector('#beer-builder-root .app') || document.querySelector('.app');
  // Three-column layout: chat, sliders and recipe stand side by side, each filling the row.
  // There is no split to divide, so drop the inline height the two-row layout wrote on the
  // slider, hand the chat back whatever the brewer had dragged it to, and clear the
  // short-window scroll mode — that is a two-row concept, and leaving it on here would
  // unpin the app's height for no reason.
  if (hopSliderThreeCol()) {
    pane.style.removeProperty('height');
    app?.classList.remove('app--scroll');
    const saved = loadPaneHeights();
    if (saved.chat) chat.style.height = `${saved.chat}px`;
    else chat.style.removeProperty('height');
    return;
  }
  // Two-row layout: the chat is the flexible row, so an inline height on it would fight
  // the grid (and overflow into the clipped area). The split lives on the slider instead.
  chat.style.removeProperty('height');
  // MEASURE WITH THE CLASS OFF. `app--scroll` releases the app's fixed height, which
  // changes the very number this decision is based on — leaving it on while measuring made
  // the check flip-flop between "scrolling" and "not" on consecutive resizes.
  if (app) app.classList.remove('app--scroll');
  const lim = hopSliderLimits();
  const saved = loadPaneHeights();
  // Nothing sane to divide: the builder is measuring zero (hidden container, background
  // tab, mid-layout). Leave every height alone — the next fit, on resize or when it becomes
  // visible, does the real work. Writing a height from a bogus measurement is how a panel
  // ends up 766px tall in a 0px viewport.
  if (!lim || !Number.isFinite(lim.available) || lim.available < 200) return;

  // In a window too short to hold a usable chat AND a usable slider, stop dividing space
  // that isn't there: pin both to their minimums, let the app outgrow the viewport, and let
  // the page scroll — what the stacked mobile layout already does, and what Kyle asked for
  // after the slider ate the left column on a short desktop window.
  if (lim && lim.available < CHAT_MIN_H + SLIDER_MIN_H) {
    if (app) app.classList.add('app--scroll');
    // Explicit heights, because with the app unpinned the chat's flex:1 would otherwise
    // grow to fill a page it is itself defining (it ballooned to 1150px on a 600px window).
    // COMFORTABLE, not minimum: once the page scrolls there's no space to fight over, so
    // pinning both to their floors would just look mean next to a full-height recipe.
    chat.style.height = `${SCROLL_MODE_CHAT_H}px`;
    pane.style.height = `${Math.max(SLIDER_MIN_H, saved.hops || SLIDER_DEFAULT_H)}px`;
    return;
  }
  // Default to a SHARE of the column rather than a flat 302px. On a 1080px-tall window
  // 302px is a comfortable panel; on an 800px one it left the chat at its bare minimum,
  // which is what "it took up almost all the vertical real estate" was. The panel's body
  // scrolls, so the smaller default costs nothing but a scroll on short screens.
  const fallback = lim ? Math.min(SLIDER_DEFAULT_H, Math.round(lim.available * 0.45)) : SLIDER_DEFAULT_H;
  setHopSliderHeight(saved.hops || fallback, false);
}

function injectPaneGrips() {
  const saved = loadPaneHeights();
  for (const [selector, key] of [['.chat', 'chat'], ['.recipe', 'recipe'], ['.hopslider', 'hops']]) {
    const pane = document.querySelector(`#beer-builder-root ${selector}`) || document.querySelector(selector);
    if (!pane || pane.dataset.gripped) continue;
    pane.dataset.gripped = '1';
    // In the three-column layout the slider fills its own column, so a height saved from
    // the two-row split would shrink it — and would do it here, before the first
    // fitHopSliderPane gets to clear it.
    if (saved[key] && !(key === 'hops' && hopSliderThreeCol())) pane.style.height = `${saved[key]}px`;

    const grip = document.createElement('div');
    grip.className = 'pane-grip';
    grip.setAttribute('role', 'separator');
    grip.setAttribute('aria-orientation', 'horizontal');
    grip.setAttribute('aria-label', 'Drag to resize this panel');
    grip.setAttribute('tabindex', '0');
    grip.innerHTML = '<span></span><span></span><span></span>';
    pane.appendChild(grip);

    /**
     * While the slider is showing, chat and slider are two halves of one split, so BOTH
     * grips move the same divider: the slider's own grip sizes it directly, and the chat's
     * grip sizes it inversely (drag the chat down, the slider gives way). Either way the
     * clamp in setHopSliderHeight is what stops the chat collapsing.
     */
    const splitLive = () => {
      const sliderPane = $('hopSliderPane');
      return sliderPane && !sliderPane.hidden && window.innerWidth > 920 && !hopSliderThreeCol();
    };
    let startY = 0;
    let startH = 0;
    const applyDrag = (dy, persist) => {
      if (splitLive() && (key === 'hops' || key === 'chat')) {
        setHopSliderHeight(key === 'hops' ? startH + dy : startH - dy, persist);
        return;
      }
      // Floor keeps a pane from collapsing to nothing; the viewport cap keeps the grip
      // reachable without scrolling away to find it.
      const h = Math.max(160, Math.min(window.innerHeight * 1.5, startH + dy));
      pane.style.height = `${Math.round(h)}px`;
      if (persist) {
        const heights = loadPaneHeights();
        heights[key] = Math.round(h);
        savePaneHeights(heights);
      }
    };
    const onMove = (e) => {
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      applyDrag(y - startY, false);
    };
    const onEnd = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onEnd);
      document.body.classList.remove('is-resizing');
      const heights = loadPaneHeights();
      if (splitLive() && (key === 'hops' || key === 'chat')) {
        heights.hops = Math.round($('hopSliderPane').getBoundingClientRect().height);
      } else {
        heights[key] = Math.round(pane.getBoundingClientRect().height);
      }
      savePaneHeights(heights);
    };
    grip.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      startY = e.clientY;
      startH = pane.getBoundingClientRect().height;
      document.body.classList.add('is-resizing');
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onEnd);
    });
    // Keyboard equivalent, since a drag handle is otherwise unreachable without a pointer.
    grip.addEventListener('keydown', (e) => {
      const step = e.key === 'ArrowUp' ? -40 : e.key === 'ArrowDown' ? 40 : 0;
      if (!step) return;
      e.preventDefault();
      startH = pane.getBoundingClientRect().height;
      applyDrag(step, true);
    });
  }
}
injectPaneGrips();

// ── searchable dropdowns ─────────────────────────────────────────────
// The Style and Equipment pickers hold ~100 styles and ~70 rigs; a native dropdown
// that long can only be scrolled. Each picker keeps its native <select> — every
// .value read/write, session restore, and 'change' listener works untouched — but
// opening it now shows a floating type-to-filter panel instead of the native list.
// The panel is rebuilt from the select's live options each time it opens, so the
// async-loaded lists, the session profile, and custom-profile optgroups always
// appear. Multi-word filters match all words in any order ("ipa english" finds
// English IPA). Styles are injected from here so no theme CSS asset has to change.
// Palette is hardcoded per scheme (light values + a prefers-color-scheme:dark block)
// rather than var()-based: the panel is appended to document.body, which on the
// storefront is OUTSIDE #beer-builder-root — the builder's CSS variables don't reach
// it there, so var() would silently fall back. The dark values mirror the dark tokens
// in styles.css; keep the two in sync.
// The search box declares width:auto because the theme sets `input,select,textarea
// {width:100%}` — that resolves against the panel's content box, so the box came out
// exactly as wide as the panel and its 8px side margins had nowhere to go: the border
// landed on the panel's own border on both edges. width:auto hands sizing back to the
// flex column's stretch, which sizes to the container MINUS the margins.
const COMBO_CSS = `
.bb-combo{position:fixed;z-index:2147483000;display:flex;flex-direction:column;overflow:hidden;background:#fff;color:#131313;border:1px solid #d9d9d9;border-radius:10px;box-shadow:0 12px 32px rgba(0,0,0,.18);font-size:14px}
.bb-combo__search{width:auto;box-sizing:border-box;margin:8px 8px 4px;padding:8px 10px;border:1px solid #c9c9c9;border-radius:8px;background:#fff;color:#131313;font-size:16px;outline:none}
.bb-combo__search:focus{border-color:#8a8a8a}
.bb-combo__list{overflow-y:auto;padding:4px 6px 8px;-webkit-overflow-scrolling:touch}
.bb-combo__group{font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#8a8a8a;padding:8px 6px 2px}
.bb-combo__item{padding:7px 10px;border-radius:7px;cursor:pointer}
.bb-combo__item:hover,.bb-combo__item.is-active{background:#ececec}
.bb-combo__item.is-current{font-weight:700}
.bb-combo__empty{padding:10px;color:#8a8a8a}
@media (prefers-color-scheme:dark){
.bb-combo{background:#252c2c;color:#eef1f1;border-color:#4b5455;box-shadow:0 12px 32px rgba(0,0,0,.55)}
.bb-combo__search{background:#1b2121;color:#eef1f1;border-color:#4b5455}
.bb-combo__search:focus{border-color:#8f9a9b}
.bb-combo__group{color:#9aa4a5}
.bb-combo__item:hover,.bb-combo__item.is-active{background:#333c3c}
.bb-combo__empty{color:#9aa4a5}
}`;

// iOS Safari opens the native picker whenever the <select> takes FOCUS — a
// programmatic sel.focus() does it, and so does a tap on its <label for=…>. Neither
// path hit-tests the select, so pointer-events:none cannot block them: the brewer
// tapped a row in the panel, the panel closed, focus went back to the select and the
// OS wheel appeared on top of it. That's the "two menus, formatted differently" on
// phones. Focus is only worth restoring where a hardware pointer implies a hardware
// keyboard; on touch there is no keyboard flow to preserve.
const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

function enhanceSearchSelect(sel) {
  if (!sel || sel.dataset.searchable) return;
  sel.dataset.searchable = '1';
  if (!document.getElementById('bbComboCss')) {
    const st = document.createElement('style');
    st.id = 'bbComboCss';
    st.textContent = COMBO_CSS;
    document.head.appendChild(st);
  }
  let panel = null, searchEl = null, listEl = null, items = [], active = -1, closedAt = 0;

  const close = () => {
    if (!panel) return;
    panel.remove();
    panel = null;
    closedAt = Date.now();
    document.removeEventListener('mousedown', onOutside, true);
    window.removeEventListener('resize', onMove);
    window.removeEventListener('scroll', onMove, true);
  };
  const onOutside = (e) => { if (panel && !panel.contains(e.target) && e.target !== sel) close(); };
  // Follow the select on scroll/resize rather than closing. Closing here broke phones
  // outright: the on-screen keyboard's arrival scrolls/resizes the page, so the panel
  // died the instant it opened ("a keyboard shows up and then disappears").
  const onMove = (e) => { if (panel && !(e.target instanceof Node && panel.contains(e.target))) place(); };

  // Position under the select (or above it when the viewport space below is tight).
  const place = () => {
    const r = sel.getBoundingClientRect();
    const width = Math.max(r.width, 240);
    panel.style.minWidth = `${width}px`;
    panel.style.maxWidth = `${Math.min(420, window.innerWidth - 16)}px`;
    panel.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - width - 8))}px`;
    panel.style.top = panel.style.bottom = '';
    const below = window.innerHeight - r.bottom;
    if (below < 220 && r.top > below) {
      panel.style.bottom = `${window.innerHeight - r.top + 4}px`;
      panel.style.maxHeight = `${Math.min(340, r.top - 12)}px`;
    } else {
      panel.style.top = `${r.bottom + 4}px`;
      panel.style.maxHeight = `${Math.min(340, below - 12)}px`;
    }
  };

  // The select's CURRENT options, optgroup labels preserved. The empty-value
  // placeholder row stays listed (as a "clear" action) until typing starts.
  const collect = () => {
    const out = [];
    for (const node of sel.children) {
      if (node.tagName === 'OPTGROUP') {
        out.push({ group: node.label || '' });
        for (const o of node.querySelectorAll('option')) out.push({ value: o.value, label: o.textContent });
      } else if (node.tagName === 'OPTION') {
        out.push({ value: node.value, label: node.textContent });
      }
    }
    return out;
  };

  const render = () => {
    const words = searchEl.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
    items = [];
    let html = '';
    let pendingGroup = null;
    for (const it of collect()) {
      if (it.group != null) { pendingGroup = it.group; continue; }
      if (!it.value && words.length) continue;
      if (words.length && !words.every((w) => it.label.toLowerCase().includes(w))) continue;
      if (pendingGroup != null) {
        html += `<div class="bb-combo__group">${escapeHtml(pendingGroup)}</div>`;
        pendingGroup = null;
      }
      const idx = items.length;
      items.push(it);
      html += `<div class="bb-combo__item${it.value === sel.value ? ' is-current' : ''}${idx === active ? ' is-active' : ''}" data-i="${idx}">${escapeHtml(it.label)}</div>`;
    }
    listEl.innerHTML = html || '<div class="bb-combo__empty">No matches.</div>';
  };
  const scrollActive = () => listEl.querySelector('.is-active')?.scrollIntoView({ block: 'nearest' });

  const pick = (i) => {
    const it = items[i];
    if (!it) return;
    close();
    if (sel.value !== it.value) {
      sel.value = it.value;
      sel.dispatchEvent(new Event('change', { bubbles: true })); // same as a native pick
    }
    if (finePointer()) sel.focus(); // on touch this is what summoned the iOS wheel
  };

  const open = (seed) => {
    if (panel) return;
    active = -1;
    panel = document.createElement('div');
    panel.className = 'bb-combo';
    panel.innerHTML =
      '<input class="bb-combo__search" type="text" placeholder="Type to search…" autocapitalize="off" autocomplete="off" spellcheck="false">' +
      '<div class="bb-combo__list" role="listbox"></div>';
    document.body.appendChild(panel);
    place();
    searchEl = panel.querySelector('.bb-combo__search');
    listEl = panel.querySelector('.bb-combo__list');
    if (seed) searchEl.value = seed;
    render();
    // Autofocus only where a hardware pointer implies a hardware keyboard. On phones
    // it summoned the on-screen keyboard over the list; there the box focuses on tap.
    if (finePointer()) searchEl.focus();
    searchEl.addEventListener('input', () => { active = -1; render(); });
    searchEl.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { close(); if (finePointer()) sel.focus(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); active = Math.min(active + 1, items.length - 1); render(); scrollActive(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); active = Math.max(active - 1, 0); render(); scrollActive(); }
      else if (e.key === 'Enter') { e.preventDefault(); pick(active >= 0 ? active : items.length === 1 ? 0 : -1); }
      else if (e.key === 'Tab') close();
    });
    listEl.addEventListener('mousedown', (e) => {
      const row = e.target.closest('.bb-combo__item');
      if (row) { e.preventDefault(); pick(Number(row.dataset.i)); }
    });
    document.addEventListener('mousedown', onOutside, true);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
  };

  // Safari (macOS + iOS) opens the native <select> menu even when mousedown is
  // preventDefault-ed — the panel appeared UNDER the OS dark-mode picker. So the
  // select is made mouse-inert entirely (native menu physically can't open) and the
  // click is caught at the document level by hit-testing the select's box. Applied
  // from JS, not CSS, so if scripting ever fails the native select still works.
  sel.style.pointerEvents = 'none';
  const onHit = (e) => {
    if (!sel.isConnected) { document.removeEventListener('mousedown', onHit, true); close(); return; }
    if (e.button) return; // left button only
    const r = sel.getBoundingClientRect();
    if (!r.width || e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return;
    // The box test is coordinates only — it knows nothing about what is stacked ABOVE the
    // select. The topbar stays put behind an open modal, so taps aimed at the equipment
    // editor's own fields fell inside the Style/Equipment box and opened this panel over
    // the dialog (z-index 2147483000 beats everything), while the preventDefault below ate
    // the tap so the number field never took focus: "brings up the dropdown for style
    // choice instead of being able to add a value" (bug report #15, 2026-08-03). Confirm
    // the point genuinely belongs to the select before claiming the event. The select is
    // pointer-events:none, so a real hit resolves to one of its ancestors (.field /
    // .equip-picker); anything painted over it resolves elsewhere. body/html are excluded
    // because they contain the select trivially.
    const top = document.elementFromPoint(e.clientX, e.clientY);
    if (!top || top === document.body || top === document.documentElement) return;
    if (top !== sel && !top.contains(sel)) return;
    e.preventDefault();
    if (panel) close();
    else open();
  };
  document.addEventListener('mousedown', onHit, true);

  // The other focus route: a <label for=…> forwards its activation to the control no
  // matter what pointer-events says, so tapping "Style" or "Equipment" opened the OS
  // picker too. Cancel that and show the panel instead. The guard covers the tap that
  // lands on the label while the panel is open — its mousedown already closed the
  // panel via onOutside, and reopening on the same tap would make it un-closable.
  // (The Equipment label also carries the "Manage" button; leave real controls alone.)
  // A WRAPPING <label> associates implicitly — no for= needed — so it forwards taps
  // the same way. Cover both, or a select wrapped in a label shows the OS picker on
  // top of the panel: the "two menus in different styles" bug, exactly as it hit the
  // water editor's Profile/Target pickers (Kyle, 8/8).
  const labels = new Set(sel.id ? document.querySelectorAll(`label[for="${CSS.escape(sel.id)}"]`) : []);
  const wrapping = sel.closest('label');
  if (wrapping) labels.add(wrapping);
  for (const lab of labels) {
    if (lab.dataset.comboLabel) continue;
    lab.dataset.comboLabel = '1';
    lab.addEventListener('click', (e) => {
      if (e.target.closest('button, a, input')) return;
      e.preventDefault();
      if (!panel && Date.now() - closedAt > 300) open();
    });
  }

  sel.addEventListener('keydown', (e) => {
    if (panel) return; // the panel's search box has focus; keys never land here
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); open(); }
    else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) { e.preventDefault(); open(e.key); }
  });
}
enhanceSearchSelect(styleSelect);
enhanceSearchSelect(equipSelect);

// ── display-unit toggles ─────────────────────────────────────────────
// Four independent segments (water gal/L · hops oz/g · grain lb/kg · temp °F/°C)
// injected under the recipe metrics. JS-injected like the Start-over button, so no
// theme edit.
const UNITS_CSS = `
.bb-units__btns{display:inline-flex;border:1px solid #d0d0d0;border-radius:6px;overflow:hidden}
.bb-units__btns button{border:0;background:#fff;color:#555;font:inherit;padding:2px 8px;cursor:pointer}
.bb-units__btns button.is-on{background:#2d2d2d;color:#fff}
#batchUnitSeg,#batchUnitSeg button,#tempUnitSeg,#tempUnitSeg button{pointer-events:auto !important}
/* The header Units + Temp fields: pills sized like the other header controls so they
   sit on the same baseline as the batch input beside them. */
.field--temp,.field--bunit{flex:none}
#batchUnitSeg,#tempUnitSeg{height:var(--bb-ctl-h, 38px);border-radius:9px;border-color:var(--line-strong, #d0d0d0);align-items:stretch;box-sizing:border-box}
#batchUnitSeg button,#tempUnitSeg button{padding:0 12px;font-size:12.5px}
/* The equipment editor's own gal/L pill (bug #30), a row above the field grid. */
.equip-units{display:flex;align-items:center;gap:10px;margin:0 0 12px}
.equip-units__label{font-size:13px;color:#555}
#equipUnitSeg,#equipUnitSeg button{pointer-events:auto !important}
#equipUnitSeg button{padding:4px 12px;font-size:12.5px;text-transform:none;letter-spacing:0;min-height:0;width:auto;appearance:none;-webkit-appearance:none}
@media (prefers-color-scheme:dark){.equip-units__label{color:#b9c2c3}}
/* The header Efficiency field: narrow, fixed, styled by the shared .batch-input rules. */
.field--eff{width:92px;flex:none}
.field--eff input{padding-right:26px}
.field--eff input.is-override{border-color:var(--primary, #14161a);font-weight:600}
/* The hops oz/g toggle sits BESIDE the Hop-schedule heading button, not inside it
   (inside, the heading's border wrapped the toggle and the two were hard to tell
   apart on mobile). A flex wrapper holds heading + toggle on one row. */
.bb-sec-head{display:flex;align-items:stretch;gap:8px;margin:0 0 11px}
#beer-builder-root .bb-sec-head .section-link,.bb-sec-head .section-link{flex:1;width:auto;margin:0}
.bb-units__btns--head{pointer-events:auto;flex:none;align-items:stretch}
.bb-units__btns--head button{padding:0 11px;font-size:11.5px;text-transform:none;letter-spacing:0}
@media (prefers-color-scheme:dark){
.bb-units__btns{border-color:#4b5455}
.bb-units__btns button{background:#252c2c;color:#b9c2c3}
.bb-units__btns button.is-on{background:#e8ebeb;color:#171b1c}
}`;

function refreshUnitsUI() {
  try { localStorage.setItem('bb:units', JSON.stringify(displayUnits)); } catch (e) { /* private mode */ }
  renderSectionUnitSegs();
  syncBatchUnitUI();
  if (currentReport) renderRecipe(currentReport);          // amounts + process line
  if (!$('detailModal')?.hidden) renderDetailRows();       // open editor follows too
}

function setDisplayUnit(key, value) {
  if (displayUnits[key] === value) return;
  displayUnits[key] = value;
  refreshUnitsUI();
}

/** The header gal/L switch is the master: picking L flips the WHOLE view metric
 *  (hops → g, grain → kg, temps → °C) so a metric brewer sets one thing; picking gal
 *  flips it all back. The per-category toggles under the metrics still allow mixing. */
function setAllUnits(metric) {
  displayUnits.water = metric ? 'l' : 'gal';
  displayUnits.hops = metric ? 'g' : 'oz';
  displayUnits.grain = metric ? 'kg' : 'lb';
  displayUnits.temp = metric ? 'c' : 'f';
  refreshUnitsUI();
}

/** Per-section unit toggles: hops oz/g and grain lb/kg each sit NEXT TO their section's
 *  heading button in a flex wrapper — separate controls, so a tap on one can't land on
 *  the other. Water and temp have no section of their own; they live in the topbar. */
function renderSectionUnitSegs() {
  const defs = [
    { hostId: 'hops', segId: 'hopUnitSeg', key: 'hops', opts: [['oz', 'oz'], ['g', 'g']] },
    { hostId: 'fermentables', segId: 'grainUnitSeg', key: 'grain', opts: [['lb', 'lb'], ['kg', 'kg']] },
  ];
  for (const { hostId, segId, key, opts } of defs) {
    const host = $(hostId)?.closest('.recipe__section')?.querySelector('h3');
    if (!host) continue;
    let seg = $(segId);
    if (!seg) {
      ensureUnitsCss();
      seg = document.createElement('span');
      seg.id = segId;
      seg.className = 'bb-units__btns bb-units__btns--head';
      seg.innerHTML = opts.map(([v, label]) => `<button type="button" data-u="${v}">${label}</button>`).join('');
      seg.querySelectorAll('button').forEach((b) =>
        b.addEventListener('click', () => setDisplayUnit(key, b.dataset.u)));
      const wrap = document.createElement('div');
      wrap.className = 'bb-sec-head';
      host.parentNode.insertBefore(wrap, host);
      wrap.appendChild(host);
      wrap.appendChild(seg);
    }
    seg.querySelectorAll('button').forEach((b) =>
      b.classList.toggle('is-on', displayUnits[key] === b.dataset.u));
  }
}

function ensureUnitsCss() {
  if ($('bbUnitsCss')) return;
  const st = document.createElement('style');
  st.id = 'bbUnitsCss';
  st.textContent = UNITS_CSS;
  document.head.appendChild(st);
}

renderSectionUnitSegs();

// The header batch-size input is read as GALLONS by every payload and session path, so
// it never changes meaning. In liters mode it hides behind a metric twin that shows
// the converted value and writes gallons back through the normal change event.
function syncBatchUnitUI() {
  const inp = $('batchSize');
  if (!inp) return;
  const suffix = inp.parentElement?.querySelector('span:not(.bb-units__btns)');
  // The gal/L master switch is its own labeled header field NEXT TO the batch input —
  // embedded inside it, the input's border wrapped the pill and the two were hard to
  // tell apart on mobile (same problem the hop toggle had inside the section heading).
  // Present from first load, BEFORE any recipe exists, so a metric brewer can set up
  // in liters from the start. The suffix span shows the active unit inside the input.
  let seg = $('batchUnitSeg');
  if (!seg) {
    ensureUnitsCss();
    const bfield = document.createElement('div');
    bfield.className = 'field field--bunit';
    const blab = document.createElement('label');
    blab.textContent = 'Units';
    bfield.appendChild(blab);
    seg = document.createElement('span');
    seg.id = 'batchUnitSeg';
    seg.className = 'bb-units__btns';
    seg.innerHTML = '<button type="button" data-bu="gal">gal</button><button type="button" data-bu="l">L</button>';
    bfield.appendChild(seg);
    (inp.closest('.field') || inp.parentElement)?.insertAdjacentElement('afterend', bfield);
    seg.querySelectorAll('button').forEach((b) =>
      b.addEventListener('click', () => setAllUnits(b.dataset.bu === 'l')));
  }
  seg.querySelector('[data-bu="gal"]')?.classList.toggle('is-on', displayUnits.water === 'gal');
  seg.querySelector('[data-bu="l"]')?.classList.toggle('is-on', displayUnits.water === 'l');
  if (suffix) { suffix.hidden = false; suffix.textContent = displayUnits.water === 'l' ? 'L' : 'gal'; }
  // °F/°C rides beside the batch field so a metric brewer can set temperatures BEFORE
  // the first recipe or chat turn — the first model reply then speaks °C too. Its own
  // labeled header field: the batch field is a fixed 130px, and anything squeezed
  // INSIDE .batch-input either clips or slides under the right-pinned gal/L overlay
  // (both measured before landing here). Unlike the gal/L master this only moves the
  // temp unit (the per-category mixing rule); picking L still flips it to °C too.
  let tseg = $('tempUnitSeg');
  if (!tseg) {
    const tfield = document.createElement('div');
    tfield.className = 'field field--temp';
    const lab = document.createElement('label');
    lab.textContent = 'Temp';
    tfield.appendChild(lab);
    tseg = document.createElement('span');
    tseg.id = 'tempUnitSeg';
    tseg.className = 'bb-units__btns';
    tseg.innerHTML = '<button type="button" data-tu="f">°F</button><button type="button" data-tu="c">°C</button>';
    tfield.appendChild(tseg);
    // After the Units field, so the header reads Batch size · Units · Temp · Efficiency.
    (seg.closest('.field') || inp.closest('.field') || inp.parentElement)?.insertAdjacentElement('afterend', tfield);
    tseg.querySelectorAll('button').forEach((b) =>
      b.addEventListener('click', () => setDisplayUnit('temp', b.dataset.tu)));
  }
  tseg.querySelector('[data-tu="f"]')?.classList.toggle('is-on', displayUnits.temp === 'f');
  tseg.querySelector('[data-tu="c"]')?.classList.toggle('is-on', displayUnits.temp === 'c');
  // Brewhouse efficiency, right where brewers look for it (beside the rig and batch
  // pickers) instead of buried in a custom equipment profile. Prefilled from the
  // selected rig; typing a different number overrides it, clearing goes back.
  if (!$('effInput')) {
    const efield = document.createElement('div');
    efield.className = 'field field--eff';
    efield.innerHTML = '<label for="effInput">Efficiency</label>'
      + '<div class="batch-input"><input id="effInput" type="number" step="0.5" min="30" max="100" inputmode="decimal" /><span>%</span></div>';
    tseg.closest('.field')?.insertAdjacentElement('afterend', efield);
    efield.querySelector('input').addEventListener('change', onEffChange);
  }
  syncEffField();
  let twin = $('batchSizeMetric');
  if (displayUnits.water === 'l') {
    if (!twin) {
      twin = document.createElement('input');
      twin.id = 'batchSizeMetric';
      twin.type = 'number';
      twin.step = '0.5';
      twin.min = '2';
      twin.inputMode = 'decimal';
      inp.insertAdjacentElement('afterend', twin);
      twin.addEventListener('change', () => {
        const l = Number(twin.value);
        if (!Number.isFinite(l) || l <= 0) return;
        inp.value = String(Math.round((l / GAL_L) * 100) / 100);
        inp.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
    twin.value = String(Math.round((Number(inp.value) || 5) * GAL_L * 10) / 10);
    twin.hidden = false;
    inp.hidden = true;
  } else {
    if (twin) twin.hidden = true;
    inp.hidden = false;
  }
}
syncBatchUnitUI();

/** Keep the efficiency field showing the number in effect: the override, else the rig's own. */
function syncEffField() {
  const eff = $('effInput');
  if (!eff) return;
  const rigEff = activeEquipProfile()?.mashEfficiency ?? null;
  // Never stomp mid-edit — renders re-run this while the brewer may be typing.
  if (document.activeElement !== eff) eff.value = effOverride ?? rigEff ?? '';
  eff.classList.toggle('is-override', effOverride != null);
  eff.title = effOverride != null && rigEff != null
    ? `Your brewhouse efficiency. Rig default: ${rigEff}% — clear the field to use it.`
    : 'Mash efficiency % — prefilled from the brew system. Type yours to override it.';
}

function onEffChange() {
  const eff = $('effInput');
  const raw = String(eff.value).trim();
  const rigEff = activeEquipProfile()?.mashEfficiency ?? null;
  let next = null;
  if (raw !== '') {
    const n = Number(raw);
    if (!Number.isFinite(n)) { syncEffField(); return; }
    const clamped = Math.min(100, Math.max(30, Math.round(n * 10) / 10));
    // Typing the rig's own number is not an override — it clears cleanly, so a later
    // rig change shows THAT rig's efficiency instead of pinning the old one.
    next = clamped === rigEff ? null : clamped;
  }
  if (next === effOverride) { syncEffField(); return; }
  effOverride = next;
  try {
    if (next == null) localStorage.removeItem('bb:eff');
    else localStorage.setItem('bb:eff', String(next));
  } catch (e) { /* private mode */ }
  syncEffField();
  recalcForEquipmentChange(); // no recipe yet = no-op; otherwise the panel follows immediately
}
